// Client/src/App.js — Single-page Analyzer with intro + deep assessment rendering
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import ValueBreakdown from "./ValueBreakdown";
import Recommendations, { RoadmapChapter, getRoadmapDisplayLabels, hasStructuredRoadmap, isClosedWorkflowInfrastructureCase, isIssuanceLifecycleOptimizationCase, isFundAdministrationOptimizationCase, isPermissionedDefiLendingCase, isWarehouseReceiptWorkflowCase, getFundAdministrationRecommendationCopy, getPermissionedDefiRecommendationCopy, getWarehouseReceiptStakeholderCopy, getWarehouseReceiptMethodologyCopy, getPermissionedDefiMethodologyCopy, getPermissionedDefiRoadmapOperationalCopy } from "./Recommendations";
import AssetClassContext from "./AssetClassContext";
import RiskHeatmap from "./RiskHeatmap";
import { analyzeCase as evaluate, localizeReport, downloadReportPdf } from "./api";
import { normalizeReportLanguageCode, getReportLabels, getReportLanguageName as getI18nReportLanguageName, getScenarioLabUiLabels, getStaticAppendixAModules, getReportProviderDirectoryContent, getReportGlossaryEntries, getReportTranslatedDisclaimerParagraphs, getReportLanguageReferenceNote, localizeMaturityLevel, localizeVerdictLabel, localizeStatic, genericStakeholderCopy, workflowStakeholderCopy, workflowStakeholderTimeHorizonCopy, capitalFormationStakeholderCopy, issuanceLifecycleStakeholderCopy } from "./reportI18n";
import { UI_LANGUAGES, normalizeUiLanguageCode, uiText, getUiFieldTooltip, getUiOptionHelp } from "./uiI18n";
import {
  assertRenderedReportQuality,
  deriveFundLifecycleScope,
  humanizeReportInputLabel,
} from "./reportGrounding";

const REPORT_RENDER_SCHEMA_VERSION = "report-quality-v20-pagination-cover-time-horizon";
const SCENARIO_LAB_PENDING_BASELINE_KEY = "tvac_scenario_lab_pending_baseline_v1";
const SCENARIO_LAB_PENDING_BASELINE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

function relabelAppendix(value, letter) {
  const text = String(value || "");
  if (!text) return text;
  return text
    .replace(/\b(Appendix|Appendiks|Anhang|Annexe|Apéndice)\s+[B-E]\b/i, `$1 ${letter}`)
    .replace(/附录\s*[B-E]/, `附录${letter}`);
}

function getInterfaceLabelBoundaryNote(reportLanguageCode = "en") {
  const code = normalizeReportLanguageCode(reportLanguageCode);
  const copy = {
    en: "Custody, venue, liquidity, disclosure and investor-segment terms above are user-selected TVAC design labels. They do not establish local legal categories, permissions or requirements.",
    da: "Betegnelserne for custody, venue, likviditet, disclosure og investorsegmenter ovenfor er brugerens valgte TVAC-designlabels. De fastlægger ikke lokale juridiske kategorier, tilladelser eller krav.",
    de: "Die vorstehenden Begriffe zu Custody, Venue, Liquidität, Offenlegung und Investorensegmenten sind vom Nutzer ausgewählte TVAC-Designlabels. Sie begründen keine lokalen Rechtskategorien, Erlaubnisse oder Anforderungen.",
    fr: "Les termes ci-dessus relatifs à la custody, au venue, à la liquidité, à l’information et aux segments d’investisseurs sont des labels de conception TVAC sélectionnés par l’utilisateur. Ils n’établissent aucune catégorie, autorisation ou exigence juridique locale.",
    es: "Los términos anteriores sobre custodia, venue, liquidez, disclosure y segmentos de inversores son etiquetas de diseño de TVAC seleccionadas por el usuario. No establecen categorías, permisos ni requisitos jurídicos locales.",
    "zh-Hans": "上述托管、交易环境、流动性、披露和投资者类别术语均为用户选择的TVAC设计标签，并不构成当地法律分类、许可或要求。",
    "pt-BR": "Os termos acima relativos a custódia, ambiente de negociação, liquidez, divulgação e segmentos de investidores são rótulos de desenho selecionados pelo usuário no TVAC. Eles não estabelecem categorias, autorizações ou requisitos jurídicos locais.",
  };
  return copy[code] || copy.en;
}

function getStoredTvacAccessEmail() {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage?.getItem("tvac_access_email") || "";
  } catch {
    return "";
  }
}

const TVAC_API_BASE = process.env.REACT_APP_API_BASE || "";
const TVAC_RENDER_API_BASE = process.env.REACT_APP_API_BASE || "https://tokenlex-api-new.onrender.com";

async function fetchApiReportRenderJob(token) {
  const safeToken = String(token || "").trim();
  if (!safeToken) throw new Error("Missing API report render token.");

  const response = await fetch(
    `${TVAC_RENDER_API_BASE}/api/internal/report-render-jobs/${encodeURIComponent(safeToken)}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    }
  );

  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json || json.ok === false) {
    throw new Error(
      (json && (json.message || json.error)) ||
        `Could not load API report render job (HTTP ${response.status}).`
    );
  }

  return json;
}


function normalizeApiInvestorTypesForForm(value) {
  if (Array.isArray(value)) {
    return value
      .map((x) => String(x || "").trim().toLowerCase())
      .filter(Boolean)
      .map((x) => {
        if (x.includes("professional")) return "professional";
        if (x.includes("accredited") || x.includes("qualified")) return "accredited";
        if (x.includes("retail")) return "retail";
        return x;
      })
      .filter((x, idx, arr) => ["professional", "accredited", "retail"].includes(x) && arr.indexOf(x) === idx);
  }

  if (value && typeof value === "object") {
    const out = [];
    if (value.Professional || value.professional || value.professional_investors) out.push("professional");
    if (value.Accredited || value.accredited || value.qualified_investors) out.push("accredited");
    if (value.Retail || value.retail || value.retail_investors) out.push("retail");
    return out;
  }

  return [];
}

function normalizeApiInvestorTypesForReport(value) {
  if (Array.isArray(value)) {
    const normalized = normalizeApiInvestorTypesForForm(value);
    return {
      Professional: normalized.includes("professional"),
      Accredited: normalized.includes("accredited"),
      Retail: normalized.includes("retail"),
    };
  }

  if (value && typeof value === "object") {
    return {
      ...value,
      Professional: Boolean(value.Professional || value.professional || value.professional_investors),
      Accredited: Boolean(value.Accredited || value.accredited || value.qualified_investors),
      Retail: Boolean(value.Retail || value.retail || value.retail_investors),
    };
  }

  return { Professional: false, Accredited: false, Retail: false };
}

function normalizeApiReportPayloadForDisplay(payload = {}) {
  const p = payload && typeof payload === "object" ? payload : {};
  const text = String(p.text || p.description || "").trim();
  const saleJurisdictions = Array.isArray(p.saleJurisdictions)
    ? p.saleJurisdictions.map((x) => String(x || "").trim()).filter(Boolean)
    : String(p.saleJurisdictions || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  return {
    ...p,
    text,
    description: p.description || text,
    saleJurisdictions,
    marketVenue: p.marketVenue || p.venueType || p.venue || "",
    disclosure: p.disclosure || p.offeringDisclosure || p.disclosureRegime || "",
    investorTypes: normalizeApiInvestorTypesForReport(p.investorTypes),
  };
}

function normalizeApiReportPayloadForForm(payload = {}) {
  const p = normalizeApiReportPayloadForDisplay(payload);
  return {
    ...p,
    saleJurisdictions: Array.isArray(p.saleJurisdictions) ? p.saleJurisdictions.join(", ") : String(p.saleJurisdictions || ""),
    venueType: p.venueType || p.marketVenue || p.venue || "",
    disclosure: p.disclosure || p.offeringDisclosure || p.disclosureRegime || "",
    investorTypes: normalizeApiInvestorTypesForForm(p.investorTypes),
  };
}

const STRUCTURED_INPUT_SELECT_OPTIONS = {
  instrumentType: [
    ["wrapper", "option.instrument.wrapper"],
    ["sole-record", "option.instrument.soleRecord"],
  ],
  custodyModel: [
    ["regulated-custodian", "option.custody.regulated"],
    ["ta/csd", "option.custody.taCsd"],
    ["self-custody", "option.custody.self"],
  ],
  liquidityCadence: [
    ["none", "option.liquidity.none"],
    ["ad-hoc otc", "option.liquidity.otc"],
    ["quarterly", "option.liquidity.quarterly"],
    ["continuous", "option.liquidity.continuous"],
  ],
  venueType: [
    ["ATS/MTF", "option.venue.ats"],
    ["DEX", "option.venue.dex"],
    ["OTC", "option.venue.otc"],
    ["CSD/TA-registered", "option.venue.csd"],
  ],
  settlementModel: [
    ["not-decided", "common.notDecided"],
    ["not-material-now", "option.settlement.notMaterial"],
    ["traditional-fiat-rails", "option.settlement.fiat"],
    ["custodian-bank-settlement", "option.settlement.custodian"],
    ["stablecoin-tokenized-cash", "option.settlement.stablecoin"],
    ["deposit-token-tokenized-deposit", "option.settlement.depositToken"],
    ["T+0 atomic", "option.settlement.atomic"],
    ["T+1/2", "option.settlement.t12"],
    ["off-chain", "option.settlement.offChain"],
  ],
  disclosure: [
    ["KID", "option.disclosure.kid"],
    ["Prospectus", "option.disclosure.prospectus"],
    ["Exempt", "option.disclosure.exempt"],
  ],
  investorTypes: [
    ["professional", "option.investor.professional"],
    ["accredited", "option.investor.accredited"],
    ["retail", "option.investor.retail"],
  ],
};

function getStructuredInputRecord(payload = {}, reportLanguageCode = "en", labels = {}) {
  const p = normalizeApiReportPayloadForDisplay(payload);
  const notSpecified = labels.notSpecified || "Not specified";
  const notApplicable = labels.notApplicable || "Not applicable";
  const selectedInvestors = normalizeApiInvestorTypesForForm(p.investorTypes);
  const textValue = (value) => {
    if (Array.isArray(value)) {
      const items = value.map((item) => String(item || "").trim()).filter(Boolean);
      return items.length ? items.join(", ") : notSpecified;
    }
    const valueText = String(value || "").trim();
    return valueText || notSpecified;
  };
  const selectValue = (fieldKey, rawValue, emptyValues = []) => {
    const normalizedValue = String(rawValue || "").trim();
    const options = STRUCTURED_INPUT_SELECT_OPTIONS[fieldKey] || [];
    const isUnspecified = !normalizedValue || emptyValues.includes(normalizedValue);
    const selectedOption = options.find(([value]) => value === normalizedValue);
    return {
      response: isUnspecified
        ? notSpecified
        : selectedOption
          ? uiText(reportLanguageCode, selectedOption[1])
          : normalizedValue,
      alternatives: options
        .filter(([value]) => isUnspecified || value !== normalizedValue)
        .map(([, labelKey]) => uiText(reportLanguageCode, labelKey))
        .join("; ") || notApplicable,
    };
  };
  const textRow = (labelKey, value) => ({
    field: uiText(reportLanguageCode, labelKey),
    response: textValue(value),
    alternatives: notApplicable,
  });
  const selectRow = (labelKey, fieldKey, value, emptyValues = []) => ({
    field: uiText(reportLanguageCode, labelKey),
    ...selectValue(fieldKey, value, emptyValues),
  });

  const investorOptions = STRUCTURED_INPUT_SELECT_OPTIONS.investorTypes;
  return [
    textRow("field.issuanceJurisdiction", p.issuanceJurisdiction),
    textRow("field.saleJurisdictions", p.saleJurisdictions),
    selectRow("field.instrumentType", "instrumentType", p.instrumentType, ["unknown"]),
    selectRow("field.custodyModel", "custodyModel", p.custodyModel, ["unknown"]),
    selectRow("field.liquidityCadence", "liquidityCadence", p.liquidityCadence),
    selectRow("field.venueType", "venueType", p.venueType || p.marketVenue, ["unknown"]),
    selectRow("field.settlementModel", "settlementModel", p.settlementModel),
    selectRow("field.disclosure", "disclosure", p.disclosure),
    textRow("field.transferRestrictions", p.transferRestrictions),
    textRow("field.taxNotes", p.taxNotes),
    {
      field: uiText(reportLanguageCode, "field.investorTypes"),
      response: selectedInvestors.length
        ? investorOptions
            .filter(([value]) => selectedInvestors.includes(value))
            .map(([, labelKey]) => uiText(reportLanguageCode, labelKey))
            .join("; ")
        : notSpecified,
      alternatives: investorOptions
        .filter(([value]) => !selectedInvestors.includes(value))
        .map(([, labelKey]) => uiText(reportLanguageCode, labelKey))
        .join("; ") || notApplicable,
    },
  ];
}


function normalizeReportBrandingColor(value, fallback = "#ff7a00") {
  const raw = String(value || "").trim();
  const match = raw.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return fallback;
  let hex = match[1].toLowerCase();
  if (hex.length === 3) {
    hex = hex.split("").map((ch) => ch + ch).join("");
  }
  return `#${hex}`;
}

function hexToRgbParts(hexValue, fallback = "255, 122, 0") {
  const hex = normalizeReportBrandingColor(hexValue, "#ff7a00").replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (![r, g, b].every(Number.isFinite)) return fallback;
  return `${r}, ${g}, ${b}`;
}

function safeReportBrandingImageUrl(value) {
  const raw = String(value || "").trim();
  if (!raw || raw.length > 600) return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return "";
    if (url.username || url.password) return "";
    return url.toString();
  } catch {
    return "";
  }
}

function cleanReportBrandingText(value, maxLength = 160) {
  return String(value || "")
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

function normalizeReportBrandingForDisplay(value = {}) {
  const b = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const rawMode = String(b.mode || b.branding_mode || b.brandingMode || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  const explicitWhiteLabel = b.white_label === true || b.whiteLabel === true || rawMode === "white_label";
  const explicitCoBranded = b.co_branded === true || b.coBranded === true || rawMode === "co_branded" || rawMode === "cobranded";

  let mode = explicitWhiteLabel ? "white_label" : explicitCoBranded ? "co_branded" : "tvac";

  const partnerName = cleanReportBrandingText(
    b.partner_name || b.partnerName || b.company_name || b.companyName || b.organization || b.organisation || "",
    120
  );
  const logoUrl = safeReportBrandingImageUrl(b.logo_url || b.logoUrl || b.logo || "");
  const primaryColor = normalizeReportBrandingColor(
    b.primary_color || b.primaryColor || b.brand_color || b.brandColor || "",
    "#ff7a00"
  );
  const accentColor = normalizeReportBrandingColor(
    b.accent_color || b.accentColor || b.secondary_color || b.secondaryColor || primaryColor,
    primaryColor
  );
  const footerText = cleanReportBrandingText(
    b.footer_text || b.footerText || (partnerName ? `Prepared by ${partnerName} using TVAC.` : ""),
    240
  );
  const contactName = cleanReportBrandingText(
    b.contact_name || b.contactName || b.report_contact_name || b.reportContactName || "",
    120
  );
  const contactEmail = cleanReportBrandingText(
    b.contact_email || b.contactEmail || b.support_email || b.supportEmail || "",
    254
  );
  const contactPhone = cleanReportBrandingText(
    b.contact_phone || b.contactPhone || b.support_phone || b.supportPhone || "",
    64
  ).replace(/[^0-9+().\-\s]/g, "");
  const contactUrl = safeReportBrandingImageUrl(
    b.contact_url || b.contactUrl || b.website_url || b.websiteUrl || b.website || ""
  );
  const contactUrlLabel = cleanReportBrandingText(
    b.contact_url_label || b.contactUrlLabel || b.website_label || b.websiteLabel || "",
    100
  );
  const copyrightText = cleanReportBrandingText(
    b.copyright_text || b.copyrightText || "",
    180
  );

  const enabled = Boolean(
    b.enabled === true ||
      explicitWhiteLabel ||
      explicitCoBranded ||
      partnerName ||
      logoUrl ||
      b.primary_color ||
      b.primaryColor ||
      b.brand_color ||
      b.brandColor ||
      b.accent_color ||
      b.accentColor
  );

  if (!enabled) {
    return {
      enabled: false,
      mode: "tvac",
      partner_name: "",
      logo_url: "",
      primary_color: "#ff7a00",
      accent_color: "#ff7a00",
      footer_text: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      contact_url: "",
      contact_url_label: "",
      copyright_text: "",
      white_label: false,
      co_branded: false,
    };
  }

  if (mode === "tvac") mode = "co_branded";

  return {
    enabled: true,
    mode,
    partner_name: partnerName || "Partner",
    logo_url: logoUrl,
    primary_color: primaryColor,
    accent_color: accentColor,
    footer_text: footerText || (partnerName ? `${partnerName} | TVAC methodology` : "TVAC methodology"),
    contact_name: contactName,
    contact_email: contactEmail,
    contact_phone: contactPhone,
    contact_url: contactUrl,
    contact_url_label: contactUrlLabel,
    copyright_text: copyrightText,
    white_label: mode === "white_label",
    co_branded: mode === "co_branded",
  };
}


function getReportBrandingStyleVars(branding) {
  if (!branding?.enabled) return {};
  const primary = normalizeReportBrandingColor(branding.primary_color, "#ff7a00");
  const accent = normalizeReportBrandingColor(branding.accent_color || branding.primary_color, primary);
  const primaryRgb = hexToRgbParts(primary);
  const accentRgb = hexToRgbParts(accent, primaryRgb);
  return {
    "--tvac-report-primary": primary,
    "--tvac-report-accent": accent,
    "--tvac-report-primary-rgb": primaryRgb,
    "--tvac-report-accent-rgb": accentRgb,
    "--tvac-report-primary-bg": `rgba(${primaryRgb}, 0.08)`,
    "--tvac-report-primary-soft": `rgba(${primaryRgb}, 0.30)`,
    "--tvac-report-accent-soft": `rgba(${accentRgb}, 0.22)`,
  };
}

function getBrandInitials(name = "") {
  const cleaned = cleanReportBrandingText(name, 80);
  if (!cleaned) return "P";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 3).map((p) => p[0]).join("").toUpperCase();
  return initials || cleaned.slice(0, 2).toUpperCase();
}

function getReportCoverKicker(branding) {
  if (!branding?.enabled) return "Tokenization Value‑Add Calculator";
  if (branding.mode === "white_label") {
    return `${branding.partner_name} · TVAC methodology`;
  }
  return `${branding.partner_name} · powered by TVAC`;
}

function ReportBrandingStyles({ branding }) {
  if (!branding?.enabled) return null;
  return (
    <style>{`
.print-area.tvac-branded-report .cover-band {
  background: linear-gradient(90deg, var(--tvac-report-primary), var(--tvac-report-accent-soft)) !important;
}
.print-area.tvac-branded-report .formula-brand-box,
.print-area.tvac-branded-report #section-analysis .verdict-panel {
  background: var(--tvac-report-primary-bg) !important;
  border-color: var(--tvac-report-primary-soft) !important;
}
.print-area.tvac-branded-report .formula-brand-box.cover-formula-line {
  background: transparent !important;
  border-color: rgba(0,0,0,0.12) !important;
}
.print-area.tvac-branded-report .report-partner-logo--fallback {
  border-color: var(--tvac-report-primary) !important;
}
.print-area.tvac-branded-report .report-partner-initials {
  color: var(--tvac-report-primary) !important;
}
.print-area.tvac-branded-report .report-brand-cover-note {
  color: #444 !important;
}
@media print {
  .print-area.tvac-branded-report .report-partner-logo--fallback {
    width: 46px !important;
    height: 46px !important;
    border-radius: 10px !important;
  }
  .print-area.tvac-branded-report .report-partner-logo--image {
    width: 86px !important;
    height: 42px !important;
  }
  .print-area.tvac-branded-report .report-partner-initials {
    font-size: 14px !important;
  }
}
`}</style>
  );
}

function ReportCoverBrand({ branding }) {
  if (!branding?.enabled) {
    return (
      <div
        className="tvac-report-logo"
        aria-label="TVAC logo"
        style={{
          width: 110,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0b0f16",
          border: "1px solid #ff7a00",
          borderRadius: 0,
          boxShadow: "0 1px 0 rgba(0,0,0,0.15)",
          flex: "0 0 auto",
        }}
      >
        <span
          className="tvac-report-logo-text"
          style={{
            color: "#ff7a00",
            fontWeight: 800,
            letterSpacing: 1.2,
            fontSize: 22,
            lineHeight: 1,
            fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
          }}
        >
          TVAC
        </span>
      </div>
    );
  }

  const logoUrl = safeReportBrandingImageUrl(branding.logo_url);
  const primary = normalizeReportBrandingColor(branding.primary_color, "#ff7a00");
  const primaryRgb = hexToRgbParts(primary);
  const isImageLogo = Boolean(logoUrl);

  return (
    <div className="report-cover-brand-lockup" style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
      <div
        className={`report-partner-logo ${isImageLogo ? "report-partner-logo--image" : "report-partner-logo--fallback"}`}
        aria-label={`${branding.partner_name} logo`}
        style={{
          width: isImageLogo ? 126 : 68,
          height: isImageLogo ? 54 : 54,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isImageLogo ? "transparent" : `rgba(${primaryRgb}, 0.08)`,
          border: isImageLogo ? "none" : `1px solid ${primary}`,
          borderRadius: isImageLogo ? 0 : 14,
          boxShadow: isImageLogo ? "none" : "0 1px 0 rgba(0,0,0,0.10)",
          overflow: "hidden",
          flex: "0 0 auto",
          padding: 0,
        }}
      >
        {isImageLogo ? (
          <img
            src={logoUrl}
            alt={`${branding.partner_name} logo`}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <span
            className="report-partner-initials"
            style={{
              color: primary,
              fontWeight: 850,
              letterSpacing: 1.1,
              fontSize: 18,
              lineHeight: 1,
              fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
            }}
          >
            {getBrandInitials(branding.partner_name)}
          </span>
        )}
      </div>
    </div>
  );
}

function safeReportContactEmail(value) {
  const email = cleanReportBrandingText(value, 254).toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
}

function getReportContactForDisplay(branding) {
  const tvacDefault = {
    heading: "Contact",
    name: "Astoval",
    email: "support@astoval.com",
    phone: "",
    url: "https://tvacai.com",
    url_label: "tvacai.com",
    copyright: "© 2026 Astoval · All rights reserved.",
    methodology_note: "",
  };

  if (!branding?.enabled) return tvacDefault;

  const partnerName = cleanReportBrandingText(branding.partner_name || "", 120);
  const contactName = cleanReportBrandingText(branding.contact_name || "", 120);
  const contactEmail = safeReportContactEmail(branding.contact_email);
  const contactPhone = cleanReportBrandingText(branding.contact_phone || "", 64)
    .replace(/[^0-9+().\-\s]/g, "");
  const contactUrl = safeReportBrandingImageUrl(branding.contact_url || "");
  const contactUrlLabel =
    cleanReportBrandingText(branding.contact_url_label || "", 100) ||
    (() => {
      try {
        return contactUrl ? new URL(contactUrl).hostname.replace(/^www\./, "") : "";
      } catch {
        return "";
      }
    })();
  const customCopyright = cleanReportBrandingText(branding.copyright_text || "", 180);

  if (branding.mode === "white_label") {
    return {
      heading: "Report contact",
      name: contactName || partnerName,
      email: contactEmail,
      phone: contactPhone,
      url: contactUrl,
      url_label: contactUrlLabel,
      copyright: customCopyright || "TVAC methodology © 2026 Astoval. All rights reserved.",
      methodology_note: "This report is generated using the TVAC methodology.",
    };
  }

  // Co-branded reports must never misrepresent Astoval support details as the
  // partner's contact details. Show only verified partner details when supplied.
  return {
    heading: "Report contact",
    name: contactName || partnerName || "Partner",
    email: contactEmail,
    phone: contactPhone,
    url: contactUrl,
    url_label: contactUrlLabel,
    copyright: customCopyright || "TVAC methodology © 2026 Astoval. All rights reserved.",
    methodology_note: "",
  };
}

function ReportContactBlock({ branding }) {
  const contact = getReportContactForDisplay(branding);
  // The partner signature is shown once in the final contact/sign-off block.
  // It must not be rendered as a fixed footer on every PDF page: fixed print
  // elements can collide with dense report content.
  const partnerSignoff = branding?.enabled
    ? cleanReportBrandingText(branding.footer_text || "", 240)
    : "";
  const email = safeReportContactEmail(contact.email);
  const phone = cleanReportBrandingText(contact.phone || "", 64).replace(/[^0-9+().\-\s]/g, "");
  const url = safeReportBrandingImageUrl(contact.url || "");
  const label = cleanReportBrandingText(contact.url_label || "", 100);

  return (
    <div
      id="section-contact"
      style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: "1px solid #e5e7eb",
        textAlign: "center",
      }}
    >
      <div className="small" style={{ lineHeight: 1.55 }}>
        {contact.heading && <strong>{contact.heading}</strong>}
        {contact.name && <><br />{contact.name}</>}
        {email && <><br /><a href={`mailto:${email}`}>{email}</a></>}
        {phone && <><br /><a href={`tel:${phone.replace(/[^0-9+]/g, "")}`}>{phone}</a></>}
        {url && <><br /><a href={url} target="_blank" rel="noreferrer">{label || url}</a></>}
      </div>

      {partnerSignoff && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {partnerSignoff}
        </div>
      )}

      {contact.methodology_note && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {contact.methodology_note}
        </div>
      )}

      {contact.copyright && (
        <div className="muted small" style={{ marginTop: 10 }}>
          {contact.copyright}
        </div>
      )}
    </div>
  );
}

async function postScenarioLabJson(path, body) {
  const response = await fetch(`${TVAC_API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const message =
      (json && typeof json.error === "string" && json.error) ||
      `Request failed (HTTP ${response.status}).`;
    throw new Error(message);
  }

  return json || {};
}

async function checkScenarioLabAccess(email, baselineAssessmentId = "") {
  return postScenarioLabJson("/api/scenario-lab/access/check", {
    email,
    baselineAssessmentId,
  });
}

async function createScenarioLabCheckout(payload) {
  return postScenarioLabJson("/api/stripe/scenario-lab/create-checkout-session", payload);
}

/* ---------- Simple access gate (Phase 1 – now bypassed, index.js handles login) ---------- */

function AccessGate({ children }) {
  // Adgangskoden håndteres nu i Client/src/index.js.
  // Denne gate returnerer bare children uden ekstra tjek.
  return children;
}

/* ---------- Top formula ---------- */
function FormulaBox({ uiLanguageCode = "en" }) {
  const nbsp = "\u00A0";
  const term = (key) => uiText(uiLanguageCode, key);
  const no = term("formula.no").replace(/ /g, nbsp);
  const cs = term("formula.cs").replace(/ /g, nbsp);
  const rr = term("formula.rr").replace(/ /g, nbsp);
  const tc = term("formula.tc").replace(/ /g, nbsp);
  const nr = term("formula.nr").replace(/ /g, nbsp);
  return (
    <div className="formula-box">
      <div className="formula-label">{term("formula.label")}</div>
      <div className="formula-inner mono">
        <strong>{term("formula.av")}</strong> = ({no} + {cs} + {rr}) – ({tc} + {nr})
      </div>
    </div>
  );
}

/* ---------- UI helpers ---------- */

function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function getRecImproveTocItems(result, payload) {
  if (isIssuanceLifecycleOptimizationCase(payload || {}, result || {})) {
    return [
      "Prove improvement against the existing issuance-programme baseline",
      "Resolve authoritative records, responsibility and DvP recovery",
      "Retire legacy steps and align provider economics with evidence",
      "Execution implication for an issuance and lifecycle pilot",
    ];
  }

  if (isFundAdministrationOptimizationCase(payload || {}, result || {})) {
    const copy = getFundAdministrationRecommendationCopy("en");
    const nonViable = ["non_viable", "non-viable", "non viable", "no-go", "no go", "stop"].includes(String(result?.verdict || "").trim().toLowerCase());
    return [...copy.priorities.map((item) => item.title), nonViable ? "Redesign implication under current structure" : copy.conclusionTitle];
  }

  if (isPermissionedDefiLendingCase(payload || {}, result || {})) {
    const copy = getPermissionedDefiRecommendationCopy("en");
    const nonViable = ["non_viable", "non-viable", "non viable", "no-go", "no go", "stop"].includes(String(result?.verdict || "").trim().toLowerCase());
    return [...copy.priorities.map((item) => item.title), nonViable ? "Redesign implication under current structure" : copy.conclusionTitle];
  }

  if (isClosedWorkflowInfrastructureCase(payload || {}, result || {})) {
    return [
      "Prove measurable operational savings and legacy-process retirement",
      "Prove settlement synchronisation, finality and recovery before scale",
      "Align provider economics and operational governance with measurable outcomes",
      "Execution implication for a controlled institutional workflow",
    ];
  }

  const fs = result?.factor_scores || {};
  const no = Number(fs.new_opportunities || 0);
  const rr = Number(fs.risk_reduction || 0);
  const tc = Number(fs.tokenization_costs || 0);
  const nr = Number(fs.new_risks || 0);

  const verdictKey = String(result?.verdict || "").toLowerCase().trim();

  const nonViableSet = new Set([
    "no-go",
    "no go",
    "non-viable",
    "non viable",
    "stop",
    "non_viable",
  ]);
  const conditionalSet = new Set([
    "conditional_go",
    "conditional go",
    "conditional",
  ]);

  const isNonViable = nonViableSet.has(verdictKey);
  const isConditional = conditionalSet.has(verdictKey);

  const saleJ =
    (Array.isArray(payload?.saleJurisdictions) &&
      payload.saleJurisdictions.filter(Boolean)) ||
    (Array.isArray(payload?.jurisdictions) &&
      payload.jurisdictions.filter(Boolean)) ||
    [];

  const investorTypes = payload?.investorTypes || {};
  const hasRetail =
    investorTypes.retail ||
    investorTypes.Retail ||
    investorTypes["retail_investors"];
  const hasProfessional =
    investorTypes.professional ||
    investorTypes.Professional ||
    investorTypes["professional_investors"];
  const hasAccredited =
    investorTypes.accredited ||
    investorTypes.Accredited ||
    investorTypes["qualified_investors"];

  const payloadText = [
    payload?.text,
    payload?.description,
    payload?.caseDescription,
    payload?.case_description,
    payload?.narrative,
    payload?.caseSummary,
    payload?.case_summary,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  const custodyLc = String(payload?.custodyModel || "").toLowerCase();
  const venueLc = String(payload?.venueType || payload?.marketVenue || "").toLowerCase();
  const settlementLc = String(payload?.settlementModel || "").toLowerCase();
  const liquidityLc = String(payload?.liquidityCadence || "").toLowerCase();

  const fiTreasuryLike =
    /money market fund|mmf|treasury|cash managers|fund units|collateral usability|collateral utility|subscription\/redemption/i.test(
      payloadText
    ) &&
    /ta\/csd/i.test(custodyLc) &&
    /off-chain/i.test(settlementLc) &&
    hasProfessional &&
    !hasRetail;

  const privateCreditLike =
    /private credit|receivables?|trade credit|invoices?|originators?|collections?|true sale|bankruptcy[- ]remote|third[- ]party servicing|receivables? assignment/i.test(
      payloadText
    );

  const quarterlyOtcPrivateCredit =
    privateCreditLike &&
    /quarterly/i.test(liquidityLc) &&
    /otc/i.test(venueLc);

  const deep = result?.deep_assessment || {};
  const legalText = Array.isArray(deep.legal_regulatory)
    ? deep.legal_regulatory.join(" ").toLowerCase()
    : "";

  const hasLegalUnclear =
    legalText.includes("unclear") ||
    legalText.includes("tbd") ||
    legalText.includes("unknown") ||
    legalText.includes("not specified") ||
    legalText.includes("to be defined");

  const noSecondaryLiquidityPlanned = /^(none|no secondary liquidity planned|no secondary liquidity)$/i.test(
    String(payload?.liquidityCadence || "").trim()
  );

  const liquidityUncommitted = (() => {
    if (noSecondaryLiquidityPlanned) return false;
    const keys = [
      "liquidityPathway",
      "liquidity_pathway",
      "secondaryVenue",
      "secondary_venue",
      "tradingVenue",
      "trading_venue",
      "venue",
      "mtf",
      "ats",
      "marketMaking",
      "market_making",
      "marketMaker",
      "market_maker",
    ];
    return !keys.some(
      (k) => typeof payload?.[k] === "string" && payload[k].trim().length >= 6
    );
  })();

  const settlementUnclear = (() => {
    const keys = [
      "settlementModel",
      "settlement_model",
      "cashLeg",
      "cash_leg",
      "paymentRails",
      "payment_rails",
      "pspBank",
      "psp_bank",
      "bankPartner",
      "bank_partner",
    ];
    return !keys.some(
      (k) => typeof payload?.[k] === "string" && payload[k].trim().length >= 6
    );
  })();

  const sections = [
    {
      title: "Rebalance Cost and Risk Allocation Away from the Asset Owner",
      score: (tc >= 4 ? 6 : tc >= 3 ? 4 : 2) + (nr >= 3 ? 1 : 0),
    },
    {
      title: fiTreasuryLike
        ? "Treat Operational Utility and Transfer Controls as a Design Requirement, Not an Assumption"
        : "Treat Distribution and Liquidity as a Design Requirement, Not an Assumption",
      score: fiTreasuryLike
        ? (nr >= 4 ? 4 : nr >= 3 ? 3 : 1) + (settlementUnclear ? 1 : 0)
        : (nr >= 4 ? 6 : nr >= 3 ? 4 : 2) + (liquidityUncommitted ? 3 : 0),
    },
    {
      title: "Simplify Jurisdictional and Investor Scope in the Initial Phase",
      score:
        (hasRetail ? 5 : 0) +
        (saleJ.length >= 3 ? 3 : saleJ.length === 2 ? 1 : 0) +
        (settlementUnclear ? 1 : 0),
    },
    {
      title: "Align Token Design More Tightly with Monetisable Rights",
      score: (no <= 2 ? 3 : 0) + (rr <= 2 ? 1 : 0) + (hasLegalUnclear ? 3 : 0),
    },
  ];

  const ranked = [...sections].sort((a, b) => b.score - a.score);

  const primary = (() => {
    const top2 = ranked.slice(0, 2);
    const third = ranked[2];
    if (!third) return top2;
    const secondScore = top2[1]?.score ?? 0;
    return secondScore - third.score <= 2 ? ranked.slice(0, 3) : top2;
  })();

  const conclusionTitle = isNonViable
    ? "Redesign implication under current structure"
    : isConditional
    ? "Expected Outcome After Restructuring"
    : "Execution implication";

  return [...primary.map((s) => s.title), conclusionTitle];
}

function PrintStyles() {
  return (
    <style>{`
/* -------------------- Report verdict header (screen + print) -------------------- */
.analysis-result-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
}
.analysis-result-title {
  line-height: 1.25;
}
.verdict-panel {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: min(100%, 620px);
  padding: 9px 12px;
  border: 1px solid rgba(255, 122, 0, 0.32);
  background: rgba(255, 122, 0, 0.06);
  border-radius: 12px;
}
.verdict-panel-label {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 1.7px;
  text-transform: uppercase;
  color: #8a4b13;
}
.verdict-panel-badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}
.verdict-panel-badge .badge {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
  max-width: none !important;
  min-width: max-content !important;
}

/* -------------------- PRINT / PDF (professional layout) -------------------- */
@media print {
  @page { margin: 14mm 12mm; }
  html, body { background: #fff !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Print ONLY the report area */
  body * { visibility: hidden; }
  .print-area, .print-area * { visibility: visible; }
  .print-area { position: absolute; left: 0; top: 0; width: 100%; }
    /* Repeated page number on every printed page */
  .print-area .print-page-number {
    position: fixed;
    bottom: 4mm;
    right: 6mm;
    font-size: 10px;
    line-height: 1;
    color: #666 !important;
    background: transparent;
    z-index: 9999;
  }

  .print-area .print-page-number::after {
    content: counter(page);
  }

  /* Hide interactive UI inside the report */
  .print-area .no-print { display: none !important; }

  /* Cards become "report blocks" */
  .print-area .card {
    box-shadow: none !important;
    border: none !important;
    background: transparent !important;
  }

  /* Keep the cover readable (no boxy frame, but preserve white backdrop) */
  .print-area .cover-card {
    background: #fff !important;
  }

  /* Keep the heading and verdict together, but let Analysis Result use the
     remaining page space when the preceding chapter ends early. */
  .print-area #section-analysis .analysis-result-top,
  .print-area #section-analysis .verdict-panel {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .print-area #section-analysis .verdict-panel {
    width: 100% !important;
    max-width: 118mm !important;
    padding: 7px 9px !important;
    border: 1px solid #f0b36d !important;
    background: #fff7ed !important;
    border-radius: 8px !important;
    margin-top: 5px !important;
    margin-bottom: 8px !important;
  }

  .print-area #section-analysis .verdict-panel-label {
    color: #7a3f08 !important;
    font-size: 9.5px !important;
    letter-spacing: 1.15px !important;
    white-space: nowrap !important;
  }

  .print-area #section-analysis .verdict-panel-badge .badge {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    max-width: none !important;
    min-width: max-content !important;
    width: auto !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
/* Normalize hand-built layout blocks in chapters 3 and 4 */
.print-area #section-deep-assessment .card,
.print-area #section-stakeholders .card {
  padding: 0 !important;
  margin: 0 0 8px 0 !important;
}

.print-area #section-deep-assessment .grid-2,
.print-area #section-stakeholders .grid-2 {
  gap: 10px !important;
}

.print-area #section-deep-assessment h4,
.print-area #section-stakeholders h4 {
  margin-top: 4px !important;
  margin-bottom: 4px !important;
}

.print-area #section-deep-assessment ul,
.print-area #section-stakeholders ul {
  margin-top: 2px !important;
  margin-bottom: 6px !important;
}
/* Force linear print flow in chapters 3 and 4 */
.print-area #section-deep-assessment .grid-2,
.print-area #section-stakeholders .grid-2 {
  display: block !important;
}

.print-area #section-deep-assessment .grid-2 > .card,
.print-area #section-stakeholders .grid-2 > div {
  width: 100% !important;
  margin-bottom: 8px !important;
}
  /* Avoid awkward breaks (be careful: too many 'avoid' rules create blank pages) */
  .print-area table,
  .print-area pre,
  .print-area svg,
  .print-area canvas,
  .print-area img {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Allow cards/sections to flow naturally to reduce near-empty pages */
  .print-area .card,
  .print-area section {
    break-inside: auto;
    page-break-inside: auto;
  }

  /* Optional manual page breaks */
  .print-area .page-break {
    break-before: page;
    page-break-before: always;
  }

  
  /* Major chapters that should start on a new page */
  .print-area #section-recommendations,
  .print-area #section-roadmap,
  .print-area #section-glossary,
  .print-area #section-sources,
  .print-area #section-appendix {

    break-before: page;
    page-break-before: always;
  }

  /* Avoid headings stranded at the bottom of a page */
  .print-area h2,
  .print-area h3,
  .print-area h4,
  .print-area h5 {
    break-after: avoid-page;
    page-break-after: avoid;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .print-area p,
  .print-area ul,
  .print-area ol {
    orphans: 2;
    widows: 2;
  }
/* Typography tuning + vertical rhythm */
.print-area .cover-card { margin-bottom: 0 !important; }
.print-area .case-excerpt { font-size: 12px !important; line-height: 1.4 !important; }
.print-area .cover-band { margin-bottom: 14px !important; }
.print-area .report-title { margin: 0 0 6px 0 !important; }
.print-area h2,
.print-area h3,
.print-area h4,
.print-area h5 { color: #000 !important; }

.print-area section > :first-child,
.print-area .card > :first-child {
  margin-top: 0 !important;
}

.print-area section > :last-child,
.print-area .card > :last-child {
  margin-bottom: 0 !important;
}

.print-area h2 { margin: 20px 0 10px 0 !important; }
.print-area h3 { margin: 18px 0 8px 0 !important; }
.print-area h4 { margin: 14px 0 6px 0 !important; }
.print-area h5 { margin: 8px 0 6px 0 !important; }

.print-area ul + h3,
.print-area ul + h4,
.print-area ol + h3,
.print-area ol + h4 {
  margin-top: 20px !important;
}

.print-area p {
  margin: 0 0 8px 0 !important;
  line-height: 1.35 !important;
}

.print-area .small {
  line-height: 1.35 !important;
}

.print-area ul,
.print-area ol {
  margin: 2px 0 6px 0 !important;
  padding-left: 18px !important;
}

.print-area li {
  margin: 0 0 4px 0 !important;
  line-height: 1.35 !important;
}

.print-area li:last-child {
  margin-bottom: 0 !important;
}

.print-area pre {
  margin: 8px 0 10px 0 !important;
}

.print-area .result-grid > * {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.print-area .md-spacer {
  height: 8px !important;
}

  /* TOC formatting */
  .print-area .toc { margin-top: 6px !important; }
  .print-area .toc a { text-decoration: none; color: #000 !important; }
  .print-area .toc ul { margin: 4px 0 0 16px !important; padding: 0 !important; }
  .print-area .toc li { margin: 2px 0 !important; line-height: 1.28 !important; }
  .print-area .toc .toc-level-2 { margin-left: 14px !important; }
  .print-area .toc .toc-level-3 { margin-left: 28px !important; }
    .print-area #section-appendix .card,
  .print-area #section-appendix .appendix-verbatim {
    overflow: visible !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .print-area #section-appendix .appendix-verbatim {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    border-radius: 0 !important;
  }

  .print-area #section-appendix .appendix-verbatim p {
    margin: 0 0 12px 0 !important;
  }

  .print-area #section-appendix .appendix-verbatim p:last-child {
    margin-bottom: 0 !important;
  }

  .print-area #section-appendix table.structured-input-record {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .print-area #section-appendix table.structured-input-record tr {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  /* Scenario Lab Comparison Report print mode */
  body.scenario-comparison-print * {
    visibility: hidden !important;
  }

  body.scenario-comparison-print .print-area,
  body.scenario-comparison-print .print-area * {
    visibility: hidden !important;
  }

  /* The comparison mini-report is rendered inside the Scenario Lab app panel,
     which normally has .no-print so it never appears in ordinary report PDFs.
     During dedicated mini-report printing, the panel ancestor must be displayable;
     we collapse its layout so the hidden app UI does not create blank pages. */
  body.scenario-comparison-print .scenario-lab-panel {
    display: block !important;
    visibility: hidden !important;
    height: 0 !important;
    min-height: 0 !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
    border: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  body.scenario-comparison-print .scenario-comparison-print-area,
  body.scenario-comparison-print .scenario-comparison-print-area * {
    visibility: visible !important;
  }

  body.scenario-comparison-print .scenario-comparison-print-area {
    display: block !important;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background: #fff !important;
  }

  body.scenario-comparison-print .scenario-comparison-print-area .card {
    box-shadow: none !important;
    background: transparent !important;
  }

  body.scenario-comparison-print .scenario-comparison-page-break {
    break-before: page;
    page-break-before: always;
  }

  body.scenario-comparison-print .scenario-comparison-document h1,
  body.scenario-comparison-print .scenario-comparison-document h2,
  body.scenario-comparison-print .scenario-comparison-document h3,
  body.scenario-comparison-print .scenario-comparison-document h4 {
    color: #000 !important;
    break-after: avoid-page;
    page-break-after: avoid;
  }

  body.scenario-comparison-print .scenario-comparison-document p,
  body.scenario-comparison-print .scenario-comparison-document li,
  body.scenario-comparison-print .scenario-comparison-document td {
    line-height: 1.38 !important;
  }

}

/* Screen-only helpers for nicer TOC */
.toc a.toc-link { cursor: pointer; }
.toc .toc-level-2 { margin-left: 14px; }
.toc .toc-level-3 { margin-left: 28px; }
/* --- Report cover & appendix helpers --- */
.cover-band{
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,122,0,0.95), rgba(255,122,0,0.20));
  margin-top: 18px;
  margin-bottom: 10px;
}
.cover-card h2{ font-size: 30px; }
.case-excerpt{
  margin-top: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f7f7f7;
  border: 1px solid #eee;
  white-space: pre-wrap;
}
.cover-formula-block{
  margin-top: 16px;
  max-width: 880px;
  line-height: 1.4;
}
.cover-formula-line{
  margin-top: 5px;
  padding: 7px 0;
  border: 0;
  border-top: 1px solid rgba(0,0,0,0.12);
  border-bottom: 1px solid rgba(0,0,0,0.12);
  border-radius: 0;
  background: transparent !important;
  font-weight: 700;
}
.cover-gray-blocks{
  display: grid;
  gap: 8px;
  margin-top: 10px;
  max-width: 880px;
}
.cover-design-box,
.cover-case-block{
  padding: 9px 11px;
  border-radius: 9px;
  background: #f7f7f7;
  border: 1px solid #e8e8e8;
  line-height: 1.4;
}
.cover-case-block .case-excerpt{
  padding: 0;
  border: 0;
  background: transparent;
}
.case-verbatim{
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid #eee;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.45;
}
.glossary-grid{
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}
.glossary-grid td, .glossary-grid th{
  border: 1px solid #eee;
  padding: 8px 10px;
  vertical-align: top;
}
.glossary-grid th{
  background: #f7f7f7;
  text-align: left;
}
.structured-input-record{
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 12px;
}
.structured-input-record td, .structured-input-record th{
  border: 1px solid #eee;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.structured-input-record th{
  background: #f7f7f7;
}
@media print{
  .cover-band{ border-radius: 0; height: 14px; margin-top: 0; }
  .cover-card{ page-break-after: auto; }
}

/* ---- Patch: print pagination + premium verdict card ---- */
@media print {
  /* Page 1 should be the cover only, without spilling a few disclaimer lines onto page 2. */
  .print-area #section-cover {
    break-after: page !important;
    page-break-after: always !important;
  }

  .print-area #section-cover .cover-card {
    break-after: auto !important;
    page-break-after: auto !important;
    padding: 10mm 10mm 8mm !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .print-area #section-cover .cover-band {
    height: 6px !important;
    margin: 0 0 5mm 0 !important;
  }

  .print-area #section-cover .tvac-report-logo {
    width: 72px !important;
    height: 46px !important;
  }

  .print-area #section-cover .tvac-report-logo-text {
    font-size: 15px !important;
    letter-spacing: 0.9px !important;
  }

  .print-area #section-cover .cover-left-row {
    gap: 9px !important;
  }

  .print-area #section-cover .cover-title-block h1,
  .print-area #section-cover .report-title {
    font-size: 38px !important;
    line-height: 1.02 !important;
    margin: 0 0 2px 0 !important;
  }

  .print-area #section-cover .cover-formula-block {
    margin-top: 4mm !important;
    line-height: 1.28 !important;
  }

  .print-area #section-cover .cover-formula-line {
    margin-top: 2mm !important;
    padding: 2mm 0 !important;
    background: transparent !important;
    border-color: #d5d5d5 !important;
  }

  .print-area #section-cover .cover-gray-blocks {
    gap: 2mm !important;
    margin-top: 3mm !important;
  }

  .print-area #section-cover .cover-design-box,
  .print-area #section-cover .cover-case-block {
    padding: 2.2mm 2.8mm !important;
  }

  .print-area #section-cover .cover-title-block .muted,
  .print-area #section-cover .small,
  .print-area #section-cover .muted {
    font-size: 9px !important;
    line-height: 1.22 !important;
  }

  .print-area #section-cover .case-excerpt {
    font-size: 8.7px !important;
    line-height: 1.23 !important;
    padding: 5px 7px !important;
    margin-top: 4px !important;
    max-height: 28mm !important;
    overflow: hidden !important;
  }

  .print-area #section-cover .cover-disclaimer {
    margin-top: 4.5mm !important;
    padding-top: 3mm !important;
    border-top: 1px solid #e6e6e6 !important;
    max-width: 148mm !important;
    color: #444 !important;
    font-size: 7.2px !important;
    line-height: 1.24 !important;
  }

  .print-area #section-cover .cover-disclaimer p,
  .print-area #section-cover .cover-disclaimer p.small,
  .print-area #section-cover .cover-disclaimer .small,
  .print-area #section-cover .cover-disclaimer strong {
    font-size: 7.2px !important;
    line-height: 1.24 !important;
    color: #444 !important;
  }

  .print-area #section-cover .cover-disclaimer p {
    margin: 0 0 2.2px 0 !important;
  }

  /* Page 2 should be the Table of Contents, and the Executive Summary should start after it. */
  .print-area .toc-card {
    break-after: page !important;
    page-break-after: always !important;
    padding: 7mm 9mm !important;
    margin: 0 !important;
  }

  .print-area .toc-card > strong {
    display: block !important;
    font-size: 14px !important;
    margin-bottom: 5px !important;
  }

  .print-area .toc-card .toc {
    font-size: 8.8px !important;
    line-height: 1.11 !important;
    margin: 2px 0 0 0 !important;
    padding-left: 14px !important;
  }

  .print-area .toc-card .toc ul,
  .print-area .toc-card .toc-sub {
    margin: 1px 0 1px 12px !important;
    padding-left: 8px !important;
  }

  .print-area .toc-card .toc li {
    margin: 0 0 1.2px 0 !important;
    line-height: 1.1 !important;
  }

  .print-area .toc-card .toc-level-2 { margin-left: 8px !important; }
  .print-area .toc-card .toc-level-3 { margin-left: 16px !important; }
}

.verdict-panel {
  display: block;
  width: 100%;
  max-width: 720px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 122, 0, 0.34);
  background: rgba(255, 122, 0, 0.055);
  border-radius: 13px;
}

.verdict-panel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.verdict-panel-metrics {
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid rgba(0,0,0,0.08);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  line-height: 1.25;
  color: #4b5563;
}

.verdict-panel-separator {
  color: #9ca3af;
}

.cover-disclaimer,
.cover-disclaimer p {
  font-size: 12px;
  line-height: 1.42;
  color: #667085;
}

.verdict-panel--viable {
  border-color: rgba(26, 127, 55, 0.35);
  background: rgba(26, 127, 55, 0.055);
}

.verdict-panel--nonviable {
  border-color: rgba(209, 36, 47, 0.35);
  background: rgba(209, 36, 47, 0.055);
}

@media print {
  .print-area #section-analysis .verdict-panel {
    width: 100% !important;
    max-width: none !important;
    padding: 9px 11px !important;
    border-radius: 9px !important;
    margin: 5px 0 9px 0 !important;
  }

  .print-area #section-analysis .verdict-panel-row {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
  }

  .print-area #section-analysis .verdict-panel-metrics {
    font-size: 9.2px !important;
    margin-top: 6px !important;
    padding-top: 5px !important;
    gap: 10px !important;
    color: #555 !important;
  }

  .print-area #section-analysis .verdict-panel-label {
    font-size: 8.8px !important;
    letter-spacing: 1.0px !important;
  }
}
`}</style>
  );
}

// Map backend verdicts ("go", "no-go", "conditional_go") til tre faste labels
function Badge({ verdict, reportLanguageCode = "en" }) {
  const labels = getReportLabels(reportLanguageCode);
  const v = String(verdict || "").toLowerCase().trim();

  let label = "Conditional Go";
  let cls = "badge badge-borderline";

  const conditionalSet = new Set(["conditional_go", "conditional go", "conditional"]);
  const viableSet = new Set(["go", "viable", "go / viable"]);
  const nonViableSet = new Set([
    "no-go",
    "no go",
    "non-viable",
    "non viable",
    "stop",
    "non_viable",
  ]);

  if (conditionalSet.has(v)) {
    label = labels.verdictConditional;
    cls = "badge badge-borderline";
  } else if (viableSet.has(v)) {
    label = labels.verdictViable;
    cls = "badge badge-viable";
  } else if (nonViableSet.has(v)) {
    label = labels.verdictNonViable;
    cls = "badge badge-nonviable";
  }

  return <span className={cls}>{label}</span>;
}

function getVerdictPanelTone(verdict) {
  const v = String(verdict || "").toLowerCase().trim();
  if (["go", "viable", "go / viable"].includes(v)) return "verdict-panel--viable";
  if (["no-go", "no go", "non-viable", "non viable", "stop", "non_viable"].includes(v)) return "verdict-panel--nonviable";
  return "verdict-panel--conditional";
}

function formatReportNumber(value, { signed = false } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const formatted = n.toFixed(2);
  if (signed && n > 0) return `+${formatted}`;
  return formatted;
}

function printWithTemporaryBlankBrowserTitle(payload = {}) {
  // Browser header/footer rows are controlled by the print dialog. If users leave
  // "Headers and footers" enabled, Chrome/Edge may still print date/URL/page numbers.
  // This helper only prevents the report title from appearing in that browser-generated header.
  const reportElement = document.querySelector(".print-area");
  assertRenderedReportQuality(reportElement?.innerText || "", payload);

  const previousTitle = document.title;
  const restore = () => {
    document.title = previousTitle || "Tokenization Value-Add Calculator";
    window.removeEventListener("afterprint", restore);
  };
  try {
    document.title = "TVAC Assessment Report";
    window.addEventListener("afterprint", restore);
    window.print();
    window.setTimeout(restore, 1200);
  } catch (e) {
    restore();
    throw e;
  }
}

function containsYieldReturnSignal(source) {
  const text = JSON.stringify(source || {})
    .toLowerCase()
    .replace(/\u00a0/g, " ");

  return (
    /\b(yield|coupon|interest\s+rate|fixed\s+interest|annual\s+interest|monthly\s+interest|expected\s+return|projected\s+return|target\s+return|irr|apy|p\.a\.|per\s+annum|return\s+on\s+investment)\b/i.test(text) ||
    /\b\d+(?:\.\d+)?\s*%\s*(?:p\.a\.|per\s+annum|annual(?:ly)?|yield|coupon|interest|return|irr|apy)\b/i.test(text)
  );
}

function ScopeNoteBox({ labels, showYieldNote = false, compact = false }) {
  const boxStyle = {
    marginTop: compact ? 8 : 10,
    padding: compact ? "8px 10px" : "10px 12px",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 10,
    background: "rgba(255,122,0,0.06)",
    lineHeight: 1.4,
  };

  if (compact) {
    const compactCopy = {
      en: "Scope reminder: the verdict concerns tokenization added value only; the full interpretation and yield boundaries are stated in the Executive Summary.",
      da: "Scope-påmindelse: Verdictet vedrører kun tokeniseringens Added Value; de fulde fortolknings- og yieldgrænser står i Executive Summary.",
      de: "Hinweis zum Umfang: Das Urteil betrifft nur den Added Value der Tokenisierung; die vollständigen Auslegungs- und Renditegrenzen stehen in der Executive Summary.",
      fr: "Rappel de périmètre : le verdict porte uniquement sur l’Added Value de la tokenisation ; les limites complètes d’interprétation et de rendement figurent dans l’Executive Summary.",
      es: "Recordatorio de alcance: el veredicto se refiere únicamente al Added Value de la tokenización; los límites completos de interpretación y rentabilidad figuran en el Executive Summary.",
      "zh-Hans": "范围提示：本结论仅涉及代币化的Added Value；完整的解释边界和收益率边界见Executive Summary。",
      "pt-BR": "Lembrete de escopo: o veredicto se refere apenas ao Valor Agregado da tokenização; os limites completos de interpretação e de rentabilidade estão descritos no Resumo Executivo.",
    };
    const code = normalizeReportLanguageCode(labels.languageCode || "en");
    return <p className="muted small" style={{ margin: "8px 0 0 0" }}>{compactCopy[code] || compactCopy.en}</p>;
  }

  return (
    <div className="small" style={boxStyle}>
      <p style={{ margin: 0 }}>
        <strong>{labels.verdictScopeNoteTitle || "Important interpretation note"}:</strong>{" "}
        {labels.verdictScopeNoteText ||
          "This verdict concerns tokenization added value only. It is not an investment, credit, yield, valuation, legal, tax, or suitability assessment."}
      </p>
      {showYieldNote && (
        <p style={{ margin: "8px 0 0 0" }}>
          <strong>{labels.yieldNeutralityNoteTitle || "Yield neutrality note"}:</strong>{" "}
          {labels.yieldNeutralityNoteText ||
            "The stated coupon, yield, interest rate, or expected return has not been treated as tokenization value in itself. TVAC does not validate projected returns or investor suitability."}
        </p>
      )}
    </div>
  );
}

function Tip({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="tip-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span className="tip">
        {label}
        <span className="dot" aria-label="info">
          i
        </span>
      </span>
      {open && <span className="bubble">{children}</span>}
    </span>
  );
}

/* Kort forklaring af hvert felt (tooltip-tekst) */
const L = {
  instrument:
    "How the security legally lives: is the token just a digital wrapper recorded by a CSD/transfer agent, or is the token itself the primary legal record of ownership?",
  liquidity:
    "How often holders can trade on a secondary market: none, ad-hoc OTC, periodic windows (e.g. quarterly auctions), or continuous trading.",
  transfer:
    "Legal/technical limits on who can hold/transfer (e.g., whitelist, 144A/Reg S, lock-ups, holding periods).",
  venue:
    "Where secondary trading happens (if any): ATS/MTF, permissioned DEX, OTC, or a registrar/CSD facility.",
  disclosure:
    "Which independently verified offering, disclosure and investor-protection route applies in the stated jurisdiction and investor scope?",
  tax: "Anything tax-relevant you already know (optional): withholding, stamp duty/transfer tax, treaty considerations, local vs. foreign investors, etc.",
  jurisdictionLens:
    "Choose the primary jurisdiction lens for local regulatory references in the report. " +
    "This is not necessarily the same as all sale jurisdictions. Use the lens that best matches " +
    "where the offering is structured or marketed and where key compliance decisions are anchored " +
    "(often the issuer’s main jurisdiction or the main target market). " +
    "If multiple jurisdictions apply, select the one that matters most for your intended distribution. " +
    "If you are unsure or want a neutral report, choose General (no local references).",
  settlement:
    "Choose the closest description of how the payment / cash leg will be handled. If the project is early-stage, it is acceptable to choose “Not decided yet”; TVAC will treat this as a design-readiness gap, not as a failure by itself.",
  custody:
    "Who actually safekeeps the positions: a regulated custodian, a transfer agent/CSD register, or the investors themselves in self-custody wallets.",
  investorTypes:
    "Who you actually target: professional institutions, accredited/qualified investors, and/or mass-market retail investors.",
};

/* Ekstra forklaring pr. dropdown-valg (vises under feltet) */
const REPORT_LANGUAGES = [
  { code: "en", label: "English", name: "English" },
  { code: "de", label: "German", name: "German" },
  { code: "fr", label: "French", name: "French" },
  { code: "es", label: "Spanish", name: "Spanish" },
  { code: "da", label: "Danish", name: "Danish" },
  { code: "zh-Hans", label: "Chinese — Simplified", name: "Simplified Chinese" },
  { code: "pt-BR", label: "Português (Brasil)", name: "Brazilian Portuguese" },
];

function parseReportLanguageFeatureFlag(value, fallbackCodes) {
  const supportedCodes = new Set(REPORT_LANGUAGES.map((language) => language.code));
  const configuredCodes = String(value || "")
    .split(",")
    .map((code) => code.trim())
    .filter((code) => supportedCodes.has(code));

  return new Set(configuredCodes.length ? configuredCodes : fallbackCodes);
}

// Public availability and internal QA availability are intentionally separate.
// Render can change either comma-separated list and redeploy without a code change.
const PUBLIC_REPORT_LANGUAGE_CODES = parseReportLanguageFeatureFlag(
  process.env.REACT_APP_PUBLIC_REPORT_LANGUAGES,
  ["en"]
);
const INTERNAL_REPORT_LANGUAGE_CODES = parseReportLanguageFeatureFlag(
  process.env.REACT_APP_INTERNAL_REPORT_LANGUAGES,
  REPORT_LANGUAGES.map((language) => language.code)
);

function reportLanguageIsAvailable(code, internalAccess = false) {
  return (internalAccess ? INTERNAL_REPORT_LANGUAGE_CODES : PUBLIC_REPORT_LANGUAGE_CODES).has(code);
}

function getReportLanguageName(code) {
  return getI18nReportLanguageName(code);
}

function polishReportLabels(labels, code = "en") {
  const lang = String(code || "en");
  const out = { ...labels };
  if (lang === "da") {
    out.coverTitle = "TVAC-vurderingsrapport";
    out.coverSubtitle = "En case-specifik vurdering af egnethed til tokenisering og værdiskabelse.";
    out.costsRisksMinus = "Omkostninger og risici (minus)";
    out.phase1 = "Fase 1 — Forberedelse og gennemførlighed";
    out.phase3 = "Fase 3 — Opbygning, integration og leverandøropkobling";
    out.printSavePdf = "Udskriv / gem som PDF";
    out.exportReportHelp = "Download den færdige, paginerede PDF med faste sidenumre.";
    out.formulaText = "Merværdi = (Nye muligheder (NO) + Omkostningsbesparelser (CS) + Risikoreduktion (RR)) – (Tokeniseringsomkostninger (TC) + Nye risici (NR))";
    out.translatedDisclaimerHeading = "Vejledende dansk oversættelse";
    out.englishDisclaimerHeading = "Original engelsk ansvarsfraskrivelse (har forrang)";
  } else if (lang === "de") {
    out.coverTitle = "TVAC-Bewertungsbericht";
    out.formulaText = "Mehrwert = (Neue Chancen (NO) + Kosteneinsparungen (CS) + Risikominderung (RR)) – (Tokenisierungskosten (TC) + Neue Risiken (NR))";
    out.plusFormula = "Plus (NO + CS + RR)";
    out.minusFormula = "Minus (TC + NR)";
    out.addedValueFormula = "Mehrwert (Plus – Minus)";
    out.printSavePdf = "Drucken / als PDF speichern";
    out.translatedDisclaimerHeading = "Unverbindliche deutsche Übersetzung";
    out.englishDisclaimerHeading = "Englischer Original-Haftungsausschluss (maßgeblich)";
  } else if (lang === "es") {
    out.formulaText = "Valor añadido = (Nuevas oportunidades (NO) + Ahorro de costes (CS) + Reducción de riesgos (RR)) – (Costes de tokenización (TC) + Nuevos riesgos (NR))";
    out.plusFormula = "Plus (NO + CS + RR)";
    out.minusFormula = "Minus (TC + NR)";
    out.addedValueFormula = "Valor añadido (plus – minus)";
    out.printSavePdf = "Imprimir / guardar como PDF";
    out.translatedDisclaimerHeading = "Traducción orientativa al español";
    out.englishDisclaimerHeading = "Descargo de responsabilidad original en inglés (prevalece)";
  } else if (lang === "zh-Hans") {
    out.formulaText = "增值 =（新机会 (NO) + 成本节约 (CS) + 风险降低 (RR)）–（代币化成本 (TC) + 新风险 (NR)）";
    out.plusFormula = "正向因素（NO + CS + RR）";
    out.minusFormula = "负向因素（TC + NR）";
    out.addedValueFormula = "增值（正向 – 负向）";
    out.printSavePdf = "打印 / 另存为 PDF";
    out.translatedDisclaimerHeading = "中文参考译文";
    out.englishDisclaimerHeading = "英文原始免责声明（以英文为准）";
  } else if (lang === "fr") {
    out.formulaText = "Valeur ajoutée = (Nouvelles opportunités (NO) + Économies de coûts (CS) + Réduction des risques (RR)) – (Coûts de tokenisation (TC) + Nouveaux risques (NR))";
    out.plusFormula = "Plus (NO + CS + RR)";
    out.minusFormula = "Moins (TC + NR)";
    out.addedValueFormula = "Valeur ajoutée (plus – moins)";
    out.printSavePdf = "Imprimer / enregistrer en PDF";
    out.translatedDisclaimerHeading = "Traduction française à titre indicatif";
    out.englishDisclaimerHeading = "Clause de non-responsabilité originale en anglais (fait foi)";
  } else if (lang === "pt-BR") {
    out.coverTitle = "Relatório de avaliação TVAC";
    out.coverSubtitle = "Uma avaliação específica do caso sobre a adequação da tokenização e a criação de valor.";
    out.formulaText = "Valor Agregado = (Novas Oportunidades (NO) + Economia de Custos (CS) + Redução de Riscos (RR)) – (Custos de Tokenização (TC) + Novos Riscos (NR))";
    out.plusFormula = "Plus (NO + CS + RR)";
    out.minusFormula = "Minus (TC + NR)";
    out.addedValueFormula = "Valor Agregado (Plus – Minus)";
    out.printSavePdf = "Imprimir / salvar como PDF";
    out.translatedDisclaimerHeading = "Tradução para português do Brasil (para conveniência)";
    out.englishDisclaimerHeading = "Aviso legal original em inglês (prevalece em caso de divergência)";
  } else {
    out.formulaText = "Added Value = (New Opportunities (NO) + Cost Savings (CS) + Risk Reduction (RR)) – (Tokenization Costs (TC) + New Risks (NR))";
    out.addedValueFormula = "Added Value (Plus – Minus)";
    out.theoreticalRange = "Theoretical range";
    out.forThisCase = "For this case";
    out.calculatorFormulaIntro = "The calculator scores each of the five components in your case and combines them into a single Added Value number using the formula below.";
    out.printSavePdf = "Download paginated PDF";
    out.exportReportHelp = "Download the finished PDF with fixed Page X / Y numbering.";
    out.translatedDisclaimerHeading = "Translated disclaimer (for convenience)";
    out.englishDisclaimerHeading = "English original disclaimer";
  }
  if (lang === "da") {
    out.executiveContextText = "Dette ledelsesresumé sammenfatter TVACs dybe vurdering i en struktureret form: hvad vurderingen betyder, hvad der driver den, og hvad der skal være opfyldt, for at casen holder.";
    out.executiveObjectiveText = "Stress-test af, om tokenisering skaber reel merværdi for denne struktur, og identificering af de få handlinger, der mest sandsynligt forbedrer gennemførlighed og resultat.";
    out.computeAddedValue = "Sådan beregnes merværdi";
    out.overallVerdict = "Samlet vurdering";
    out.overallAddedValue = "Samlet merværdi";
    out.theoreticalRange = "Teoretisk interval";
    out.forThisCase = "For denne case";
    out.calculatorFormulaIntro = "TVAC scorer de fem komponenter i casen og samler dem i ét merværdital ved hjælp af formlen nedenfor.";
  } else if (lang === "de") {
    out.appendixB = "Anhang B — TVAC-Anbieterverzeichnis (illustrativ)";
    out.executiveContextText = "Diese Zusammenfassung verdichtet die TVAC-Tiefenbewertung zu einer strukturierten Sicht: was das Urteil bedeutet, was es antreibt und welche Bedingungen erfüllt sein müssen, damit der Fall trägt.";
    out.computeAddedValue = "So berechnen wir den Mehrwert";
    out.theoreticalRange = "Theoretischer Bereich";
    out.forThisCase = "Für diesen Fall";
    out.calculatorFormulaIntro = "TVAC bewertet die fünf Komponenten des Falls und verdichtet sie anhand der folgenden Formel zu einem Mehrwertwert.";
  } else if (lang === "fr") {
    out.appendixB = "Annexe B — Répertoire des prestataires TVAC (illustratif)";
    out.executiveContextText = "Ce résumé exécutif condense l’évaluation approfondie de TVAC en une vue structurée : ce que signifie la conclusion, ce qui la motive et ce qui doit être vrai pour que le cas tienne.";
    out.computeAddedValue = "Comment nous calculons la valeur ajoutée";
    out.theoreticalRange = "Fourchette théorique";
    out.forThisCase = "Pour ce cas";
    out.calculatorFormulaIntro = "TVAC note les cinq composantes du cas et les combine en une valeur ajoutée unique à l’aide de la formule ci-dessous.";
  } else if (lang === "es") {
    out.appendixB = "Apéndice B — Directorio de proveedores TVAC (ilustrativo)";
    out.executiveContextText = "Este resumen ejecutivo condensa la evaluación profunda de TVAC en una visión estructurada: qué significa el resultado, qué lo impulsa y qué debe cumplirse para que el caso sea sostenible.";
    out.computeAddedValue = "Cómo calculamos el valor añadido";
    out.theoreticalRange = "Rango teórico";
    out.forThisCase = "Para este caso";
    out.calculatorFormulaIntro = "TVAC puntúa los cinco componentes del caso y los combina en una única cifra de valor añadido mediante la fórmula siguiente.";
  } else if (lang === "zh-Hans") {
    out.appendixB = "附录B——TVAC服务提供商目录（示例）";
    out.executiveContextText = "本执行摘要将TVAC深度评估浓缩为结构化视图：结论意味着什么、由什么驱动，以及哪些条件必须成立。";
    out.computeAddedValue = "增值的计算方式";
    out.theoreticalRange = "理论范围";
    out.forThisCase = "本案例";
    out.calculatorFormulaIntro = "TVAC对本案例的五个组成部分进行评分，并使用下列公式合成为一个增值数值。";
  } else if (lang === "pt-BR") {
    out.appendixB = "Apêndice B — Diretório de prestadores TVAC (ilustrativo)";
    out.executiveContextText = "Este resumo executivo condensa a avaliação aprofundada do TVAC em uma visão estruturada: o que o resultado significa, o que o impulsiona e quais condições precisam ser atendidas para que o caso se sustente.";
    out.computeAddedValue = "Como calculamos o Valor Agregado";
    out.theoreticalRange = "Faixa teórica";
    out.forThisCase = "Para este caso";
    out.calculatorFormulaIntro = "O TVAC pontua os cinco componentes do caso e os combina em um único valor de Valor Agregado usando a fórmula abaixo.";
  }
  return out;
}

function localizeMaturityNoteText(value, code = "en") {
  const s = String(value || "").trim();
  if (!s || code === "en") return s;
  const structured = "This assessment is based on a partially defined design. The case is sufficiently clear for a meaningful case-specific evaluation, but several structural choices remain open.";
  if (s !== structured) return s;
  const map = {
    da: "Vurderingen bygger på et delvist defineret design. Casen er klar nok til en meningsfuld, case-specifik vurdering, men flere strukturelle valg er fortsat åbne.",
    de: "Diese Bewertung basiert auf einem teilweise definierten Design. Der Fall ist ausreichend klar für eine sinnvolle fallspezifische Bewertung, aber mehrere strukturelle Entscheidungen sind noch offen.",
    fr: "Cette évaluation repose sur une conception partiellement définie. Le cas est suffisamment clair pour une évaluation spécifique significative, mais plusieurs choix structurels restent ouverts.",
    es: "Esta evaluación se basa en un diseño parcialmente definido. El caso es suficientemente claro para una evaluación específica significativa, pero varias decisiones estructurales siguen abiertas.",
    "zh-Hans": "本评估基于部分定义的设计。该案例已足够清晰，可进行有意义的案例专属评估，但若干结构性选择仍未最终确定。",
    "pt-BR": "Esta avaliação se baseia em um desenho parcialmente definido. O caso está suficientemente claro para uma avaliação específica e significativa, mas várias escolhas estruturais ainda permanecem em aberto.",
  };
  return map[code] || s;
}

function localizeStopCriterionText(value, code = "en") {
  const s = String(value || "").trim();
  if (!s || code === "en") return s;
  const canonical = s.replace(/^STOP:\s*/i, "");
  const map = {
    "If legal enforceability / rights mapping cannot be made explicit and enforceable, stop or re-structure.": {
      da: "STOP: Hvis juridisk håndhævelse og mapping af rettigheder ikke kan gøres eksplicit og håndhævelig, stop eller omstrukturér.",
      de: "STOP: Wenn rechtliche Durchsetzbarkeit und Rights Mapping nicht ausdrücklich und durchsetzbar gestaltet werden können, stoppen oder neu strukturieren.",
      fr: "STOP : si la force exécutoire juridique et la cartographie des droits ne peuvent pas être rendues explicites et exécutoires, arrêter ou restructurer.",
      es: "STOP: Si la exigibilidad jurídica y el mapeo de derechos no pueden hacerse explícitos y exigibles, detener o reestructurar.",
      "zh-Hans": "STOP：如果法律可执行性和权利映射无法明确且可执行，应停止或重构。",
      "pt-BR": "STOP: Se a exigibilidade jurídica e o mapeamento de direitos não puderem ser explicitados e tornados exequíveis, interrompa ou reestruture o caso.",
    },
    "If the project depends on near-term transferability or secondary liquidity, and no legally permitted, operationally defined transfer process with committed participants is available, stop or revert to a conventional structure.": {
      da: "STOP: Hvis projektet afhænger af kortsigtet overførbarhed eller sekundær likviditet, og der ikke findes en juridisk tilladt og operationelt defineret transferproces med forpligtede deltagere, bør casen stoppes eller føres tilbage til en konventionel struktur.",
      de: "STOP: Wenn das Projekt von kurzfristiger Übertragbarkeit oder Sekundärliquidität abhängt und kein rechtlich zulässiger, operativ definierter Transferprozess mit verbindlichen Teilnehmern verfügbar ist, stoppen oder zu einer konventionellen Struktur zurückkehren.",
      fr: "STOP : si le projet dépend d’une transférabilité ou d’une liquidité secondaire à court terme sans processus de transfert juridiquement autorisé, défini sur le plan opérationnel et soutenu par des participants engagés, arrêter ou revenir à une structure conventionnelle.",
      es: "STOP: Si el proyecto depende de transferibilidad o liquidez secundaria a corto plazo y no existe un proceso de transferencia jurídicamente permitido, definido operativamente y con participantes comprometidos, detener o volver a una estructura convencional.",
      "zh-Hans": "STOP：如果项目依赖近期可转让性或二级流动性，但没有法律允许、运营定义明确且有参与方承诺的转让流程，应停止或回到传统结构。",
      "pt-BR": "STOP: Se o projeto depender de transferibilidade no curto prazo ou de liquidez secundária e não houver um processo de transferência juridicamente permitido, operacionalmente definido e com participantes comprometidos, interrompa ou retorne a uma estrutura convencional.",
    },
    "If settlement/cash-leg cannot be operationalized for the target investors, re-scope investor base or settlement design.": {
      da: "STOP: Hvis afvikling og cash leg ikke kan operationaliseres for de målrettede investorer, bør investorkredsen eller afviklingsdesignet afgrænses på ny.",
      de: "STOP: Wenn Settlement/Cash-Leg für die Zielinvestoren nicht operationalisiert werden kann, Investorengruppe oder Settlement-Design neu zuschneiden.",
      fr: "STOP : si le règlement/cash leg ne peut pas être opérationnalisé pour les investisseurs cibles, redéfinir le périmètre des investisseurs ou le design de règlement.",
      es: "STOP: Si el settlement/cash leg no puede operativizarse para los inversores objetivo, redefinir la base inversora o el diseño de settlement.",
      "zh-Hans": "STOP：如果结算/现金端无法面向目标投资者实现运营化，应重新界定投资者范围或结算设计。",
      "pt-BR": "STOP: Se a liquidação/cash leg não puder ser operacionalizada para os investidores-alvo, redefina o universo de investidores ou o desenho de liquidação.",
    },
    "If the distribution and compliance plan cannot support the chosen investor scope, re-scope to an investor segment supported by the independently confirmed offering pathway.": {
      da: "STOP: Hvis distributions- og complianceplanen ikke kan understøtte den valgte investorkreds, bør den afgrænses til et investorsegment, som understøttes af den uafhængigt bekræftede udbudsmodel.",
      de: "STOP: Wenn Distributions- und Compliance-Plan den gewählten Anlegerkreis nicht tragen können, auf ein Anlegersegment begrenzen, das durch den unabhängig bestätigten Angebotsweg unterstützt wird.",
      fr: "STOP : si le plan de distribution et de conformité ne peut pas soutenir le périmètre d’investisseurs choisi, le redéfinir vers un segment compatible avec la voie d’offre confirmée de manière indépendante.",
      es: "STOP: Si el plan de distribución y cumplimiento no puede sostener el alcance de inversores elegido, redefinirlo hacia un segmento respaldado por la vía de oferta confirmada de forma independiente.",
      "zh-Hans": "STOP：如果分销和合规计划无法支持所选投资者范围，应重新限定为经独立确认的发行路径所支持的投资者群体。",
      "pt-BR": "STOP: Se o plano de distribuição e compliance não puder sustentar o universo de investidores escolhido, restrinja-o a um segmento compatível com a via de oferta confirmada de forma independente.",
    },
  };
  return map[canonical]?.[code] || s;
}

function getCriterionKind(value) {
  const s = String(value || "").trim();
  if (/^STOP\s*[:：]/i.test(s) || /^ARRÊT\s*[:：]/i.test(s) || /^ARRET\s*[:：]/i.test(s) || /^停止/.test(s)) return "STOP";
  if (/^BLOCKER\s*[:：]/i.test(s) || /^BLOQUEADOR\s*[:：]/i.test(s) || /^BLOCAGE\s*[:：]/i.test(s) || /^阻碍/.test(s)) return "BLOCKER";
  if (/^CONDITION\s*[:：]/i.test(s) || /^CONDICIÓN\s*[:：]/i.test(s) || /^CONDICION\s*[:：]/i.test(s) || /^CONDIÇÃO\s*[:：]/i.test(s) || /^CONDICAO\s*[:：]/i.test(s) || /^BEDINGUNG\s*[:：]/i.test(s) || /^条件/.test(s)) return "CONDITION";
  return "";
}

function stripCriterionMarker(value) {
  return String(value || "")
    .trim()
    .replace(/^(STOP|BLOCKER|CONDITION|BLOQUEADOR|BLOQUEO|CONDICIÓN|CONDICION|CONDIÇÃO|CONDICAO|BLOCAGE|ARRÊT|ARRET|STOPP|BEDINGUNG|阻碍因素|阻碍|条件|停止|停止条件)\s*[:：]\s*/i, "")
    .trim();
}

function localizeBlockerCriterionText(value, code = "en") {
  const body = stripCriterionMarker(value);
  if (!body) return body;

  const canonical = body.replace(/[.\s]+$/g, "");
  const retail = "Retail scope combined with current complexity/risk profile is not viable without a strong compliance + distribution plan";

  const map = {
    [retail]: {
      da: "Detailomfang kombineret med den nuværende kompleksitets- og risikoprofil er ikke levedygtigt uden en stærk compliance- og distributionsplan.",
      de: "Retail-Umfang in Verbindung mit dem aktuellen Komplexitäts- und Risikoprofil ist ohne einen starken Compliance- und Distributionsplan nicht tragfähig.",
      fr: "Le périmètre retail, combiné au profil actuel de complexité et de risque, n’est pas viable sans un dispositif solide de conformité et de distribution.",
      es: "El alcance minorista, combinado con el perfil actual de complejidad y riesgo, no es viable sin un plan sólido de cumplimiento y distribución.",
      "zh-Hans": "在当前复杂度和风险水平下，面向零售投资者的范围若没有强有力的合规与分销方案，则不具备可行性。",
      "pt-BR": "Um escopo de investidores de varejo, combinado com o atual perfil de complexidade e risco, não é viável sem um plano robusto de compliance e distribuição.",
    },
  };

  return map[canonical]?.[code] || body;
}

function getLocalizedGlossaryEntries(code = "en") {
  const entries = {
    da: [
      ["Added Value / Merværdi (AV)", "TVACs netto-vurdering af, om tokenisering forbedrer casen, når både upside og friktion vejes sammen."],
      ["Conditional Go", "TVAC-verdict, hvor casen kan være relevant, men afhænger af tydelige designvalg, implementering og gating-betingelser."],
      ["Custody", "Sikring, kontrol og registrering af aktiver eller positioner på vegne af investorer eller markedsdeltagere."],
      ["KYC / AML", "Kontroller for kundeidentifikation og bekæmpelse af hvidvask."],
      ["Liquidity window", "Et afgrænset tidspunkt eller periode, hvor investorer kan forsøge at sælge eller overføre en position."],
      ["New Opportunities (NO)", "Case-specifik upside, som tokenisering skaber eller styrker, fx investoradgang, programmability eller operationel nytte."],
      ["New Risks (NR)", "Nye risici introduceret af tokenisering, herunder juridiske, regulatoriske, operationelle, teknologiske eller markedsmæssige risici."],
      ["Tokenization Costs (TC)", "Ekstra omkostninger introduceret af tokenisering, fx strukturering, compliance, custody, platform og integration."],
      ["Rights mapping", "Afklaring af hvilke juridiske, økonomiske, governance- og transfer-rettigheder en token repræsenterer."],
      ["Whitelist", "Mekanisme der begrænser adgang eller transferability til forhåndsgodkendte investorer, wallets eller deltagere."],
    ],
    es: [
      ["Added Value / Valor añadido (AV)", "Evaluación neta de TVAC sobre si la tokenización mejora el caso al ponderar upside y fricciones."],
      ["Conditional Go", "Conclusión de TVAC que indica que el caso puede ser viable, pero depende de disciplina de diseño, implementación y condiciones previas."],
      ["Custodia", "Salvaguarda, control y registro de activos o posiciones en nombre de inversores o participantes de mercado."],
      ["KYC / AML", "Controles de identificación de clientes y prevención del blanqueo de capitales."],
      ["Ventana de liquidez", "Periodo definido en el que los inversores pueden intentar vender o transferir una posición."],
      ["New Opportunities (NO)", "Upside específico del caso que la tokenización crea o refuerza, por ejemplo acceso a inversores, programmability o utilidad operativa."],
      ["New Risks (NR)", "Riesgos adicionales introducidos por la tokenización, incluidos riesgos legales, regulatorios, operativos, tecnológicos o de mercado."],
      ["Tokenization Costs (TC)", "Costes adicionales introducidos por la tokenización, como estructuración, compliance, custodia, plataforma e integración."],
      ["Rights mapping", "Clarificación de los derechos legales, económicos, de gobernanza y transferencia que representa el token."],
      ["Whitelist", "Mecanismo que restringe acceso o transferencias a inversores, wallets o participantes previamente aprobados."],
    ],
    de: [
      ["Added Value / Mehrwert (AV)", "TVACs Nettoeinschätzung, ob Tokenisierung den Fall verbessert, wenn Upside und Friktionen zusammen betrachtet werden."],
      ["Conditional Go", "TVAC-Urteil, bei dem der Fall möglich erscheint, aber von Design-Disziplin, Umsetzung und Gating-Bedingungen abhängt."],
      ["Custody", "Verwahrung, Kontrolle und Nachweisführung von Vermögenswerten oder Positionen für Investoren oder Marktteilnehmer."],
      ["KYC / AML", "Kontrollen zur Kundenidentifikation und Geldwäscheprävention."],
      ["Liquiditätsfenster", "Definierter Zeitraum, in dem Investoren versuchen können, eine Position zu verkaufen oder zu übertragen."],
      ["New Opportunities (NO)", "Fallspezifischer Upside, den Tokenisierung schafft oder wesentlich stärkt."],
      ["New Risks (NR)", "Zusätzliche Risiken durch Tokenisierung, einschließlich rechtlicher, regulatorischer, operativer, technologischer oder marktstruktureller Risiken."],
      ["Tokenization Costs (TC)", "Zusätzliche Kosten durch Tokenisierung, etwa Strukturierung, Compliance, Custody, Plattform und Integration."],
      ["Rights mapping", "Klärung, welche Rechte ein Token repräsentiert und wie diese Rechte durchsetzbar sind."],
      ["Whitelist", "Mechanismus zur Beschränkung von Teilnahme oder Übertragung auf vorab genehmigte Investoren, Wallets oder Parteien."],
    ],
    fr: [
      ["Added Value / Valeur ajoutée (AV)", "Évaluation nette par TVAC de l’amélioration du cas une fois l’upside et les frictions pondérés ensemble."],
      ["Conditional Go", "Verdict TVAC indiquant que le cas peut être pertinent, mais dépend de la discipline de conception, de l’exécution et de conditions de gating."],
      ["Custody", "Conservation, contrôle et tenue de registre des actifs ou positions pour des investisseurs ou participants de marché."],
      ["KYC / AML", "Contrôles d’identification client et de lutte contre le blanchiment."],
      ["Fenêtre de liquidité", "Période définie pendant laquelle les investisseurs peuvent tenter de vendre ou transférer une position."],
      ["New Opportunities (NO)", "Upside spécifique au cas créé ou renforcé par la tokenisation."],
      ["New Risks (NR)", "Risques additionnels introduits par la tokenisation, notamment juridiques, réglementaires, opérationnels, technologiques ou de marché."],
      ["Tokenization Costs (TC)", "Coûts additionnels introduits par la tokenisation, par exemple structuration, compliance, custody, plateforme et intégration."],
      ["Rights mapping", "Clarification des droits représentés par le token et de leur caractère exécutoire."],
      ["Whitelist", "Mécanisme limitant la participation ou les transferts à des investisseurs, wallets ou participants pré-approuvés."],
    ],
    "zh-Hans": [
      ["Added Value / 增值 (AV)", "TVAC对代币化是否在综合上行价值与摩擦后改善该案例的净评估。"],
      ["Conditional Go", "TVAC结论，表示该案例可能可行，但取决于设计纪律、实施选择和关键门槛条件。"],
      ["托管（Custody）", "代表投资者或市场参与方保管、控制并记录资产或头寸。"],
      ["KYC / AML", "客户身份识别与反洗钱控制。"],
      ["流动性窗口", "投资者可尝试出售或转让头寸的限定时间或期间。"],
      ["New Opportunities (NO)", "代币化在具体案例中创造或显著强化的上行价值，例如投资者准入、可编程性或运营效用。"],
      ["New Risks (NR)", "代币化引入的新风险，包括法律、监管、运营、技术或市场结构风险。"],
      ["Tokenization Costs (TC)", "代币化引入的额外成本，例如结构设计、合规、托管、平台和集成成本。"],
      ["Rights mapping", "明确token代表哪些法律、经济、治理和转让权利，以及这些权利如何执行。"],
      ["白名单（Whitelist）", "将参与或转让限制在预先批准的投资者、钱包、交易对手或参与方范围内的机制。"],
    ],
    "pt-BR": [
      ["Added Value / Valor Agregado (AV)", "Avaliação líquida do TVAC sobre se a tokenização melhora o caso quando benefícios e fricções são considerados em conjunto."],
      ["Conditional Go", "Verdict do TVAC que indica que o caso pode ser relevante, mas depende de disciplina de desenho, implementação e condições de decisão claramente definidas."],
      ["Custódia", "Salvaguarda, controle e registro de ativos ou posições em nome de investidores ou participantes de mercado."],
      ["KYC / AML", "Controles de identificação de clientes e prevenção à lavagem de dinheiro."],
      ["Janela de liquidez", "Período definido no qual investidores podem tentar vender ou transferir uma posição."],
      ["New Opportunities (NO)", "Benefícios específicos do caso que a tokenização cria ou reforça, como acesso a investidores, programabilidade ou utilidade operacional."],
      ["New Risks (NR)", "Novos riscos introduzidos pela tokenização, incluindo riscos jurídicos, regulatórios, operacionais, tecnológicos ou de estrutura de mercado."],
      ["Tokenization Costs (TC)", "Custos adicionais introduzidos pela tokenização, como estruturação, compliance, custódia, plataforma e integração."],
      ["Mapeamento de direitos", "Definição dos direitos jurídicos, econômicos, de governança e de transferência representados pelo token e de como esses direitos são exercidos."],
      ["Whitelist", "Mecanismo que restringe participação ou transferências a investidores, wallets, contrapartes ou participantes previamente aprovados."],
    ],
  };
  return (entries[code] || entries.en || []).map(([term, def]) => ({ term, def }));
}


function getLocalizedVerdictReasonText(code = "en") {
  const map = {
    en: "This verdict reflects the combined assessment of New Opportunities, Cost Savings, Risk Reduction, Tokenization Costs and New Risks in this case. Higher scores on the first three and lower scores on the last two support a Viable verdict; the reverse pushes the case towards Conditional Go or Non-viable.",
    da: "Denne vurdering afspejler den samlede analyse af Nye muligheder, Omkostningsbesparelser, Risikoreduktion, Tokeniseringsomkostninger og Nye risici i denne case. Højere scorer på de tre første faktorer og lavere scorer på de to sidste understøtter et Viable-resultat; det modsatte trækker casen mod Conditional Go eller Non-viable.",
    de: "Dieses Ergebnis spiegelt die kombinierte Bewertung von Neuen Chancen, Kosteneinsparungen, Risikoreduktion, Tokenisierungskosten und Neuen Risiken in diesem Fall wider. Höhere Werte bei den ersten drei Faktoren und niedrigere Werte bei den letzten beiden stützen ein Viable-Ergebnis; das Gegenteil verschiebt den Fall in Richtung Conditional Go oder Non-viable.",
    fr: "Ce résultat reflète l’évaluation combinée des Nouvelles opportunités, Économies de coûts, Réduction des risques, Coûts de tokenisation et Nouveaux risques dans ce cas. Des scores plus élevés sur les trois premiers facteurs et plus faibles sur les deux derniers soutiennent un résultat Viable ; l’inverse pousse le cas vers Conditional Go ou Non-viable.",
    es: "Este resultado refleja la evaluación combinada de Nuevas oportunidades, Ahorro de costes, Reducción de riesgos, Costes de tokenización y Nuevos riesgos en este caso. Puntuaciones más altas en los tres primeros factores y más bajas en los dos últimos apoyan un resultado Viable; lo contrario empuja el caso hacia Conditional Go o Non-viable.",
    "zh-Hans": "该结果反映了本案例对新机会、成本节约、风险降低、代币化成本和新风险的综合评估。前三项得分越高、后两项得分越低，越支持Viable结论；相反，则会将案例推向Conditional Go或Non-viable。",
    "pt-BR": "Este resultado reflete a avaliação combinada de Novas Oportunidades, Economia de Custos, Redução de Riscos, Custos de Tokenização e Novos Riscos neste caso. Pontuações mais altas nos três primeiros fatores e mais baixas nos dois últimos sustentam um resultado Viable; o inverso desloca o caso para Conditional Go ou Non-viable.",
  };
  return map[code] || map.en;
}


function capitalizeFirstVisible(value) {
  const s = String(value || "").trim();
  if (!s) return s;
  const idx = s.search(/[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\u0100-\u017E\u0391-\u03C9\u0410-\u044F\u4E00-\u9FA5]/);
  if (idx < 0) return s;
  return s.slice(0, idx) + s.charAt(idx).toUpperCase() + s.slice(idx + 1);
}

function stripScoreLeadIn(value) {
  let out = String(value || "").trim();
  // Remove customer-facing score lead-ins such as:
  // "Score 6 (upper-moderate) because...", "Bewertung 6 (...), da...",
  // "得分6（中上），因为...". The score already appears in the table, so the
  // driver text should read as analysis rather than duplicated scoring syntax.
  const leadIns = [
    /^(?:Score|Scoring)\s+(?:of\s+)?\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[,，:：\-–—]?\s*(?:(?:because|since|because of|as)\s*)?/i,
    /^Score\s+(?:of\s+)?\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[,，:：\-–—]?\s*(?:(?:fordi|weil|da|car|porque|por que|parce que)\s*)?/i,
    /^(?:Bewertung|Beurteilung)\s+(?:von\s+)?\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[,，:：\-–—]?\s*(?:(?:weil|da)\s*)?/i,
    /^(?:Puntuaci[oó]n|Puntaje|Evaluaci[oó]n|Pontuaç[aã]o|Avali[aá]ção)\s+(?:de\s+)?\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[,，:：\-–—]?\s*(?:(?:porque|ya que)\s*)?/i,
    /^(?:Note|Évaluation|Evaluation)\s+(?:de\s+)?\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[,，:：\-–—]?\s*(?:(?:car|parce que)\s*)?/i,
    /^(?:得分|评分)\s*\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[，,:：\-–—]?\s*(?:因为|由於|由于)?\s*/i,
  ];

  for (let i = 0; i < 3; i += 1) {
    const before = out;
    leadIns.forEach((pattern) => {
      out = out.replace(pattern, "");
    });
    out = out.replace(/^[,，:：\-–—\s]+/, "").trim();
    if (out === before) break;
  }

  return capitalizeFirstVisible(out);
}

function scrubScoreLeadInsEverywhere(value) {
  let out = String(value || "");
  const latinLead = "(?:(?:Score|Scoring)\\s+(?:of\\s+)?\\d+|(?:Bewertung|Beurteilung)\\s+(?:von\\s+)?\\d+|(?:Puntuaci[oó]n|Puntaje|Evaluaci[oó]n|Note|Évaluation|Evaluation)\\s+(?:de\\s+)?\\d+)(?:[.,]\\d+)?\\s*(?:\\([^)]*\\)|（[^）]*）)?\\s*[,，:：\\-–—]?\\s*(?:(?:because|since|because of|as|fordi|weil|da|car|porque|ya que|parce que)\\s*)?";
  out = out.replace(new RegExp(`(^|[.!?。；;]\\s*)${latinLead}`, "gi"), "$1");
  out = out.replace(/(^|[.!?。；;]\s*)(?:得分|评分)\s*\d+(?:[.,]\d+)?\s*(?:\([^)]*\)|（[^）]*）)?\s*[，,:：\-–—]?\s*(?:因为|由於|由于)?\s*/gi, "$1");
  return out.replace(/\s{2,}/g, " ").trim();
}




function polishKnownEnglishReportText(value) {
  const source = String(value || "");
  if (!source) return source;

  // Last-mile display guard for documented English defects. The backend remains
  // the source of truth; this prevents rendering or mirror reconstruction from
  // reintroducing over-assertive evidence language or malformed punctuation.
  return source
    .replace(/\bactual amounts have not been evidenced\.\s*,\s*/gi, "actual amounts have not been evidenced, ")
    .replace(/\bactual amounts have not been evidenced\.\s+especially\b/gi, "actual amounts have not been evidenced, especially")
    .replace(/\bevidenced\.\s*,\s*/gi, "evidenced, ")
    .replace(/\bNear[- ]instant DvP settlement reduces counterparty exposure during settlement windows\.?/gi,
      "Near-instant DvP settlement may reduce counterparty exposure during settlement windows if asset- and cash-leg finality, exception handling and recovery operate as intended; no realized risk-reduction evidence was provided.")
    .replace(/\bControlled DvP settlement reduces counterparty exposure during settlement windows\.?/gi,
      "Controlled DvP settlement may reduce counterparty exposure during settlement windows if asset- and cash-leg finality, exception handling and recovery operate as intended; no realized risk-reduction evidence was provided.")
    .replace(/\bThe permissioned, institutional-only design enables near-instant, controlled DvP settlement, reducing counterparty exposure and settlement fails\.?/gi,
      "The permissioned, institutional-only design may enable near-instant, controlled DvP settlement and may reduce counterparty exposure and settlement fails if finality, exception handling and recovery controls are validated; no realized outcome evidence was provided.")
    .replace(/\bAutomated event trails and operational governance lower the risk of disputes and accelerate resolution\.?/gi,
      "Automated event trails and operational governance may reduce dispute risk and support faster resolution if the records, controls and operating procedures work as intended.")
    .replace(/\bStrict eligibility controls and regulated custody reduce compliance and operational risk\.?/gi,
      "Strict eligibility controls and regulated custody may reduce parts of the compliance and operational risk if the applicable perimeter, roles and controls are independently validated and operate as intended.")
    .replace(/\bRegulated custody and third[- ]party servicing reduce some operational and key[- ]management risks\.?/gi,
      "The selected regulated-custody and third-party-servicing model may reduce some operational and key-management risks if roles, controls and service performance are validated.")
    .replace(/\bWhitelist[- ]based transfer restrictions and compliance checks help mitigate regulatory breach risk\.?/gi,
      "Whitelist-based transfer restrictions and compliance checks may reduce eligibility and transfer-control risk if correctly designed and operated; no control-effectiveness evidence was provided.")
    .replace(/\bAutomated controls and auditable event trails reduce operational risk and accelerate dispute resolution\.?/gi,
      "Automated controls and auditable event trails may reduce operational risk and support faster dispute resolution if the controls and records operate as intended.")
    .replace(/\bStrict eligibility controls and regulated custody reduce regulatory and operational risk\.?/gi,
      "Strict eligibility controls and regulated custody may reduce parts of the regulatory and operational risk if the applicable perimeter, roles and controls are correctly validated and operated.")
    .replace(/\bOngoing operational costs include ([^.!?]+), but are offset by measurable efficiency gains\.?/gi,
      "Ongoing operational costs include $1. Those costs may be offset by efficiency gains only if pilot or run-state evidence demonstrates them.")
    .replace(/\bTokenization introduces integration, legal, and governance costs, but these are offset by structural savings from reduced reconciliation and manual intervention\.?/gi,
      "Tokenization introduces integration, legal and governance costs. Those costs may be offset by structural savings only if pilot or run-state evidence confirms that reconciliation and manual intervention are materially reduced.")
    .replace(/\bIntegration, legal, and governance costs remain, but are offset by measurable efficiency gains at scale\.?/gi,
      "Integration, legal and governance costs remain. They may be offset by efficiency gains at scale only if those gains are demonstrated against a defined baseline.")
    .replace(/\bThe Asset Owner and participating institutions directly benefit from reduced operational friction, fewer exception tickets, and tighter control over collateral release\.?/gi,
      "The Asset Owner and participating institutions may benefit from reduced operational friction, fewer exception tickets and tighter control over collateral release if those outcomes are demonstrated in pilot or run-state evidence.")
    .replace(/\bThe design leverages existing custody and tri-party\/agent workflows, minimizing disruption and focusing tokenization on incremental, high-value process improvements\.?/gi,
      "The design leverages existing custody and tri-party/agent workflows and is intended to limit disruption by focusing tokenization on incremental process improvements; the actual effect remains to be validated.")
    .replace(/\bThe incremental, non-disruptive integration approach helps contain both upfront and ongoing costs\.?/gi,
      "The incremental integration approach may help contain upfront and ongoing costs, subject to validated implementation scope and provider evidence.")
    .replace(/\bFewer settlement fails and exception tickets reduce operational workload and associated costs\.?/gi,
      "Fewer settlement fails and exception tickets could reduce operational workload and associated costs if those outcomes are demonstrated against the pre-implementation baseline.")
    .replace(/\bno credible compliance path\b/gi, "no validated compliance pathway under the current design")
    .replace(/\bThis approach is incompatible with baseline regulatory requirements in ([^.!?]+), which typically restrict retail access to regulated, disclosed, and controlled offerings\.?/gi,
      "The described approach does not yet demonstrate a validated regulated retail pathway across $1. Jurisdiction-specific classification, disclosure, eligibility, marketing and distribution requirements must be independently assessed.")
    .replace(/\bThis approach is incompatible with baseline regulatory requirements in ([^.!?]+)\.?/gi,
      "The described approach does not yet demonstrate a validated regulated retail pathway across $1. Jurisdiction-specific requirements and the final legal application remain to be independently assessed.")
    .replace(/\bNo credible path to compliant, enforceable, and sustainable value creation is demonstrated under the current design\.?/gi,
      "No validated path to compliant, enforceable, and sustainable value creation is demonstrated under the current design.")
    .replace(/\bNo credible path to sustainable, compliant, and enforceable value creation is demonstrated under the current design\.?/gi,
      "No validated path to sustainable, compliant, and enforceable value creation is demonstrated under the current design.")
    .replace(/\bCross-border securities law violations\b/gi,
      "Potential non-compliant cross-border distribution")
    .replace(/\bCustody, venue, and recordkeeping arrangements are not fit for purpose in a regulated retail investment context\.?/gi,
      "The described custody, venue and recordkeeping arrangements do not currently demonstrate the controls normally expected for a credible regulated retail-investment pathway.")
    .replace(/\bCross-border securities law, tax, and AML risks are unaddressed and could result in severe legal and financial consequences\.?/gi,
      "Cross-border legal, distribution, tax and AML risks remain unresolved and could create material legal and financial exposure; final applicability and severity require jurisdiction-specific validation.")
    .replace(/\bRedesign (?:the )?offering to restrict access to eligible investors and comply with local securities laws\.?/gi,
      "Redesign the distribution model around independently validated investor eligibility, onboarding, disclosure and transfer controls in each relevant jurisdiction; final legal requirements remain to be confirmed.")
    .replace(/\bcomply with local securities laws\b/gi,
      "align with independently confirmed local offering, marketing, eligibility and transfer requirements")
    .replace(/\bPotential costs from regulatory action, litigation, or asset seizure are significant and unquantified\.?/gi,
      "Potential legal, dispute, remediation and enforcement-related cost exposure may be material, but applicability and amounts have not been established.")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function polishKnownDanishReportText(value) {
  const source = String(value || "");
  if (!source) return source;

  // This is deliberately a small, deterministic guard for documented Danish
  // defects from localized case prose. It is not a general translation layer,
  // does not change facts, numbers, scores or report structure, and only runs
  // after the backend has completed localization.
  return source
    .replace(/\baktie-forvaring\b/gi, "værdipapirforvaring")
    .replace(/\bSystemiske risikoreduktion\b/gi, "Den samlede risikoreduktion")
    .replace(/\bcustody-/gi, "forvarings-")
    .replace(/\bworkflow-effektivitet\b/gi, "effektivitet i arbejdsgangen")
    .replace(/\bworkflowtilstande\b/gi, "processtatusser")
    .replace(/\bmodelleret upside\b/gi, "modelleret potentiale")
    .replace(/\bupside-siden\b/gi, "den positive side")
    .replace(
      /\batomar DvP(?:-afvikling)? og næsten realtids overførsel af ([^—–.;,\n]+?)(?=(?:\s+mellem\b|\s+for\b|\s+i\b|[—–.;,\n]|$))/gi,
      "atomar DvP-afvikling og overførsel af $1 næsten i realtid"
    );
}

function polishCaseSpecificDriverText(value, code = "en") {
  let out = capitalizeFirstVisible(stripScoreLeadIn(value));
  if (code === "en") out = polishKnownEnglishReportText(out);

  // The evaluator normally returns complete sentences. These targeted Danish
  // starts prevent a known bullet-style verb lead-in from becoming a fragment
  // when it is embedded in the deterministic verdict rationale. This is not a
  // general translation or cleanup pass.
  if (code === "da") {
    out = out
      .replace(/^Muliggør\b/, "Strukturen muliggør")
      .replace(/^Reducerer\b/, "Strukturen reducerer")
      .replace(/^Åbner for\b/, "Strukturen åbner for");
    out = polishKnownDanishReportText(out);
  }

  return out;
}

function localizeRationaleBoundarySentence(code = "en") {
  const map = {
    en: "This rationale concerns tokenization added value only and does not validate legal, regulatory, tax, financial, investment, credit, yield, issuer, or implementation suitability.",
    da: "Denne begrundelse vedrører kun tokeniseringens merværdi og validerer ikke juridisk, regulatorisk, skattemæssig, finansiel, investerings-, kredit-, afkast-, udsteder- eller implementeringsegnethed.",
    de: "Diese Begründung betrifft ausschließlich den Mehrwert der Tokenisierung und validiert keine rechtliche, regulatorische, steuerliche, finanzielle, investmentbezogene, kreditbezogene, renditebezogene, emittentenbezogene oder umsetzungsbezogene Eignung.",
    fr: "Cette justification concerne uniquement la valeur ajoutée de la tokenisation et ne valide pas l’adéquation juridique, réglementaire, fiscale, financière, d’investissement, de crédit, de rendement, d’émetteur ou de mise en œuvre.",
    es: "Esta justificación se refiere únicamente al valor añadido de la tokenización y no valida la idoneidad legal, regulatoria, fiscal, financiera, de inversión, crediticia, de rendimiento, del emisor o de implementación.",
    "zh-Hans": "该理由仅涉及代币化增值本身，并不验证法律、监管、税务、金融、投资、信用、收益、发行方或实施适配性。",
    "pt-BR": "Esta justificativa se refere exclusivamente ao Valor Agregado da tokenização e não valida adequação jurídica, regulatória, tributária, financeira, de investimento, crédito, rentabilidade, emissor ou implementação.",
  };
  return map[code] || map.en;
}

function getLocalizedRationalePosture(verdict, code = "en") {
  const v = String(verdict || "").toLowerCase().replace(/[-\s]+/g, "_");
  const isNon = v.includes("non_viable") || v.includes("no_go") || v === "stop";
  const isViable = !isNon && (v === "go" || v === "viable" || v.includes("viable"));
  const map = {
    en: isNon ? "This case is assessed as Non-viable" : isViable ? "This case is assessed as Viable" : "This case lands at Conditional Go",
    da: isNon ? "Denne case vurderes som Non-viable" : isViable ? "Denne case vurderes som Viable" : "Denne case lander på Conditional Go",
    de: isNon ? "Dieser Fall wird als Non-viable bewertet" : isViable ? "Dieser Fall wird als Viable bewertet" : "Dieser Fall landet bei Conditional Go",
    fr: isNon ? "Ce cas est évalué comme Non-viable" : isViable ? "Ce cas est évalué comme Viable" : "Ce cas aboutit à Conditional Go",
    es: isNon ? "Este caso se evalúa como Non-viable" : isViable ? "Este caso se evalúa como Viable" : "Este caso queda en Conditional Go",
    "zh-Hans": isNon ? "本案例被评估为 Non-viable" : isViable ? "本案例被评估为 Viable" : "本案例的结论为 Conditional Go",
    "pt-BR": isNon ? "Este caso é avaliado como Non-viable" : isViable ? "Este caso é avaliado como Viable" : "Este caso resulta em Conditional Go",
  };
  return map[code] || map.en;
}

function buildLocalizedVerdictRationaleText({ code = "en", verdict, scoreText, plusText, minusText, topUp, topFriction, secondFriction }) {
  const topUpLabel = topUp ? `${topUp.label} (${topUp.score}/10)` : "the positive value drivers";
  const topFrictionLabel = topFriction ? `${topFriction.label} (${topFriction.score}/10)` : "the cost and risk drivers";
  const supportText = topUp?.interpretation ? polishCaseSpecificDriverText(topUp.interpretation, code) : "";
  const dragText = topFriction?.interpretation ? polishCaseSpecificDriverText(topFriction.interpretation, code) : "";
  const secondDragText = secondFriction?.interpretation ? polishCaseSpecificDriverText(secondFriction.interpretation, code) : "";
  const support = supportText ? ` ${supportText}` : "";
  const drag = dragText ? ` ${dragText}` : "";
  const secondDrag = secondFriction?.score >= 6 && secondDragText
    ? ` ${secondFriction.label} ${code === "da" ? "er også væsentlig" : code === "de" ? "ist ebenfalls wesentlich" : code === "fr" ? "est également important" : code === "es" ? "también es relevante" : code === "zh-Hans" ? "也很重要" : code === "pt-BR" ? "também é relevante" : "is also material"}: ${secondDragText}`
    : "";
  const posture = getLocalizedRationalePosture(verdict, code);
  const boundary = localizeRationaleBoundarySentence(code);
  if (code === "da") {
    return `${posture}, fordi den beskrevne struktur giver en merværdi på ${scoreText} (plus ${plusText} minus ${minusText}). Den stærkeste positive værdidriver er ${topUpLabel}, mens den største friktion er ${topFrictionLabel}.${support}${drag}${secondDrag} ${boundary}`;
  }
  if (code === "de") {
    return `${posture}, weil die aktuelle nutzerdefinierte Struktur einen Mehrwert von ${scoreText} erzeugt (Plus ${plusText} minus ${minusText}). Der stärkste positive Treiber ist ${topUpLabel}, während die wichtigste Friktion ${topFrictionLabel} ist.${support}${drag}${secondDrag} ${boundary}`;
  }
  if (code === "fr") {
    return `${posture}, car la structure actuelle définie par l’utilisateur produit une valeur ajoutée de ${scoreText} (plus ${plusText} moins ${minusText}). Le principal moteur positif est ${topUpLabel}, tandis que la principale friction est ${topFrictionLabel}.${support}${drag}${secondDrag} ${boundary}`;
  }
  if (code === "es") {
    return `${posture} porque la estructura actual definida por el usuario genera un valor añadido de ${scoreText} (plus ${plusText} menos ${minusText}). El principal impulsor positivo es ${topUpLabel}, mientras que la principal fricción es ${topFrictionLabel}.${support}${drag}${secondDrag} ${boundary}`;
  }
  if (code === "zh-Hans") {
    return `${posture}，因为当前由用户定义的结构产生的增值为 ${scoreText}（正向 ${plusText}，负向 ${minusText}）。最强的正向驱动因素是${topUpLabel}，主要摩擦因素是${topFrictionLabel}。${support}${drag}${secondDrag} ${boundary}`;
  }
  if (code === "pt-BR") {
    return `${posture}, porque a estrutura atual definida pelo usuário gera Valor Agregado de ${scoreText} (Plus ${plusText} menos Minus ${minusText}). O principal fator positivo é ${topUpLabel}, enquanto a principal fricção é ${topFrictionLabel}.${support}${drag}${secondDrag} ${boundary}`;
  }
  return `${posture} because the current structure produces an Added Value of ${scoreText}: the strongest supportive driver is ${topUpLabel}, while the main friction is ${topFrictionLabel}.${support}${drag}${secondDrag} ${boundary}`;
}

function localizeGeneratedText(value, reportLanguageCode = "en") {
  if (typeof value !== "string") return value;
  if (reportLanguageCode === "en") return polishKnownEnglishReportText(value);
  if (reportLanguageCode === "da") return polishKnownDanishReportText(value);
  if (reportLanguageCode === "pt-BR") {
    const ptBrLockedText = {
      "Obtain written jurisdiction-specific legal analysis of classification, offering pathway, rights mapping and transfer controls.": "Obtenha análise jurídica escrita e específica da jurisdição sobre classificação, via de oferta, mapeamento de direitos e controles de transferência.",
      "Document and validate the enforceable link between the token, underlying rights, ownership records and cash-flow waterfall.": "Documente e valide o vínculo juridicamente exigível entre o token, os direitos subjacentes, os registros de titularidade e o waterfall de fluxos de caixa.",
      "Obtain binding provider scope, responsibilities, service levels, pricing, portability and exit terms.": "Obtenha dos prestadores escopo, responsabilidades, níveis de serviço, preços, portabilidade e condições de saída contratualmente vinculantes.",
      "The user describes the proposed regulatory pathway as unconfirmed. TVAC therefore treats it as a structuring hypothesis rather than a legal conclusion.": "O usuário descreve a via regulatória proposta como não confirmada. Por isso, o TVAC a trata como hipótese de estruturação, e não como conclusão jurídica.",
      "For a Resolution 88 offering, the consolidated rule provides alternatives based on book-entry registration or platform-based ownership and participation control. A separately regulated custodian may be a chosen design component, but TVAC does not treat it as a universal Resolution 88 requirement.": "Para uma oferta nos termos da Resolução CVM 88, a norma consolidada prevê alternativas baseadas em registro escritural ou em controle de titularidade e participação pela plataforma. Um custodiante regulado separadamente pode ser um componente escolhido do desenho, mas o TVAC não o trata como requisito universal da Resolução CVM 88.",
      "No market-making arrangement has been stated by the user or established from verified sources, so none has been assumed.": "Nenhum arranjo de formador de mercado foi declarado pelo usuário ou estabelecido com base em fontes verificadas; portanto, nenhum foi presumido.",
      "CVM guidance requires a fact-specific assessment of whether a receivables or fixed-income token is a security, including whether it constitutes a collective investment contract or securitization instrument. Tokenization does not remove that classification analysis.": "A orientação da CVM exige uma avaliação específica dos fatos para determinar se um token de recebíveis ou de renda fixa é valor mobiliário, inclusive se constitui contrato de investimento coletivo ou instrumento de securitização. A tokenização não elimina essa análise de classificação.",
      "What is the final legal classification of the token and underlying receivables structure?": "Qual é a classificação jurídica final do token e da estrutura de recebíveis subjacente?",
      "Which offering pathway applies to the final issuer, instrument, amount and investor scope?": "Qual via de oferta se aplica ao emissor, instrumento, volume e universo de investidores finais?",
      "Custody model (on-chain / off-chain / hybrid)": "Modelo de custódia (on-chain / off-chain / híbrido)",
      "Secondary-market venue / trading venue": "Ambiente de mercado secundário / ambiente de negociação",
      "Offering disclosure / documentation (prospectus / memorandum etc.)": "Divulgação / documentação da oferta (prospecto, memorando etc.)",
      "Issuer structure, investor rights or repayment terms remain open": "A estrutura do emissor, os direitos dos investidores ou as condições de pagamento permanecem em aberto",
      "custody model": "modelo de custódia",
      "offering / disclosure route": "via de oferta / divulgação",
    };
    return ptBrLockedText[value.trim()] || value;
  }
  return value;
}

function formatSettlementCashLegReadinessText(value, reportLanguageCode = "en") {
  const text = localizeGeneratedText(value, reportLanguageCode);
  if (reportLanguageCode !== "da" || typeof text !== "string") return text;

  // These sentences are generated from the deterministic cash-leg profile.
  // Render their Danish status wording consistently without changing the
  // underlying dependency, readiness level or any assessment content.
  return text
    .replace(
      /\bAfhængigheden af kontantdelen\s*\(cash leg\)\s+vurderes som\s+Kritisk\b/i,
      "Kontantdelen (cash leg) vurderes som kritisk for casen"
    )
    .replace(/\bReadiness vurderes som Avanceret\b/i, "Paratheden vurderes som avanceret")
    .replace(/\bReadiness vurderes som Stærk\b/i, "Paratheden vurderes som stærk")
    .replace(/\bReadiness vurderes som Moderat\b/i, "Paratheden vurderes som moderat")
    .replace(/\bReadiness vurderes som Grundlæggende\b/i, "Paratheden vurderes som grundlæggende")
    .replace(/\bReadiness vurderes som Udefineret\b/i, "Paratheden er uafklaret")
    .replace(/\bAktuel indikation for betaling\s*\/\s*afvikling\b/i, "Aktuel betalings- og afviklingsmodel")
    .replace(/\bT\+0 atomar(?=\.(?:\s|$))/i, "T+0 atomar afvikling");
}



function getReportFactorLabel(key, code = "en") {
  const labels = {
    en: {
      new_opportunities: "New Opportunities",
      cost_savings: "Cost Savings",
      risk_reduction: "Risk Reduction",
      tokenization_costs: "Tokenization Costs",
      new_risks: "New Risks",
    },
    da: {
      new_opportunities: "Nye muligheder",
      cost_savings: "Omkostningsbesparelser",
      risk_reduction: "Risikoreduktion",
      tokenization_costs: "Tokeniseringsomkostninger",
      new_risks: "Nye risici",
    },
    de: {
      new_opportunities: "Neue Chancen",
      cost_savings: "Kosteneinsparungen",
      risk_reduction: "Risikominderung",
      tokenization_costs: "Tokenisierungskosten",
      new_risks: "Neue Risiken",
    },
    fr: {
      new_opportunities: "Nouvelles opportunités",
      cost_savings: "Économies de coûts",
      risk_reduction: "Réduction des risques",
      tokenization_costs: "Coûts de tokenisation",
      new_risks: "Nouveaux risques",
    },
    es: {
      new_opportunities: "Nuevas oportunidades",
      cost_savings: "Ahorro de costes",
      risk_reduction: "Reducción de riesgos",
      tokenization_costs: "Costes de tokenización",
      new_risks: "Nuevos riesgos",
    },
    "zh-Hans": {
      new_opportunities: "新机会",
      cost_savings: "成本节约",
      risk_reduction: "风险降低",
      tokenization_costs: "代币化成本",
      new_risks: "新风险",
    },
    "pt-BR": {
      new_opportunities: "Novas Oportunidades",
      cost_savings: "Economia de Custos",
      risk_reduction: "Redução de Riscos",
      tokenization_costs: "Custos de Tokenização",
      new_risks: "Novos Riscos",
    },
  };
  return (labels[code] || labels.en)[key] || key;
}

function getReportFactorRole(key, code = "en") {
  const positive = ["new_opportunities", "cost_savings", "risk_reduction"].includes(key);
  const map = {
    en: positive ? "supports added value" : "reduces added value",
    da: positive ? "understøtter merværdi" : "reducerer merværdi",
    de: positive ? "stützt den Mehrwert" : "reduziert den Mehrwert",
    fr: positive ? "soutient la valeur ajoutée" : "réduit la valeur ajoutée",
    es: positive ? "apoya el valor añadido" : "reduce el valor añadido",
    "zh-Hans": positive ? "支持增值" : "降低增值",
    "pt-BR": positive ? "sustenta o Valor Agregado" : "reduz o Valor Agregado",
  };
  return map[code] || map.en;
}

function extractCaseSpecificFactorInterpretation(result, key, code = "en") {
  const raw = result?.factor_explanations?.[key] || result?.factorExplanations?.[key] || [];
  const arr = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];
  const first = arr.map((x) => String(x || "").trim()).find(Boolean);
  if (!first) return "";

  let cleaned = polishCaseSpecificDriverText(first, code);

  cleaned = localizeGeneratedText(cleaned, code) || cleaned;
  cleaned = polishCaseSpecificDriverText(cleaned, code);
  if (cleaned.length <= 420) return cleaned;
  const sentenceEnd = cleaned.slice(0, 520).match(/^(.+?[.!?。！？])(?:\s|$)/);
  if (sentenceEnd && sentenceEnd[1] && sentenceEnd[1].length >= 80) return sentenceEnd[1].trim();
  const sentence = cleaned.slice(0, 440).replace(/\s+\S*$/, "").trim();
  return sentence ? `${sentence}…` : cleaned.slice(0, 420).trim() + "…";
}

function getReportFactorRows(result, code = "en") {
  const fs = result?.factor_scores || result?.factorScores || {};
  const read = (snake, camel) => {
    const n = Number(fs?.[snake] ?? fs?.[camel]);
    return Number.isFinite(n) ? n : null;
  };
  const keys = [
    ["new_opportunities", "newOpportunities"],
    ["cost_savings", "costSavings"],
    ["risk_reduction", "riskReduction"],
    ["tokenization_costs", "tokenizationCosts"],
    ["new_risks", "newRisks"],
  ];
  return keys.map(([key, camel]) => ({
    key,
    label: getReportFactorLabel(key, code),
    role: getReportFactorRole(key, code),
    score: read(key, camel),
    interpretation: extractCaseSpecificFactorInterpretation(result, key, code),
  }));
}

function getFactorTableChrome(code = "en") {
  const map = {
    en: { factor: "Factor", score: "Score", interpretation: "Case-specific driver" },
    da: { factor: "Faktor", score: "Score", interpretation: "Case-specifik driver" },
    de: { factor: "Faktor", score: "Score", interpretation: "Fallspezifischer Treiber" },
    fr: { factor: "Facteur", score: "Score", interpretation: "Facteur propre au cas" },
    es: { factor: "Factor", score: "Puntuación", interpretation: "Driver específico del caso" },
    "zh-Hans": { factor: "因素", score: "评分", interpretation: "案例特定驱动因素" },
    "pt-BR": { factor: "Fator", score: "Pontuação", interpretation: "Fator específico do caso" },
  };
  return map[code] || map.en;
}

function FactorScoreTable({ result, reportLanguageCode = "en" }) {
  const rows = getReportFactorRows(result, reportLanguageCode);
  const chrome = getFactorTableChrome(reportLanguageCode);
  return (
    <div className="factor-score-profile">
      <table className="factor-score-table">
        <thead>
          <tr>
            <th>{chrome.factor}</th>
            <th>{chrome.score}</th>
            <th>{chrome.interpretation}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td><strong>{row.label}</strong></td>
              <td className="factor-score-number">{row.score === null ? "—" : `${row.score}/10`}</td>
              <td>{row.interpretation || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isGenericFormulaReason(value) {
  const s = String(value || "").toLowerCase();
  return (
    !s.trim() ||
    s.includes("combined assessment of new opportunities") ||
    s.includes("higher scores on the first three") ||
    s.includes("added value =") ||
    s.includes("thresholds: viable") ||
    s.includes("deterministic verdict mapping applied")
  );
}

function buildClientVerdictRationale(result, labels, code = "en") {
  if (!result) return getLocalizedVerdictReasonText(code);
  const av = result?.added_value || result?.addedValue || {};
  const rows = getReportFactorRows(result, code);
  const upside = rows
    .filter((x) => ["new_opportunities", "cost_savings", "risk_reduction"].includes(x.key) && Number.isFinite(x.score))
    .sort((a, b) => b.score - a.score);
  const friction = rows
    .filter((x) => ["tokenization_costs", "new_risks"].includes(x.key) && Number.isFinite(x.score))
    .sort((a, b) => b.score - a.score);
  const topUp = upside[0];
  const topFriction = friction[0];
  const secondFriction = friction[1];
  const score = Number(av.score);
  const plus = Number(av.plus);
  const minus = Number(av.minus);
  const scoreText = Number.isFinite(score) ? formatReportNumber(score, { signed: true }) : "—";
  const plusText = Number.isFinite(plus) ? formatReportNumber(plus) : "—";
  const minusText = Number.isFinite(minus) ? formatReportNumber(minus) : "—";

  const base = buildLocalizedVerdictRationaleText({
    code,
    verdict: result?.verdict,
    scoreText,
    plusText,
    minusText,
    topUp,
    topFriction,
    secondFriction,
  });
  const initiatorNet = Number(result?.initiator_viability?.asset_owner_net);
  const longHorizon = result?.initiator_viability?.long_horizon_signals === true;
  const capApplied = result?.initiator_viability?.initiator_cap_applied === true;
  if (code === "en" && Number.isFinite(initiatorNet) && initiatorNet < 0 && longHorizon) {
    return `${base} The initiating institution's stakeholder net is ${formatReportNumber(initiatorNet, { signed: true })}. This is an implementation-phase perspective, not an arithmetic deduction from overall Added Value: the case qualifies for long-horizon treatment because recurring operational benefits are intended to accrue after legacy steps are retired. The verdict therefore remains subject to evidence that cumulative run-state savings and risk reduction are actually captured over operating cycles.`;
  }
  if (code === "en" && Number.isFinite(initiatorNet) && initiatorNet < 0 && capApplied) {
    return `${base} The initiating party's stakeholder net is ${formatReportNumber(initiatorNet, { signed: true })}. Because the case does not qualify for long-horizon institutional treatment, the otherwise Viable five-factor result is capped at Conditional Go until the initiating party's value capture is improved and evidenced.`;
  }
  return base;
}

function getNoHardBlockerNarrative(verdict, code = "en") {
  const v = String(verdict || "").toLowerCase().replace(/[-\s]+/g, "_");
  const kind = v.includes("non_viable") || v.includes("no_go") || v === "stop"
    ? "non"
    : v.includes("viable") && !v.includes("non")
    ? "viable"
    : "conditional";
  const map = {
    en: {
      viable: "No absolute blocker was isolated. The main task is to preserve the current value case through disciplined execution, cost control, provider selection, and independent validation of legal, regulatory, tax, technical, and commercial assumptions.",
      conditional: "No single absolute blocker was isolated. The verdict remains conditional because material gates still need independent validation before any build-out or distribution decision.",
      non: "No single absolute blocker was isolated. The Non-viable verdict is driven by the cumulative imbalance between limited tokenization upside and the legal, compliance, custody, liquidity, tax, operating, or execution burden under the current structure.",
    },
    da: {
      viable: "Der er ikke identificeret én absolut hindring. Den centrale opgave er at bevare den nuværende værdilogik gennem disciplineret eksekvering, omkostningskontrol, leverandørvalg og uafhængig validering af juridiske, regulatoriske, skattemæssige, tekniske og kommercielle antagelser.",
      conditional: "Der er ikke identificeret én absolut hindring. Vurderingen er fortsat betinget, fordi væsentlige beslutningspunkter stadig skal valideres uafhængigt før opbygning eller distribution.",
      non: "Der er ikke identificeret én enkelt absolut hindring. Non-viable-vurderingen skyldes den samlede ubalance mellem begrænset tokeniseringspotentiale og den juridiske, compliance-mæssige, forvarings-, likviditets-, skattemæssige, driftsmæssige eller eksekveringsmæssige byrde i den nuværende struktur.",
    },
    de: {
      viable: "Es wurde kein absoluter Blocker isoliert. Entscheidend ist, den aktuellen Value Case durch disziplinierte Umsetzung, Kostenkontrolle, Anbieterauswahl und unabhängige Validierung rechtlicher, regulatorischer, steuerlicher, technischer und kommerzieller Annahmen zu erhalten.",
      conditional: "Es wurde kein einzelner absoluter Blocker isoliert. Das Ergebnis bleibt bedingt, weil wesentliche Entscheidungstore vor Build-out oder Distribution unabhängig validiert werden müssen.",
      non: "Es wurde kein einzelner absoluter Blocker isoliert. Das Non-viable-Ergebnis wird durch die kumulative Schieflage zwischen begrenztem Tokenisierungs-Upside und rechtlicher, Compliance-, Custody-, Liquiditäts-, Steuer-, Betriebs- oder Umsetzungsbelastung unter der aktuellen Struktur getrieben.",
    },
    fr: {
      viable: "Aucun blocage absolu n’a été isolé. L’enjeu principal est de préserver le cas de valeur actuel par une exécution disciplinée, le contrôle des coûts, le choix des prestataires et la validation indépendante des hypothèses juridiques, réglementaires, fiscales, techniques et commerciales.",
      conditional: "Aucun blocage absolu unique n’a été isolé. Le verdict reste conditionnel parce que des points de décision matériels doivent encore être validés indépendamment avant tout build-out ou distribution.",
      non: "Aucun blocage absolu unique n’a été isolé. Le verdict Non-viable est dû au déséquilibre cumulé entre un upside de tokenisation limité et la charge juridique, conformité, custody, liquidité, fiscale, opérationnelle ou d’exécution de la structure actuelle.",
    },
    es: {
      viable: "No se aisló ningún bloqueo absoluto. La tarea principal es preservar el caso de valor actual mediante ejecución disciplinada, control de costes, selección de proveedores y validación independiente de supuestos legales, regulatorios, fiscales, técnicos y comerciales.",
      conditional: "No se aisló un bloqueo absoluto único. El resultado sigue siendo condicional porque aún deben validarse de forma independiente puntos de decisión materiales antes de cualquier build-out o distribución.",
      non: "No se aisló un bloqueo absoluto único. El resultado Non-viable se debe al desequilibrio acumulado entre un upside de tokenización limitado y la carga legal, de cumplimiento, custodia, liquidez, fiscal, operativa o de ejecución bajo la estructura actual.",
    },
    "zh-Hans": {
      viable: "未识别出单一绝对阻碍。主要任务是通过有纪律的执行、成本控制、服务商选择，以及对法律、监管、税务、技术和商业假设的独立验证，保持当前价值逻辑。",
      conditional: "未识别出单一绝对阻碍。结论仍为有条件，因为在任何构建或分销决定前，若干重大决策门槛仍需独立验证。",
      non: "未识别出单一绝对阻碍。Non-viable 结论源于当前结构下有限的代币化上行价值与法律、合规、托管、流动性、税务、运营或执行负担之间的累积失衡。",
    },
    "pt-BR": {
      viable: "Nenhum bloqueador absoluto isolado foi identificado. A principal tarefa é preservar o atual caso de valor por meio de execução disciplinada, controle de custos, seleção de prestadores e validação independente das premissas jurídicas, regulatórias, tributárias, técnicas e comerciais.",
      conditional: "Nenhum bloqueador absoluto isolado foi identificado. O resultado permanece condicional porque gates materiais ainda precisam de validação independente antes de qualquer decisão de implementação ou distribuição.",
      non: "Nenhum bloqueador absoluto isolado foi identificado. O resultado Non-viable decorre do desequilíbrio cumulativo entre benefícios limitados da tokenização e a carga jurídica, de compliance, custódia, liquidez, tributária, operacional ou de execução da estrutura atual.",
    },
  };
  return (map[code] || map.en)[kind];
}

function getLocalizedFactorReadAcrossText(code = "en") {
  const map = {
    en: "Read these scores alongside the Stakeholder added value section. Stakeholder incentives, operational responsibilities, and cross-party alignment often determine whether modeled upside (New Opportunities, Cost Savings, Risk Reduction) is realized in practice, and whether frictions (Tokenization Costs, New Risks) remain acceptable. Material misalignment should be treated as a practical discount to feasibility and expected value.",
    da: "Læs disse scorer sammen med afsnittet om merværdi for interessenter. Incitamenter, operationelle ansvarsområder og sammenhængen mellem parterne afgør ofte, om den forventede positive værdiskabelse (Nye muligheder, Omkostningsbesparelser og Risikoreduktion) faktisk realiseres, og om friktionerne (Tokeniseringsomkostninger og Nye risici) forbliver acceptable. Væsentlig skævhed i incitamenterne bør behandles som et praktisk fradrag i gennemførlighed og forventet værdi.",
    de: "Lesen Sie diese Scores zusammen mit dem Abschnitt zum Mehrwert für Stakeholder. Anreize, operative Verantwortlichkeiten und die Ausrichtung zwischen den Parteien bestimmen häufig, ob modellierter Upside (Neue Chancen, Kosteneinsparungen und Risikoreduktion) praktisch realisiert wird und ob Friktionen (Tokenisierungskosten und Neue Risiken) akzeptabel bleiben. Wesentliche Fehlanreize sollten als praktischer Abschlag auf Machbarkeit und erwarteten Wert behandelt werden.",
    fr: "Lisez ces scores avec la section sur la valeur ajoutée pour les parties prenantes. Les incitations, responsabilités opérationnelles et l’alignement entre les parties déterminent souvent si l’upside modélisé (Nouvelles opportunités, Économies de coûts et Réduction des risques) se matérialise réellement, et si les frictions (Coûts de tokenisation et Nouveaux risques) restent acceptables. Un désalignement matériel doit être traité comme une décote pratique sur la faisabilité et la valeur attendue.",
    es: "Lea estas puntuaciones junto con la sección de valor añadido por stakeholder. Los incentivos, las responsabilidades operativas y la alineación entre las partes suelen determinar si el upside modelado (Nuevas oportunidades, Ahorro de costes y Reducción de riesgos) se materializa en la práctica, y si las fricciones (Costes de tokenización y Nuevos riesgos) siguen siendo aceptables. Una desalineación material debe tratarse como un descuento práctico sobre la viabilidad y el valor esperado.",
    "zh-Hans": "请将这些评分与利益相关方增值分析一并阅读。各方激励、运营责任以及跨主体协同，往往决定模型中的上行因素（新机会、成本节约、风险降低）能否在实践中实现，以及摩擦因素（代币化成本和新风险）是否仍可接受。重大的激励错配应被视为对可行性和预期价值的实际折减。",
    "pt-BR": "Leia estas pontuações em conjunto com a seção de Valor Agregado por stakeholder. Incentivos, responsabilidades operacionais e alinhamento entre as partes frequentemente determinam se os benefícios modelados (Novas Oportunidades, Economia de Custos e Redução de Riscos) serão realizados na prática e se as fricções (Custos de Tokenização e Novos Riscos) permanecerão aceitáveis. Desalinhamentos materiais devem ser tratados como um desconto prático sobre a viabilidade e o valor esperado.",
  };
  return map[code] || map.en;
}

function getLocalizedStakeholderScoreNote(code = "en") {
  const map = {
    en: "Net = (New Opportunities + Cost Savings + Risk Reduction) − (Tokenization Costs + New Risks). Stakeholder scores are perspective scores, not a decomposition of the overall project score. They show how the same structure looks from each party’s position, depending on which benefits they capture and which costs, risks, and delivery burdens they actually bear.",
    da: "Netto = (Nye muligheder + Omkostningsbesparelser + Risikoreduktion) − (Tokeniseringsomkostninger + Nye risici). Interessent-scorerne er perspektivscorer, ikke en dekomponering af den samlede projektscore. De viser, hvordan den samme struktur ser ud fra hver parts position, afhængigt af hvilke gevinster de opnår, og hvilke omkostninger, risici og leverancebyrder de faktisk bærer.",
    de: "Netto = (Neue Chancen + Kosteneinsparungen + Risikoreduktion) − (Tokenisierungskosten + Neue Risiken). Stakeholder-Scores sind Perspektiv-Scores und keine Zerlegung des gesamten Projektscores. Sie zeigen, wie dieselbe Struktur aus Sicht der jeweiligen Partei aussieht – abhängig davon, welche Vorteile sie erfasst und welche Kosten, Risiken und Lieferlasten sie tatsächlich trägt.",
    fr: "Net = (Nouvelles opportunités + Économies de coûts + Réduction des risques) − (Coûts de tokenisation + Nouveaux risques). Les scores par partie prenante sont des scores de perspective, non une décomposition du score global du projet. Ils montrent comment la même structure se présente du point de vue de chaque partie, selon les bénéfices captés et les coûts, risques et charges de livraison réellement assumés.",
    es: "Neto = (Nuevas oportunidades + Ahorro de costes + Reducción de riesgos) − (Costes de tokenización + Nuevos riesgos). Las puntuaciones por stakeholder son puntuaciones de perspectiva, no una descomposición de la puntuación global del proyecto. Muestran cómo se ve la misma estructura desde la posición de cada parte, según los beneficios que captura y los costes, riesgos y cargas de ejecución que realmente asume.",
    "zh-Hans": "净值 =（新机会 + 成本节约 + 风险降低）−（代币化成本 + 新风险）。利益相关方评分是视角评分，而不是整体项目评分的拆分。它们展示同一结构从不同参与方角度看会如何变化，取决于该方实际获得哪些收益，以及承担哪些成本、风险和交付负担。",
    "pt-BR": "Líquido = (Novas Oportunidades + Economia de Custos + Redução de Riscos) − (Custos de Tokenização + Novos Riscos). As pontuações por stakeholder são pontuações de perspectiva, não uma decomposição da pontuação geral do projeto. Elas mostram como a mesma estrutura se apresenta para cada parte, conforme os benefícios que captura e os custos, riscos e encargos de execução que efetivamente assume.",
  };
  return map[code] || map.en;
}

function getLocalizedWhoBearsCopy(code = "en") {
  const map = {
    en: {
      intro: "A frequent failure mode in tokenization is misaligned incentives: the issuer/asset owner carries most of the build, integration, legal, and compliance burden, while infrastructure providers may still earn fees and investors may still demand protections if rights, transfer mechanics, or operational utility remain unclear. Where that imbalance becomes too pronounced, tokenization can become commercially more attractive to service providers than to the initiating Asset Owner itself. Use the scores above to sanity-check incentive balance before you proceed.",
      assetOwnerNegative: "If the asset owner net score is materially negative while infrastructure is positive, the current configuration may be economically more attractive to service providers than to the initiating Asset Owner. In that situation, renegotiate scope, fee stack, delivery burden, and structural complexity before proceeding.",
      investorNegative: "If investor net score is low or negative, expect weak demand unless enforceability, disclosures, custody, and realistic exit paths are improved.",
      everyonePositive: "If everyone is positive, prioritize execution: timeline, partners, and operational readiness usually decide outcomes.",
      vendorCaptureConclusion: "Under the current initial configuration, this tokenization appears commercially more attractive to service providers than to the initiating Asset Owner.",
    },
    da: {
      intro: "En hyppig fejlkilde i tokenisering er skæve incitamenter: udstederen eller aktivejeren bærer størstedelen af build, integration, juridisk arbejde og compliance, mens infrastrukturleverandører stadig kan tjene honorarer, og investorer stadig kan kræve beskyttelse, hvis rettigheder, transfermekanik eller operationel nytte er uklar. Hvis denne ubalance bliver for stor, kan tokenisering blive mere kommercielt attraktiv for serviceleverandører end for den aktivejer, der initierer projektet. Brug scorerne ovenfor til at teste incitamentsbalancen, før projektet fortsættes.",
      assetOwnerNegative: "Hvis aktivejerens nettoscore er klart negativ, mens infrastrukturen er positiv, kan den nuværende konfiguration være økonomisk mere attraktiv for serviceleverandører end for den initierende aktivejer. I den situation bør scope, fee stack, leverancebyrde og strukturel kompleksitet genforhandles før videre eksekvering.",
      investorNegative: "Hvis investorernes nettoscore er lav eller negativ, må der forventes svag efterspørgsel, medmindre håndhævelighed, disclosures, custody og realistiske exit-veje forbedres.",
      everyonePositive: "Hvis alle interessenter er positive, bør fokus flyttes til eksekvering: tidsplan, partnere og operationel parathed afgør typisk udfaldet.",
      vendorCaptureConclusion: "Under den nuværende indledende konfiguration fremstår tokeniseringen kommercielt mere attraktiv for serviceleverandører end for den initierende aktivejer.",
    },
    de: {
      intro: "Ein häufiger Fehler in Tokenisierungsprojekten sind falsch ausgerichtete Anreize: Der Emittent oder Asset Owner trägt den Großteil von Aufbau, Integration, rechtlicher Strukturierung und Compliance, während Infrastrukturanbieter weiterhin Gebühren verdienen können und Investoren zusätzliche Schutzmechanismen verlangen, wenn Rechte, Transfermechanik oder operative Nutzbarkeit unklar bleiben. Wird dieses Ungleichgewicht zu stark, kann die Tokenisierung für Dienstleister kommerziell attraktiver werden als für den initiierenden Asset Owner selbst. Nutzen Sie die obigen Scores als Plausibilitätscheck für die Anreizbalance, bevor Sie fortfahren.",
      assetOwnerNegative: "Wenn der Nettoscore des Asset Owners deutlich negativ ist, während die Infrastruktur positiv abschneidet, kann die aktuelle Konfiguration wirtschaftlich attraktiver für Dienstleister sein als für den initiierenden Asset Owner. In dieser Situation sollten Umfang, Gebührenstruktur, Lieferlast und strukturelle Komplexität vor dem Fortfahren neu verhandelt werden.",
      investorNegative: "Wenn der Nettoscore der Investoren niedrig oder negativ ist, ist mit schwacher Nachfrage zu rechnen, sofern Durchsetzbarkeit, Offenlegung, Verwahrung und realistische Exit-Pfade nicht verbessert werden.",
      everyonePositive: "Wenn alle Stakeholder positiv abschneiden, sollte die Umsetzung priorisiert werden: Zeitplan, Partner und operative Bereitschaft entscheiden in der Regel über den Erfolg.",
      vendorCaptureConclusion: "Unter der aktuellen Ausgangskonfiguration erscheint diese Tokenisierung kommerziell attraktiver für Dienstleister als für den initiierenden Asset Owner.",
    },
    fr: {
      intro: "Un mode d’échec fréquent dans la tokenisation est le mauvais alignement des incitations : l’émetteur ou le propriétaire de l’actif supporte l’essentiel de la construction, de l’intégration, du travail juridique et de la conformité, tandis que les prestataires d’infrastructure peuvent continuer à percevoir des frais et que les investisseurs peuvent exiger des protections si les droits, la mécanique de transfert ou l’utilité opérationnelle restent flous. Lorsque ce déséquilibre devient trop marqué, la tokenisation peut devenir commercialement plus attractive pour les prestataires que pour le propriétaire de l’actif à l’origine du projet. Utilisez les scores ci-dessus pour vérifier l’équilibre des incitations avant de poursuivre.",
      assetOwnerNegative: "Si le score net du propriétaire de l’actif est nettement négatif alors que celui de l’infrastructure est positif, la configuration actuelle peut être économiquement plus attractive pour les prestataires de services que pour le propriétaire de l’actif à l’origine du projet. Dans ce cas, renégociez le périmètre, la structure de frais, la charge d’exécution et la complexité structurelle avant de poursuivre.",
      investorNegative: "Si le score net des investisseurs est faible ou négatif, il faut s’attendre à une demande limitée, sauf si l’opposabilité, les informations, la conservation et les voies de sortie réalistes sont améliorées.",
      everyonePositive: "Si tous les acteurs sont positifs, priorisez l’exécution : calendrier, partenaires et préparation opérationnelle déterminent généralement le résultat.",
      vendorCaptureConclusion: "Dans la configuration initiale actuelle, cette tokenisation paraît commercialement plus attractive pour les prestataires de services que pour le propriétaire de l’actif à l’origine du projet.",
    },
    es: {
      intro: "Un fallo frecuente en la tokenización es la desalineación de incentivos: el emisor o propietario del activo asume gran parte del build, la integración, el trabajo legal y el compliance, mientras que los proveedores de infraestructura pueden seguir cobrando honorarios y los inversores pueden exigir protecciones si los derechos, la mecánica de transferencia o la utilidad operativa no están claros. Si este desequilibrio es demasiado pronunciado, la tokenización puede resultar más atractiva comercialmente para los proveedores que para el propietario que inicia el proyecto. Utilice las puntuaciones anteriores para comprobar el equilibrio de incentivos antes de avanzar.",
      assetOwnerNegative: "Si la puntuación neta del propietario del activo es materialmente negativa mientras la infraestructura es positiva, la configuración actual puede ser económicamente más atractiva para los proveedores de servicios que para el propietario que inicia el proyecto. En ese caso, renegocie alcance, estructura de costes, carga de ejecución y complejidad estructural antes de continuar.",
      investorNegative: "Si la puntuación neta de los inversores es baja o negativa, cabe esperar una demanda débil salvo que mejoren la exigibilidad, la divulgación, la custodia y las vías realistas de salida.",
      everyonePositive: "Si todos los stakeholders son positivos, priorice la ejecución: calendario, socios y preparación operativa suelen decidir el resultado.",
      vendorCaptureConclusion: "Bajo la configuración inicial actual, esta tokenización parece comercialmente más atractiva para los proveedores de servicios que para el propietario del activo que inicia el proyecto.",
    },
    "zh-Hans": {
      intro: "代币化项目中常见的失败模式是激励错配：发行人或资产所有人承担了大部分构建、集成、法律和合规负担，而基础设施服务商仍可收取费用；如果权利、转让机制或运营效用不清晰，投资者也仍会要求保护。当这种不平衡过于明显时，代币化可能对服务商比对发起项目的资产所有人更具商业吸引力。请使用上述评分，在继续推进前检查激励是否平衡。",
      assetOwnerNegative: "如果资产所有人的净分明显为负，而基础设施服务商为正，则当前结构可能在经济上对服务商比对发起项目的资产所有人更有吸引力。在这种情况下，应在继续推进前重新谈判范围、费用结构、交付负担和结构复杂性。",
      investorNegative: "如果投资者净分较低或为负，除非在可执行性、披露、托管和现实退出路径方面作出改进，否则应预期需求较弱。",
      everyonePositive: "如果所有利益相关方均为正面，应优先关注执行：时间表、合作伙伴和运营准备通常决定最终结果。",
      vendorCaptureConclusion: "在当前初始配置下，该代币化安排对服务提供方的商业吸引力似乎高于对发起项目的资产所有人。",
    },
    "pt-BR": {
      intro: "Um modo recorrente de falha em projetos de tokenização é o desalinhamento de incentivos: o emissor ou proprietário do ativo assume grande parte da construção, integração, estruturação jurídica e compliance, enquanto prestadores de infraestrutura ainda podem receber fees e investidores podem exigir proteções adicionais quando direitos, mecânica de transferência ou utilidade operacional permanecem pouco claros. Quando esse desequilíbrio se torna excessivo, a tokenização pode ser comercialmente mais atraente para os prestadores do que para o proprietário do ativo que iniciou o projeto. Use as pontuações acima para verificar o equilíbrio de incentivos antes de avançar.",
      assetOwnerNegative: "Se a pontuação líquida do proprietário do ativo for materialmente negativa enquanto a infraestrutura tiver pontuação positiva, a configuração atual pode ser economicamente mais atraente para os prestadores de serviços do que para o proprietário que iniciou o projeto. Nesse caso, renegocie escopo, estrutura de fees, carga de execução e complexidade estrutural antes de avançar.",
      investorNegative: "Se a pontuação líquida dos investidores for baixa ou negativa, espere demanda fraca a menos que exigibilidade, disclosures, custódia e rotas realistas de saída sejam melhoradas.",
      everyonePositive: "Se todos os stakeholders apresentarem pontuação positiva, priorize a execução: cronograma, parceiros e prontidão operacional normalmente determinam o resultado.",
      vendorCaptureConclusion: "Na configuração inicial atual, esta tokenização parece comercialmente mais atraente para os prestadores de serviços do que para o proprietário do ativo que iniciou o projeto.",
    },
  };
  return map[code] || map.en;
}

const ENGLISH_FULL_DISCLAIMER = [
  "This report is provided for informational and decision-support purposes only. It does not constitute legal, regulatory, tax, accounting, investment, valuation, or financial advice, and it should not be relied upon as a substitute for professional advice tailored to the specific facts and circumstances of the case.",
  "TVAC is a structured assessment tool designed to evaluate whether and when tokenization may create added value relative to conventional alternatives. Its outputs reflect a methodology-based analytical framework operating under uncertainty. They are not guarantees of commercial success, feasibility, investor demand, regulatory approval, liquidity, execution quality, budget outcome, or risk reduction.",
  "Any scores, verdicts, scenarios, stakeholder assessments, suggested actions, implementation observations, and roadmap elements in this report are indicative only and depend on the quality, completeness, and interpretation of the input data, as well as on assumptions about structure, jurisdiction, service-provider readiness, market conditions, execution capability, and timing.",
  "Tokenization outcomes can differ materially from this assessment if key conditions change, including legal structuring, investor scope, jurisdiction count, custody model, distribution pathway, settlement design, governance arrangements, servicing readiness, technology integration, cost allocation, evidence quality, or liquidity support.",
  "A TVAC verdict is a tokenization added-value verdict only. It must not be presented or used as investment approval, credit rating, yield validation, issuer endorsement, regulatory approval, due diligence report, or investor recommendation. TVAC does not validate projected returns, coupon payments, credit quality, valuations, legal enforceability, tax treatment, or investor suitability.",
  "This report does not replace formal legal analysis, regulatory review, tax analysis, technical architecture review, cybersecurity review, accounting assessment, operational diligence, commercial validation, or board-level decision processes. Independent advice should always be obtained before any structuring, issuance, distribution, investment, operational, or implementation decision is made.",
  "Neither TVAC nor Astoval assumes responsibility for actions taken on the basis of this report alone. Users remain solely responsible for all commercial, legal, regulatory, operational, and strategic decisions related to any proposed tokenization initiative.",
];



const OPTION_HELP = {
  instrumentType: {
    wrapper:
      "The token acts as a digital representation or wrapper around rights recorded in a transfer agent, registrar, CSD, or comparable off-chain record.",
    "Tokenized security via legal wrapper (SPV structure)":
      "The “real” security is still recorded at a CSD/registrar or transfer agent. The token is a digital representation that mirrors the traditional share/bond register.",
    "sole-record":
      "The token ledger is the primary legal record of ownership. There is no parallel paper or CSD register – the token itself is the security.",
  },
  custodyModel: {
    "regulated-custodian":
      "A licensed bank, broker or investment firm safekeeps the tokens on omnibus or segregated accounts on behalf of investors.",
    "ta/csd":
      "A classic registrar, transfer agent or CSD holds the underlying positions, while tokens represent claims or positions on that register.",
    "self-custody":
      "Investors hold tokens directly in their own wallets, using private keys and on-chain addresses instead of traditional custody accounts.",
  },
  liquidityCadence: {
    none:
      "No organised secondary market is expected – investors should assume a buy-and-hold position with only exceptional exits.",
    "ad-hoc otc":
      "Occasional, bilateral transfers or brokered deals may happen, but there is no standing market or central order book.",
    quarterly:
      "Trading is only possible in defined windows (e.g. quarterly auctions or redemption periods) with no continuous intraday trading.",
    continuous:
      "Investors can trade on an ongoing basis during market hours, with standing bids/offers and market-driven prices.",
  },
  venueType: {
    "ATS/MTF":
      "A regulated multilateral trading facility or ATS where orders are matched under a rulebook and overseen by a financial regulator.",
    DEX:
      "A blockchain-based trading venue where only whitelisted participants can trade under KYC/AML and venue rules.",
    OTC:
      "Over-the-counter trading via dealers, brokers, or bilateral agreements, typically with negotiated prices and limited transparency.",
    "CSD/TA-registered":
      "Transfers are processed directly on the registrar, TA or CSD’s books – often using instructions from custodians rather than a public market.",
  },
  settlementModel: {
    "not-decided":
      "The payment and settlement model has not been decided yet. TVAC will treat this as an open design question, not as an automatic failure.",
    "not-material-now":
      "The project does not currently rely on fast or digital settlement. TVAC will keep the cash-leg issue low-weight unless the case claims settlement-dependent benefits.",
    "traditional-fiat-rails":
      "Payments are expected to use ordinary bank transfers or other conventional fiat rails. This may be sufficient for early-stage or low-liquidity cases.",
    "custodian-bank-settlement":
      "Payments are expected to be handled through a custodian, bank, transfer agent, CSD or comparable regulated operational setup.",
    "stablecoin-tokenized-cash":
      "The case expects to use a regulated stablecoin, tokenized cash or similar digital money instrument for the cash leg.",
    "deposit-token-tokenized-deposit":
      "The case expects to use a bank deposit token, tokenized deposit, institutional DvP rail or comparable bank-backed digital cash model.",
    "T+0 atomic":
      "Advanced/technical: cash and tokens move simultaneously on (almost) a real-time basis – no gap between trade and final settlement.",
    "T+1/2":
      "Advanced/technical: traditional securities-style settlement where trades are agreed today and settled one or two business days later.",
    "off-chain":
      "Advanced/technical: economic transfers are recorded off-chain, with on-chain movements only reflecting aggregated or periodic updates.",
  },
  disclosure: {
    Unknown:
      "The disclosure regime is not yet decided – TVAC will treat this as a major uncertainty for feasibility and investor access.",
    KID: "A short-form key information document or similar PRIIPs-style regime, typically used for certain retail-facing offerings.",
    Prospectus:
      "A full, approved prospectus under securities law, enabling broader investor access but with higher cost and complexity.",
    Exempt:
      "Reliance on exemptions (e.g. to professional/qualified investors or high minimum tickets), avoiding a full prospectus but limiting who can invest.",
  },
};

/* ---------- Example cases (UX helper) ---------- */

const EXAMPLE_CASES = [
  {
    id: "re-eu-spv",
    title: "Example 1 — EU real estate holding + SPV notes",
    region: "Europe",
    category: "Capital raising · Real estate",
    summary: "A Luxembourg note structure funding German residential upgrades, with cross-border distribution and quarterly transfer ambitions.",
    text:
      "We are a privately owned real estate holding company owning three stabilized residential assets in Berlin and Hamburg through German GmbHs. The properties generate steady rental income but require energy upgrades, façade improvements, and selected tenant-area refurbishments to remain competitive and compliant with evolving ESG standards.\n\n" +
      "We intend to raise EUR 15–20 million to finance renovations, partially refinance bank debt, and strengthen liquidity buffers. Instead of raising additional traditional mezzanine capital, we are evaluating tokenization through a Luxembourg SPV issuing profit-participating notes linked to net rental cash flow after operating costs and senior debt service.\n\n" +
      "The tokens would represent clear contractual economic rights, including profit participation and principal repayment, but no operational control over the properties. We would target professional and accredited investors in Germany and the Netherlands only, using a whitelist-based onboarding process with full KYC/AML.\n\n" +
      "We plan a 12-month lock-up followed by structured quarterly liquidity windows on a regulated venue. Custody would be provided by a regulated custodian. Settlement follows T+1/2 standards aligned with existing capital market workflows. Key considerations include cross-border tax treatment, renovation execution risk, valuation stability, and interest-rate sensitivity. The primary rationale for tokenization is improved investor transparency, fractional access for smaller professional tickets, and long-term administrative efficiency.",
    structured: {
      issuanceJurisdiction: "Luxembourg",
      saleJurisdictions: "Germany, Netherlands",
      instrumentType: "wrapper",
      custodyModel: "regulated-custodian",
      liquidityCadence: "quarterly",
      venueType: "ATS/MTF",
      settlementModel: "T+1/2",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist + EU gating + 12-month lock-up",
      taxNotes: "Cross-border withholding considerations",
      investorTypes: ["professional", "accredited"],
    },
  },
  {
    id: "private-credit",
    title: "Example 2 — US private credit with limited cross-border distribution",
    region: "United States",
    category: "Capital raising · Private credit",
    summary: "Tokenized notes backed by short-dated receivables, using restricted US distribution and a small professional offshore tranche.",
    text:
      "We operate a diversified private credit strategy purchasing short-dated SME and mid-market receivables from vetted originators. The pool is diversified, short duration, and run with strict eligibility criteria, concentration limits, and third-party servicing. Historically, capital has come from a small group of institutional investors.\n\n" +
      "We are considering issuing tokenized notes via a US entity (bankruptcy-remote SPV or equivalent) where token holders receive contractual cash-flow rights (coupon + principal) tied to net collections from the receivables pool. The objective is operational efficiency and scalable investor onboarding, not DeFi-style composability: standardized documentation, automated reporting, and streamlined distribution of cash flows.\n\n" +
      "Distribution is planned as US accredited investors under Reg D (e.g., 506(c)) with transfer restrictions enforced via whitelist and compliance checks. We also consider a limited Reg S tranche for a small set of professional investors outside the US (e.g., Canada and the UK) with clear jurisdiction gating and no retail marketing.\n\n" +
      "We do not target continuous exchange trading. Any secondary transfers would be controlled, occurring in defined quarterly liquidity windows, and routed via approved intermediaries and/or an eligible private-market venue. Custody is via a regulated custodian (institutional wallet infrastructure). Settlement is conservative (T+1/2) using standard cash rails; no stablecoin dependency is assumed.\n\n" +
      "Key risks include US securities compliance (investor verification, transfer restrictions, broker-dealer/ATS considerations for any secondary activity), state-level and cross-border marketing constraints, and enforceability of receivables assignments. The final distribution perimeter, service-provider responsibilities and post-trade operating model still need to be confirmed.",
    structured: {
      issuanceJurisdiction: "United States",
      saleJurisdictions: "US, Canada, UK",
      instrumentType: "wrapper",
      custodyModel: "regulated-custodian",
      liquidityCadence: "quarterly",
      venueType: "OTC",
      settlementModel: "T+1/2",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist + accredited-only (Reg D) + jurisdiction gating (Reg S tranche)",
      taxNotes: "US withholding and investor tax reporting depend on structure; cross-border investors require additional tax analysis.",
      investorTypes: ["accredited", "professional"],
    },
  },
  {
    id: "late-stage-pe-viable",
    title: "Example 3 — US commercial paper with institutional DvP rails",
    region: "United States",
    category: "Institutional workflow · Commercial paper",
    summary: "An existing commercial-paper programme exploring a tokenized lane to reduce reconciliation, exceptions and settlement friction.",
    text: `We are a US-based corporate issuer with an existing, recurring commercial paper (CP) program used for short-term working capital. Today the program relies on multiple intermediaries, manual confirmations, repeated reconciliations between the issuer’s treasury team, issuing/placing agents, custodians, and investors, and a settlement cycle that creates operational friction and occasional settlement fails.

We want to introduce a tokenized CP note format (economically equivalent to our existing CP) for professional and accredited investors only. This is not a retail product and not a public exchange initiative. The scope is deliberately conservative: a single US issuance + distribution perimeter, whitelisted participation, and institutional-grade custody. The primary objective is post-trade efficiency and risk reduction rather than “new liquidity”.

The tokenized format is designed to (1) reduce reconciliation breaks by moving to a single shared position record, (2) automate issuance and lifecycle events (issuance confirmations, coupon/accrual reporting, maturity repayment notices), and (3) tighten settlement controls through delivery-versus-payment (DvP) style workflows. Importantly, we commit to decommissioning duplicated legacy steps for the tokenized tranche (no permanent parallel “double-booking”), so savings are structural rather than cosmetic.

We expect measurable cost savings (reduced agent/custody operations per issuance, fewer manual exceptions) and measurable risk reduction (fewer settlement fails, cleaner audit trail, reduced counterparty/operational exposure). Any secondary transfers, if permitted at all, are tightly controlled and limited to eligible whitelisted holders under standard securities restrictions. The issuer is the primary beneficiary of the operational savings and control improvements under this design.`,
    structured: {
      issuanceJurisdiction: "United States",
      saleJurisdictions: "US",
      instrumentType: "wrapper",
      custodyModel: "regulated-custodian",
      liquidityCadence: "none",
      venueType: "OTC",
      settlementModel: "T+1/2",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist + accredited/professional-only + issuer/agent-approved transfers",
      taxNotes: "Standard US withholding and investor reporting depend on holder type; tax handled under existing CP program processes.",
      investorTypes: ["professional", "accredited"],
    },
  },
  {
    id: "tokenized-fund-shareclass",
    title: "Example 4 — US collateral / repo settlement rail with permissioned DvP",
    region: "United States",
    category: "Institutional workflow · Collateral / repo",
    summary: "A closed institutional collateral lane focused on intraday mobility, DvP, fewer settlement fails and reduced reconciliation.",
    text: `We are an institutional market participant running a high-volume collateral and secured financing (repo-style) workflow in US government securities. The underlying instruments are already liquid; the value-add of tokenization is not “creating liquidity”, but reducing post-trade friction, settlement fails, and operational risk in collateral movements.

We want to run a permissioned tokenized collateral rail where eligible participants (professional institutions only) can move tokenized collateral positions under strict eligibility controls, with regulated custody and clear operational governance. The design is single-jurisdiction (US) and deliberately avoids retail access, public DeFi venues, and open-ended secondary trading.

Crucially, this is not a greenfield build: the custody and tri-party / agent workflows already exist, and the token layer is implemented as an incremental module on top of existing collateral eligibility rules, margining processes, and reporting. Participants are whitelisted, controls are institutional-grade, and operational responsibilities are clearly assigned. The Asset Owner / initiating participant is the primary beneficiary of the operational savings and risk reduction (not external vendors).

The tokenization layer is used to implement (1) near-instant, controlled delivery-versus-payment (DvP) settlement to reduce counterparty exposure during settlement windows, (2) cleaner intraday collateral mobility (substitution, margining, and return flows) with fewer manual breaks, and (3) an auditable, tamper-evident event trail that reduces disputes and accelerates reconciliation between counterparties and custodians. We explicitly decommission duplicated reconciliation steps for the tokenized lane (no permanent parallel “double-booking”), so savings are structural.

Expected benefits are measurable at scale: fewer fails, fewer exception tickets, faster dispute resolution, tighter control over collateral release, and reduced reconciliation time per movement. Costs and risks remain real (legal/compliance scoping, integration, governance), but the scope is bounded: permissioned participants, regulated custody, conservative US-only perimeter, and no reliance on stablecoins or permissionless liquidity.`,
    structured: {
      issuanceJurisdiction: "United States",
      saleJurisdictions: "US",
      instrumentType: "wrapper",
      custodyModel: "regulated-custodian",
      liquidityCadence: "ad-hoc otc",
      venueType: "OTC",
      settlementModel: "T+0 atomic",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist + professional-only + permissioned counterparties + controlled DvP settlement",
      taxNotes: "Standard tax posture for government securities; operational/tax reporting handled under existing institutional processes.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "public-retail-fund",
    title: "Example 5 — Global retail fine-art fractionalization",
    region: "Global / offshore",
    category: "Special case · Retail / fine art",
    summary: "An intentionally weak retail NFT concept with unclear rights, unrestricted transfers and heavy reliance on perceived DEX liquidity.",
    text:
      "We plan to acquire a portfolio of fine art pieces and offer fractional ownership to a global retail audience through NFTs. Marketing would be conducted via social media campaigns, and participation would be open to anyone able to connect a self-custody wallet.\n\n" +
      "The legal structure is not fully defined. Tokens may represent a mixture of contractual claims, informal profit-sharing promises, and membership rights tied to an offshore holding entity. We do not intend to publish a prospectus or formal disclosure document and prefer minimal onboarding friction.\n\n" +
      "Tokens would trade continuously on decentralized exchanges with atomic on-chain settlement. Transfers would not be restricted by jurisdiction, and investors would self-custody their tokens.\n\n" +
      "The business thesis relies on global retail demand, social momentum, and perceived liquidity from DEX trading. However, significant risks include unclear investor protection, cross-border securities law exposure, valuation disputes, provenance challenges, market manipulation, and regulatory enforcement uncertainty. The economic and legal alignment between token holders and the underlying artwork ownership remains structurally ambiguous.",
    structured: {
      issuanceJurisdiction: "Cayman Islands",
      saleJurisdictions: "US, UK, EU, Singapore",
      instrumentType: "sole-record",
      custodyModel: "self-custody",
      liquidityCadence: "continuous",
      venueType: "DEX",
      settlementModel: "T+0 atomic",
      disclosure: "",
      transferRestrictions: "None",
      taxNotes: "Unclear cross-border tax treatment",
      investorTypes: ["retail"],
    },
  },
  {
    id: "brazil-agribusiness-receivables",
    title: "Example 6 — Brazilian agribusiness receivables",
    region: "Brazil",
    category: "Capital raising · Agribusiness receivables",
    summary: "A receivables pool seeking professional capital, while the vehicle, evidence package, custody and transfer design remain open.",
    text: `We are a Brazilian agricultural finance company working with midsized grain and input distributors. We purchase short-dated receivables arising from sales to farms and cooperatives, and we currently finance the portfolio through bank facilities and a small number of private investors. The portfolio has historical payment data, but the data is held across several originators and has not yet been standardized for external investors.

We are considering a tokenized note or participation structure linked to a diversified pool of receivables. The objective is to broaden access to professional investors, improve reporting on collections and concentration limits, and reduce some of the manual administration between originators, servicers and investors. We have not decided whether the issuance should sit in a dedicated securitization vehicle, another SPV structure, or an existing fund arrangement.

The initial idea is a Brazil-only professional-investor offering with whitelist-based onboarding. We do not need continuous trading, and a buy-and-hold structure may be sufficient. We have spoken informally with technology and custody providers, but we do not yet have binding quotations, a final registry model or a confirmed approach to secondary transfers.

The key unresolved questions are the legal and operational link between the token and the receivables, assignment and servicing continuity, data quality across originators, cash-flow controls, tax treatment and whether investors will value tokenization enough to offset the additional structuring and provider costs. No legal opinion or investor commitment has been obtained yet.`,
    structured: {
      issuanceJurisdiction: "Brazil",
      saleJurisdictions: "Brazil",
      instrumentType: "wrapper",
      custodyModel: "unknown",
      liquidityCadence: "none",
      venueType: "unknown",
      settlementModel: "traditional-fiat-rails",
      disclosure: "",
      transferRestrictions: "Whitelist; professional investors only; final holding and transfer rules not decided",
      taxNotes: "Tax, withholding and vehicle treatment have not yet been confirmed.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "argentina-export-receivables",
    title: "Example 7 — Argentine export receivables",
    region: "Argentina / Latin America",
    category: "Capital raising · Export receivables",
    summary: "USD-linked export receivables considered for tokenized funding amid unresolved vehicle, currency-flow and cross-border questions.",
    text: `We are a group of Argentine food exporters with recurring receivables from established buyers in Europe and Latin America. Payment terms are typically 60–120 days, and working-capital needs increase sharply during the production season. Today we rely on local bank credit, bilateral factoring and shareholder funding, none of which fully matches the timing of the export cycle.

We are exploring whether a tokenized financing structure could give professional investors exposure to a diversified pool of export receivables. The tentative idea is that investors would receive contractual rights to collections after fees and reserves, but we have not decided whether the issuer should be an Argentine vehicle, an offshore SPV, a trust-like structure or an existing fund. We are also unsure whether the token would merely mirror an off-chain register or be used as the primary position record.

The first phase would be limited to professional investors. We are not assuming a liquid secondary market, and ordinary bank payments may be sufficient. A provider has suggested using a stablecoin for faster cross-border settlement, but we have not assessed whether that is necessary, legally workable or acceptable to investors.

Major open questions include receivables assignment and perfection, foreign-exchange and payment-flow constraints, withholding and tax treatment, servicing continuity, investor reporting, custody, and whether the additional cross-border structure would cost more than conventional trade-finance alternatives. We have transaction data but no completed legal or investor-demand validation.`,
    structured: {
      issuanceJurisdiction: "Argentina",
      saleJurisdictions: "Argentina, Uruguay, Chile",
      instrumentType: "unknown",
      custodyModel: "unknown",
      liquidityCadence: "",
      venueType: "unknown",
      settlementModel: "not-decided",
      disclosure: "",
      transferRestrictions: "Professional investors only; jurisdiction gating under consideration",
      taxNotes: "Currency-flow, withholding and cross-border tax treatment remain unresolved.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "panama-warehouse-receipts",
    title: "Example 8 — Panama warehouse-receipt trade finance",
    region: "Central America",
    category: "Institutional workflow · Trade-finance collateral",
    summary: "A permissioned shared record for warehouse receipts and bank financing, with authority, inspection and cash-leg design still unresolved.",
    text: `We operate a regional warehousing and commodity-logistics business in Panama serving exporters of coffee, cocoa and other agricultural products. Financing is currently arranged through banks using warehouse receipts, inspection reports and separate collateral-control documents. The process involves repeated document checks, manual reconciliation and delays when goods move, are substituted or are released.

We are considering a permissioned tokenized warehouse-receipt system that could be used by approved warehouses, banks, exporters, inspectors and insurers. The intended value is not public fundraising or speculative trading. It is a clearer shared record of pledged inventory, faster approval of collateral changes, better audit trails and fewer discrepancies between the warehouse, lender and borrower records.

We have not yet decided whether the token would have legal status as the authoritative warehouse receipt or merely mirror the existing paper/electronic record. We also do not know whether one registry operator, a consortium or an external technology provider should control issuance and cancellation. The first pilot would likely cover Panama-based goods and a small number of regional banks, with no retail participation and no open secondary market.

Open questions include the legal hierarchy between token and conventional records, inspection and quantity data, fraud and double-pledge controls, responsibility for erroneous releases, interoperability with bank systems, settlement of financing proceeds, cross-border goods movements and the economics of replacing rather than duplicating existing processes. No production pilot or binding bank commitment exists yet.`,
    structured: {
      issuanceJurisdiction: "Panama",
      saleJurisdictions: "Panama",
      instrumentType: "wrapper",
      custodyModel: "unknown",
      liquidityCadence: "none",
      venueType: "CSD/TA-registered",
      settlementModel: "custodian-bank-settlement",
      disclosure: "",
      transferRestrictions: "Permissioned banks, warehouses and approved trade participants; final control model not decided",
      taxNotes: "Cross-border trade-finance, stamp and transfer-tax implications require review.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "chile-environmental-revenue-rights",
    title: "Example 9 — Chilean environmental revenue rights",
    region: "Chile / Andean region",
    category: "Capital raising · Environmental revenue rights",
    summary: "A renewable-energy developer considering tokenized revenue participation, with registry, measurement and investor-demand evidence incomplete.",
    text: `We are a Chilean renewable-energy developer with several operating solar assets and a pipeline of smaller projects. We are considering raising capital against a defined share of future project revenues, potentially including energy-sale income and environmental attributes where those rights can be clearly identified and transferred. The objective is to fund new construction without selling full ownership of the operating companies.

One option is a tokenized participation note issued through a dedicated vehicle. Investors would receive contractual economic rights, but not control of the projects. We expect to target professional investors in Chile and possibly a limited number of investors elsewhere in the region. We have not decided whether environmental attributes should be included in the instrument, kept separate, or excluded until their registry and verification treatment is clearer.

We do not currently assume continuous trading. Periodic transfers or redemptions might be useful, but no venue or liquidity provider has been selected. Custody, the authoritative investor register and the payment process are also open, and we have not decided which party would operate those functions. We have engineering and production data for the operating assets, but no independent assessment of the proposed token rights, projected investor demand or the incremental cost of tokenization.

The main uncertainties are rights mapping, project and vehicle cash-flow waterfalls, measurement and verification of environmental attributes, changes in registry treatment, construction risk for new assets, cross-border distribution, tax, and whether investors would prefer a conventional private placement.`,
    structured: {
      issuanceJurisdiction: "Chile",
      saleJurisdictions: "Chile, Peru, Colombia",
      instrumentType: "wrapper",
      custodyModel: "unknown",
      liquidityCadence: "ad-hoc otc",
      venueType: "unknown",
      settlementModel: "traditional-fiat-rails",
      disclosure: "",
      transferRestrictions: "Whitelist; professional investors; regional distribution not yet confirmed",
      taxNotes: "Vehicle, withholding and treatment of environmental-related revenues remain unresolved.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "uae-asset-backed-digital-notes",
    title: "Example 10 — UAE asset-backed digital notes",
    region: "UAE / GCC",
    category: "Capital raising · Asset-backed notes",
    summary: "A logistics sponsor exploring a Shariah-compliant asset-backed structure, while legal form, approvals, custody and distribution remain open.",
    text: `We are a UAE-based logistics group seeking financing for a portfolio of leased warehouse and transport assets. The assets generate contractual lease and service payments, and we are considering whether a digital asset-backed note or sukuk-like structure could attract professional investors while improving administration and reporting.

The current concept is to place selected assets or contractual cash-flow rights into a dedicated vehicle and issue tokens representing defined payment entitlements. We want the economic structure to be capable of Shariah review, but the exact contractual form, asset-transfer mechanics and approval process have not been selected. We have not received a legal opinion or Shariah board approval.

The first issuance would probably be based in the UAE and marketed to professional investors in the UAE and a small number of GCC markets. We are unsure whether cross-border distribution is necessary for the pilot. Regulated custody is likely, but no provider has been chosen. We do not require continuous secondary trading; controlled bilateral transfers may be enough. The cash leg could use ordinary bank settlement, although one provider has proposed tokenized deposits for later phases.

Key questions include ownership and enforceability of the underlying asset rights, insolvency treatment, servicing and payment waterfalls, investor eligibility, disclosure, cross-border marketing, tax and zakat considerations, custody, and whether the added technology and structuring costs are justified at the intended issuance size.`,
    structured: {
      issuanceJurisdiction: "United Arab Emirates",
      saleJurisdictions: "United Arab Emirates, Saudi Arabia, Bahrain",
      instrumentType: "wrapper",
      custodyModel: "unknown",
      liquidityCadence: "ad-hoc otc",
      venueType: "OTC",
      settlementModel: "traditional-fiat-rails",
      disclosure: "",
      transferRestrictions: "Whitelist; professional investors; GCC jurisdiction gating to be defined",
      taxNotes: "Cross-border tax, withholding and zakat treatment require qualified review.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "saudi-infrastructure-fund-units",
    title: "Example 11 — Saudi infrastructure fund units",
    region: "Saudi Arabia / GCC",
    category: "Fund units · Infrastructure",
    summary: "A professional infrastructure vehicle considering tokenized units for smaller tickets and better administration, without a settled transfer model.",
    text: `We manage a Saudi infrastructure investment vehicle holding interests in operating utility and logistics projects. Capital has so far come from a limited number of institutions and family investment groups. We are considering a tokenized feeder or unit class to support smaller professional-investor tickets, more efficient onboarding and clearer position reporting.

The underlying investments would remain unchanged. The token would represent an interest in the fund or feeder structure rather than direct ownership of infrastructure assets. We have not decided whether the token should mirror the existing administrator register or become a primary record for a new unit class. We also have not chosen the issuing vehicle, platform, custodian or transfer administrator.

The pilot would likely target Saudi professional investors, with possible later distribution to selected GCC investors. We do not want retail participation. Secondary liquidity is not essential to the initial case, although some investors have asked whether periodic transfer windows could be offered. No venue, matching mechanism or market maker has been identified.

The main open issues are fund and securities-law classification, investor eligibility, cross-border distribution, governance and voting rights, capital calls and distributions, valuation and NAV reporting, custody, transfer approvals, tax and zakat treatment, and the cost of operating both tokenized and conventional unit classes. We have informal interest from several investors but no documented commitments or provider quotations.`,
    structured: {
      issuanceJurisdiction: "Saudi Arabia",
      saleJurisdictions: "Saudi Arabia, United Arab Emirates, Bahrain",
      instrumentType: "wrapper",
      custodyModel: "unknown",
      liquidityCadence: "",
      venueType: "unknown",
      settlementModel: "custodian-bank-settlement",
      disclosure: "",
      transferRestrictions: "Professional investors only; transfer approvals and GCC gating not yet defined",
      taxNotes: "Tax, withholding and zakat treatment have not been finalized.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "singapore-private-markets-fund",
    title: "Example 12 — Singapore private-markets fund units",
    region: "Singapore / Asia",
    category: "Fund units · Private markets",
    summary: "A private-markets manager exploring a tokenized feeder for onboarding and lifecycle efficiency, with transfer and cross-border scope unresolved.",
    text: `We are a Singapore-based private-markets manager investing in growth companies and private credit across Asia. Our existing funds use conventional subscription documents, administrator records, capital calls and periodic investor reporting. We are assessing whether a tokenized feeder fund or tokenized unit class could reduce onboarding friction and improve the administration of investor positions.

The intended investors are professional and institutional investors. We are not proposing public retail distribution. The token would probably mirror interests recorded by a fund administrator, but we have not decided whether the administrator, a transfer agent or the token platform would hold the authoritative record. We are also unsure whether tokenization should cover only subscriptions and ownership records or extend to capital calls, distributions and limited secondary transfers.

The initial jurisdiction could be Singapore, with possible participation by professional investors in Hong Kong and the UAE. That cross-border scope is not fixed. A regulated custodian may be used, but some potential investors prefer positions reflected through their existing custody arrangements. Secondary liquidity is not central to the business case; occasional manager-approved transfers may be enough.

We have a draft operating concept and historical administration-cost data, but no completed provider comparison, legal analysis or investor survey. Key questions include fund structure, distribution permissions, authoritative registry, transfer restrictions, privacy, capital-call workflows, settlement, tax, provider portability and whether the tokenized feeder would genuinely replace manual processes or merely add another layer.`,
    structured: {
      issuanceJurisdiction: "Singapore",
      saleJurisdictions: "Singapore, Hong Kong, United Arab Emirates",
      instrumentType: "wrapper",
      custodyModel: "ta/csd",
      liquidityCadence: "ad-hoc otc",
      venueType: "CSD/TA-registered",
      settlementModel: "custodian-bank-settlement",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist; professional/institutional investors; manager-approved transfers",
      taxNotes: "Fund, investor and cross-border tax treatment require structure-specific analysis.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "hong-kong-tokenized-treasury-fund",
    title: "Example 13 — Hong Kong tokenized treasury fund and cash leg",
    region: "Hong Kong / Asia",
    category: "Treasury / cash leg · Tokenized fund",
    summary: "A corporate-treasury product testing whether tokenized fund units and bank-backed digital cash create real settlement value.",
    text: `We are a Hong Kong financial-services group considering a tokenized share class of a short-duration treasury or money-market fund for corporate and institutional clients. The conventional fund already offers daily subscriptions and redemptions through bank and fund-administration channels. The proposed tokenized version is intended to improve intraday position visibility, automate subscriptions and redemptions, and potentially support settlement against bank-backed digital cash.

We have not yet established whether the tokenized share class would create meaningful value beyond a better digital interface. The fund administrator would probably remain the authoritative register, but the operating model between administrator, custodian, bank and token platform is still being designed. We are also unsure whether clients actually require 24/7 movement or whether normal business-day processing is sufficient.

The first phase would be restricted to professional investors in Hong Kong, with Singapore considered later. No public secondary market is planned. One bank has discussed a tokenized-deposit or institutional DvP rail, but there is no commitment, finality analysis or complete recovery process. Ordinary bank settlement remains a fallback.

The key questions are whether subscriptions and redemptions can be synchronized with the cash leg, how finality and reversals would work, who controls wallets and investor records, whether the tokenized class reduces reconciliation rather than duplicating it, provider and integration costs, cross-border distribution, and whether clients will pay for the additional functionality.`,
    structured: {
      issuanceJurisdiction: "Hong Kong",
      saleJurisdictions: "Hong Kong, Singapore",
      instrumentType: "wrapper",
      custodyModel: "regulated-custodian",
      liquidityCadence: "none",
      venueType: "CSD/TA-registered",
      settlementModel: "deposit-token-tokenized-deposit",
      disclosure: "Exempt",
      transferRestrictions: "Whitelist; professional investors; subscriptions/redemptions through approved channels",
      taxNotes: "Fund and cross-border investor tax treatment to be confirmed.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "japan-corporate-bond-dvp",
    title: "Example 14 — Japanese corporate bond with DLT-based settlement",
    region: "Japan",
    category: "Hybrid · Corporate bond + settlement",
    summary: "A repeat bond issuer exploring both a tokenized instrument and improved lifecycle/settlement processes without assuming new liquidity.",
    text: `We are a Japanese corporate issuer with an established domestic bond programme. We are considering a smaller tokenized bond issuance for professional investors, partly to test digital issuance and partly to reduce manual work across allocation, settlement, position reporting and coupon administration.

The economic terms would be conventional fixed-income terms. We are not relying on tokenization to create a new asset class or guaranteed secondary liquidity. The main question is whether a tokenized format can improve the issuance and post-issuance workflow enough to justify the additional legal, technology and provider work.

We have discussed a structure in which a transfer agent or similar regulated operator maintains the authoritative record while the token layer supports investor positions and lifecycle events. Another option is a more native ledger-based record, but we have not determined whether that is legally or operationally preferable. Custody arrangements, investor wallets and the exact responsibility split between issuer, arranger, registry operator and settlement bank are still open.

A bank-backed digital cash or deposit-token rail has been mentioned for DvP, but ordinary securities-style settlement remains possible and may be adequate for the pilot. We have no confirmed secondary venue or investor commitments. Key unresolved issues include instrument and registry structure, cash-leg finality, recovery and cancellation processes, integration with existing systems, investor onboarding, disclosure, tax, costs and whether the pilot can retire any legacy steps rather than operate permanently in parallel.`,
    structured: {
      issuanceJurisdiction: "Japan",
      saleJurisdictions: "Japan",
      instrumentType: "wrapper",
      custodyModel: "ta/csd",
      liquidityCadence: "none",
      venueType: "CSD/TA-registered",
      settlementModel: "not-decided",
      disclosure: "",
      transferRestrictions: "Professional investors; transfer and registry controls not yet finalized",
      taxNotes: "Domestic bond and investor tax treatment requires structure-specific confirmation.",
      investorTypes: ["professional"],
    },
  },
  {
    id: "permissioned-defi-rwa-lending",
    title: "Example 15 — Permissioned DeFi lending with tokenized real-world assets",
    region: "Cross-border / DeFi-native",
    category: "DeFi-native · RWA lending",
    summary: "A permissioned lending pool using tokenized receivables as collateral, with dynamic liquidity, oracle, governance and cash-leg dependencies.",
    text: `We are developing a permissioned lending pool intended for professional participants. Borrowers would post tokenized short-term trade-finance or receivables positions as collateral and draw a stablecoin or other digital cash asset. Lenders would receive pool tokens representing their economic interest in the lending strategy.

The proposed value comes from programmable collateral controls, faster funding and the possibility of integrating the pool with other approved on-chain applications. However, the design is still early. We have not finalized the legal entity, governing law, treatment of pool tokens, collateral SPVs, custody model, oracle providers, liquidation process or whether participants may use the positions outside the pool.

Participation would be permissioned and limited to professional or institutional users, but the potential jurisdictions are not yet fixed. We are considering an operating entity in Switzerland or the UAE and investors from several markets. No final distribution analysis has been completed. The cash leg is currently assumed to be a stablecoin, although bank-backed tokenized deposits could be considered later.

The economics depend on borrower demand, lender liquidity, collateral performance, protocol integrations and governance decisions that may change after launch. Key risks include legal enforceability of collateral rights, oracle and valuation failures, smart-contract vulnerabilities, liquidation under stress, stablecoin dependency, liquidity concentration, governance changes, provider and bridge dependencies, and the possibility that composability creates risks not visible in a static initial assessment. We have a prototype but no audited production system or committed liquidity.`,
    structured: {
      issuanceJurisdiction: "",
      saleJurisdictions: "",
      instrumentType: "unknown",
      custodyModel: "unknown",
      liquidityCadence: "continuous",
      venueType: "DEX",
      settlementModel: "stablecoin-tokenized-cash",
      disclosure: "",
      transferRestrictions: "Permissioned participants; whitelist and jurisdiction controls under design",
      taxNotes: "Entity, pool-token, collateral and cross-border tax treatment remain unresolved.",
      investorTypes: ["professional"],
    },
  },
];

function ExampleCasesModal({ open, onClose, onUseExample, uiLanguageCode = "en" }) {
  const t = (key, vars = {}) => uiText(uiLanguageCode, key, vars);
  const [expandedId, setExpandedId] = useState("");

  useEffect(() => {
    if (!open) setExpandedId("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="tvac-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="tvac-example-library-title">
      <div className="tvac-modal card">
        <div className="tvac-modal-header">
          <div>
            <h4 id="tvac-example-library-title" style={{ margin: 0 }}>{t("examples.title")}</h4>
            <div className="muted small" style={{ marginTop: 4 }}>
              {t("examples.intro")}
            </div>
          </div>
          <button className="tvac-modal-close" type="button" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            ✕
          </button>
        </div>

        <div className="tvac-example-library-summary small">
          <strong>{EXAMPLE_CASES.length} examples</strong>
          <span>{t("examples.summary")}</span>
        </div>

        <div className="tvac-examples">
          {EXAMPLE_CASES.map((ex) => {
            const isExpanded = expandedId === ex.id;
            return (
              <div key={ex.id} className={`tvac-example card${isExpanded ? " is-expanded" : ""}`}>
                <div className="tvac-example-heading">
                  <div className="tvac-example-title-block">
                    <strong>{ex.title}</strong>
                    <div className="tvac-example-tags small">
                      <span>{ex.region}</span>
                      <span>{ex.category}</span>
                    </div>
                    <div className="muted small tvac-example-summary">{ex.summary}</div>
                  </div>
                  <div className="tvac-example-actions">
                    <button
                      className="tvac-example-view"
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedId(isExpanded ? "" : ex.id)}
                    >
                      {isExpanded ? t("examples.hide") : t("examples.view")}
                    </button>
                    <button className="action tvac-example-use" type="button" onClick={() => onUseExample(ex)}>
                      {t("examples.use")}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="tvac-example-details">
                    <div className="case-verbatim">{ex.text}</div>
                    <div className="tvac-example-structured">
                      <div className="muted small" style={{ marginBottom: 6 }}>
                        {t("examples.structured")}
                      </div>
                      <ul className="small" style={{ marginTop: 0, paddingLeft: 18 }}>
                        <li><strong>{t("examples.field.issuance")}:</strong> {ex.structured.issuanceJurisdiction || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.sale")}:</strong> {ex.structured.saleJurisdictions || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.instrument")}:</strong> {ex.structured.instrumentType === "unknown" ? t("common.notDecided") : ex.structured.instrumentType || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.custody")}:</strong> {ex.structured.custodyModel === "unknown" ? t("common.notDecided") : ex.structured.custodyModel || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.liquidity")}:</strong> {ex.structured.liquidityCadence || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.venue")}:</strong> {ex.structured.venueType === "unknown" ? t("common.notDecided") : ex.structured.venueType || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.settlement")}:</strong> {ex.structured.settlementModel === "not-decided" ? t("common.notDecided") : ex.structured.settlementModel || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.disclosure")}:</strong> {ex.structured.disclosure || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.transfer")}:</strong> {ex.structured.transferRestrictions || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.tax")}:</strong> {ex.structured.taxNotes || t("common.notDecided")}</li>
                        <li><strong>{t("examples.field.investors")}:</strong> {(ex.structured.investorTypes || []).join(", ") || t("common.notDecided")}</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ExampleCreditWarningModal({ example, onCancel, onStartOwnCase, onRunAnyway, uiLanguageCode = "en" }) {
  const t = (key) => uiText(uiLanguageCode, key);
  if (!example) return null;

  return (
    <div className="tvac-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="example-credit-warning-title">
      <div className="tvac-modal tvac-warning-modal card">
        <div className="tvac-modal-header tvac-warning-modal-header">
          <div>
            <div className="tvac-warning-kicker">{t("examples.warningKicker")}</div>
            <h4 id="example-credit-warning-title" className="tvac-warning-title">{t("examples.warningTitle")}</h4>
          </div>
          <button className="tvac-modal-close" type="button" onClick={onCancel} aria-label={t("common.close")} title={t("common.close")}>
            ✕
          </button>
        </div>

        <div className="tvac-warning-case-line">
          <span>{t("examples.loaded")}</span>
          <strong>{example.title}</strong>
        </div>

        <p className="tvac-warning-copy">
          {t("examples.warningBody")}
        </p>
        <p className="tvac-warning-copy tvac-warning-copy-muted">
          {t("examples.warningSingle")}
        </p>

        <div className="tvac-warning-actions">
          <button className="action" type="button" onClick={onCancel}>{t("common.cancel")}</button>
          <button className="action" type="button" onClick={onStartOwnCase}>{t("examples.ownCase")}</button>
          <button className="btn" type="button" onClick={onRunAnyway}>{t("examples.runAnyway")}</button>
        </div>
      </div>
    </div>
  );
}

function LabelWithTip({ text, tipFor, uiLanguageCode = "en" }) {
  const localizedTip = getUiFieldTooltip(uiLanguageCode, tipFor) || L[tipFor];
  return (
    <label>
      <Tip label={<span>{text}</span>}>{localizedTip}</Tip>
    </label>
  );
}

/* ---------- Inputs ---------- */

function StructuredInputs({ state, setState, refsMap, uiLanguageCode = "en" }) {
  const t = (key) => uiText(uiLanguageCode, key);
  const on = (k) => (e) => setState((o) => ({ ...o, [k]: e.target.value }));
  const onCheck = (name) => (e) =>
    setState((o) => {
      const set = new Set(o.investorTypes || []);
      if (e.target.checked) set.add(name);
      else set.delete(name);
      return { ...o, investorTypes: Array.from(set) };
    });

  /* hjælpetekst under dropdowns */
  const [selectedHelp, setSelectedHelp] = useState({});

  const onSelect = (fieldKey) => (e) => {
    const value = e.target.value;
    setState((o) => ({ ...o, [fieldKey]: value }));
    const help =
      getUiOptionHelp(uiLanguageCode, fieldKey, value) ||
      (OPTION_HELP[fieldKey] && OPTION_HELP[fieldKey][value]
        ? OPTION_HELP[fieldKey][value]
        : "");
    setSelectedHelp((prev) => ({ ...prev, [fieldKey]: help }));
  };

  return (
    <div style={{ marginTop: 4 }}>
      <div className="grid-2">
        <div>
          <label>{t("field.issuanceJurisdiction")}</label>
          <input
            id="issuanceJurisdiction"
            ref={refsMap.issuanceJurisdiction}
            value={state.issuanceJurisdiction || ""}
            onChange={on("issuanceJurisdiction")}
            placeholder={t("field.issuanceJurisdictionPlaceholder")}
          />
        </div>

        <div>
          <label>{t("field.saleJurisdictions")}</label>
          <input
            id="saleJurisdictions"
            ref={refsMap.saleJurisdictions}
            value={state.saleJurisdictions || ""}
            onChange={on("saleJurisdictions")}
            placeholder={t("field.saleJurisdictionsPlaceholder")}
          />
        </div>
                         
        <div>
          <LabelWithTip text={t("field.instrumentType")} tipFor="instrument" uiLanguageCode={uiLanguageCode} />
          <select
            id="instrumentType"
            ref={refsMap.instrumentType}
            value={state.instrumentType || "unknown"}
            onChange={onSelect("instrumentType")}
          >
            <option value="unknown">{t("common.select")}</option>
            <option value="wrapper">{t("option.instrument.wrapper")}</option>
            <option value="sole-record">
              {t("option.instrument.soleRecord")}
            </option>
          </select>
          {selectedHelp.instrumentType && (
            <p className="field-helper-text">{selectedHelp.instrumentType}</p>
          )}
        </div>

        <div>
          <LabelWithTip text={t("field.custodyModel")} tipFor="custody" uiLanguageCode={uiLanguageCode} />
          <select
            id="custodyModel"
            ref={refsMap.custodyModel}
            value={state.custodyModel || "unknown"}
            onChange={onSelect("custodyModel")}
          >
            <option value="unknown">{t("common.select")}</option>
            <option value="regulated-custodian">{t("option.custody.regulated")}</option>
            <option value="ta/csd">{t("option.custody.taCsd")}</option>
            <option value="self-custody">{t("option.custody.self")}</option>
          </select>
          {selectedHelp.custodyModel && (
            <p className="field-helper-text">{selectedHelp.custodyModel}</p>
          )}
        </div>

        <div>
          <LabelWithTip text={t("field.liquidityCadence")} tipFor="liquidity" uiLanguageCode={uiLanguageCode} />
          <select
            id="liquidityCadence"
            ref={refsMap.liquidityCadence}
            value={state.liquidityCadence || ""}
            onChange={onSelect("liquidityCadence")}
          >
            <option value="">{t("common.selectDots")}</option>
            <option value="none">{t("option.liquidity.none")}</option>
            <option value="ad-hoc otc">{t("option.liquidity.otc")}</option>
            <option value="quarterly">{t("option.liquidity.quarterly")}</option>
            <option value="continuous">{t("option.liquidity.continuous")}</option>
          </select>
          {selectedHelp.liquidityCadence && (
            <p className="field-helper-text">
              {selectedHelp.liquidityCadence}
            </p>
          )}
        </div>

        <div>
          <LabelWithTip text={t("field.venueType")} tipFor="venue" uiLanguageCode={uiLanguageCode} />
          <select
            id="venueType"
            ref={refsMap.venueType}
            value={state.venueType || "unknown"}
            onChange={onSelect("venueType")}
          >
            <option value="unknown">{t("common.select")}</option>
            <option value="ATS/MTF">{t("option.venue.ats")}</option>
            <option value="DEX">{t("option.venue.dex")}</option>
            <option value="OTC">{t("option.venue.otc")}</option>
            <option value="CSD/TA-registered">{t("option.venue.csd")}</option>
          </select>
          {selectedHelp.venueType && (
            <p className="field-helper-text">{selectedHelp.venueType}</p>
          )}
        </div>

        <div>
          <LabelWithTip text={t("field.settlementModel")} tipFor="settlement" uiLanguageCode={uiLanguageCode} />
          <select
            id="settlementModel"
            ref={refsMap.settlementModel}
            value={state.settlementModel || "not-decided"}
            onChange={onSelect("settlementModel")}
          >
            <option value="not-decided">{t("common.notDecided")}</option>
            <option value="not-material-now">{t("option.settlement.notMaterial")}</option>
            <option value="traditional-fiat-rails">{t("option.settlement.fiat")}</option>
            <option value="custodian-bank-settlement">{t("option.settlement.custodian")}</option>
            <option value="stablecoin-tokenized-cash">{t("option.settlement.stablecoin")}</option>
            <option value="deposit-token-tokenized-deposit">{t("option.settlement.depositToken")}</option>
            <option value="T+0 atomic">{t("option.settlement.atomic")}</option>
            <option value="T+1/2">{t("option.settlement.t12")}</option>
            <option value="off-chain">{t("option.settlement.offChain")}</option>
          </select>
          {selectedHelp.settlementModel && (
            <p className="field-helper-text">{selectedHelp.settlementModel}</p>
          )}
          <p className="field-helper-text">
            {t("settlement.helper")}
          </p>
        </div>

        <div>
          <LabelWithTip text={t("field.disclosure")} tipFor="disclosure" uiLanguageCode={uiLanguageCode} />
          <select
            id="disclosure"
            ref={refsMap.disclosure}
            value={state.disclosure || ""}
            onChange={onSelect("disclosure")}
          >
            <option value="">{t("common.selectDots")}</option>
            <option value="KID">{t("option.disclosure.kid")}</option>
            <option value="Prospectus">{t("option.disclosure.prospectus")}</option>
            <option value="Exempt">{t("option.disclosure.exempt")}</option>
          </select>
          {selectedHelp.disclosure && (
            <p className="field-helper-text">{selectedHelp.disclosure}</p>
          )}
        </div>

        <div style={{ gridColumn: "1 / span 2" }}>
          <LabelWithTip text={t("field.transferRestrictions")} tipFor="transfer" uiLanguageCode={uiLanguageCode} />
          <input
            id="transferRestrictions"
            ref={refsMap.transferRestrictions}
            value={state.transferRestrictions || ""}
            onChange={on("transferRestrictions")}
            placeholder={t("field.transferRestrictionsPlaceholder")}
          />
        </div>

        <div style={{ gridColumn: "1 / span 2" }}>
          <LabelWithTip text={t("field.taxNotes")} tipFor="tax" uiLanguageCode={uiLanguageCode} />
          <input
            id="taxNotes"
            ref={refsMap.taxNotes}
            value={state.taxNotes || ""}
            onChange={on("taxNotes")}
            placeholder={t("field.taxNotesPlaceholder")}
          />
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>
          <Tip label={<span>{t("field.investorTypes")}</span>}>{getUiFieldTooltip(uiLanguageCode, "investorTypes") || L.investorTypes}</Tip>:
        </strong>{" "}
        <span className="checkrow">
          <input
            type="checkbox"
            checked={(state.investorTypes || []).includes("professional")}
            onChange={onCheck("professional")}
          />
          <span>{t("option.investor.professional")}</span>
        </span>
        <span className="checkrow">
          <input
            type="checkbox"
            checked={(state.investorTypes || []).includes("accredited")}
            onChange={onCheck("accredited")}
          />
          <span>{t("option.investor.accredited")}</span>
        </span>
        <span className="checkrow">
          <input
            type="checkbox"
            checked={(state.investorTypes || []).includes("retail")}
            onChange={onCheck("retail")}
          />
          <span>{t("option.investor.retail")}</span>
        </span>
      </div>
    </div>
  );
}

/* ---------- Confidence card ---------- */

function ConfidenceCard({ confidence }) {
  if (confidence == null) return null;

  const c = Number(confidence) || 0; // forventet 0–10
  let band = "Low";
  if (c >= 7) band = "High";
  else if (c >= 3.5) band = "Medium";

  let headline;
  if (band === "High") {
    headline =
      "High (" +
      c.toFixed(1) +
      "/10): Well-specified case with limited unresolved risk.";
  } else if (band === "Medium") {
    headline =
      "Medium (" +
      c.toFixed(1) +
      "/10): Several assumptions remain; add details for a firmer verdict.";
  } else {
    headline =
      "Low (" +
      c.toFixed(1) +
      "/10): Thin inputs or unresolved compliance, custody or structuring issues.";
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h4>Confidence level</h4>
      <p className="muted">{headline}</p>
      <p className="muted small">
        The confidence score runs from 0–10 and reflects how robust and
        well-specified this particular case appears, given the information
        provided. It is not a probability of success, but an internal quality
        and clarity indicator for this assessment.
      </p>
    </div>
  );
}

/* ---------- Helper: remove repeated ALL CAPS module headings inside boxes ---------- */
function stripModuleHeading(s) {
  if (typeof s !== "string") return s;

  const lines = s.split(/\r?\n/);

  // Find first non-empty line
  let i = 0;
  while (i < lines.length && !String(lines[i]).trim()) i++;
  if (i >= lines.length) return s;

  const first = String(lines[i]).trim();

  // Allow headings like:
  // "HOW TO READ THIS REPORT"
  // "MINIMUM EVIDENCE & READINESS CHECKLIST (indicative)"
  // where the main part is ALL CAPS, but an optional trailing parenthetical may contain lowercase.
  const firstNoParen = first.replace(/\s*\([^)]*\)\s*$/, "").trim();

  const hasLetters = /[A-Za-z]/.test(firstNoParen);
  const hasLower = /[a-z]/.test(firstNoParen);

  const looksHeading =
    hasLetters &&
    !hasLower &&
    firstNoParen === firstNoParen.toUpperCase() &&
    firstNoParen.length <= 90 &&
    firstNoParen.length >= 8 &&
    !/\.$/.test(firstNoParen);

  if (!looksHeading) return s;

  // Drop the heading line
  lines.splice(i, 1);

  // Drop one following blank line
  if (i < lines.length && !String(lines[i]).trim()) lines.splice(i, 1);

  return lines.join("\n").trimStart();
}

/* ---------- Methodology / calibration card (inside report) ---------- */


function ReportRichText({ text }) {
  if (!text || typeof text !== "string") return null;
  const raw = text.replace(/\r/g, "").trim();
  if (!raw) return null;

  const lines = raw.split("\n");
  const blocks = [];
  let list = [];
  let orderedList = [];
  let para = [];

  const flushList = () => {
    if (!list.length) return;
    blocks.push({ type: "ul", items: list.slice() });
    list = [];
  };

  const flushOrderedList = () => {
    if (!orderedList.length) return;
    blocks.push({ type: "ol", items: orderedList.slice() });
    orderedList = [];
  };

  const flushPara = () => {
    if (!para.length) return;
    blocks.push({ type: "p", text: para.join(" ").trim() });
    para = [];
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const line = String(rawLine || "").trim();
    if (!line) {
      flushPara();
      flushList();
      flushOrderedList();
      continue;
    }

    if (/^#{1,4}\s+/.test(line)) {
      flushPara();
      flushList();
      flushOrderedList();
      blocks.push({ type: "h", text: line.replace(/^#{1,4}\s+/, "") });
      continue;
    }

    if (/^\d+[.)]\s+/.test(line)) {
      flushPara();
      flushList();
      orderedList.push(line.replace(/^\d+[.)]\s+/, ""));
      continue;
    }

    if (/^[-•]\s+/.test(line)) {
      flushPara();
      flushOrderedList();
      list.push(line.replace(/^[-•]\s+/, ""));
      continue;
    }

    const nextNonEmptyLine = lines
      .slice(lineIndex + 1)
      .map((value) => String(value || "").trim())
      .find(Boolean) || "";
    const looksLikePlainSubheading =
      line.length <= 72 &&
      !/[.!?:;]$/.test(line) &&
      (/^\d+[.)]\s+/.test(nextNonEmptyLine) ||
        /^[-•]\s+/.test(nextNonEmptyLine) ||
        /^(?:How to use the report|Reading discipline|Case snapshot|What the Tokenization Value Verdict means|What can change the verdict|What TVAC did not assess|Operational baseline and value capture|Settlement, custody and control evidence|Governance and operating model|Legal and perimeter discipline|Top levers to test|Interpretation rule)/i.test(line));

    if (looksLikePlainSubheading) {
      flushPara();
      flushList();
      flushOrderedList();
      blocks.push({ type: "h", text: line });
      continue;
    }

    flushList();
    flushOrderedList();
    para.push(line);
  }
  flushPara();
  flushList();
  flushOrderedList();

  return (
    <div>
      {blocks.map((b, i) => {
        if (b.type === "h") {
          return <h5 key={i} style={{ margin: "10px 0 5px", fontWeight: 700 }}>{b.text}</h5>;
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
              {b.items.map((item, j) => <li key={j} style={{ marginBottom: 4 }}>{item}</li>)}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={i} className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
              {b.items.map((item, j) => <li key={j} style={{ marginBottom: 4 }}>{item}</li>)}
            </ol>
          );
        }
        return <p key={i} className="small" style={{ marginTop: 6 }}>{b.text}</p>;
      })}
    </div>
  );
}

function renderSafeText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(renderSafeText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    const active = Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => String(key || "").replace(/[_-]+/g, " "));
    return active.join(", ");
  }
  return "";
}

function formatCoverageDescriptor(coverage = {}) {
  const raw = [coverage?.jurisdiction, coverage?.verification_status, coverage?.coverage_level]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const seen = new Set();
  return raw.filter((value) => {
    const key = value.toLowerCase().replace(/[\s_-]+/g, " ");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).join(" · ") || "—";
}

function compactRegisterSummary(value, maxLength = 210) {
  const text = renderSafeText(value).replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).replace(/\s+\S*$/, "")}…`;
}

function getAssessmentInputStatus(completeness, labels = {}) {
  const raw = Number(completeness?.score ?? completeness);
  const score = Number.isFinite(raw) ? (raw <= 1 ? raw * 100 : raw) : null;
  if (score == null) return labels.assessmentInputPartial || "Partial";
  if (score >= 99.5) return labels.assessmentInputComplete || "Complete";
  if (score >= 75) return labels.assessmentInputMostlyComplete || "Mostly complete";
  return labels.assessmentInputPartial || "Partial";
}

function getSupportingEvidenceLevel(evidence = {}, labels = {}) {
  const level = String(evidence?.level || "Preliminary").trim().toLowerCase();
  if (level === "substantiated") return labels.supportingEvidenceSubstantiated || "Substantiated";
  if (level === "developing") return labels.supportingEvidenceDeveloping || "Developing";
  return labels.supportingEvidencePreliminary || "Preliminary";
}

function ClaimSourceRegister({ result, reportLanguageCode = "en" }) {
  const labels = polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode);
  const register = result?.claim_source_register || result?.claimSourceRegister;
  const evidence = result?.evidence_readiness || result?.evidenceReadiness || register?.evidence_readiness;
  const coverage = result?.regulatory_coverage || result?.regulatoryCoverage || register?.regulatory_coverage;
  if (!register && !evidence && !coverage) return null;

  const claims = Array.isArray(register?.activated_regulatory_claims) ? register.activated_regulatory_claims : [];
  const questions = Array.isArray(register?.regulatory_scoping_questions) ? register.regulatory_scoping_questions : [];
  const sources = Array.isArray(register?.verified_sources) ? register.verified_sources : [];
  const interfaceLabels = Array.isArray(register?.interface_labels) ? register.interface_labels : [];
  const renderInterfaceLabelValue = (item = {}) => {
    const field = String(item?.field || "").toLowerCase();
    const value = item?.value;
    if (field.includes("settlement") || field.includes("cash_leg") || field.includes("cash-leg")) {
      return humanizeReportInputLabel(value, "settlement");
    }
    if (field.includes("custody")) return humanizeReportInputLabel(value, "custody");
    if (field.includes("venue")) return humanizeReportInputLabel(value, "venue");
    if (field.includes("liquidity")) return humanizeReportInputLabel(value, "liquidity");
    if (field.includes("disclosure")) return humanizeReportInputLabel(value, "disclosure");
    return renderSafeText(value);
  };
  const pending = coverage?.pending_regulatory_change;
  const saleCoverage = Array.isArray(coverage?.sale_jurisdictions) ? coverage.sale_jurisdictions : [];
  const scoreProvenance = result?.score_provenance || result?.scoreProvenance || register?.score_provenance;

  return (
    <div id="section-provenance-register" style={{ marginTop: 14 }}>
      <h4>A.6 {labels.provenanceRegister || "Evidence, provenance & regulatory source register"}</h4>
      <p className="small" style={{ marginTop: 6 }}>
        {labels.provenanceIntro || "This register shows the evidence status, activated verified regulatory claims, official sources and generic interface labels used in the report. It supports auditability but does not constitute legal advice or a conclusion that any cited rule applies to the final facts."}
      </p>

      {evidence && (
        <div className="small" style={{ marginTop: 8 }}>
          <strong>{labels.evidenceReadiness || "Supporting evidence"}:</strong>{" "}
          {getSupportingEvidenceLevel(evidence, labels)}
          <span className="muted"> · {labels.supportingEvidenceUserReportedNote || "Any supporting evidence mentioned in the case description is treated as user-reported and has not been independently reviewed or verified by TVAC."}</span>
        </div>
      )}

      {coverage && (
        <div className="small" style={{ marginTop: 8 }}>
          <strong>{labels.regulatoryCoverage || "Regulatory coverage"}:</strong>{" "}
          {formatCoverageDescriptor(coverage)}
          {coverage.last_reviewed ? ` · ${labels.lastReviewed || "last reviewed"} ${coverage.last_reviewed}` : ""}
        </div>
      )}

      {saleCoverage.length > 0 && (
        <div className="small" style={{ marginTop: 8 }}>
          <strong>{labels.saleJurisdictionCoverage || "Sale-jurisdiction coverage"}:</strong>
          <ul style={{ margin: "4px 0 0 18px" }}>
            {saleCoverage.map((item, index) => (
              <li key={`${item.jurisdiction || "sale"}-${index}`}>
                {formatCoverageDescriptor(item)}
                {item.last_reviewed ? ` · ${labels.lastReviewed || "last reviewed"} ${item.last_reviewed}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending && (
        <div className="small" style={{ marginTop: 8 }}>
          <strong>{labels.pendingRegulatoryChange || "Pending regulatory change"}:</strong>{" "}
          {pending.title || pending.status || "Pending change recorded"}
          {pending.status ? ` · ${pending.status}` : ""}
          {pending.checked_at ? ` · ${labels.checked || "checked"} ${pending.checked_at}` : ""}
        </div>
      )}

      {register?.legal_advice_boundary?.statement && (
        <div className="small" style={{ marginTop: 8, padding: "8px 10px", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8 }}>
          <strong>{labels.legalAdviceBoundary || "Legal-advice boundary"}:</strong>{" "}
          {labels.legalAdviceBoundaryText || "Regulatory scoping only; no legal opinion, compliance determination or case-specific application of law is provided."}
        </div>
      )}

      {Array.isArray(scoreProvenance?.factors) && scoreProvenance.factors.length > 0 && (
        <>
          <h5 style={{ marginTop: 12 }}>{labels.scoreProvenance || "Score provenance"}</h5>
          <p className="small muted">{labels.scoreProvenanceIntro || "Factor scores are rubric-based methodology judgments grounded in supplied inputs and explicit evidence gaps. They are not measurements, legal conclusions or due-diligence findings."}</p>
          <table className="table small">
            <thead><tr><th>{labels.factor || "Factor"}</th><th>{labels.score || "Score"}</th><th>{labels.provenanceClass || "Provenance class"}</th><th>{labels.inputReferences || "Input references"}</th></tr></thead>
            <tbody>
              {scoreProvenance.factors.map((item) => (
                <tr key={item.factor}>
                  <td>{item.label}</td>
                  <td>{item.score == null ? "—" : `${item.score}/10`}</td>
                  <td>{item.provenance_class}</td>
                  <td>{Array.isArray(item.input_references) && item.input_references.length > 0 ? item.input_references.map((ref) => ref.field).join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h5 style={{ marginTop: 12 }}>{labels.activatedVerifiedClaims || "Activated verified regulatory claims"}</h5>
      <p className="small muted" style={{ marginTop: 4 }}>The table records claim scope and source IDs; the full case-facing explanation appears in Chapter 3.5.</p>
      {claims.length ? (
        <table className="table small">
          <thead>
            <tr>
              <th>{labels.claimId || "Claim ID"}</th>
              <th>{labels.verifiedScope || "Verified scope summary"}</th>
              <th>{labels.source || "Source ID(s)"}</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((claim) => (
              <tr key={claim.id}>
                <td><strong>{claim.id}</strong></td>
                <td>{compactRegisterSummary(claim.summary || claim.title || claim.label || claim.text)}</td>
                <td>{Array.isArray(claim.source_ids) && claim.source_ids.length ? claim.source_ids.join(", ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="small muted">{labels.noClaimsActivated || "No verified jurisdiction-specific claim was activated for the stated case facts."}</p>}

      <h5 style={{ marginTop: 12 }}>{labels.regulatoryScopingQuestions || "Jurisdiction-specific validation questions"}</h5>
      {questions.length ? (
        <ul className="small">
          {questions.map((item) => (
            <li key={item.id}>
              {item.jurisdiction ? <strong>{item.jurisdiction}: </strong> : null}{item.question}
            </li>
          ))}
        </ul>
      ) : <p className="small muted">{labels.noScopingQuestions || "No additional jurisdiction-specific validation question was activated."}</p>}

      <h5 style={{ marginTop: 12 }}>{labels.verifiedSources || "Verified official sources"}</h5>
      {sources.length ? (
        <ul className="small">
          {sources.map((source) => (
            <li key={source.id}>
              {source.url ? <a href={source.url} target="_blank" rel="noreferrer">{source.title || source.id}</a> : (source.title || source.id)}
              {source.authority ? ` · ${source.authority}` : ""}
              {source.checked_at ? ` · ${labels.checked || "checked"} ${source.checked_at}` : ""}
            </li>
          ))}
        </ul>
      ) : <p className="small muted">{labels.noVerifiedSources || "No activated verified jurisdiction-specific source applies to the stated facts."}</p>}

      {interfaceLabels.length > 0 && (
        <>
          <h5 style={{ marginTop: 12 }}>{labels.interfaceLabels || "Generic interface labels"}</h5>
          <p className="small muted">{labels.interfaceLabelInterpretation || "These are user-selected TVAC design labels. They are not treated as local legal classifications, permissions or requirements."}</p>
          <table className="table small">
            <thead><tr><th>{labels.field || "Field"}</th><th>{labels.value || "Value"}</th></tr></thead>
            <tbody>
              {interfaceLabels.map((item) => (
                <tr key={item.field}><td>{item.field}</td><td>{renderInterfaceLabelValue(item) || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function MethodologyCard({ modules, result, reportLanguageCode = "en", payload = {} }) {
  const labels = polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode);
  const fs = result?.factor_scores || {};
  const no = Number(fs.new_opportunities || 0);
  const cs = Number(fs.cost_savings || 0);
  const rr = Number(fs.risk_reduction || 0);
  const tc = Number(fs.tokenization_costs || 0);
  const nr = Number(fs.new_risks || 0);

  const plus = no + cs + rr;
  const minus = tc + nr;
  const added =
    typeof result?.added_value === "number"
      ? result.added_value
      : typeof result?.added_value === "object" &&
        typeof result?.added_value?.score === "number"
      ? result.added_value.score
      : plus - minus;

  const warehouseReceiptWorkflow = isWarehouseReceiptWorkflowCase(payload || {}, result || {});
  const permissionedDefiLending = isPermissionedDefiLendingCase(payload || {}, result || {});
  const archetypeMethodologyModules = warehouseReceiptWorkflow
    ? getWarehouseReceiptMethodologyCopy(reportLanguageCode)
    : permissionedDefiLending
    ? getPermissionedDefiMethodologyCopy(reportLanguageCode)
    : null;

  const lockedStaticModules =
    labels.languageCode === "en"
      ? null
      : getStaticAppendixAModules(
          reportLanguageCode,
          isClosedWorkflowInfrastructureCase(payload || {}, result || {})
            ? "closed_workflow_infrastructure"
            : "generic_neutral"
        );
  const moduleMap = lockedStaticModules || (modules && typeof modules === "object" ? modules : null);
  const moduleValue = (key) => {
    const value = moduleMap?.[key];
    return typeof value === "string" && value.trim() ? value.trim() : "";
  };

  // Closed institutional workflow cases need an Appendix A that reflects their
  // actual decision context. This display-only layer leaves the assessment,
  // scores, verdict, report modules and all non-workflow cases untouched.
  const closedWorkflowMethodologyModules =
    labels.languageCode === "en" &&
    isClosedWorkflowInfrastructureCase(payload || {}, result || {})
      ? {
          how_to_read: `This report is a structured decision-support assessment of a controlled institutional workflow. It evaluates whether tokenization creates net added value through measurable post-trade efficiency, stronger controls and reduced operational risk — not through public distribution, capital raising or speculative secondary liquidity.

How to use the report (recommended sequence)
1) Start with the Tokenization Value Verdict and the five-factor score profile.
2) Read the workflow assumptions, value pathways and risk register to identify where value depends on execution evidence.
3) Use the recommendations and roadmap to test whether the tokenized lane can remove legacy reconciliation, strengthen settlement control and retain a credible recovery path before scale.

Recommendations in this report are analytical suggestions derived from the TVAC methodology and do not constitute legal, regulatory, financial, tax, investment or implementation advice.

Reading discipline
Treat stated operational benefits as hypotheses until they are demonstrated against baseline metrics.
Prefer staged commitments: validate the highest-impact operational, control and governance uncertainties before expanding scope.`,
          decision_integrity: `This report provides informational decision support only. It does not constitute legal, regulatory, tax, financial or investment advice, and it does not provide a compliance determination, operational audit, technical assurance or provider endorsement.

For a controlled institutional workflow, the Tokenization Value Verdict concerns whether the stated tokenization design appears likely to create added value relative to the existing workflow. It does not confirm legal enforceability, settlement finality, custody synchronisation, business-continuity readiness, regulatory treatment or implementation feasibility.

What can change the verdict (high impact)
- Whether duplicate reconciliation and manual exception handling are genuinely retired rather than retained in parallel
- Whether asset and cash legs remain synchronised, final and recoverable under normal and stressed conditions
- Whether custody, tri-party, agent and technology-provider responsibilities are explicit and operationally testable
- Whether the initiating institution captures measurable value after integration, governance and provider costs
- Whether the permissioned participant perimeter and legal operating model remain controlled as the workflow scales

What TVAC did not assess
- No definitive legal, regulatory, tax or investment conclusions
- No operational-readiness audit, cyber assessment or technical architecture validation
- No assurance of settlement finality, recovery performance, service resilience or provider delivery
- No validation of transaction volumes, cost savings or implementation budgets`,
          minimum_evidence: `Before moving from a controlled pilot toward operational scale, decision-makers should be able to substantiate a minimum level of evidence across the areas below.

This checklist is not exhaustive and does not imply legal, regulatory or operational sufficiency. It reflects the evidence normally needed to test whether a workflow value case is sufficiently supported rather than merely theoretical.

Operational baseline and value capture
- Baseline data for settlement fails, reconciliation time, exception tickets, dispute-resolution effort and manual interventions
- A documented definition of which legacy controls and reconciliation steps are removed for the tokenized lane
- A quantified view of implementation, governance, provider and ongoing operating costs against expected savings

Settlement, custody and control evidence
- Defined DvP sequencing, cash-leg finality, custody or tri-party synchronisation and position-integrity controls
- Tested exception handling, rollback, recovery, kill-switch and incident communication procedures
- Clear evidence that permissioning, eligibility and movement controls operate as designed

Governance and operating model
- Explicit roles for the initiating institution, custodians, agents, counterparties and technology providers
- A documented RACI for reconciliation, incident handling, audit evidence, reporting and change control
- Service-level, portability, audit-right and exit arrangements proportionate to the workflow's criticality

Legal and perimeter discipline
- Clear legal mapping between the token layer, underlying positions and operating responsibilities
- Confirmed participant eligibility, transfer restrictions and the stated operating perimeter where relevant
- Independent legal, technical and operational validation before material scale-up`,
          sensitivity_levers: `The main decision levers in a controlled institutional workflow are operational rather than distribution-led.

Top levers to test
- Legacy-process retirement: savings weaken materially if the tokenized lane leaves double-booking, manual approvals or parallel reconciliation in place
- Settlement synchronisation and recovery: unresolved cash-leg finality, custody synchronisation or rollback procedures should increase New Risks and pause scale-up
- Provider economics and governance: value erodes if fees, integration burden or operational responsibility are concentrated on the initiating institution without measurable benefit
- Scope discipline: adding retail access, public trading, new jurisdictions or liquidity promises turns the case into a different distribution and market-structure assessment
- Evidence quality: pilot KPIs, incident tests and independent assurance should carry more weight than theoretical efficiency claims

Interpretation rule
Improve the underlying workflow evidence and control model before adding new features. In this case type, the strongest value case usually comes from proving durable operational improvement within a tightly controlled perimeter.`,
        }
      : null;

  const effectiveModuleValue = (key) =>
    archetypeMethodologyModules?.[key] ||
    closedWorkflowMethodologyModules?.[key] ||
    moduleValue(key);

  // English retains the canonical report module generated by the backend. Every
  // non-English report uses the locked language module selected above. This avoids
  // runtime translation or frontend rewriting of Appendix A prose.
  if ((archetypeMethodologyModules || closedWorkflowMethodologyModules || moduleMap) && (effectiveModuleValue("how_to_read") || effectiveModuleValue("decision_integrity") || effectiveModuleValue("minimum_evidence") || effectiveModuleValue("sensitivity_levers"))) {
    return (
      <div className="card" id="section-methodology" style={{ marginTop: 12 }}>
        <h3>{labels.appendixA}</h3>

        <div id="section-how-to-read" style={{ marginTop: 12 }}>
          <h4>A.1 {labels.howToRead}</h4>
          <ReportRichText text={effectiveModuleValue("how_to_read")} />
        </div>

        <div id="section-decision-integrity" style={{ marginTop: 12 }}>
          <h4>A.2 {labels.decisionIntegrity}</h4>
          <ReportRichText text={effectiveModuleValue("decision_integrity")} />
        </div>

        <div id="section-minimum-evidence" style={{ marginTop: 12 }}>
          <h4>A.3 {labels.minimumEvidence}</h4>
          <ReportRichText text={effectiveModuleValue("minimum_evidence")} />
        </div>

        <div id="section-sensitivity-levers" style={{ marginTop: 12 }}>
          <h4>A.4 {labels.sensitivityLevers}</h4>
          <ReportRichText text={effectiveModuleValue("sensitivity_levers")} />
        </div>

        <div id="section-how-we-compute" style={{ marginTop: 12 }}>
          <h4>A.5 {labels.computeAddedValue}</h4>
          <pre className="pre mono small">{`${labels.formulaText}

${labels.plusFormula} = ${formatScoreInt(plus)}
${labels.minusFormula} = ${formatScoreInt(minus)}
${labels.addedValueFormula} = ${formatScoreInt(added)}`}</pre>
        </div>
        <ClaimSourceRegister result={result} reportLanguageCode={reportLanguageCode} />
      </div>
    );
  }



  return (
    <div className="card" id="section-methodology" style={{ marginTop: 12 }}>
      <h3>{labels.appendixA}</h3>

      <div id="section-how-to-read" style={{ marginTop: 12 }}>
        <h4>A.1 {labels.howToRead}</h4>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyRead1}
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyRead2}
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyRead3}
        </p>
      </div>

      <div id="section-decision-integrity" style={{ marginTop: 12 }}>
        <h4>A.2 {labels.decisionIntegrity}</h4>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyIntegrity1}
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyIntegrity2}
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyIntegrity3}
        </p>
      </div>

      <div id="section-minimum-evidence" style={{ marginTop: 12 }}>
        <h4>A.3 {labels.minimumEvidence}</h4>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyChecklistIntro}
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li><strong>New Opportunities (NO):</strong> case-specific upside created or materially strengthened by tokenization</li>
          <li><strong>Cost Savings (CS):</strong> structural cost reductions relative to a conventional setup</li>
          <li><strong>Risk Reduction (RR):</strong> reduction of existing operational, governance, transparency, control, or counterparty risk</li>
          <li><strong>Tokenization Costs (TC):</strong> additional cost burden introduced by tokenization</li>
          <li><strong>New Risks (NR):</strong> additional risks introduced by tokenization</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          These five factors are combined into a net Added Value view:
        </p>
        <p className="small mono" style={{ marginTop: 6 }}>
          <strong>Added Value = (NO + CS + RR) – (TC + NR)</strong>
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologyFormulaPurpose}
        </p>
      </div>

      <div id="section-sensitivity-levers" style={{ marginTop: 12 }}>
        <h4>A.4 {labels.sensitivityLevers}</h4>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologySensitivity1}
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          {labels.methodologySensitivityGuide}
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>0–2 indicates low impact</li>
          <li>3–4 indicates limited impact</li>
          <li>5–6 indicates moderate impact</li>
          <li>7–8 indicates strong impact</li>
          <li>9–10 indicates very strong impact</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          For upside factors (NO, CS, RR), higher scores are better. For friction factors (TC, NR), higher scores are worse.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          Small differences between adjacent scores should not be overinterpreted. The scoring system is designed to support structured comparison and disciplined judgment, not to imply false precision.
        </p>
      </div>

      <div id="section-how-we-compute" style={{ marginTop: 12 }}>
        <h4>A.5 {labels.computeAddedValue}</h4>
        <p className="muted" style={{ marginTop: 6 }}>
          {labels.calculatorFormulaIntro ||
            "The calculator scores each of the five components in your case and combines them into a single Added Value number using the formula below."}
        </p>

        <div className="result-grid" style={{ marginTop: 8 }}>
          <pre className="pre mono small">{`${labels.formulaText}

${labels.theoreticalRange || "Theoretical range"}: –20 to +30

${labels.forThisCase || "For this case"}:
${labels.plusFormula} = ${formatScoreInt(plus)}
${labels.minusFormula} = ${formatScoreInt(minus)}
${labels.addedValueFormula} = ${formatScoreInt(added)}`}</pre>

          <div className="small">
            <p>
              <strong>Plus</strong> is the combined upside from New Opportunities,
              Cost Savings and Risk Reduction.
              <br />
              <strong>Minus</strong> is the combined burden of Tokenization Costs
              and New Risks.
            </p>
            <p>
              Added Value is <strong>Plus minus Minus</strong>. Positive values
              suggest net value creation, negative values suggest that costs and
              risks dominate, and values near zero indicate a balanced but fragile case
              rather than a straightforward go decision.
            </p>
            <ul>
              <li>
                Each factor score typically ranges from 0–10 based on your inputs
                and the evaluator&apos;s judgement.
              </li>
              <li>
                New Opportunities, Cost Savings and Risk Reduction contribute
                positively to the <strong>Plus</strong> score.
              </li>
              <li>
                Tokenization Costs and New Risks contribute to the{" "}
                <strong>Minus</strong> score as frictions and downside.
              </li>
              <li>
                Higher Added Value suggests a stronger case for tokenization
                compared to conventional solutions.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <ClaimSourceRegister result={result} reportLanguageCode={reportLanguageCode} />
    </div>
  );
}

/* ---------- Added Value formula card inside report ---------- */

function formatScoreInt(value) {
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : "—";
}

function AddedValueCard({ result, reportLanguageCode = "en" }) {
  if (!result) return null;
  const labels = polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode);

  const fs = result.factor_scores || {};
  const no = Number(fs.new_opportunities || 0);
  const cs = Number(fs.cost_savings || 0);
  const rr = Number(fs.risk_reduction || 0);
  const tc = Number(fs.tokenization_costs || 0);
  const nr = Number(fs.new_risks || 0);

  const plus = no + cs + rr;
  const minus = tc + nr;
  const added =
    typeof result.added_value === "number"
      ? result.added_value
      : typeof result.added_value === "object" &&
        typeof result.added_value.score === "number"
      ? result.added_value.score
      : plus - minus;

  return (
    <div className="card" id="section-how-we-compute" style={{ marginTop: 16 }}>
      <h4>A.5 {labels.computeAddedValue}</h4>
      <p className="muted">
        {labels.calculatorFormulaIntro ||
          "The calculator scores each of the five components in your case and combines them into a single Added Value number using the formula below."}
      </p>

     <div className="result-grid" style={{ marginTop: 8 }}>
  <pre className="pre mono small">{`${labels.formulaText}

${labels.theoreticalRange || "Theoretical range"}: –20 to +30

${labels.forThisCase || "For this case"}:
${labels.plusFormula} = ${formatScoreInt(plus)}
${labels.minusFormula} = ${formatScoreInt(minus)}
${labels.addedValueFormula} = ${formatScoreInt(added)}`}</pre>

  <div className="small">
    <p>
      <strong>Plus</strong> is the combined upside from New Opportunities,
      Cost Savings and Risk Reduction.
      <br />
      <strong>Minus</strong> is the combined burden of Tokenization Costs
      and New Risks.
    </p>
    <p>
      Added Value is <strong>Plus minus Minus</strong>. Positive values
      suggest net value creation, negative values suggest that costs and
      risks dominate, and values near zero indicate a balanced but fragile case
      rather than a straightforward go decision.
    </p>
    <ul>
      <li>
        Each factor score typically ranges from 0–10 based on your inputs
        and the evaluator&apos;s judgement.
      </li>
      <li>
        New Opportunities, Cost Savings and Risk Reduction contribute
        positively to the <strong>Plus</strong> score.
      </li>
      <li>
        Tokenization Costs and New Risks contribute to the{" "}
        <strong>Minus</strong> score as frictions and downside.
      </li>
      <li>
        Higher Added Value suggests a stronger case for tokenization
        compared to conventional solutions.
      </li>
    </ul>
  </div>
</div>
</div>
  );
}


function localizeCaseSummaryDriver(label, code = "en") {
  const key = String(label || "").toLowerCase().trim();
  const map = {
    da: {
      "new opportunities": "nye muligheder",
      "cost savings": "omkostningsbesparelser",
      "risk reduction": "risikoreduktion",
      "tokenization costs": "tokeniseringsomkostninger",
      "new risks": "nye risici",
      "limited measurable upside": "begrænset målbar positiv værdiskabelse",
      "execution burden and new risk": "implementeringsbyrde og nye risici",
    },
    de: {
      "new opportunities": "neue Chancen",
      "cost savings": "Kosteneinsparungen",
      "risk reduction": "Risikominderung",
      "tokenization costs": "Tokenisierungskosten",
      "new risks": "neue Risiken",
      "limited measurable upside": "begrenztes messbares Wertpotenzial",
      "execution burden and new risk": "Umsetzungsaufwand und neue Risiken",
    },
    fr: {
      "new opportunities": "nouvelles opportunités",
      "cost savings": "économies de coûts",
      "risk reduction": "réduction des risques",
      "tokenization costs": "coûts de tokenisation",
      "new risks": "nouveaux risques",
      "limited measurable upside": "potentiel positif mesurable limité",
      "execution burden and new risk": "charge d’exécution et nouveaux risques",
    },
    es: {
      "new opportunities": "nuevas oportunidades",
      "cost savings": "ahorro de costes",
      "risk reduction": "reducción de riesgos",
      "tokenization costs": "costes de tokenización",
      "new risks": "nuevos riesgos",
      "limited measurable upside": "potencial positivo medible limitado",
      "execution burden and new risk": "carga de ejecución y nuevos riesgos",
    },
    "zh-Hans": {
      "new opportunities": "新机会",
      "cost savings": "成本节约",
      "risk reduction": "风险降低",
      "tokenization costs": "代币化成本",
      "new risks": "新风险",
      "limited measurable upside": "有限的可衡量上行价值",
      "execution burden and new risk": "实施负担和新风险",
    },
    "pt-BR": {
      "new opportunities": "novas oportunidades",
      "cost savings": "economia de custos",
      "risk reduction": "redução de riscos",
      "tokenization costs": "custos de tokenização",
      "new risks": "novos riscos",
      "limited measurable upside": "benefício mensurável limitado",
      "execution burden and new risk": "carga de execução e novos riscos",
    },
  };
  return map[code]?.[key] || label;
}

function joinNaturalLocalized(items, code = "en") {
  const clean = (items || []).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (code === "zh-Hans") {
    if (clean.length === 2) return `${clean[0]}和${clean[1]}`;
    return `${clean.slice(0, -1).join("、")}和${clean[clean.length - 1]}`;
  }
  const conj = code === "da" ? "og" : code === "de" ? "und" : code === "fr" ? "et" : code === "es" ? "y" : code === "pt-BR" ? "e" : "and";
  if (clean.length === 2) return `${clean[0]} ${conj} ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")} ${conj} ${clean[clean.length - 1]}`;
}

function buildLocalizedCaseSummaryCopy(args) {
  const {
    code,
    labels,
    verdictLabel,
    netScore,
    plus,
    minus,
    completenessPct,
    confidence,
    isNonViable,
    isViable,
    isEarlyMaturity,
    isStructuredMaturity,
    isDesignedMaturity,
    isNearBoundaryConditional,
    hasProviderCaptureRisk,
    primaryBlocker,
    primaryStop,
    upsideDrivers,
    downsideDrivers,
    summaryPrimaryArchetype,
    fineArtRetailCase,
    ambiguousRights,
    investorsRetail,
    custodyLc,
    venueLc,
    continuousLiquidity,
    disclosureLc,
    crossBorder,
    permissioned,
    periodicLiquidity,
    adHocOrNoneLiquidity,
    investorsProfessional,
    investorsAccredited,
  } = args || {};

  if (!code || code === "en") return null;
  const n = (value) => (Number.isFinite(value) ? value.toFixed(2) : "—");
  const ending = code === "zh-Hans" ? "。" : ".";
  const coverage = "";
  const confidenceText = confidence != null && Number.isFinite(confidence)
    ? code === "zh-Hans"
      ? ` 置信度为${confidence.toFixed(1)}/10。`
      : code === "pt-BR"
      ? ` Confiança: ${confidence.toFixed(1)}/10.`
      : ` Confidence: ${confidence.toFixed(1)}/10.`
    : "";
  const scorePart = code === "zh-Hans"
    ? `${n(netScore)}（${labels.plus}: ${n(plus)} · ${labels.minus}: ${n(minus)}）`
    : `${n(netScore)} (${labels.plus}: ${n(plus)} · ${labels.minus}: ${n(minus)})`;
  const upsideText = joinNaturalLocalized(
    (upsideDrivers && upsideDrivers.length ? upsideDrivers : ["limited measurable upside"]).map((x) => localizeCaseSummaryDriver(x, code)),
    code
  );
  const downsideText = joinNaturalLocalized(
    (downsideDrivers && downsideDrivers.length ? downsideDrivers : ["execution burden and new risk"]).map((x) => localizeCaseSummaryDriver(x, code)),
    code
  );

  const blocker = primaryBlocker
    ? {
        da: `En case-specifik hindring er allerede synlig: ${primaryBlocker}.`,
        de: `Ein fallspezifischer Blocker ist bereits sichtbar: ${primaryBlocker}.`,
        fr: `Un blocage spécifique au cas est déjà visible : ${primaryBlocker}.`,
        es: `Ya existe un bloqueador específico del caso: ${primaryBlocker}.`,
        "zh-Hans": `本案例已有一个明确的案例特定阻碍因素：${primaryBlocker}。`,
        "pt-BR": `Um bloqueador específico do caso já está visível: ${primaryBlocker}.`,
      }[code]
    : "";

  const providerRisk = hasProviderCaptureRisk
    ? {
        da: " Under den nuværende kommercielle konfiguration kan strukturen være mere attraktiv for infrastrukturleverandører end for den initierende aktivejer, medmindre afgrænsning, honorarstruktur og leveringsøkonomi strammes.",
        de: " In der aktuellen kommerziellen Konfiguration kann die Struktur für Infrastrukturanbieter attraktiver sein als für den initiierenden Asset Owner, sofern Umfang, Gebührenstack und Lieferökonomie nicht gestrafft werden.",
        fr: " Dans la configuration commerciale actuelle, la structure peut être plus attractive pour les prestataires d’infrastructure que pour le propriétaire de l’actif initiateur, sauf si le périmètre, la pile de frais et l’économie d’exécution sont resserrés.",
        es: " En la configuración comercial actual, la estructura puede resultar más atractiva para los proveedores de infraestructura que para el propietario del activo que inicia el proyecto, salvo que se ajusten el alcance, la pila de comisiones y la economía de ejecución.",
        "zh-Hans": " 在当前商业配置下，若不收紧范围、费用结构和交付经济性，该结构对基础设施服务商的吸引力可能高于对发起资产所有人的吸引力。",
        "pt-BR": " Na configuração comercial atual, a estrutura pode ser mais atraente para os prestadores de infraestrutura do que para o proprietário do ativo que iniciou o projeto, a menos que escopo, estrutura de fees e economia de execução sejam mais rigorosos.",
      }[code]
    : "";

  const nearBoundary = isNearBoundaryConditional
    ? {
        da: " En merværdi tæt på nul er ikke et svagt positivt signal; det viser en reelt balanceret case, hvor få strukturelle designvalg stadig kan flytte resultatet væsentligt.",
        de: " Ein Mehrwert nahe null ist kein schwach positives Signal; er zeigt einen tatsächlich ausgewogenen Fall, in dem wenige strukturelle Designentscheidungen das Ergebnis noch wesentlich verschieben können.",
        fr: " Une valeur ajoutée proche de zéro n’est pas un signal faiblement positif ; elle indique un cas réellement équilibré dans lequel quelques choix structurels peuvent encore déplacer sensiblement le résultat.",
        es: " Un valor añadido cercano a cero no es una señal débilmente positiva; indica un caso realmente equilibrado en el que unas pocas decisiones estructurales todavía pueden desplazar materialmente el resultado.",
        "zh-Hans": " 接近零的增值并不是微弱正面信号；它表明这是一个真正平衡的案例，少数结构性设计选择仍可能显著改变结果。",
        "pt-BR": " Um Valor Agregado próximo de zero não é um sinal levemente positivo; indica um caso efetivamente equilibrado, no qual poucas escolhas estruturais ainda podem alterar materialmente o resultado.",
      }[code]
    : "";

  const copy = { headline: "", findings: "", constraints: "", posture: "", impact: "", whatDifferent: "" };

  if (isNonViable) {
    copy.headline = {
      da: `Under den nuværende struktur bør denne case ikke fortsætte. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Unter der aktuellen Struktur sollte dieser Fall nicht weiterverfolgt werden. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `Dans la structure actuelle, ce cas ne devrait pas être poursuivi. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `Con la estructura actual, este caso no debería avanzar. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `在当前结构下，本案例不应继续推进。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `Na estrutura atual, este caso não deve avançar. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Den positive værdiskabelse er koncentreret i ${upsideText}, samlet ${n(plus)}, men den er ikke stærk nok til at opveje den nuværende risikoprofil.`,
      de: `Etwaiges positives Wertpotenzial konzentriert sich auf ${upsideText}, zusammen ${n(plus)}, reicht aber nicht aus, um das aktuelle Downside-Profil auszugleichen.`,
      fr: `L’éventuel potentiel positif se concentre dans ${upsideText}, pour un total de ${n(plus)}, mais cela ne suffit pas à compenser le profil de downside actuel.`,
      es: `Cualquier potencial positivo se concentra en ${upsideText}, con un total de ${n(plus)}, pero no es suficiente para compensar el perfil negativo actual.`,
      "zh-Hans": `可能的上行价值主要集中在${upsideText}，合计${n(plus)}，但不足以抵消当前的下行结构。`,
      "pt-BR": `O potencial positivo está concentrado principalmente em ${upsideText}, totalizando ${n(plus)}, mas não é suficiente para compensar o atual perfil de fricção e risco.`,
    }[code];
    copy.constraints = {
      da: `Casen tynges primært af ${downsideText}, samlet ${n(minus)}.${blocker ? ` ${blocker}` : ""}`,
      de: `Der Fall wird vor allem durch ${downsideText} belastet, zusammen ${n(minus)}.${blocker ? ` ${blocker}` : ""}`,
      fr: `Le cas est principalement alourdi par ${downsideText}, pour un total de ${n(minus)}.${blocker ? ` ${blocker}` : ""}`,
      es: `El caso está lastrado principalmente por ${downsideText}, con un total de ${n(minus)}.${blocker ? ` ${blocker}` : ""}`,
      "zh-Hans": `本案例主要受到${downsideText}拖累，合计${n(minus)}。${blocker || ""}`,
      "pt-BR": `O caso é limitado principalmente por ${downsideText}, totalizando ${n(minus)}.${blocker ? ` ${blocker}` : ""}`,
    }[code];
    copy.posture = primaryStop
      ? {
          da: `Gå ikke videre til implementering eller distribution under det nuværende koncept. Overvej først casen igen efter et væsentligt redesign af rettighedskortlægning, investorkreds, compliance-kontroller, forvarings- og afviklingsdesign eller andre centrale strukturelle forudsætninger. En central stopbetingelse i denne case er: ${primaryStop}.`,
          de: `Unter dem aktuellen Konzept nicht in Umsetzung oder Vertrieb gehen. Eine erneute Prüfung ist erst nach einer wesentlichen Neugestaltung von Rechtemapping, Investorenumfang, Compliance-Kontrollen, Verwahrungs-/Abwicklungsdesign oder anderen zentralen Strukturannahmen sinnvoll. Eine zentrale Stoppschwelle in diesem Fall lautet: ${primaryStop}.`,
          fr: `Ne pas passer à la mise en œuvre ou à la distribution dans le concept actuel. Ne reconsidérer le cas qu’après une refonte substantielle de la cartographie des droits, du périmètre investisseur, des contrôles de conformité, du design de conservation/règlement ou d’autres hypothèses structurelles clés. Un seuil d’arrêt central dans ce cas est : ${primaryStop}.`,
          es: `No pasar a implementación ni distribución con el concepto actual. Reconsiderar solo después de un rediseño material del mapeo de derechos, alcance inversor, controles de cumplimiento, diseño de custodia/liquidación u otras hipótesis estructurales centrales. Un umbral de parada clave en este caso es: ${primaryStop}.`,
          "zh-Hans": `在当前概念下，不应进入实施或分销阶段。只有在对权利映射、投资者范围、合规控制、托管/结算设计或其他核心结构假设进行重大重构后，才应重新考虑。本案例中已可见的一个关键停止阈值是：${primaryStop}。`,
          "pt-BR": `Não avance para implementação ou distribuição com o conceito atual. Reavalie somente após um redesenho material do mapeamento de direitos, universo de investidores, controles de compliance, desenho de custódia/liquidação ou outras premissas estruturais centrais. Um stop criterion relevante neste caso é: ${primaryStop}.`,
        }[code]
      : {
          da: "Gå ikke videre til implementering eller distribution under det nuværende koncept. Dette er en redesigncase, ikke en case der kan løses med mindre justeringer. Overvej kun casen igen, hvis strukturen afgrænses væsentligt på ny.",
          de: "Unter dem aktuellen Konzept nicht in Umsetzung oder Vertrieb gehen. Dies ist ein Redesign-Fall, kein Feinjustierungsfall. Eine erneute Prüfung ist nur sinnvoll, wenn die Struktur wesentlich neu zugeschnitten wird.",
          fr: "Ne pas passer à la mise en œuvre ou à la distribution dans le concept actuel. Il s’agit d’un cas de refonte, non d’un simple réglage. Ne reconsidérer le cas que si la structure est matériellement redéfinie.",
          es: "No pasar a implementación ni distribución con el concepto actual. Este es un caso de rediseño, no de ajuste fino. Reconsiderar solo si la estructura se redefine materialmente.",
          "zh-Hans": "在当前概念下，不应进入实施或分销阶段。这是一个需要重构的案例，而不是微调案例；只有在结构被实质性重新界定后才应重新考虑。",
          "pt-BR": "Não avance para implementação ou distribuição com o conceito atual. Este é um caso de redesenho, não de ajuste incremental; reavalie somente se a estrutura for materialmente redefinida.",
        }[code];
    copy.impact = {
      da: "Mindre justeringer vil næppe ændre resultatet alene. Den aktuelle positive værdiskabelse er for svag til at opveje den juridiske, compliance-mæssige, strukturelle og operationelle byrde, som det beskrevne design indebærer.",
      de: "Inkrementelle Optimierung wird das Ergebnis voraussichtlich nicht allein verändern. Das aktuelle Wertpotenzial ist zu schwach, um die rechtlichen, Compliance-, Strukturierungs- und Betriebsbelastungen des beschriebenen Designs aufzunehmen.",
      fr: "Une optimisation incrémentale a peu de chances de modifier seule le résultat. Le potentiel positif actuel est trop faible pour absorber les charges juridiques, de conformité, de structuration et d’exploitation impliquées par le design décrit.",
      es: "La optimización incremental difícilmente cambiará el resultado por sí sola. El potencial positivo actual es demasiado débil para absorber las cargas legales, de cumplimiento, estructuración y operación que implica el diseño descrito.",
      "zh-Hans": "单靠增量优化不太可能改变结果。当前上行价值过弱，无法吸收所述设计带来的法律、合规、结构设计和运营负担。",
      "pt-BR": "Ajustes incrementais, isoladamente, dificilmente mudarão o resultado. O benefício atual é insuficiente para absorver a carga jurídica, de compliance, estruturação e operação introduzida pelo desenho descrito.",
    }[code];
  } else if (isViable) {
    copy.headline = {
      da: `Analysen understøtter en levedygtig case for at fortsætte med tokenisering i den foreslåede retning. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Die Analyse stützt einen tragfähigen Fall, die Tokenisierung in der vorgeschlagenen Richtung weiterzuverfolgen. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `L’analyse soutient un cas viable pour poursuivre la tokenisation dans la direction proposée. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `El análisis respalda un caso viable para avanzar con la tokenización en la dirección propuesta. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `分析支持按拟议方向继续推进代币化的可行案例。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `A análise sustenta um caso viável para prosseguir com a tokenização na direção proposta. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Den stærkeste metodiske støtte kommer fra ${upsideText}, samlet ${n(plus)}. TVAC identificerer en positiv værdihypotese under det beskrevne design, men effekterne skal fortsat valideres.`,
      de: `Die stärkste methodische Unterstützung kommt von ${upsideText}, zusammen ${n(plus)}. TVAC identifiziert unter dem beschriebenen Design eine positive Werthypothese; die Wirkungen müssen jedoch noch validiert werden.`,
      fr: `Le soutien méthodologique le plus fort vient de ${upsideText}, pour un total de ${n(plus)}. TVAC identifie une hypothèse de valeur positive selon la conception décrite, mais les effets doivent encore être validés.`,
      es: `El apoyo metodológico más fuerte procede de ${upsideText}, con un total de ${n(plus)}. TVAC identifica una hipótesis de valor positiva bajo el diseño descrito, pero los efectos aún deben validarse.`,
      "zh-Hans": `最强的方法论支持来自${upsideText}，合计${n(plus)}。TVAC在所述设计下识别出正向价值假设，但其效果仍需验证。`,
      "pt-BR": `O suporte metodológico mais forte vem de ${upsideText}, totalizando ${n(plus)}. O TVAC identifica premissas positivas de valor no desenho descrito, embora sua realização ainda precise ser validada.`,
    }[code];
    copy.constraints = {
      da: `Det vigtigste disciplinpunkt er at holde ${downsideText} begrænset til ${n(minus)} og undgå en unødvendig udvidelse af omfanget, som kan udvande den nuværende værdilogik.${primaryBlocker ? ` Bemærk: ${primaryBlocker}.` : ""}`,
      de: `Der zentrale Disziplinpunkt besteht darin, ${downsideText} bei ${n(minus)} zu halten und Scope Creep zu vermeiden, der den aktuellen Value Case verwässern würde.${primaryBlocker ? ` Hinweis: ${primaryBlocker}.` : ""}`,
      fr: `Le principal point de discipline consiste à contenir ${downsideText} à ${n(minus)} et à éviter un élargissement du périmètre qui diluerait le cas de valeur actuel.${primaryBlocker ? ` Note : ${primaryBlocker}.` : ""}`,
      es: `El principal punto de disciplina es mantener ${downsideText} contenido en ${n(minus)} y evitar una expansión del alcance que diluya el caso de valor actual.${primaryBlocker ? ` Nota: ${primaryBlocker}.` : ""}`,
      "zh-Hans": `关键纪律点是将${downsideText}控制在${n(minus)}，并避免范围蔓延削弱当前价值案例。${primaryBlocker ? `注意：${primaryBlocker}。` : ""}`,
      "pt-BR": `O principal ponto de disciplina é manter ${downsideText} sob controle em ${n(minus)} e evitar expansão de escopo que enfraqueça o atual caso de valor.${primaryBlocker ? ` Atenção: ${primaryBlocker}.` : ""}`,
    }[code];
    copy.posture = {
      da: "Fortsæt med disciplineret implementering. Bevar de elementer, der skaber værdi, men undgå unødvendig udvidelse af jurisdiktionsomfanget, likviditetsløfter eller teknisk kompleksitet ud over det, casen faktisk kræver.",
      de: "Mit disziplinierter Umsetzung fortfahren. Die wertschaffenden Merkmale bewahren, aber unnötige Ausweitung von Jurisdiktionsumfang, Liquiditätsversprechen oder technischer Komplexität über das hinaus vermeiden, was der Fall tatsächlich braucht.",
      fr: "Poursuivre avec une mise en œuvre disciplinée. Préserver les caractéristiques qui créent de la valeur, mais éviter toute extension inutile du périmètre juridictionnel, des promesses de liquidité ou de la complexité technique au-delà de ce que le cas exige réellement.",
      es: "Avanzar con una implementación disciplinada. Preservar los elementos que crean valor, pero evitar ampliar innecesariamente el alcance jurisdiccional, las promesas de liquidez o la complejidad técnica más allá de lo que el caso realmente requiere.",
      "zh-Hans": "以严格纪律推进实施。保留创造价值的特征，但避免不必要地扩大司法辖区范围、流动性承诺或技术复杂性，超过本案例实际需要。",
      "pt-BR": "Avance para implementação com disciplina rigorosa. Preserve as características que criam valor, mas evite ampliar desnecessariamente o escopo jurisdicional, compromissos de liquidez ou complexidade tecnológica além do que o caso efetivamente exige.",
    }[code];
    copy.impact = {
      da: "Dette er primært en kontrolleret fremdriftscase. Den positive værdiskabelse fremstår plausibel under det beskrevne design, men skal valideres og indfanges uden at genindføre unødvendige TC/NR gennem overudvikling eller for ambitiøs distribution.",
      de: "Dies ist primär ein kontrollierter Fortschrittsfall. Das Upside erscheint unter dem beschriebenen Design plausibel, muss jedoch validiert und erfasst werden, ohne durch Overengineering oder überzogene Distributionsreichweite vermeidbare TC/NR wieder einzuführen.",
      fr: "Il s’agit principalement d’un cas de progression contrôlée. Le potentiel positif paraît plausible selon la conception décrite, mais il doit être validé et capturé sans réintroduire des TC/NR évitables par suringénierie ou excès d’ambition de distribution.",
      es: "Este es principalmente un caso de avance controlado. El potencial positivo parece plausible bajo el diseño descrito, pero debe validarse y capturarse sin reintroducir TC/NR evitables mediante sobreingeniería o ambición excesiva de distribución.",
      "zh-Hans": "这主要是一个受控推进型案例。根据所述设计，上行价值具有合理性，但仍需验证，并且不得因过度工程化或过度扩大分销范围而重新引入可避免的TC/NR。",
      "pt-BR": "Este é principalmente um caso de avanço controlado. No desenho descrito, o benefício é plausível, mas ainda precisa ser validado e não deve ser erodido por excesso de engenharia ou expansão indevida da distribuição que reintroduza TC/NR evitáveis.",
    }[code];
  } else if (isEarlyMaturity) {
    copy.headline = {
      da: `Dette er en retningsgivende vurdering af et tidligt tokeniseringskoncept, ikke en konklusion på et færdigt struktureret design. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Dies ist eine richtungsweisende Bewertung eines frühen Tokenisierungskonzepts und kein Urteil über eine finalisierte Struktur. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `Il s’agit d’une évaluation directionnelle d’un concept de tokenisation à un stade précoce, et non d’un verdict sur une structure finalisée. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `Se trata de una evaluación direccional de un concepto temprano de tokenización, no de un veredicto sobre una estructura finalizada. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `这是对早期代币化概念的方向性评估，而不是对最终结构的结论。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `Esta é uma avaliação direcional de um conceito de tokenização em estágio inicial, não uma conclusão sobre a estrutura final. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Det nuværende koncept viser et plausibelt potentiale primært gennem ${upsideText}, samlet ${n(plus)}, men den positive værdiskabelse er stadig konceptuel snarere end strukturelt sikret.`,
      de: `Das aktuelle Konzept erhält methodische Unterstützung vor allem durch ${upsideText}, zusammen ${n(plus)}, aber dieses Upside ist noch konzeptionell und nicht durch Nachweise abgesichert.`,
      fr: `Le concept actuel reçoit un soutien méthodologique principalement via ${upsideText}, pour un total de ${n(plus)}, mais ce potentiel reste conceptuel et non étayé par des preuves.`,
      es: `El concepto actual recibe apoyo metodológico principalmente a través de ${upsideText}, con un total de ${n(plus)}, pero ese potencial sigue siendo conceptual y no está respaldado por evidencia.`,
      "zh-Hans": `当前概念主要通过${upsideText}获得方法论支持，合计${n(plus)}，但该上行价值仍属概念性，尚未得到证据支持。`,
      "pt-BR": `O conceito atual recebe suporte metodológico principalmente de ${upsideText}, totalizando ${n(plus)}, mas esse benefício ainda é conceitual e não está sustentado por evidências suficientes.`,
    }[code];
    copy.constraints = {
      da: `Den væsentligste friktion kommer primært fra ${downsideText}, samlet ${n(minus)}. ${blocker || "Flere centrale designvalg er stadig åbne, især omkring rettighedskortlægning, forvaring, afvikling, overførselsdesign og leveringsmodel."}`,
      de: `Das aktuelle Downside wird vor allem durch ${downsideText} getrieben, zusammen ${n(minus)}. ${blocker || "Mehrere zentrale Designentscheidungen sind noch offen, insbesondere bei Rechtemapping, Verwahrung, Abwicklung, Transferdesign und Liefermodell."}`,
      fr: `Le downside actuel est principalement porté par ${downsideText}, pour un total de ${n(minus)}. ${blocker || "Plusieurs choix de conception clés restent ouverts, notamment la cartographie des droits, la conservation, le règlement, le design des transferts et le modèle d’exécution."}`,
      es: `El downside actual está impulsado principalmente por ${downsideText}, con un total de ${n(minus)}. ${blocker || "Varias decisiones de diseño centrales siguen abiertas, especialmente en torno al mapeo de derechos, custodia, liquidación, diseño de transferencias y modelo de entrega."}`,
      "zh-Hans": `当前下行因素主要来自${downsideText}，合计${n(minus)}。${blocker || "若干核心设计选择仍未确定，尤其涉及权利映射、托管、结算、转让设计和交付模式。"}`,
      "pt-BR": `As principais fricções atuais vêm de ${downsideText}, totalizando ${n(minus)}.${blocker ? ` ${blocker}` : " Várias escolhas centrais de desenho permanecem em aberto, especialmente quanto a mapeamento de direitos, custódia, liquidação, desenho de transferências e modelo de execução."}`,
    }[code];
    copy.posture = {
      da: "Læs resultatet som en retningsgivende screening, ikke som en endelig go-beslutning for designet. Gå kun videre til afklaring, afgrænsning og strukturdefinition, før der bindes ressourcer til implementering, juridisk strukturering eller leverandørtilslutning.",
      de: "Das Ergebnis als richtungsweisendes Screening lesen, nicht als designfinale Go-Entscheidung. Zunächst nur in Klärung, Scoping und Strukturdefinition weitergehen, bevor Ressourcen für Umsetzung, rechtliche Ausführung oder Anbieteranbindung gebunden werden.",
      fr: "Lire le résultat comme un screening directionnel, non comme une décision go finale sur le design. Ne poursuivre qu’avec clarification, cadrage et définition de structure avant de s’engager dans la mise en œuvre, l’exécution juridique ou l’onboarding de prestataires.",
      es: "Leer el resultado como un cribado direccional, no como una decisión go final de diseño. Avanzar solo hacia aclaración, delimitación y definición estructural antes de comprometerse con implementación, ejecución legal u onboarding de proveedores.",
      "zh-Hans": "应将该结果视为方向性筛选，而非最终设计的go决策。在投入实施、法律执行或服务商接入前，仅应推进澄清、范围界定和结构定义工作。",
      "pt-BR": "Trate este resultado como uma triagem direcional, não como uma decisão final de go sobre o desenho. Antes de comprometer recursos com implementação, estruturação jurídica ou onboarding de prestadores, avance apenas com esclarecimento, delimitação de escopo e definição estrutural.",
    }[code];
    copy.impact = {
      da: `Casen kan stadig bevæge sig i begge retninger afhængigt af, hvordan strukturen færdiggøres. Opgaven nu er at omsætte et interessant koncept til en defineret struktur med håndhævelige rettigheder, en disciplineret afgrænsning og realistiske driftsforudsætninger.${nearBoundary}${providerRisk}`,
      de: `Der Fall kann sich je nach Finalisierung der Struktur noch in beide Richtungen entwickeln. Die zentrale Aufgabe besteht jetzt darin, ein interessantes Konzept in eine definierte Struktur mit durchsetzbaren Rechten, diszipliniertem Umfang und realistischen Betriebsannahmen zu überführen.${nearBoundary}${providerRisk}`,
      fr: `Le cas peut encore évoluer dans les deux sens selon la manière dont la structure est finalisée. La tâche principale consiste à transformer un concept intéressant en une structure définie, avec des droits exécutoires, un périmètre discipliné et des hypothèses opérationnelles réalistes.${nearBoundary}${providerRisk}`,
      es: `El caso aún puede moverse en ambas direcciones según cómo se finalice la estructura. La tarea principal ahora es convertir un concepto interesante en una estructura definida con derechos exigibles, alcance disciplinado y supuestos operativos realistas.${nearBoundary}${providerRisk}`,
      "zh-Hans": `本案例仍可能根据结构如何最终确定而向两个方向变化。当前关键任务是把一个有吸引力的概念转化为具备可执行权利、纪律化范围和现实运营假设的明确结构。${nearBoundary}${providerRisk}`,
      "pt-BR": `Este caso ainda pode se mover em qualquer direção dependendo de como a estrutura final for definida. A prioridade agora é transformar um conceito atraente em uma estrutura clara, com direitos exequíveis, escopo disciplinado e premissas operacionais realistas.${nearBoundary}${providerRisk}`,
    }[code];
  } else if (isStructuredMaturity) {
    copy.headline = {
      da: `Casen er tilstrækkeligt defineret til en meningsfuld case-specifik vurdering, men den er endnu ikke et færdigt eksekveringsdesign. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Der Fall ist ausreichend definiert für eine sinnvolle fallspezifische Bewertung, aber noch kein finalisiertes Umsetzungsdesign. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `Le cas est suffisamment défini pour une évaluation spécifique significative, mais il ne constitue pas encore un design d’exécution finalisé. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `El caso está suficientemente definido para una evaluación específica significativa, pero aún no es un diseño de ejecución finalizado. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `本案例已足够明确，可进行有意义的案例专属评估，但尚不是最终执行设计。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `Este caso está suficientemente definido para uma avaliação específica e significativa, mas ainda não constitui um desenho final de execução. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Den aktuelle værdilogik understøttes metodisk især af ${upsideText}, samlet ${n(plus)}. TVAC identificerer case-specifikt potentiale, som fortsat skal valideres og afhænger af de resterende strukturelle valg.`,
      de: `Die aktuelle Wertlogik wird methodisch vor allem durch ${upsideText} gestützt, zusammen ${n(plus)}. TVAC identifiziert fallspezifisches Potenzial, das jedoch noch validiert werden muss und von den verbleibenden Strukturentscheidungen abhängt.`,
      fr: `La logique de valeur actuelle reçoit un soutien méthodologique surtout de ${upsideText}, pour un total de ${n(plus)}. TVAC identifie un potentiel spécifique au cas qui doit encore être validé et dépend des choix structurels restants.`,
      es: `La lógica de valor actual recibe apoyo metodológico sobre todo de ${upsideText}, con un total de ${n(plus)}. TVAC identifica potencial específico del caso, pero aún debe validarse y depende de cómo se cierren las decisiones estructurales restantes.`,
      "zh-Hans": `当前价值逻辑主要在方法上得到${upsideText}的支持，合计${n(plus)}。TVAC识别出案例专属潜力，但该潜力仍需验证，并取决于剩余结构性选择如何最终确定。`,
      "pt-BR": `A lógica de valor atual é sustentada metodologicamente principalmente por ${upsideText}, totalizando ${n(plus)}. O TVAC identifica potencial específico do caso, mas esse potencial ainda precisa ser validado e depende de como as escolhas estruturais restantes forem fechadas.`,
    }[code];
    copy.constraints = {
      da: `Den væsentligste friktion kommer fra ${downsideText}, samlet ${n(minus)}. ${blocker || "Vurderingen er fortsat følsom over for uafklarede designvalg, især hvor forvaring, afvikling, compliance-kontroller og leveringsøkonomi endnu ikke er fuldt afklaret."}`,
      de: `Die größte Reibung entsteht durch ${downsideText}, zusammen ${n(minus)}. ${blocker || "Das Urteil bleibt empfindlich gegenüber ungelösten Designentscheidungen, insbesondere dort, wo Verwahrung, Abwicklung, Compliance-Kontrollen und Lieferökonomie noch nicht vollständig festgelegt sind."}`,
      fr: `La principale friction provient de ${downsideText}, pour un total de ${n(minus)}. ${blocker || "La conclusion reste sensible aux choix de conception non résolus, notamment lorsque la conservation, le règlement, les contrôles de conformité et l’économie d’exécution ne sont pas encore complètement verrouillés."}`,
      es: `La principal fricción procede de ${downsideText}, con un total de ${n(minus)}. ${blocker || "El resultado sigue siendo sensible a decisiones de diseño no resueltas, especialmente cuando la custodia, la liquidación, los controles de cumplimiento y la economía de entrega aún no están plenamente cerrados."}`,
      "zh-Hans": `主要摩擦来自${downsideText}，合计${n(minus)}。${blocker || "该结论仍对未解决的设计选择敏感，尤其是在托管、结算、合规控制和交付经济性尚未完全锁定的方面。"}`,
      "pt-BR": `As principais fricções vêm de ${downsideText}, totalizando ${n(minus)}.${blocker ? ` ${blocker}` : " O resultado permanece sensível a escolhas de desenho ainda não resolvidas, especialmente onde custódia, liquidação, controles de compliance e economia de execução ainda não foram totalmente definidos."}`,
    }[code];
    copy.posture = {
      da: "Fortsæt kun, hvis de resterende væsentlige designvalg er færdiggjort før implementering. Dette er endnu ikke blot en eksekveringscase; den kræver stadig disciplineret færdiggørelse af designet omkring investorkreds, compliance-kontroller, forvaring, afvikling og driftsmodel.",
      de: "Nur fortfahren, wenn die verbleibenden wesentlichen Designentscheidungen vor der Umsetzung finalisiert werden. Dies ist noch nicht einfach ein Umsetzungsfall; es braucht weiterhin disziplinierte Designvollendung rund um Investorenumfang, Compliance-Kontrollen, Verwahrung/Abwicklung und Betriebsmodell.",
      fr: "Ne poursuivre que si les choix de conception matériels restants sont finalisés avant la mise en œuvre. Ce n’est pas encore un simple cas d’exécution ; il exige encore une finalisation disciplinée du design autour du périmètre investisseur, des contrôles de conformité, de la conservation/du règlement et du modèle opérationnel.",
      es: "Proceder solo si las decisiones materiales de diseño restantes se finalizan antes de la implementación. Este aún no es simplemente un caso de ejecución; requiere completar disciplinadamente el diseño en torno al alcance inversor, controles de cumplimiento, custodia/liquidación y modelo operativo.",
      "zh-Hans": "只有在实施前完成剩余重大设计选择时才应继续推进。这还不只是一个执行案例；它仍需要围绕投资者范围、合规控制、托管/结算和运营模式完成有纪律的设计收口。",
      "pt-BR": "Avance somente se as principais escolhas de desenho remanescentes forem fechadas antes da implementação. Este ainda não é apenas um caso de execução; exige conclusão disciplinada do desenho quanto a universo de investidores, controles de compliance, custódia/liquidação e modelo operacional.",
    }[code];
    copy.impact = {
      da: `Hvis de resterende designvalg færdiggøres godt, har casen en plausibel vej til en stærkere værdiprofil ved at reducere Nye risici (NR) og Tokeniseringsomkostninger (TC) og gøre potentialet mere robust. Hvis ikke, forbliver den aktuelle værdilogik for skrøbelig til ligefrem eksekvering.${nearBoundary}${providerRisk}`,
      de: `Wenn die verbleibenden Designentscheidungen gut finalisiert werden, hat der Fall einen plausiblen Weg zu einem stärkeren Wertprofil, indem Neue Risiken (NR)/Tokenisierungskosten (TC) reduziert und das Upside belastbarer gemacht werden. Andernfalls bleibt der aktuelle Value Case zu fragil für eine direkte Umsetzung.${nearBoundary}${providerRisk}`,
      fr: `Si les choix de conception restants sont bien finalisés, le cas dispose d’un chemin plausible vers un profil de valeur plus fort en réduisant les Nouveaux risques (NR)/Coûts de tokenisation (TC) et en rendant le potentiel positif plus durable. Sinon, le cas de valeur actuel reste trop fragile pour une exécution directe.${nearBoundary}${providerRisk}`,
      es: `Si las decisiones de diseño restantes se cierran bien, el caso tiene una vía plausible hacia un perfil de valor más fuerte al reducir Nuevos riesgos (NR)/Costes de tokenización (TC) y hacer más duradero el upside. Si no, el caso de valor actual seguirá siendo demasiado frágil para una ejecución directa.${nearBoundary}${providerRisk}`,
      "zh-Hans": `如果剩余设计选择得到良好收口，本案例有可能通过降低新风险（NR）/代币化成本（TC）并使上行价值更持久，从而形成更强的价值画像。否则，当前价值案例仍过于脆弱，难以直接执行。${nearBoundary}${providerRisk}`,
      "pt-BR": `Se as escolhas remanescentes forem bem resolvidas, o caso pode desenvolver um perfil de valor mais forte ao reduzir Novos Riscos (NR)/Custos de Tokenização (TC) e tornar os benefícios mais duráveis. Caso contrário, o atual caso de valor permanece frágil demais para execução direta.${nearBoundary}${providerRisk}`,
    }[code];
  } else if (isDesignedMaturity) {
    copy.headline = {
      da: `Denne vurdering analyserer en væsentligt designet tokeniseringsstruktur snarere end et åbent koncept. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Diese Bewertung analysiert eine weitgehend designte Tokenisierungsstruktur und nicht ein offenes Konzept. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `Cette évaluation analyse une structure de tokenisation substantiellement conçue plutôt qu’un concept ouvert. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `Esta evaluación analiza una estructura de tokenización sustancialmente diseñada, no un concepto abierto. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `本评估分析的是一个已较充分设计的代币化结构，而非开放概念。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `Esta avaliação analisa uma estrutura de tokenização já substancialmente desenhada, e não um conceito em aberto. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Den nuværende struktur får reel støtte især fra ${upsideText}, samlet ${n(plus)}. Casen er retningsmæssigt troværdig som designet, men potentialet er stadig smallere end den samlede leveringsbyrde.`,
      de: `Die aktuelle Struktur wird real vor allem durch ${upsideText} gestützt, zusammen ${n(plus)}. Der Fall ist in seiner Ausgestaltung richtungsweise glaubwürdig, aber das Upside bleibt schmaler als die gesamte Lieferbelastung.`,
      fr: `La structure actuelle reçoit un soutien réel principalement via ${upsideText}, pour un total de ${n(plus)}. Le cas est directionnellement crédible tel qu’il est conçu, mais le potentiel positif reste plus étroit que la charge complète d’exécution.`,
      es: `La estructura actual muestra apoyo real principalmente a través de ${upsideText}, con un total de ${n(plus)}. El caso es direccionalmente creíble tal como está diseñado, pero el upside sigue siendo más estrecho que la carga total de entrega.`,
      "zh-Hans": `当前结构主要通过${upsideText}获得真实支撑，合计${n(plus)}。按当前设计，本案例方向上可信，但上行价值仍窄于完整交付负担。`,
      "pt-BR": `A estrutura atual recebe suporte concreto principalmente de ${upsideText}, totalizando ${n(plus)}. No desenho atual, o caso é direcionalmente crível, mas o benefício ainda é mais estreito do que a carga total de execução.`,
    }[code];
    copy.constraints = {
      da: `Den væsentligste friktion kommer fra ${downsideText}, samlet ${n(minus)}. ${blocker || "Vurderingen holdes ikke tilbage af manglende konceptdefinition, men af økonomi, compliance-omfang og leveringsbyrde i den nuværende struktur."}`,
      de: `Die größte Reibung entsteht durch ${downsideText}, zusammen ${n(minus)}. ${blocker || "Das Urteil wird nicht durch fehlende Konzeptdefinition gebremst, sondern durch Ökonomie, Compliance-Umfang und Lieferbelastung der aktuell designten Struktur."}`,
      fr: `La principale friction provient de ${downsideText}, pour un total de ${n(minus)}. ${blocker || "La conclusion n’est pas freinée par un manque de définition conceptuelle, mais par l’économie, le périmètre de conformité et la charge d’exécution de la structure telle qu’elle est conçue."}`,
      es: `La principal fricción procede de ${downsideText}, con un total de ${n(minus)}. ${blocker || "El resultado no está limitado por falta de definición conceptual, sino por la economía, el alcance de cumplimiento y la carga de entrega de la estructura tal como está diseñada."}`,
      "zh-Hans": `主要摩擦来自${downsideText}，合计${n(minus)}。${blocker || "结论受限的原因并非概念定义不足，而是当前设计结构的经济性、合规范围和交付负担。"}`,
      "pt-BR": `As principais fricções vêm de ${downsideText}, totalizando ${n(minus)}.${blocker ? ` ${blocker}` : " O resultado é limitado não por falta de definição conceitual, mas pela economia, escopo de compliance e carga de execução da estrutura atualmente desenhada."}`,
    }[code];
    copy.posture = {
      da: "Behandl dette som en Conditional Go på strukturniveau, ikke som en invitation til at gå direkte i opbygning. Fortsæt kun, hvis den beskrevne struktur kan bestå en kommerciel stresstest samt en stresstest af forvaring, afvikling, compliance og leverandørøkonomi uden at udhule aktivejerens værdi.",
      de: "Dies als Conditional Go auf Strukturebene behandeln, nicht als Einladung, standardmäßig zu bauen. Nur fortfahren, wenn die designte Struktur kommerzielle, Verwahrungs-/Abwicklungs-, Compliance- und Anbieterökonomie-Stresstests besteht, ohne den Wert für den Asset Owner zu erodieren.",
      fr: "Traiter cela comme un Conditional Go au niveau de la structure, non comme une invitation à construire par défaut. Ne poursuivre que si la structure conçue résiste aux tests de stress commerciaux, de conservation/règlement, de conformité et d’économie fournisseur sans éroder la valeur pour le propriétaire de l’actif.",
      es: "Tratar esto como un Conditional Go a nivel estructural, no como una invitación a construir por defecto. Proceder solo si la estructura diseñada puede superar pruebas de estrés comerciales, de custodia/liquidación, cumplimiento y economía de proveedores sin erosionar el valor del propietario del activo.",
      "zh-Hans": "应将其视为结构层面的Conditional Go，而不是默认建设邀请。只有在该设计结构能够经受商业、托管/结算、合规和服务商经济性压力测试且不侵蚀资产所有人价值时，才应继续推进。",
      "pt-BR": "Trate isto como um Conditional Go no nível da estrutura, não como um convite automático para construir. Avance somente se o desenho suportar testes de estresse comerciais, de custódia/liquidação, compliance e economia dos prestadores sem erodir o valor capturado pelo proprietário do ativo.",
    }[code];
    copy.impact = {
      da: `Casen kan blive stærkere gennem redesign, forenkling og strammere eksekveringsdisciplin, men den nuværende struktur retfærdiggør endnu ikke en ligefrem udrulning. Det centrale spørgsmål er ikke længere, hvad konceptet er, men om netop dette design indfanger tilstrækkelig værdi for den initierende aktivejer, når hele eksekveringsbyrden medregnes.${nearBoundary}${providerRisk}`,
      de: `Der Fall kann durch Neugestaltung, Vereinfachung und strengere Umsetzungsdisziplin stärker werden, aber die aktuelle Struktur rechtfertigt noch keinen direkten Rollout. Die Kernfrage ist nicht mehr, was das Konzept ist, sondern ob genau dieses Design genug Wert für den initiierenden Asset Owner erfasst, wenn die gesamte Umsetzungsbelastung einbezogen wird.${nearBoundary}${providerRisk}`,
      fr: `Le cas peut devenir plus fort grâce à une refonte, une simplification et une discipline d’exécution plus stricte, mais la structure actuelle ne justifie pas encore un déploiement direct. La question clé n’est plus de savoir quel est le concept, mais si ce design précis capte suffisamment de valeur pour le propriétaire de l’actif initiateur une fois toute la charge d’exécution incluse.${nearBoundary}${providerRisk}`,
      es: `El caso puede fortalecerse mediante rediseño, simplificación y mayor disciplina de ejecución, pero la estructura actual aún no justifica un despliegue directo. La pregunta clave ya no es cuál es el concepto, sino si este diseño específico captura suficiente valor para el propietario del activo que inicia el proyecto una vez incluida toda la carga de ejecución.${nearBoundary}${providerRisk}`,
      "zh-Hans": `本案例可通过重构、简化和更严格的执行纪律变得更强，但当前结构尚不足以支持直接上线。关键问题已不再是概念是什么，而是该具体设计在纳入完整执行负担后，是否为发起资产所有人捕获了足够价值。${nearBoundary}${providerRisk}`,
      "pt-BR": `O caso pode se fortalecer por meio de reestruturação, simplificação e maior disciplina de execução, mas a estrutura atual ainda não sustenta um lançamento direto. A questão central já não é qual é o conceito, mas se este desenho específico captura valor suficiente para o proprietário do ativo que iniciou o projeto depois de incorporar toda a carga de execução.${nearBoundary}${providerRisk}`,
    }[code];
  } else {
    copy.headline = {
      da: `Denne case er ikke et klart nej, men den er heller ikke klar til ligefrem eksekvering. Vurderingen ender med ${verdictLabel} og en estimeret nettomerværdi på ${scorePart}.${coverage}${confidenceText}`,
      de: `Dieser Fall ist kein klares Nein, aber auch nicht bereit für eine direkte Umsetzung. Die Bewertung kommt zu ${verdictLabel} mit einem geschätzten Netto-Mehrwert von ${scorePart}.${coverage}${confidenceText}`,
      fr: `Ce cas n’est pas un non clair, mais il n’est pas non plus prêt pour une exécution directe. L’évaluation aboutit à ${verdictLabel} avec une valeur ajoutée nette estimée de ${scorePart}.${coverage}${confidenceText}`,
      es: `Este caso no es un no claro, pero tampoco está listo para una ejecución directa. La evaluación concluye en ${verdictLabel} con un valor añadido neto estimado de ${scorePart}.${coverage}${confidenceText}`,
      "zh-Hans": `本案例并非明确否定，但也尚未准备好直接执行。本次评估结果为${verdictLabel}，估计净增值为${scorePart}${coverage}${confidenceText}`,
      "pt-BR": `Este caso não é uma rejeição clara, mas também ainda não está pronto para execução direta. A avaliação resulta em ${verdictLabel}, com Valor Agregado líquido estimado de ${scorePart}.${coverage}${confidenceText}`,
    }[code];
    copy.findings = {
      da: `Den aktuelle værdilogik understøttes især af ${upsideText}, samlet ${n(plus)}. Der er reelt potentiale, men det afhænger stadig af designet og er ikke allerede sikret.`,
      de: `Die aktuelle Wertlogik wird vor allem durch ${upsideText} gestützt, zusammen ${n(plus)}. Es gibt reales Potenzial, aber es bleibt designabhängig und ist noch nicht gesichert.`,
      fr: `La logique de valeur actuelle est soutenue surtout par ${upsideText}, pour un total de ${n(plus)}. Il existe un potentiel réel, mais il reste dépendant du design et n’est pas encore sécurisé.`,
      es: `La lógica de valor actual se apoya sobre todo en ${upsideText}, con un total de ${n(plus)}. Existe potencial real, pero sigue dependiendo del diseño y aún no está asegurado.`,
      "zh-Hans": `当前价值逻辑主要由${upsideText}支撑，合计${n(plus)}。这里存在真实潜力，但仍依赖设计，尚未被锁定。`,
      "pt-BR": `A lógica de valor atual é sustentada principalmente por ${upsideText}, totalizando ${n(plus)}. Existe potencial real, mas ele ainda depende do desenho e não está assegurado.`,
    }[code];
    copy.constraints = {
      da: `Den væsentligste friktion kommer fra ${downsideText}, samlet ${n(minus)}. ${blocker || "Vurderingen er fortsat følsom over for afgrænsning, udformning af overførsels- og compliance-kontroller samt leveringsøkonomi."}`,
      de: `Die größte Reibung entsteht durch ${downsideText}, zusammen ${n(minus)}. ${blocker || "Das Urteil bleibt empfindlich gegenüber Umfang, Transfer-/Compliance-Design und Lieferökonomie."}`,
      fr: `La principale friction provient de ${downsideText}, pour un total de ${n(minus)}. ${blocker || "La conclusion reste sensible au périmètre, au design transfert/conformité et à l’économie d’exécution."}`,
      es: `La principal fricción procede de ${downsideText}, con un total de ${n(minus)}. ${blocker || "El resultado sigue siendo sensible al alcance, al diseño de transferencias/cumplimiento y a la economía de entrega."}`,
      "zh-Hans": `主要摩擦来自${downsideText}，合计${n(minus)}。${blocker || "结论仍对范围、转让/合规设计和交付经济性敏感。"}`,
      "pt-BR": `As principais fricções vêm de ${downsideText}, totalizando ${n(minus)}.${blocker ? ` ${blocker}` : " O resultado permanece sensível ao escopo, ao desenho de transferência/compliance e à economia de execução."}`,
    }[code];
    copy.posture = {
      da: "Fortsæt kun, hvis projektet afgrænses stramt, og betingelserne omkring investor scope, compliance-kontroller, forvaring/afvikling og leveringsøkonomi låses før implementering.",
      de: "Nur fortfahren, wenn das Projekt eng zugeschnitten wird und die Bedingungen rund um Investorenumfang, Compliance-Kontrollen, Verwahrung/Abwicklung und Lieferökonomie vor der Umsetzung festgelegt sind.",
      fr: "Ne poursuivre que si le projet est strictement cadré et si les conditions relatives au périmètre investisseur, aux contrôles de conformité, à la conservation/au règlement et à l’économie d’exécution sont verrouillées avant la mise en œuvre.",
      es: "Proceder solo si el proyecto se delimita estrictamente y las condiciones en torno al alcance inversor, controles de cumplimiento, custodia/liquidación y economía de entrega quedan cerradas antes de la implementación.",
      "zh-Hans": "只有在项目范围被严格限定，并且投资者范围、合规控制、托管/结算和交付经济性等条件在实施前被锁定时，才应继续推进。",
      "pt-BR": "Avance somente se o projeto permanecer estritamente delimitado e se condições como universo de investidores, controles de compliance, custódia/liquidação e economia de execução forem definidas antes da implementação.",
    }[code];
    copy.impact = {
      da: `Hvis disse betingelser løses, har casen en plausibel vej til en stærkere værdiprofil ved at reducere Nye risici (NR) og Tokeniseringsomkostninger (TC) og gøre potentialet mere robust. Hvis ikke, er det nuværende potentiale for snævert til at bære eksekveringsfriktionen.${nearBoundary}${providerRisk}`,
      de: `Wenn diese Bedingungen gelöst werden, hat der Fall einen plausiblen Weg zu einem stärkeren Wertprofil, indem Neue Risiken (NR)/Tokenisierungskosten (TC) reduziert und das Upside belastbarer gemacht wird. Andernfalls ist das aktuelle Upside zu schmal, um den Umsetzungsaufwand zu tragen.${nearBoundary}${providerRisk}`,
      fr: `Si ces conditions sont résolues, le cas dispose d’un chemin plausible vers un profil de valeur plus fort en réduisant les Nouveaux risques (NR)/Coûts de tokenisation (TC) et en rendant le potentiel positif plus durable. Sinon, le potentiel actuel est trop étroit pour porter la friction d’exécution.${nearBoundary}${providerRisk}`,
      es: `Si estas condiciones se resuelven, el caso tiene una vía plausible hacia un perfil de valor más fuerte al reducir Nuevos riesgos (NR)/Costes de tokenización (TC) y hacer más duradero el upside. Si no, el upside actual es demasiado estrecho para soportar la fricción de ejecución.${nearBoundary}${providerRisk}`,
      "zh-Hans": `如果这些条件得到解决，本案例有可能通过降低新风险（NR）/代币化成本（TC）并使上行价值更持久，形成更强的价值画像。否则，当前上行价值过窄，无法承载执行摩擦。${nearBoundary}${providerRisk}`,
      "pt-BR": `Se essas condições forem resolvidas, o caso pode desenvolver um perfil de valor mais forte ao reduzir Novos Riscos (NR)/Custos de Tokenização (TC) e tornar os benefícios mais duráveis. Caso contrário, o atual benefício é estreito demais para absorver a fricção de execução.${nearBoundary}${providerRisk}`,
    }[code];
  }

  if (isNonViable && fineArtRetailCase) {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at flere svage designvalg forstærker hinanden i stedet for at stå alene. Uklare rettigheder, retail-scope, self-custody og DEX-lignende likviditet skaber ikke adskilte håndterbare problemer; tilsammen skaber de en strukturelt skrøbelig model med svag investorbeskyttelse og ingen valideret compliance-vej under det nuværende design.",
      de: "Das Besondere an diesem Fall ist, dass mehrere schwache Designentscheidungen einander verstärken, statt einzeln zu stehen. Unklare Rechte, Retail-Umfang, Self-Custody und DEX-ähnliche Liquidität schaffen keine getrennten handhabbaren Probleme; zusammen erzeugen sie ein strukturell fragiles Modell mit schwachem Investorenschutz und keinem validierten Compliance-Pfad im aktuellen Design.",
      fr: "Ce qui distingue ce cas, c’est que plusieurs choix de conception faibles se renforcent mutuellement au lieu d’être isolés. Des droits ambigus, un périmètre retail, la self-custody et une liquidité de type DEX ne créent pas des problèmes séparés et gérables ; ensemble, ils produisent un modèle structurellement fragile, avec une protection investisseur faible et aucun chemin de conformité validé dans le design actuel.",
      es: "Lo que diferencia este caso es que varias decisiones de diseño débiles se refuerzan entre sí en lugar de estar aisladas. Derechos ambiguos, alcance retail, self-custody y liquidez tipo DEX no crean problemas separados y manejables; juntos generan un modelo estructuralmente frágil, con débil protección del inversor y sin una vía de cumplimiento validada en el diseño actual.",
      "zh-Hans": "本案例的不同之处在于，多个薄弱设计选择相互强化，而不是彼此孤立。权利不清、面向零售、自托管和DEX式流动性并不是几个可分别管理的问题；它们共同形成了一个结构脆弱、投资者保护不足且在当前设计下缺乏经验证合规路径的模型。",
      "pt-BR": "O que diferencia este caso é que várias escolhas frágeis de desenho se reforçam mutuamente, em vez de permanecerem isoladas. Direitos pouco claros, escopo de varejo, self-custody e liquidez em estilo DEX não são problemas independentes; em conjunto, formam um modelo estruturalmente frágil, com proteção insuficiente ao investidor e sem uma via de compliance validada no desenho atual.",
    }[code];
  } else if (summaryPrimaryArchetype === "fi_treasury_fund") {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at hovedværdien ligger i operationel nytte, fleksibilitet i treasury-funktionen og anvendelse af sikkerhedsstillelse snarere end i udvidelse af investorkredsen eller spekulativ likviditet. Det gør casen mere troværdig end mange tokeniseringskoncepter, men også mere afhængig af, om tegning og indløsning, overførselskontroller og operationel integration er konkrete nok til at fjerne reel friktion i ældre processer.",
      de: "Das Besondere an diesem Fall ist, dass der Hauptwert in operativem Nutzen, Treasury-Flexibilität und Collateral-Nutzbarkeit liegt, nicht in Investorenerweiterung oder spekulativer Liquidität. Das macht den Fall glaubwürdiger als viele Tokenisierungskonzepte, aber auch abhängiger davon, ob Zeichnung/Rücknahme, Transferkontrollen und operative Integration konkret genug sind, um reale Legacy-Reibung zu beseitigen.",
      fr: "Ce qui distingue ce cas, c’est que la valeur principale se situe dans l’utilité opérationnelle, la flexibilité de trésorerie et l’usage en collatéral plutôt que dans l’expansion investisseur ou la liquidité spéculative. Cela rend le cas plus crédible que de nombreux concepts de tokenisation, mais aussi plus dépendant de la concrétisation de la souscription/du rachat, des contrôles de transfert et de l’intégration opérationnelle pour supprimer une friction legacy réelle.",
      es: "Lo que diferencia este caso es que el valor principal reside en la utilidad operativa, la flexibilidad de tesorería y la utilidad como colateral, más que en la expansión de inversores o liquidez especulativa. Esto hace que el caso sea más creíble que muchos conceptos de tokenización, pero también más dependiente de que suscripción/reembolso, controles de transferencia e integración operativa sean lo bastante concretos para eliminar fricción legacy real.",
      "zh-Hans": "本案例的不同之处在于，主要价值在于运营效用、资金管理灵活性和抵押品可用性，而不是投资者扩张或投机性流动性。这使其比许多代币化概念更可信，但也更依赖认购/赎回、转让控制和运营集成是否足够具体，以真正消除传统流程摩擦。",
      "pt-BR": "O que diferencia este caso é que o principal valor está na utilidade operacional, flexibilidade de gestão de caixa e mobilidade de colateral, e não na expansão da base de investidores ou em liquidez especulativa. Isso torna o caso mais crível do que muitos conceitos de tokenização, mas também mais dependente de subscrição/resgate, controles de transferência e integração operacional suficientemente concretos para realmente remover fricções dos processos convencionais.",
    }[code];
  } else if (summaryPrimaryArchetype === "real_estate_income_wrapper") {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at kapitalrejsningsfortællingen kan lyde stærkere, end den underliggende økonomi kan bære. Casen kan fungere, men kun hvis den juridiske struktur, investor scope og likviditetsdesignet holdes disciplineret nok til, at tokenisering forbedrer finansieringsfleksibiliteten uden at tilføje uforholdsmæssig strukturerings- og servicing-friktion.",
      de: "Das Besondere an diesem Fall ist, dass die Kapitalbeschaffungsstory stärker klingen kann, als die zugrunde liegende Ökonomie trägt. Der Fall kann funktionieren, aber nur wenn rechtliche Struktur, Investorenumfang und Liquiditätsdesign diszipliniert genug bleiben, damit Tokenisierung die Finanzierungsflexibilität verbessert, ohne unverhältnismäßigen Strukturierungs- und Servicing-Drag hinzuzufügen.",
      fr: "Ce qui distingue ce cas, c’est que le récit de levée de capitaux peut sembler plus fort que ne le justifient les fondamentaux économiques. Le cas peut fonctionner, mais seulement si la structure juridique, le périmètre investisseur et le design de liquidité restent suffisamment disciplinés pour que la tokenisation améliore la flexibilité de financement sans ajouter une friction disproportionnée de structuration et de servicing.",
      es: "Lo que diferencia este caso es que la narrativa de captación de capital puede sonar más fuerte de lo que justifican los fundamentos económicos. El caso puede funcionar, pero solo si la estructura jurídica, el alcance inversor y el diseño de liquidez se mantienen lo bastante disciplinados para que la tokenización mejore la flexibilidad de financiación sin añadir una fricción desproporcionada de estructuración y servicing.",
      "zh-Hans": "本案例的不同之处在于，募资叙事听起来可能比底层经济性本身更强。该案例可以成立，但前提是法律结构、投资者范围和流动性设计保持足够纪律，使代币化改善融资灵活性，而不是增加不成比例的结构设计和服务摩擦。",
      "pt-BR": "O que diferencia este caso é que a narrativa de captação pode parecer mais forte do que a economia subjacente. O caso pode funcionar, mas somente se a estrutura jurídica, o universo de investidores e o desenho de liquidez permanecerem suficientemente disciplinados para que a tokenização aumente a flexibilidade de financiamento, em vez de adicionar fricções desproporcionais de estruturação e serviços.",
    }[code];
  } else if (summaryPrimaryArchetype === "private_credit_notes") {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at den underliggende aktivlogik ikke er den største svaghed. Receivables-strategien, det kontrollerede investor scope og de konservative forvarings-/afviklingsvalg gør casen retningsmæssigt troværdig, men den holdes mere tilbage af compliance-scope, transferdisciplin og kompleksitet i driftsmodellen end af aktivklassen i sig selv.",
      de: "Das Besondere an diesem Fall ist, dass die zugrunde liegende Asset-Logik nicht die Hauptschwäche ist. Receivables-Strategie, kontrollierter Investorenumfang und konservative Verwahrungs-/Abwicklungsentscheidungen machen den Fall richtungsweise glaubwürdig, aber er wird stärker durch Compliance-Umfang, Transferdisziplin und Betriebsmodellkomplexität gebremst als durch die Assetklasse selbst.",
      fr: "Ce qui distingue ce cas, c’est que la logique de l’actif sous-jacent n’est pas la principale faiblesse. La stratégie receivables, le périmètre investisseur contrôlé et les choix prudents de conservation/règlement rendent le cas directionnellement crédible, mais il est davantage freiné par le périmètre de conformité, la discipline de transfert et la complexité du modèle opérationnel que par la classe d’actifs elle-même.",
      es: "Lo que diferencia este caso es que la lógica del activo subyacente no es la principal debilidad. La estrategia de receivables, el alcance inversor controlado y las opciones conservadoras de custodia/liquidación hacen que el caso sea direccionalmente creíble, pero está más limitado por el alcance de cumplimiento, la disciplina de transferencia y la complejidad del modelo operativo que por la clase de activo en sí.",
      "zh-Hans": "本案例的不同之处在于，底层资产逻辑并不是主要弱点。应收账款策略、受控投资者范围以及保守的托管/结算选择使该案例方向上可信，但它更多受制于合规范围、转让纪律和运营模式复杂性，而不是资产类别本身。",
      "pt-BR": "O que diferencia este caso é que a lógica do ativo subjacente não é a principal fragilidade. A estratégia de recebíveis, o universo controlado de investidores e escolhas conservadoras de custódia/liquidação tornam o caso direcionalmente crível, mas ele é mais limitado pelo escopo de compliance, disciplina de transferências e complexidade do modelo operacional do que pela própria classe de ativo.",
    }[code];
  } else if (summaryPrimaryArchetype === "closed_workflow_infrastructure") {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at hovedværdien ligger i effektivisering af arbejdsgange, kontrol og risikoreduktion snarere end i ny distribution eller spekulativ likviditet. Det gør casen mere troværdig end mange tokeniseringskoncepter, men også mere afhængig af, om designet forbliver stramt adgangsbegrænset og faktisk fjerner afstemning i ældre systemer i stedet for blot at lægge et tokenlag ovenpå.",
      de: "Das Besondere an diesem Fall ist, dass der Hauptwert in Workflow-Effizienz, Kontrolle und Risikominderung liegt, nicht in neuer Distribution oder spekulativer Liquidität. Das macht den Fall glaubwürdiger als viele Tokenisierungskonzepte, aber auch abhängiger davon, ob das Design eng permissioned bleibt und tatsächlich Legacy-Reconciliation-Schritte entfernt, statt nur eine Token-Schicht obenauf zu legen.",
      fr: "Ce qui distingue ce cas, c’est que la valeur principale se situe dans l’efficacité des workflows, le contrôle et la réduction des risques plutôt que dans une nouvelle distribution ou une liquidité spéculative. Cela rend le cas plus crédible que de nombreux concepts de tokenisation, mais aussi plus dépendant du maintien d’un design strictement permissioned et de la suppression réelle d’étapes de reconciliation legacy, plutôt que de l’ajout d’une simple couche token.",
      es: "Lo que diferencia este caso es que el valor principal reside en la eficiencia de workflow, el control y la reducción de riesgos, más que en nueva distribución o liquidez especulativa. Esto hace que el caso sea más creíble que muchos conceptos de tokenización, pero también más dependiente de que el diseño siga siendo estrictamente permissioned y elimine realmente pasos legacy de reconciliación en lugar de añadir solo una capa token.",
      "zh-Hans": "本案例的不同之处在于，主要价值来自工作流效率、控制和风险降低，而不是新分销或投机性流动性。这使其比许多代币化概念更可信，但也更依赖设计是否保持严格许可制，并真正移除传统对账步骤，而不是仅仅叠加一层token。",
      "pt-BR": "O que diferencia este caso é que o principal valor vem de eficiência de workflow, controle e redução de riscos, e não de nova distribuição ou liquidez especulativa. Isso torna o caso mais crível do que muitos conceitos de tokenização, mas também mais dependente de um desenho estritamente permissionado que realmente elimine etapas legadas de reconciliação, em vez de apenas adicionar uma camada de token.",
    }[code];
  } else if (isNonViable) {
    const drivers = [
      ambiguousRights ? { da: "uklar eller svag rettighedsmapping", de: "unklares oder schwaches Rechtemapping", fr: "cartographie des droits floue ou faible", es: "mapeo de derechos poco claro o débil", "zh-Hans": "不清晰或薄弱的权利映射", "pt-BR": "mapeamento de direitos pouco claro ou frágil" }[code] : "",
      investorsRetail ? { da: "retail-scope", de: "Retail-Umfang", fr: "périmètre retail", es: "alcance retail", "zh-Hans": "面向零售的范围", "pt-BR": "escopo de varejo" }[code] : "",
      /self-custody/i.test(custodyLc) ? { da: "self-custody", de: "Self-Custody", fr: "self-custody", es: "self-custody", "zh-Hans": "自托管", "pt-BR": "self-custody" }[code] : "",
      /dex/i.test(venueLc) || continuousLiquidity ? { da: "antagelser om åben markeds-likviditet", de: "Annahmen offener Marktliquidität", fr: "hypothèses de liquidité de marché ouverte", es: "supuestos de liquidez de mercado abierto", "zh-Hans": "开放市场流动性假设", "pt-BR": "premissas de liquidez em mercado aberto" }[code] : "",
      !disclosureLc ? { da: "tynd oplysningsarkitektur", de: "dünne Offenlegungsarchitektur", fr: "architecture d’information faible", es: "arquitectura de información insuficiente", "zh-Hans": "薄弱的信息披露架构", "pt-BR": "arquitetura de disclosure insuficiente" }[code] : "",
    ].filter(Boolean);
    copy.whatDifferent = drivers.length
      ? {
          da: `Det særlige ved denne case er, at de største svagheder forstærker hinanden frem for at stå isoleret. I praksis gør ${joinNaturalLocalized(drivers.slice(0, 4), code)} strukturen sværere at stabilisere juridisk og operationelt, end den positive værdiskabelse kan retfærdiggøre.`,
          de: `Das Besondere an diesem Fall ist, dass die wichtigsten Schwächen einander verstärken, statt isoliert zu stehen. Praktisch machen ${joinNaturalLocalized(drivers.slice(0, 4), code)} die Struktur rechtlich und operativ schwerer zu stabilisieren, als das Upside rechtfertigen kann.`,
          fr: `Ce qui distingue ce cas, c’est que les principales faiblesses se renforcent plutôt que d’être isolées. En pratique, ${joinNaturalLocalized(drivers.slice(0, 4), code)} rendent la structure plus difficile à stabiliser juridiquement et opérationnellement que le potentiel positif ne peut le justifier.`,
          es: `Lo que diferencia este caso es que las principales debilidades se refuerzan entre sí en lugar de estar aisladas. En la práctica, ${joinNaturalLocalized(drivers.slice(0, 4), code)} hacen que la estructura sea más difícil de estabilizar jurídica y operativamente de lo que el potencial positivo puede justificar.`,
          "zh-Hans": `本案例的不同之处在于，主要弱点相互强化，而不是彼此孤立。实践中，${joinNaturalLocalized(drivers.slice(0, 4), code)}使该结构在法律和运营上更难稳定，超出了上行价值所能支撑的范围。`,
          "pt-BR": `O que diferencia este caso é que as principais fragilidades se reforçam mutuamente, em vez de permanecerem isoladas. Na prática, ${joinNaturalLocalized(drivers.slice(0, 4), code)} tornam a estrutura mais difícil de estabilizar jurídica e operacionalmente do que o benefício pode justificar.`,
        }[code]
      : {
          da: "Det særlige ved denne case er, at udfordringerne er strukturelle snarere end tilfældige. Det nuværende design pålægger tokenlaget mere juridisk, compliance-mæssig og operationel byrde, end den underliggende økonomi kan retfærdiggøre.",
          de: "Das Besondere an diesem Fall ist, dass das Downside strukturell und nicht zufällig ist. Das aktuelle Design verlangt von der Token-Schicht mehr rechtliche, Compliance- und Betriebsbelastung, als die zugrunde liegende Ökonomie rechtfertigen kann.",
          fr: "Ce qui distingue ce cas, c’est que le downside est structurel plutôt qu’incidentel. Le design actuel demande à la couche token de porter davantage de charge juridique, de conformité et d’exploitation que les fondamentaux économiques ne peuvent justifier.",
          es: "Lo que diferencia este caso es que el downside es estructural más que incidental. El diseño actual exige que la capa token soporte más carga legal, de cumplimiento y operativa de la que la economía subyacente puede justificar.",
          "zh-Hans": "本案例的不同之处在于，下行问题是结构性的，而不是偶然的。当前设计要求token层承担超过底层经济性所能支撑的法律、合规和运营负担。",
          "pt-BR": "O que diferencia este caso é que as fricções são estruturais, e não incidentais. O desenho atual exige que a camada de token suporte uma carga jurídica, de compliance e operacional maior do que a economia subjacente consegue justificar.",
        }[code];
  } else if (!isViable) {
    const conditions = [
      crossBorder ? { da: "grænseoverskridende scope", de: "grenzüberschreitender Umfang", fr: "périmètre transfrontalier", es: "alcance transfronterizo", "zh-Hans": "跨境范围", "pt-BR": "escopo transfronteiriço" }[code] : "",
      permissioned ? { da: "disciplin omkring transfer controls", de: "Disziplin bei Transferkontrollen", fr: "discipline des contrôles de transfert", es: "disciplina en controles de transferencia", "zh-Hans": "转让控制纪律", "pt-BR": "disciplina nos controles de transferência" }[code] : "",
      /regulated-custodian/i.test(custodyLc) ? { da: "institutionel forvaring", de: "institutionelle Verwahrung", fr: "conservation institutionnelle", es: "custodia institucional", "zh-Hans": "机构级托管", "pt-BR": "custódia institucional" }[code] : "",
      periodicLiquidity || adHocOrNoneLiquidity ? { da: "kontrolleret likviditet", de: "kontrollierte Liquidität", fr: "liquidité contrôlée", es: "liquidez controlada", "zh-Hans": "受控流动性", "pt-BR": "liquidez controlada" }[code] : "",
      investorsProfessional || investorsAccredited ? { da: "begrænset investor scope", de: "begrenzter Investorenumfang", fr: "périmètre investisseur restreint", es: "alcance inversor restringido", "zh-Hans": "受限投资者范围", "pt-BR": "universo restrito de investidores" }[code] : "",
    ].filter(Boolean);
    copy.whatDifferent = conditions.length
      ? {
          da: `Det særlige ved denne case er, at værdilogikken er plausibel, men smallere end den først kan se ud. Projektet er ikke primært begrænset af fortællingen om aktivet; det begrænses af, om ${joinNaturalLocalized(conditions.slice(0, 4), code)} forbliver disciplineret nok til, at potentialet indfanges i stedet for at blive opslugt af leveringsfriktion.`,
          de: `Das Besondere an diesem Fall ist, dass die Wertlogik plausibel ist, aber schmaler als sie zunächst erscheinen kann. Das Projekt ist nicht primär durch die Asset-Story begrenzt; es hängt davon ab, ob ${joinNaturalLocalized(conditions.slice(0, 4), code)} diszipliniert genug bleiben, damit das Upside erfasst und nicht durch Lieferfriktion absorbiert wird.`,
          fr: `Ce qui distingue ce cas, c’est que la logique de valeur est plausible, mais plus étroite qu’elle peut le sembler au départ. Le projet n’est pas principalement limité par l’histoire de l’actif ; il dépend de la capacité de ${joinNaturalLocalized(conditions.slice(0, 4), code)} à rester suffisamment disciplinés pour que le potentiel positif soit capturé plutôt qu’absorbé par la friction d’exécution.`,
          es: `Lo que diferencia este caso es que la lógica de valor es plausible, pero más estrecha de lo que puede parecer al principio. El proyecto no está limitado principalmente por la historia del activo; depende de que ${joinNaturalLocalized(conditions.slice(0, 4), code)} se mantengan lo bastante disciplinados para que el upside se capture en lugar de ser absorbido por la fricción de entrega.`,
          "zh-Hans": `本案例的不同之处在于，价值逻辑是可信的，但比初看更窄。项目主要受限的并不是资产叙事，而是${joinNaturalLocalized(conditions.slice(0, 4), code)}是否足够有纪律，使上行价值被捕获，而不是被交付摩擦吸收。`,
          "pt-BR": `O que diferencia este caso é que a lógica de valor é plausível, porém mais estreita do que pode parecer inicialmente. O projeto não é limitado principalmente pela narrativa do ativo; depende de ${joinNaturalLocalized(conditions.slice(0, 4), code)} permanecerem suficientemente disciplinados para que o benefício seja capturado, em vez de absorvido pela fricção de execução.`,
        }[code]
      : {
          da: "Det særlige ved denne case er, at potentialet virker reelt, men stadig afhænger mere af designdisciplin end af tokenisering i sig selv. Det centrale spørgsmål er, om afgrænsning, compliance, forvaring og driftsmodel er stramme nok til at omsætte potentialet til indfanget værdi.",
          de: "Das Besondere an diesem Fall ist, dass das Upside richtungsweise real wirkt, aber stärker von Designdisziplin als von Tokenisierung allein abhängt. Die Kernfrage ist, ob Umfang, Compliance, Verwahrung und Betriebsmodell eng genug sind, um Potenzial in erfassten Wert zu verwandeln.",
          fr: "Ce qui distingue ce cas, c’est que le potentiel positif paraît directionnellement réel, mais dépend encore davantage de la discipline de conception que de la tokenisation seule. La question centrale est de savoir si le périmètre, la conformité, la conservation et le modèle opérationnel sont assez stricts pour convertir le potentiel en valeur capturée.",
          es: "Lo que diferencia este caso es que el potencial positivo parece direccionalmente real, pero aún depende más de la disciplina de diseño que de la tokenización por sí sola. La cuestión central es si el alcance, el cumplimiento, la custodia y el modelo operativo son lo bastante estrictos para convertir potencial en valor capturado.",
          "zh-Hans": "本案例的不同之处在于，上行价值方向上看是真实的，但仍更多取决于设计纪律，而不是代币化本身。核心问题是范围、合规、托管和运营模式是否足够严谨，能够将潜力转化为实际捕获的价值。",
          "pt-BR": "O que diferencia este caso é que o benefício parece direcionalmente real, mas ainda depende mais da disciplina de desenho do que da tokenização por si só. A questão central é se escopo, compliance, custódia e modelo operacional são suficientemente rigorosos para converter potencial em valor efetivamente capturado.",
        }[code];
  } else {
    copy.whatDifferent = {
      da: "Det særlige ved denne case er, at tokenisering bruges til at løse et afgrænset drifts- eller finansieringsproblem, ikke til at love bred markedsforandring. Casen fungerer, når designet forbliver disciplineret, og tokenlaget indfanger konkrete effektivitets-, kontrol- eller risikoreduktionsgevinster uden at tilføje unødvendig handelsplads-, forvarings- eller jurisdiktionskompleksitet.",
      de: "Das Besondere an diesem Fall ist, dass Tokenisierung eingesetzt wird, um ein begrenztes Betriebs- oder Finanzierungsproblem zu lösen, nicht um eine breite Markttransformation zu versprechen. Der Fall funktioniert, wenn das Design diszipliniert bleibt und die Token-Schicht konkrete Effizienz-, Kontroll- oder Risikominderungsvorteile erfasst, ohne unnötige Handelsplatz-, Verwahrungs- oder Jurisdiktionskomplexität hinzuzufügen.",
      fr: "Ce qui distingue ce cas, c’est que la tokenisation est utilisée pour résoudre un problème opérationnel ou de financement délimité, et non pour promettre une transformation large du marché. Le cas fonctionne lorsque le design reste discipliné et que la couche token capte des gains concrets d’efficacité, de contrôle ou de réduction des risques sans ajouter de complexité inutile de plateforme, de conservation ou de juridiction.",
      es: "Lo que diferencia este caso es que la tokenización se utiliza para resolver un problema operativo o de financiación delimitado, no para prometer una transformación amplia del mercado. El caso funciona cuando el diseño se mantiene disciplinado y la capa token captura beneficios concretos de eficiencia, control o reducción de riesgos sin añadir complejidad innecesaria de plataforma, custodia o jurisdicción.",
      "zh-Hans": "本案例的不同之处在于，代币化被用于解决一个边界清晰的运营或融资问题，而不是承诺广泛的市场变革。当设计保持纪律，token层捕获具体的效率、控制或风险降低收益，同时不增加不必要的交易场所、托管或司法辖区复杂性时，该案例才成立。",
      "pt-BR": "O que diferencia este caso é que a tokenização é usada para resolver um problema operacional ou de financiamento claramente delimitado, e não para prometer uma transformação ampla do mercado. O caso funciona quando o desenho permanece disciplinado e a camada de token captura ganhos concretos de eficiência, controle ou redução de riscos sem adicionar complexidade desnecessária de venue, custódia ou jurisdição.",
    }[code];
  }

  Object.keys(copy).forEach((k) => {
    copy[k] = String(copy[k] || "").replace(/\s+/g, " ").trim();
    if (copy[k] && !/[.!?。]$/.test(copy[k])) copy[k] += ending;
  });
  return copy;
}

/* ---------- Case Summary card (top of report) ---------- */

function CaseSummary({ result, payload, reportLanguageCode = "en" }) {
  const labels = polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode);
  if (!result) return null;

  const fs = result.factor_scores || result.factorScores || {};
  const no = Number(fs.new_opportunities ?? fs.newOpportunities ?? 0);
  const cs = Number(fs.cost_savings ?? fs.costSavings ?? 0);
  const rr = Number(fs.risk_reduction ?? fs.riskReduction ?? 0);
  const tc = Number(fs.tokenization_costs ?? fs.tokenizationCosts ?? 0);
  const nr = Number(fs.new_risks ?? fs.newRisks ?? 0);

  const plus = Number.isFinite(no + cs + rr) ? no + cs + rr : 0;
  const minus = Number.isFinite(tc + nr) ? tc + nr : 0;
  const netScore = Number.isFinite(plus - minus) ? plus - minus : 0;

  const confidence =
    typeof result.confidence === "number"
      ? result.confidence
      : typeof result.confidence_level === "number"
      ? result.confidence_level
      : null;

  const completeness = result.completeness || result.input_completeness || {};
  const completenessScore = Number(completeness.score);
  const completenessPct = Number.isFinite(completenessScore)
    ? completenessScore <= 1
      ? Math.round(completenessScore * 100)
      : Math.round(completenessScore)
    : null;

    const rawMaturityLevel =
  result?.maturityLevel ||
  result?.input_maturity?.level ||
  "";

const maturityLevel = String(rawMaturityLevel || "").trim();

const maturityScore =
  typeof result?.maturityScore === "number"
    ? result.maturityScore
    : typeof result?.input_maturity?.score === "number"
    ? result.input_maturity.score
    : null;

const maturityNote =
  result?.maturityNote ||
  result?.input_maturity?.note ||
  "";

const openDesignAreas = Array.isArray(result?.openDesignAreas)
  ? result.openDesignAreas
  : Array.isArray(result?.input_maturity?.open_design_areas)
  ? result.input_maturity.open_design_areas
  : [];

const isEarlyMaturity = maturityLevel === "Early Exploration";
const isStructuredMaturity = maturityLevel === "Structured Concept";
const isDesignedMaturity = maturityLevel === "Designed Structure";
  
  const verdict =
    result.verdict ||
    result.overallVerdict ||
    result.overall_verdict ||
    "-";

  const verdictLabel = localizeVerdictLabel(verdict, reportLanguageCode);
  const verdictKey = String(verdict || "").toLowerCase().trim();

  const conditionalSet = new Set(["conditional_go", "conditional go", "conditional"]);
  const viableSet = new Set(["go", "viable", "go / viable"]);
  const nonViableSet = new Set([
    "no-go",
    "no go",
    "non-viable",
    "non viable",
    "stop",
    "non_viable",
  ]);

  const isConditional = conditionalSet.has(verdictKey);
const isViable = viableSet.has(verdictKey);
const isNonViable = nonViableSet.has(verdictKey);
const isNearBoundaryConditional = isConditional && Math.abs(netScore) <= 1;
const showYieldNeutralityNote = containsYieldReturnSignal(payload);

  const stripTag = (value, tag) => {
  const s = String(value || "").trim();
  return s.startsWith(tag) ? s.slice(tag.length).trim() : s;
};

const stripTrailingPunctuation = (value) =>
  String(value || "").trim().replace(/[.\s]+$/g, "");

const joinNatural = (items) => {
  const clean = (items || []).filter(Boolean);
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
};

const noGo = (result?.deep_assessment?.go_no_go_criteria?.no_go_if || [])
  .map((x) => String(x || "").trim())
  .filter(Boolean);

const blockerItems = noGo
  .filter((x) => getCriterionKind(x) === "BLOCKER")
  .slice(0, 6);

const stopItems = noGo
  .filter((x) => getCriterionKind(x) === "STOP")
  .slice(0, 6);

const showStandardStopCriteria =
  ["conditional_go", "conditional go", "conditional", "no-go", "no go", "non-viable", "non viable", "stop", "non_viable"].includes(verdictKey) ||
  blockerItems.length > 0;

                         
const primaryBlocker = blockerItems.length
  ? stripTrailingPunctuation(localizeBlockerCriterionText(blockerItems[0], reportLanguageCode))
  : "";

const primaryStop = stopItems.length
  ? stripTrailingPunctuation(stripCriterionMarker(localizeStopCriterionText(stopItems[0], reportLanguageCode)))
  : "";

  const rankedUpsideDrivers = [
    { label: "new opportunities", value: no },
    { label: "cost savings", value: cs },
    { label: "risk reduction", value: rr },
  ]
    .filter((x) => x.value > 0)
    .sort((a, b) => b.value - a.value);

  // Preserve the existing two-driver summary, but never exclude another
  // factor that is tied at the inclusion cutoff. This prevents arbitrary
  // omissions when two or all three Plus factors share the same score.
  const upsideDriverCutoff =
    rankedUpsideDrivers.length >= 2
      ? rankedUpsideDrivers[1].value
      : rankedUpsideDrivers[0]?.value ?? 0;

  const upsideDrivers = rankedUpsideDrivers
    .filter((x) => x.value >= upsideDriverCutoff)
    .map((x) => x.label);

  const downsideDrivers = [
    { label: "tokenization costs", value: tc },
    { label: "new risks", value: nr },
  ]
    .filter((x) => x.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .map((x) => x.label);

  const upsideText = upsideDrivers.length
    ? joinNatural(upsideDrivers)
    : "limited measurable upside";

  const downsideText = downsideDrivers.length
    ? joinNatural(downsideDrivers)
    : "execution burden and new risk";

    const safeStr = (v) => {
    if (v === null || v === undefined) return "";
    if (Array.isArray(v)) return v.filter(Boolean).join(", ");
    return String(v);
  };

  const payloadText = [
    payload?.text,
    payload?.description,
    payload?.caseDescription,
    payload?.case_description,
    payload?.narrative,
    payload?.caseSummary,
    payload?.case_summary,
  ]
    .map((value) => safeStr(value).trim())
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  // Case-type routing must be based on the customer's own case description.
  // Model-generated context is deliberately excluded so one contaminated block
  // cannot cause additional cross-case leakage elsewhere in the report.
  const combinedText = payloadText;

  const issuanceLc = safeStr(payload?.issuanceJurisdiction).toLowerCase();

  const saleJurList = Array.isArray(payload?.saleJurisdictions)
    ? payload.saleJurisdictions
    : safeStr(payload?.saleJurisdictions)
        .split(/[,;]\s*/)
        .map((x) => x.trim())
        .filter(Boolean);

  const saleJurLc = saleJurList.map((x) => String(x || "").toLowerCase());

  const instrumentLc = safeStr(payload?.instrumentType).toLowerCase();
  const custodyLc = safeStr(payload?.custodyModel).toLowerCase();
  const venueLc = safeStr(payload?.marketVenue || payload?.venueType).toLowerCase();
  const settlementLc = safeStr(payload?.settlementModel).toLowerCase();
  const disclosureLc = safeStr(payload?.disclosure).toLowerCase();
  const transfersLc = safeStr(payload?.transferRestrictions).toLowerCase();
  const liquidityLc = safeStr(payload?.liquidityCadence).toLowerCase();

  const investorObj = payload?.investorTypes || {};
  const investorsRetail = Boolean(investorObj.Retail || investorObj.retail);
  const investorsProfessional = Boolean(investorObj.Professional || investorObj.professional);
  const investorsAccredited = Boolean(investorObj.Accredited || investorObj.accredited);

  const normalizeJurKey = (v) => {
  const s = String(v || "").toLowerCase().trim();
  if (/^(us|u\.s\.|usa|united states|united states of america)$/.test(s)) return "united states";
  if (/^(uk|u\.k\.|united kingdom|great britain|britain)$/.test(s)) return "united kingdom";
  if (/^(eu|european union)$/.test(s)) return "european union";
  return s;
};
        
  const issuanceKey = normalizeJurKey(issuanceLc);
const saleJurKeys = saleJurLc.map((x) => normalizeJurKey(x)).filter(Boolean);

const crossBorder =
  saleJurKeys.length > 1 ||
  (issuanceKey && saleJurKeys.some((x) => x && x !== issuanceKey));

  const permissioned =
    /whitelist|permissioned|administrator-approved|issuer\/agent-approved|jurisdiction gating|approved intermediaries/i.test(
      `${transfersLc} ${combinedText}`
    );

  const ambiguousRights =
    /ambiguous|not fully defined|informal profit-sharing|membership rights|unclear rights|unclear legal/i.test(
      combinedText
    );

  const continuousLiquidity = /continuous/i.test(liquidityLc);
  const periodicLiquidity = /quarterly|window/i.test(liquidityLc);
  const adHocOrNoneLiquidity = /ad-hoc otc|none/i.test(liquidityLc);

  const fineArtRetailCase = /fine art|artwork|collectible|nft/i.test(combinedText);
  const commercialPaperCase = /\bcommercial paper\b|\bcp program(?:me)?\b|\bcp notes?\b/i.test(combinedText);
  const privateCreditCase =
  !commercialPaperCase &&
  /\bprivate credit\b|\breceivables?\b|\btrade credit\b|\binvoices?\b|\boriginators?\b|\bcollections?\b|\btrue sale\b|\bbankruptcy[- ]remote\b|\bthird[- ]party servicing\b|\breceivables? assignment\b/i.test(
    combinedText
  );
  const fiWorkflowCase = /money market fund|mmf|treasury|collateral|repo|post-trade|reconciliation|workflow|settlement rail|dvp/i.test(combinedText);
  const realEstateCase =
  /\breal estate\b|\bresidential\b|\brental\b|\bproperties?\b|\btenant-area\b|\brenovation\b|\bfaçade\b|\bfacade\b|\bprofit-participating notes\b|\bnet rental cash flow\b/i.test(
    combinedText
  );
  const hasProviderCaptureRisk =
  isConditional &&
  privateCreditCase &&
  crossBorder &&
  (investorsProfessional || investorsAccredited) &&
  /regulated-custodian/i.test(custodyLc);

  // The backend-declared capital-formation/workflow archetype is the primary
  // routing layer. Asset context may refine copy, but may not override it.
  const declaredReportArchetype = String(
    result?.report_case_archetype || result?.reportCaseArchetype || ""
  ).trim().toLowerCase();
  const declaredClosedWorkflow = isClosedWorkflowInfrastructureCase(payload || {}, result || {});
  const declaredIssuanceLifecycle = isIssuanceLifecycleOptimizationCase(payload || {}, result || {});
  const declaredFundAdministration = isFundAdministrationOptimizationCase(payload || {}, result || {});
  const declaredPermissionedDefi = isPermissionedDefiLendingCase(payload || {}, result || {});

  const summaryPrimaryArchetype =
  (isConditional || isViable) && declaredClosedWorkflow
    ? "closed_workflow_infrastructure"
    : (isConditional || isViable) && declaredIssuanceLifecycle
    ? "issuance_lifecycle_optimization"
    : (isConditional || isViable) && declaredFundAdministration
    ? "fund_administration_optimization"
    : (isConditional || isViable) && declaredPermissionedDefi
    ? "permissioned_defi_lending"
    : (isConditional || isViable) &&
      fiWorkflowCase &&
      /money market fund|mmf|treasury|cash managers|fund units|collateral usability|collateral utility|subscription\/redemption/i.test(
        combinedText
      ) &&
      /off-chain/i.test(settlementLc) &&
      /ta\/csd/i.test(custodyLc) &&
      permissioned &&
      investorsProfessional &&
      !investorsRetail
    ? "fi_treasury_fund"
    : isConditional && realEstateCase && !privateCreditCase
    ? "real_estate_income_wrapper"
    : isConditional && privateCreditCase && !realEstateCase
    ? "private_credit_notes"
    : isNonViable && fineArtRetailCase
    ? "retail_collectible_nft"
    : "generic_neutral";
  const fundLifecycle = deriveFundLifecycleScope(payload || {});

  function buildWhatIsDifferentText() {
  if (isNonViable && fineArtRetailCase) {
    return "What is different about this case is that several weak design choices reinforce each other rather than standing alone. Ambiguous rights, retail-facing scope, self-custody, and DEX-style liquidity do not create separate manageable issues; together they create a structurally fragile model with weak investor protection and no validated compliance pathway under the current design.";
  }

  if ((isConditional || isViable) && summaryPrimaryArchetype === "fund_administration_optimization") {
    return `What is different about this case is that the token layer is being tested against an existing fund-administration and investor-lifecycle workflow rather than as a new fundraising or public-liquidity proposition. The value therefore depends on authoritative records, measurable process replacement, reliable ${fundLifecycle.phrase}, cash-leg finality and recovery, and a clear responsibility split across manager, administrator, custodian, bank and token platform.`;
  }

  if ((isConditional || isViable) && summaryPrimaryArchetype === "permissioned_defi_lending") {
    return "What is different about this case is that the strongest upside comes from programmable collateral control within a permissioned lending model, while the decisive downside lies in legal enforceability, oracle and valuation reliability, liquidation, smart-contract and stablecoin risk, governance, bridges and changing protocol dependencies. It should therefore be managed as a bounded operating and risk-control pilot, not as a generic fundraising or assumed-liquidity case.";
  }

  if ((isConditional || isViable) && summaryPrimaryArchetype === "fi_treasury_fund") {
    return "What is different about this case is that the main value sits in operational utility, position visibility, investor-lifecycle administration and reliable cash-leg coordination rather than in investor expansion or speculative liquidity. That makes the case more focused than many tokenization concepts, but also more dependent on whether subscriptions, redemptions, transfer controls and operational integration are concrete enough to remove stated legacy friction.";
  }

  if (isConditional && summaryPrimaryArchetype === "real_estate_income_wrapper") {
    return "What is different about this case is that the capital-raising story may sound stronger than the underlying economics justify. The case can work, but only if the legal wrapper, investor scope, and liquidity design remain disciplined enough that tokenization improves funding flexibility without adding disproportionate structuring and servicing drag.";
  }

  if (isConditional && summaryPrimaryArchetype === "private_credit_notes") {
    return "What is different about this case is that the underlying asset logic is not the main weakness. The described receivables/private-credit strategy and controlled investor scope make the case directionally plausible, but it is held back more by compliance scope, transfer discipline, and operating-model complexity than by the asset class itself.";
  }

  if ((isConditional || isViable) && summaryPrimaryArchetype === "closed_workflow_infrastructure") {
    return "What is different about this case is that the main value sits in workflow efficiency, control, and risk reduction rather than in new distribution or speculative liquidity. That makes the case more focused than many tokenization concepts, but also more dependent on whether the design stays tightly permissioned and demonstrably removes legacy reconciliation steps instead of merely adding a token layer on top.";
  }

  if ((isConditional || isViable) && summaryPrimaryArchetype === "issuance_lifecycle_optimization") {
    const programme = commercialPaperCase ? "commercial-paper programme" : "bond programme";
    return `What is different about this case is that the issuer already has a conventional ${programme} and is testing whether tokenization can improve issuance, settlement, position reporting and lifecycle administration. The value case therefore depends on measurable process improvement, authoritative records, DvP and recovery controls, and retirement of legacy steps — not on new capital access, anchor investors, market making or assumed secondary liquidity.`;
  }

    if (isNonViable) {
      const drivers = [
        ambiguousRights ? "unclear or weak rights mapping" : "",
        investorsRetail ? "retail-facing scope" : "",
        /self-custody/i.test(custodyLc) ? "self-custody" : "",
        /dex/i.test(venueLc) || continuousLiquidity ? "open-market liquidity assumptions" : "",
        !disclosureLc ? "thin disclosure architecture" : "",
      ].filter(Boolean);

      return drivers.length
        ? `What is different about this case is that the main weaknesses reinforce each other rather than sitting in isolation. In practice, the combined effect of ${joinNatural(drivers.slice(0, 4))} makes the structure harder to stabilise legally and operationally than the upside can justify.`
        : "What is different about this case is that the downside is structural rather than incidental. The current design asks the token layer to carry more legal, compliance, and operating burden than the underlying economics can justify, which is why this looks like a redesign case rather than a tuning case.";
    }

    if (isConditional) {
      const conditions = [
        crossBorder ? "cross-border scope" : "",
        permissioned ? "transfer-control discipline" : "",
        /regulated-custodian/i.test(custodyLc) ? "institutional custody" : "",
        periodicLiquidity || adHocOrNoneLiquidity ? "controlled liquidity" : "",
        investorsProfessional || investorsAccredited ? "restricted investor scope" : "",
      ].filter(Boolean);

      return conditions.length
        ? `What is different about this case is that the value logic is plausible, but narrower than it may first appear. The project is not mainly limited by the asset story; it is limited by whether ${joinNatural(conditions.slice(0, 4))} stay disciplined enough for the upside to be captured rather than absorbed by delivery friction.`
        : "What is different about this case is that the upside appears directionally plausible, but still depends on design discipline and validation rather than on tokenization alone. The central question is whether scope, compliance, custody, and operating model are tight enough to convert potential into captured value.";
    }

    return "What is different about this case is that tokenization is being used here to solve a bounded operating or funding problem rather than to promise broad market transformation. The case works when the design stays disciplined and the token layer captures concrete efficiency, control, or risk-reduction benefits without adding unnecessary venue, custody, or jurisdictional complexity.";
  }

  let whatIsDifferentText = buildWhatIsDifferentText();
  
  let headlineText = "";
  let findingsText = "";
  let constraintsText = "";
  let postureText = "";
  let impactText = "";

  if (isNonViable) {
    headlineText =
      `Under the current structure, this case should not proceed. ` +
      `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
      `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
      `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
      `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

    findingsText =
      `Any upside is concentrated in ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
      `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}, ` +
      `but that is not strong enough to offset the current downside profile.`;

    constraintsText =
      `The case is weighed down primarily by ${downsideText}, totalling ` +
      `${Number.isFinite(minus) ? minus.toFixed(2) : "—"}.` +
      `${primaryBlocker ? ` A case-specific blocker is already present: ${primaryBlocker}.` : ""}`;

    postureText =
      primaryStop
        ? `Do not move into implementation or distribution under the current concept. ` +
          `Reconsider only after material redesign of rights mapping, investor scope, compliance controls, ` +
          `custody/settlement design, or other core structural assumptions. One key stop threshold already visible ` +
          `in this case is: ${primaryStop}`
        : `Do not move into implementation or distribution under the current concept. ` +
          `This is a redesign case, not a tuning case. Reconsider only if the structure is materially re-scoped.`;

    impactText =
      `Incremental optimisation is unlikely to change the outcome on its own. ` +
      `The current upside is too weak to absorb the legal, compliance, structuring, and operating burdens ` +
      `implied by the design as described.`;
  } else if (isViable) {
    headlineText =
      `The analysis supports a viable case for proceeding with tokenization under the proposed direction. ` +
      `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
      `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
      `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
      `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

    findingsText =
      `The strongest support comes from ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
      `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}. ` +
      `The TVAC methodology indicates a positive value hypothesis under the stated design, but the benefits still require validation.`;

    constraintsText =
      `The main discipline point is to keep ${downsideText} contained at ` +
      `${Number.isFinite(minus) ? minus.toFixed(2) : "—"} ` +
      `and avoid scope creep that would dilute the current value case.` +
      `${primaryBlocker ? ` Note: ${primaryBlocker}.` : ""}`;

    postureText =
      `Proceed with disciplined implementation. Preserve the features that create value, ` +
      `but avoid unnecessary expansion of jurisdictional scope, liquidity promises, or technical complexity ` +
      `beyond what the case actually needs.`;

    impactText =
      `This is primarily a controlled progression case. The upside appears plausible under the stated design, but it must be validated and captured ` +
      `without reintroducing avoidable TC/NR through overengineering or distribution overreach.`;
   } else {
    if (isEarlyMaturity) {
      headlineText =
  `This is a directional assessment of an early-stage tokenization concept rather than a verdict on a finalized structure. ` +
        `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
        `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
        `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
          `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

      findingsText =
        `The current concept shows plausible upside mainly through ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
        `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}, ` +
        `but that upside is still conceptual rather than structurally secured.`;

      constraintsText =
        `The current downside is driven mainly by ${downsideText}, totalling ` +
        `${Number.isFinite(minus) ? minus.toFixed(2) : "—"}. ` +
        `${primaryBlocker
          ? `A case-specific blocker is already visible: ${primaryBlocker}.`
          : `Several core design choices are still open, especially around rights mapping, custody, settlement, transfer design, and delivery model.`}`;

      postureText =
        `Treat this result as a directional screen, not as a design-final go decision. ` +
        `Proceed only into clarification, scoping, and structure-definition work before committing to build-out, legal execution, or provider onboarding.`;

      impactText =
        `This case could move in either direction depending on how the structure is finalized. ` +
        `At this stage, the main task is to convert an interesting concept into a defined structure with enforceable rights, disciplined scope, and realistic operating assumptions.` +
        `${isNearBoundaryConditional
          ? ` An Added Value near zero is not a soft positive result; it signals a genuinely balanced early-stage case where a few structural decisions can still change the outcome materially.`
          : ""}` +
        `${hasProviderCaptureRisk
          ? ` Under the current commercial configuration, the concept may be more attractive to infrastructure providers than to the initiating Asset Owner unless scope, fee stack, and delivery economics are tightened.`
          : ""}`;
    } else if (isStructuredMaturity) {
      headlineText =
        `This case is sufficiently defined for a meaningful case-specific assessment, but it is not yet a finalized execution design. ` +
        `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
        `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
        `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
          `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

      findingsText =
        `The current value logic is supported mainly by ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
        `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}. ` +
        `TVAC identifies case-specific potential, but part of the outcome still depends on validation and on how the remaining structural choices are finalized.`;

      constraintsText =
        `The main drag comes from ${downsideText}, totalling ` +
        `${Number.isFinite(minus) ? minus.toFixed(2) : "—"}. ` +
        `${primaryBlocker
          ? `A case-specific blocker is already visible: ${primaryBlocker}.`
          : `The verdict remains sensitive to unresolved design choices, especially where custody, settlement, compliance controls, and delivery economics are not yet fully locked.`}`;

      postureText =
        `Proceed only if the remaining material design choices are finalized before build-out. ` +
        `This is not just an execution case yet; it still requires disciplined design completion around investor scope, compliance controls, custody/settlement, and operating model.`;

      impactText =
        `If those remaining design choices are finalized well, the case has a plausible path to a stronger value profile by reducing New Risks (NR)/Tokenization Costs (TC) and making the upside more durable. ` +
        `If they are not, the current value case remains too fragile for straightforward execution.` +
        `${isNearBoundaryConditional
          ? ` An Added Value near zero indicates a genuinely balanced case in which a limited number of design choices can still move the outcome materially in either direction.`
          : ""}` +
        `${hasProviderCaptureRisk
          ? ` Under the current commercial configuration, the structure may be more attractive to infrastructure providers than to the initiating Asset Owner unless scope, fee stack, and delivery economics are tightened.`
          : ""}`;
    } else if (isDesignedMaturity) {
      headlineText =
        `This assessment evaluates a substantially designed tokenization structure rather than an open concept. ` +
        `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
        `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
        `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
          `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

      findingsText =
        `The current structure receives methodology-based support mainly from ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
        `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}. ` +
        `The case is directionally plausible under the stated design, but the upside remains narrower than the full delivery burden.`;

      constraintsText =
        `The main drag comes from ${downsideText}, totalling ` +
        `${Number.isFinite(minus) ? minus.toFixed(2) : "—"}. ` +
        `${primaryBlocker
          ? `A case-specific blocker is already visible: ${primaryBlocker}.`
          : `The verdict is not held back by missing concept definition, but by the economics, compliance scope, and delivery burden of the structure as currently designed.`}`;

      postureText =
        `Treat this as a structure-level Conditional Go, not as an invitation to build by default. ` +
        `Proceed only if the designed structure can withstand commercial, custody/settlement, compliance, and provider-economics stress-testing without eroding Asset Owner value.`;

      impactText =
        `This case could become stronger through redesign, simplification, and tighter execution discipline, but the current structure does not yet justify straightforward rollout. ` +
        `The key question is no longer what the concept is, but whether this specific design captures enough value for the initiating Asset Owner once full execution burden is included.` +
        `${isNearBoundaryConditional
          ? ` An Added Value near zero still signals a balanced case, but here the balance sits inside an already designed structure rather than an open concept.`
          : ""}` +
        `${hasProviderCaptureRisk
          ? ` Under the current commercial configuration, the structure may be more attractive to infrastructure providers than to the initiating Asset Owner unless scope, fee stack, and delivery economics are tightened.`
          : ""}`;
    } else {
      headlineText =
        `This case is not a clear no, but it is not ready for straightforward execution either. ` +
        `The assessment lands at ${verdictLabel} with an estimated Net Added Value of ` +
        `${Number.isFinite(netScore) ? netScore.toFixed(2) : "—"} ` +
        `(Plus: ${Number.isFinite(plus) ? plus.toFixed(2) : "—"} · Minus: ${Number.isFinite(minus) ? minus.toFixed(2) : "—"}).` +
          `${confidence != null && Number.isFinite(confidence) ? ` Confidence is ${confidence.toFixed(1)}/10.` : ""}`;

      findingsText =
        `The current value logic is supported mainly by ${upsideText}. Across New Opportunities, Cost Savings and Risk Reduction, ` +
        `the combined Plus score is ${Number.isFinite(plus) ? plus.toFixed(2) : "—"}. ` +
        `TVAC identifies potential here, but it remains design-dependent and unverified rather than already secured.`;

      constraintsText =
        `The main drag comes from ${downsideText}, totalling ` +
        `${Number.isFinite(minus) ? minus.toFixed(2) : "—"}. ` +
        `${primaryBlocker
          ? `A case-specific blocker is already visible: ${primaryBlocker}.`
          : `The verdict remains sensitive to scope, transfer/compliance design, and delivery economics.`}`;

      postureText =
        `Proceed only if the project is tightly scoped and the gating conditions around investor scope, ` +
        `compliance controls, custody/settlement, and delivery economics are locked in before build-out.`;

      impactText =
        `If those gating conditions are resolved, the case has a plausible path to a stronger value profile ` +
        `by reducing New Risks (NR)/Tokenization Costs (TC) and making the upside more durable. If they are not, the current upside is too narrow ` +
        `to carry execution drag.` +
        `${isNearBoundaryConditional
          ? ` An Added Value near zero is not a soft positive result; it indicates a genuinely balanced case in which a small number of design choices can still move the outcome materially in either direction.`
          : ""}` +
        `${hasProviderCaptureRisk
          ? ` Under the current commercial configuration, the structure may be more attractive to infrastructure providers than to the initiating Asset Owner unless scope, fee stack, and delivery economics are tightened.`
          : ""}`;
    }
  }

  if (labels.languageCode !== "en") {
    const localizedExecutiveCopy = buildLocalizedCaseSummaryCopy({
      code: labels.languageCode,
      labels,
      verdictLabel,
      verdictKey,
      netScore,
      plus,
      minus,
      completenessPct,
      confidence,
      isNonViable,
      isViable,
      isEarlyMaturity,
      isStructuredMaturity,
      isDesignedMaturity,
      isNearBoundaryConditional,
      hasProviderCaptureRisk,
      primaryBlocker,
      primaryStop,
      upsideDrivers,
      downsideDrivers,
      summaryPrimaryArchetype,
      fineArtRetailCase,
      ambiguousRights,
      investorsRetail,
      custodyLc,
      venueLc,
      continuousLiquidity,
      disclosureLc,
      crossBorder,
      permissioned,
      periodicLiquidity,
      adHocOrNoneLiquidity,
      investorsProfessional,
      investorsAccredited,
    });
    if (localizedExecutiveCopy) {
      headlineText = localizedExecutiveCopy.headline || headlineText;
      findingsText = localizedExecutiveCopy.findings || findingsText;
      constraintsText = localizedExecutiveCopy.constraints || constraintsText;
      postureText = localizedExecutiveCopy.posture || postureText;
      impactText = localizedExecutiveCopy.impact || impactText;
      whatIsDifferentText = localizedExecutiveCopy.whatDifferent || whatIsDifferentText;
    }
  }

  return (
    <section
      id="exec-summary"
      className="card card-accent"
      style={{ marginBottom: 12 }}
    >
      <h3 style={{ marginTop: 0 }}>1. {labels.executiveSummary}</h3>

      <p className="small" style={{ marginTop: 6 }}>
        <strong>{labels.headline}:</strong>{" "}
        {headlineText}
      </p>

      <ScopeNoteBox labels={labels} showYieldNote={showYieldNeutralityNote} />

              {maturityLevel && (
        <div
          className="small"
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.08)",
            lineHeight: 1.45,
          }}
        >
          <strong>{labels.caseDesignMaturity}:</strong> {localizeMaturityLevel(maturityLevel, reportLanguageCode)}
          {maturityNote ? <div style={{ marginTop: 6 }}>{localizeMaturityNoteText(maturityNote, reportLanguageCode)}</div> : null}
          {openDesignAreas.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <span className="muted">
                <strong>{labels.openDesignAreas}:</strong> {openDesignAreas
                  .map((item) => localizeGeneratedText(String(item || "").trim().replace(/[.,;:]+$/, ""), reportLanguageCode))
                  .filter(Boolean)
                  .join("; ")}
              </span>
            </div>
          ) : null}
        </div>
      )}
      <ul className="small" style={{ marginTop: 10 }}>
        <li>
          <strong>{labels.keyFindings}:</strong>{" "}
          {findingsText}
        </li>

        <li>
          <strong>{labels.keyConstraints}:</strong>{" "}
          {constraintsText}
        </li>

        <li>
          <strong>{labels.decisionPosture}:</strong>{" "}
          {postureText}
        </li>

        <li>
          <strong>{labels.impact}:</strong>{" "}
          {impactText}
        </li>
      </ul>
                {whatIsDifferentText && (
        <div id="section-what-is-different" style={{ marginTop: 14 }}>
          <h4 style={{ margin: "0 0 6px 0" }}>1.1 {labels.whatDifferent}</h4>
          <p className="small" style={{ marginTop: 0 }}>
            {whatIsDifferentText}
          </p>
        </div>
      )}
{isNonViable && (
  <p className="small muted" style={{ marginTop: 10 }}>
    <strong>{labels.noteOmittedSections}:</strong> {labels.noteOmittedSectionsText}
  </p>
)}
    </section>
  );
}
    
/* ---------- Provider Directory section (only shown after report) ---------- */

function ProviderDirectorySection({ chapterLabel = "Appendix B", reportLanguageCode = "en" }) {
  const labels = getReportLabels(reportLanguageCode);
  const providerContent = getReportProviderDirectoryContent(labels.languageCode);
  if (providerContent) {
    const c = providerContent;
    const examples = c.commonExamples;
    const renderList = (items) => <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>{items.map((x) => <li key={x}>{x}</li>)}</ul>;
    return (
      <section id="provider-directory" className="card card-accent" style={{ marginTop: 16 }}>
        <h4 style={{ marginTop: 0 }}>{chapterLabel || labels.appendixB}</h4>
        <p className="small" style={{ marginTop: 6 }}>{c.intro1}</p>
        <p className="small" style={{ marginTop: 6 }}>{c.intro2}</p>

        <h5>{c.howTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.how1}</p>
        <p className="small" style={{ marginTop: 6 }}>{c.how2}</p>
        {renderList(c.questions)}

        <h5>{c.catTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.catIntro}</p>
        {renderList(c.categories)}

        <h5>{c.examplesTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.examplesIntro}</p>

        <h5>{c.legalTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.legalText}</p>
        <p className="small"><strong>{c.useCases}:</strong></p>
        {renderList(c.legalUses)}
        <p className="small"><strong>{c.examples}:</strong> {examples.legal.join(" · ")}</p>

        <h5>{c.issuingTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.issuingText}</p>
        <p className="small"><strong>{c.useCases}:</strong></p>
        {renderList(c.issuingUses)}
        <p className="small"><strong>{c.examples}:</strong> {examples.issuing.join(" · ")}</p>

        <h5>{c.custodyTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.custodyText}</p>
        <p className="small"><strong>{c.useCases}:</strong></p>
        {renderList(c.custodyUses)}
        <p className="small"><strong>{c.examples}:</strong> {examples.custody.join(" · ")}</p>

        <h5>{c.principlesTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.principlesIntro}</p>
        {renderList(c.principles)}

        <h5>{c.cautionTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{c.caution1}</p>
        <p className="small" style={{ marginTop: 6 }}>{c.caution2}</p>
      </section>
    );
  }
  if (labels.languageCode !== "en") {
    const intro = {
      da: "Dette appendiks er et illustrativt referenceafsnit, der hjælper læseren med at overveje, hvilke typer eksterne partnere der kan være relevante i et tokeniseringsprojekt. Det er ikke en anbefalingsliste, shortlist eller rangering.",
      es: "Este apéndice es una referencia ilustrativa que ayuda al lector a considerar qué tipos de socios externos pueden ser relevantes en un proyecto de tokenización. No es una lista de recomendaciones, shortlist ni ranking.",
      de: "Dieser Anhang ist eine illustrative Referenz, die dem Leser hilft zu prüfen, welche Arten externer Partner in einem Tokenisierungsprojekt relevant sein können. Er ist keine Empfehlungsliste, Shortlist oder Rangliste.",
      fr: "Cette annexe est une référence illustrative qui aide le lecteur à identifier les types de partenaires externes pouvant être pertinents dans un projet de tokenisation. Il ne s’agit ni d’une liste de recommandations, ni d’une shortlist, ni d’un classement.",
      "zh-Hans": "本附录是一个示例性参考层，帮助读者思考代币化项目可能需要哪些类型的外部合作伙伴。它不是推荐名单、采购短名单或排名。",
      "pt-BR": "Este apêndice é uma referência ilustrativa para ajudar o leitor a considerar quais tipos de parceiros externos podem ser relevantes em um projeto de tokenização. Não é uma lista de recomendações, shortlist de contratação ou ranking.",
    }[labels.languageCode];
    const categoryTitle = {
      da: "Typiske leverandørkategorier",
      es: "Categorías típicas de proveedores",
      de: "Typische Anbieter-Kategorien",
      fr: "Catégories typiques de prestataires",
      "zh-Hans": "典型服务商类别",
      "pt-BR": "Categorias típicas de prestadores",
    }[labels.languageCode];
    const examplesTitle = {
      da: "Illustrative eksempler",
      es: "Ejemplos ilustrativos",
      de: "Illustrative Beispiele",
      fr: "Exemples illustratifs",
      "zh-Hans": "示例机构",
      "pt-BR": "Exemplos ilustrativos",
    }[labels.languageCode];
    const principlesTitle = {
      da: "Praktiske udvælgelsesprincipper",
      es: "Principios prácticos de selección",
      de: "Praktische Auswahlprinzipien",
      fr: "Principes pratiques de sélection",
      "zh-Hans": "实务选择原则",
      "pt-BR": "Princípios práticos de seleção",
    }[labels.languageCode];
    const categories = {
      da: ["juridiske og regulatoriske rådgivere", "skatterådgivere", "issuance- eller tokenization-platform", "custody- og wallet-infrastruktur", "transfer agent / registeradministration", "trading venue / ATS / MTF / exchange-partner", "KYC/AML- og onboarding-leverandør", "betaling, settlement og bankpartner", "audit, assurance og cybersikkerhed"],
      es: ["asesores legales y regulatorios", "asesores fiscales", "plataforma de emisión o tokenización", "infraestructura de custodia y wallets", "transfer agent / administración de registro", "trading venue / ATS / MTF / exchange partner", "proveedor de KYC/AML y onboarding", "partner bancario, pagos y settlement", "audit, assurance y ciberseguridad"],
      de: ["Rechts- und Regulierungsberater", "Steuerberater", "Issuance- oder Tokenisierungsplattform", "Custody- und Wallet-Infrastruktur", "Transfer Agent / Registeradministration", "Trading Venue / ATS / MTF / Exchange-Partner", "KYC/AML- und Onboarding-Anbieter", "Zahlungs-, Settlement- und Bankpartner", "Audit, Assurance und Cybersecurity"],
      fr: ["conseillers juridiques et réglementaires", "conseillers fiscaux", "plateforme d’émission ou de tokenisation", "infrastructure de custody et wallets", "transfer agent / administration du registre", "trading venue / ATS / MTF / exchange partner", "prestataire KYC/AML et onboarding", "partenaire paiement, settlement et bancaire", "audit, assurance et cybersécurité"],
      "zh-Hans": ["法律与监管顾问", "税务顾问", "发行或代币化平台", "托管与钱包基础设施", "过户代理/登记管理", "交易场所/ATS/MTF/交易所合作方", "KYC/AML与投资者接入服务商", "支付、结算与银行合作方", "审计、鉴证与网络安全评估"],
      "pt-BR": ["assessoria jurídica e regulatória", "assessoria tributária", "plataforma de emissão ou tokenização", "infraestrutura de custódia e wallets", "agente de transferência / administração de registro", "venue / ATS / MTF / parceiro de bolsa", "prestador de KYC/AML e onboarding de investidores", "parceiro de pagamentos, liquidação e serviços bancários", "auditoria, assurance e avaliação de cibersegurança"],
    }[labels.languageCode] || [];
    const examples = ["CMS", "Cooley", "FIN LAW", "MME", "NÄGELE", "Aktionariat", "Polymesh", "Realiz Group", "Securitize", "Token City", "Tokeny", "ADDX", "Anchorage Digital", "Archax", "BitGo", "Coinbase Institutional", "Copper", "SIX Digital Exchange (SDX)", "Sygnum Bank", "Taurus"];
    const principles = {
      da: ["jurisdiktionsmæssigt fit", "regulatorisk status", "erfaring med den relevante aktivklasse", "driftsmodel og integrationsbyrde", "fee model, portabilitet og exit-muligheder", "service resilience, auditability og koncentrationsrisiko"],
      es: ["encaje jurisdiccional", "estatus regulatorio", "experiencia con la clase de activo relevante", "modelo operativo y carga de integración", "fee model, portabilidad y opciones de salida", "resiliencia del servicio, auditabilidad y riesgo de concentración"],
      de: ["jurisdiktionaler Fit", "regulatorischer Status", "Erfahrung mit der relevanten Assetklasse", "Betriebsmodell und Integrationsaufwand", "Gebührenmodell, Portabilität und Exit-Optionen", "Service-Resilienz, Auditierbarkeit und Konzentrationsrisiko"],
      fr: ["adéquation juridictionnelle", "statut réglementaire", "expérience avec la classe d’actifs concernée", "modèle opérationnel et charge d’intégration", "modèle de frais, portabilité et options de sortie", "résilience du service, auditabilité et risque de concentration"],
      "zh-Hans": ["司法辖区适配性", "监管状态", "相关资产类别经验", "运营模型与集成负担", "收费模式、可迁移性和退出选项", "服务韧性、可审计性和集中度风险"],
      "pt-BR": ["adequação à jurisdição", "status regulatório", "experiência com a classe de ativo relevante", "modelo operacional e carga de integração", "modelo de fees, portabilidade e opções de saída", "resiliência do serviço, auditabilidade e risco de concentração"],
    }[labels.languageCode] || [];
    return (
      <section id="provider-directory" className="card card-accent" style={{ marginTop: 16 }}>
        <h4 style={{ marginTop: 0 }}>{chapterLabel || labels.appendixB}</h4>
        <p className="small" style={{ marginTop: 6 }}>{intro}</p>
        <h5 style={{ marginTop: 12 }}>{categoryTitle}</h5>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>{categories.map((x) => <li key={x}>{x}</li>)}</ul>
        <h5 style={{ marginTop: 12 }}>{examplesTitle}</h5>
        <p className="small" style={{ marginTop: 6 }}>{examples.join(" · ")}</p>
        <h5 style={{ marginTop: 12 }}>{principlesTitle}</h5>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>{principles.map((x) => <li key={x}>{x}</li>)}</ul>
      </section>
    );
  }
  return (
    <section
      id="provider-directory"
      className="card card-accent"
      style={{ marginTop: 16 }}
    >
      <h4 style={{ marginTop: 0 }}>
        {labels.appendixB}
      </h4>

      <p className="small" style={{ marginTop: 6 }}>
        This appendix is an illustrative reference layer intended to help the
        reader think about which types of external partners may be relevant in a
        tokenization project.
      </p>
      <p className="small" style={{ marginTop: 6 }}>
        It is not a recommendation list, procurement shortlist, or ranking.
        Inclusion or omission does not imply endorsement, criticism, quality
        judgment, regulatory approval, or case suitability. Any provider
        mentioned here must be assessed independently for legal fit,
        jurisdictional coverage, operating model fit, technical capability,
        conflicts, cost, and execution reliability. This appendix is
        non-exhaustive and subject to change. The current TVAC reports already
        frame the Provider Directory in this way, as an early, non-exhaustive
        scaffolding meant to help the reader think about partner types rather
        than to recommend specific firms.
      </p>

      <div style={{ marginTop: 12 }}>
        <h5>B1. How to use this appendix</h5>
        <p className="small" style={{ marginTop: 6 }}>
          Use this appendix to identify the provider categories a case may
          require, not as a signal that a specific provider should be engaged.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          In many cases, the main value of the directory is not the names
          themselves, but the discipline of asking:
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>Which functions must be covered?</li>
          <li>Which functions can be combined?</li>
          <li>Which functions must remain regulated or jurisdiction-specific?</li>
          <li>Which dependencies create concentration risk or vendor-capture risk?</li>
          <li>Which parts of the stack are critical before launch, and which can wait?</li>
        </ul>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B2. Typical provider categories</h5>
        <p className="small" style={{ marginTop: 6 }}>
          Depending on the case, a tokenization project may require some or all
          of the following partner categories:
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>legal &amp; regulatory advisers</li>
          <li>tax advisers</li>
          <li>issuance / tokenization platform</li>
          <li>smart-contract / token engineering support</li>
          <li>custody / wallet infrastructure</li>
          <li>transfer agent / registry administration</li>
          <li>fund administration or SPV administration</li>
          <li>trading venue / ATS / MTF / exchange partner</li>
          <li>transfer or liquidity-support provider, only where explicitly required and legally permitted</li>
          <li>KYC / AML / onboarding provider</li>
          <li>payment / settlement / banking partner</li>
          <li>audit / assurance / cybersecurity reviewers</li>
          <li>valuation, data, or oracle-related providers</li>
          <li>distribution / placement / investor onboarding partners</li>
          <li>systems integration / workflow automation support</li>
        </ul>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B3. Illustrative examples from the current TVAC directory</h5>
        <p className="small" style={{ marginTop: 6 }}>
          The current TVAC Provider Directory groups named examples under three
          main headings: Legal &amp; regulatory advisers, Issuing platforms, and
          Custody, trading &amp; settlement. The examples below are drawn from
          that existing list only, but presented in a more compact and
          category-led form.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B3.1 Legal &amp; regulatory advisers</h5>
        <p className="small" style={{ marginTop: 6 }}>
          These advisers help determine whether the proposed structure is legally
          enforceable, properly classified, and consistent with the intended
          issuance, distribution, transfer, custody, and disclosure model.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Typical use cases:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>wrapper design</li>
          <li>securities-law classification</li>
          <li>cross-border marketing analysis</li>
          <li>transfer restriction design</li>
          <li>offering documentation</li>
          <li>legal opinions and regulatory scoping</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Illustrative examples from the current TVAC directory:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>CMS</li>
          <li>Cooley</li>
          <li>FIN LAW</li>
          <li>MME</li>
          <li>NÄGELE</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          These names are taken from the existing Legal &amp; regulatory advisers
          list in the current TVAC reports.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B3.2 Issuing platforms</h5>
        <p className="small" style={{ marginTop: 6 }}>
          These providers typically support issuance workflows, lifecycle
          management, permissions, investor access rules, and parts of the
          reporting or compliance stack.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Typical use cases:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>token issuance</li>
          <li>lifecycle events</li>
          <li>permissions / whitelisting</li>
          <li>cap-table or investor-position management</li>
          <li>compliance rule integration</li>
          <li>reporting workflows</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Illustrative examples from the current TVAC directory:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>Aktionariat</li>
          <li>Polymesh (Polymath)</li>
          <li>Realiz Group</li>
          <li>Securitize</li>
          <li>Token City</li>
          <li>Tokeny</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          These names are taken from the existing Issuing platforms list in the
          current TVAC reports, with DigiShares intentionally omitted from this
          revised appendix.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B3.3 Custody, trading &amp; settlement</h5>
        <p className="small" style={{ marginTop: 6 }}>
          These providers support safekeeping, wallet infrastructure, execution,
          venue access, settlement, and post-trade operations.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Typical use cases:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>institutional custody</li>
          <li>secure wallet infrastructure</li>
          <li>settlement support</li>
          <li>exchange / venue access</li>
          <li>post-trade servicing</li>
          <li>collateral mobility or transfer control support</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          <strong>Illustrative examples from the current TVAC directory:</strong>
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>ADDX</li>
          <li>Anchorage Digital</li>
          <li>Archax</li>
          <li>BitGo</li>
          <li>Coinbase Institutional</li>
          <li>Copper</li>
          <li>Ondo Finance</li>
          <li>SIX Digital Exchange (SDX)</li>
          <li>Sygnum Bank</li>
          <li>Taurus</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          These names are taken from the existing Custody, trading &amp;
          settlement list in the current TVAC reports.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B4. Practical selection principles</h5>
        <p className="small" style={{ marginTop: 6 }}>
          When evaluating providers, the reader should typically assess:
        </p>
        <ul className="small" style={{ marginTop: 6, paddingLeft: 20 }}>
          <li>jurisdictional fit</li>
          <li>regulatory status where relevant</li>
          <li>experience with the relevant asset class</li>
          <li>operating-model compatibility</li>
          <li>integration burden</li>
          <li>exit options / portability</li>
          <li>fee model</li>
          <li>service resilience</li>
          <li>auditability and evidence quality</li>
          <li>concentration risk</li>
        </ul>
        <p className="small" style={{ marginTop: 6 }}>
          Where possible, provider choices should reinforce the case logic rather
          than undermine it. A weak provider stack can easily erode the value
          thesis of an otherwise promising structure.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <h5>B5. Important caution</h5>
        <p className="small" style={{ marginTop: 6 }}>
          The right provider stack is highly case-dependent.
        </p>
        <p className="small" style={{ marginTop: 6 }}>
          A provider that is suitable for a closed institutional collateral
          workflow may be a poor fit for a cross-border fund wrapper. A provider
          that is strong in digital-asset custody may not be the right choice
          for transfer administration or legal structuring. A venue that looks
          attractive commercially may create regulatory or operating-model
          problems if the case does not actually need that level of
          transferability.
        </p>
      </div>
    </section>
  );
}


function getSettlementCashLegHeading(reportLanguageCode = "en") {
  const code = String(reportLanguageCode || "en");
  if (code === "da") return "Afvikling og parathed for kontantdelen";
  if (code === "de") return "Abwicklung und Bereitschaft der Zahlungsseite";
  if (code === "fr") return "Règlement-livraison et préparation du volet monétaire";
  if (code === "es") return "Liquidación y preparación del tramo de efectivo";
  if (code === "zh-Hans") return "结算与现金端准备度";
  if (code === "pt-BR") return "Prontidão de liquidação e cash leg";
  return "Settlement & Cash-Leg Readiness";
}

/* ---------- Analyzer view (main tool) ---------- */

function fmt1(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(1) : "—";
}

function fmt2(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "—";
}

function formatVerdictLabel(v) {
  const raw = (v ?? "").toString().trim();
  if (!raw) return "—";

  const key = raw
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

  const map = {
    viable: "Viable",
    conditional_go: "Conditional Go",
    conditionalgo: "Conditional Go",
    conditional: "Conditional Go",
    non_viable: "Non-viable",
    nonviable: "Non-viable",
    "non-viable": "Non-viable",
  };

  if (map[key]) return map[key];

  // fallback: gør pæn (fjern underscores og Title Case)
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function inferInvestorScope(result) {
  const parts = [
  ...(result?.verdict_reason || result?.verdictReason || []),
  ...(
    result?.deep_assessment?.investor_segmentation ||
    result?.deepAssessment?.investorSegmentation ||
    []
  ),
  ...(result?.assumptions || []),
];

  const hay = parts.join("\n").toLowerCase();

  const inferred = [];
  if (/\bsemi[-\s]?professional\b/.test(hay)) inferred.push("Semi-professional");
  if (/\bprofessional\b/.test(hay)) inferred.push("Professional");
  if (/\baccredited\b/.test(hay)) inferred.push("Accredited");
  const retailNegated =
    /\bno\s+retail\b/.test(hay) ||
    /\bwithout\s+retail\b/.test(hay) ||
    /\bexcluding\s+retail\b/.test(hay) ||
    /\bnot\s+for\s+retail\b/.test(hay) ||
    /\bprofessional[-\s]?only\b/.test(hay);

  if (!retailNegated && /\bretail\b/.test(hay)) inferred.push("Retail");

  return [...new Set(inferred)];
}

function firstSentence(s, maxLen = 180) {
  if (!s) return "";
  const str = String(s).replace(/\s+/g, " ").trim();
  const m = str.match(/^(.+?[.!?])\s/);
  const out = (m ? m[1] : str);
  return out.length > maxLen ? out.slice(0, maxLen - 1) + "…" : out;
}

function coverCaseExcerpt(text, maxLen = 700) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (!s || s.length <= maxLen) return s;
  const hard = s.slice(0, maxLen);
  const sentenceCut = Math.max(hard.lastIndexOf(". "), hard.lastIndexOf("! "), hard.lastIndexOf("? "));
  const wordCut = hard.lastIndexOf(" ");
  const cut = sentenceCut >= Math.floor(maxLen * 0.55) ? sentenceCut + 1 : wordCut;
  return `${s.slice(0, Math.max(0, cut)).trim()}…`;
}

function pickTopRisks(risks, n = 3) {
  const arr = Array.isArray(risks) ? risks : [];
  return [...arr]
    .map(r => ({
      ...r,
      _score: (Number(r.impact) || 0) * (Number(r.likelihood) || 0),
    }))
    .sort((a, b) => (b._score || 0) - (a._score || 0))
    .slice(0, n);
}


function normalizeReportLangForCheck(code) {
  const value = String(code || "en").trim();
  return value || "en";
}

function flattenStringsForLocalizationCheck(value, out = []) {
  if (typeof value === "string") {
    const s = value.trim();
    if (s) out.push(s);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((x) => flattenStringsForLocalizationCheck(x, out));
    return out;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((x) => flattenStringsForLocalizationCheck(x, out));
  }
  return out;
}

function collectRenderedNarrativeStrings(result) {
  if (!result || typeof result !== "object") return [];

  const fields = [
    result.asset_class_context,
    result.assumptions,
    result.deep_assessment?.value_pathways,
    result.deep_assessment?.investor_segmentation,
    result.deep_assessment?.operational_costs,
    result.deep_assessment?.regulatory_path,
    result.deep_assessment?.custody_venue_fit,
    result.deep_assessment?.technical_architecture,
    result.deep_assessment?.legal_regulatory,
    result.deep_assessment?.market_go_to_market,
    result.deep_assessment?.operational_risks,
    result.deep_assessment?.risk_register,
    result.factor_explanations,
    result.recommendation,
    result.next_steps,
    result.what_if_scenarios,
    result.future_scenarios,
  ];

  return fields.flatMap((field) => flattenStringsForLocalizationCheck(field));
}

function looksLikeUnlocalizedEnglishNarrative(text) {
  const s = String(text || "").trim();
  if (s.length < 45) return false;

  const strongEnglishPatterns = [
    /\bTokenization of\b/i,
    /\bThe Luxembourg SPV structure\b/i,
    /\bTokenization enables\b/i,
    /\bThe structure targets\b/i,
    /\bInvestor onboarding\b/i,
    /\bRegulated custody\b/i,
    /\bQuarterly liquidity windows\b/i,
    /\bCross-border tax treatment\b/i,
    /\bCurrent market phase\b/i,
    /\bEnables fractional\b/i,
    /\bHigh upfront and ongoing costs\b/i,
    /\bRegulatory and tax uncertainty\b/i,
    /\bLiquidity is often aspirational\b/i,
    /\bThis section provides\b/i,
    /\bThis market context note\b/i,
    /\bAll regulatory interpretations\b/i,
    /\bRenovation execution risk\b/i,
    /\bFractionalization allows\b/i,
    /\bAutomated lifecycle management\b/i,
    /\bEnhanced transparency\b/i,
    /\bThe issuance is anchored\b/i,
    /\bDistribution in Germany\b/i,
    /\bTransfer restrictions and whitelisting\b/i,
    /\bOngoing costs include\b/i,
    /\bAdministrative efficiencies may\b/i,
    /\bCost savings are credible\b/i,
  ];

  return strongEnglishPatterns.some((rx) => rx.test(s));
}

function reportObjectHasHardLocalizationFailure(result, targetLanguageCode = "en") {
  const target = normalizeReportLangForCheck(targetLanguageCode);
  if (!result || target === "en") return false;

  if (result.localization_error) return true;
  if (result?._telemetry?.localization_parity_failed || result?.localization_quality?.parity_failed) return true;
  if (result?.localization_quality?.verdict_critical_parity === false) return true;

  const actual = normalizeReportLangForCheck(result.reportLanguageCode || result.localized_report_language || result._telemetry?.localized_report_language || "");
  if (actual && actual !== target) return true;

  return false;
}

function backendLocalizationLooksDisplayReady(result, targetLanguageCode = "en") {
  const target = normalizeReportLangForCheck(targetLanguageCode);
  if (!result || target === "en") return true;

  const quality = result.localization_quality || {};
  const telemetry = result._telemetry || {};
  const hasBackendLocalizationSignal =
    result.localized_from_canonical === true ||
    telemetry.localized_from_canonical === true ||
    Boolean(quality.target_language) ||
    Boolean(quality.method) ||
    Boolean(telemetry.localized_report_language);
  const hasBackendQualityTelemetry =
    Boolean(quality.target_language) ||
    Boolean(quality.method) ||
    Object.prototype.hasOwnProperty.call(quality, "unresolved_english_signal_count");

  if (!hasBackendLocalizationSignal || !hasBackendQualityTelemetry) return false;
  if (reportObjectHasHardLocalizationFailure(result, target)) return false;

  const failedChunks = Number(quality.failed_chunk_count || 0);
  if (Number.isFinite(failedChunks) && failedChunks > 0) return false;

  // The backend detector intentionally counts some allowed English reference terms
  // (e.g. Added Value, Conditional Go, SPV, ATS/MTF). Do not block display or
  // trigger another expensive localization round merely because a handful remain.
  const unresolvedSignals = Number(quality.unresolved_english_signal_count || 0);
  const toleratedSignals = target === "zh-Hans" ? 6 : 10;
  return !Number.isFinite(unresolvedSignals) || unresolvedSignals <= toleratedSignals;
}

function reportObjectNeedsLocalization(result, targetLanguageCode = "en") {
  const target = normalizeReportLangForCheck(targetLanguageCode);
  if (!result || target === "en") return false;

  if (reportObjectHasHardLocalizationFailure(result, target)) return true;
  if (backendLocalizationLooksDisplayReady(result, target)) return false;

  const strings = collectRenderedNarrativeStrings(result);
  return strings.some((s) => looksLikeUnlocalizedEnglishNarrative(s));
}

const LOCALIZATION_PARITY_PATHS = [
  ["asset_class_context", "summary"],
  ["asset_class_context", "current_market_phase"],
  ["asset_class_context", "structural_strengths"],
  ["asset_class_context", "structural_weaknesses"],
  ["assumptions"],
  ["deep_assessment", "value_pathways"],
  ["deep_assessment", "investor_segmentation"],
  ["deep_assessment", "operational_costs"],
  ["deep_assessment", "settlement_cash_leg_readiness"],
  ["deep_assessment", "regulatory_path"],
  ["deep_assessment", "custody_venue_fit"],
  ["deep_assessment", "risk_register"],
  ["deep_assessment", "milestones"],
  ["deep_assessment", "go_no_go_criteria", "go_if"],
  ["deep_assessment", "go_no_go_criteria", "no_go_if"],
  ["factor_explanations", "new_opportunities"],
  ["factor_explanations", "cost_savings"],
  ["factor_explanations", "risk_reduction"],
  ["factor_explanations", "tokenization_costs"],
  ["factor_explanations", "new_risks"],
];

function getByPath(obj, path) {
  return (path || []).reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function narrativeSizeForParity(value) {
  if (typeof value !== "string") return 0;
  return String(value || "").replace(/\s+/g, "").length;
}

function arrayNarrativeSize(value) {
  if (!Array.isArray(value)) return 0;
  return value.map((item) => {
    if (typeof item === "string") return narrativeSizeForParity(item);
    if (item && typeof item === "object") return narrativeSizeForParity(Object.values(item).join(" "));
    return 0;
  }).reduce((sum, n) => sum + n, 0);
}

function getLocalizationParityIssues(master, candidate, targetLanguageCode = "en") {
  const target = normalizeReportLangForCheck(targetLanguageCode);
  if (!master || !candidate || target === "en") return [];
  const issues = [];
  const minRatio = target === "zh-Hans" ? 0.22 : 0.42;

  LOCALIZATION_PARITY_PATHS.forEach((path) => {
    const masterValue = getByPath(master, path);
    const localizedValue = getByPath(candidate, path);
    const pathLabel = path.join(".");

    if (Array.isArray(masterValue)) {
      const masterLen = masterValue.filter(Boolean).length;
      const localizedLen = Array.isArray(localizedValue) ? localizedValue.filter(Boolean).length : 0;
      if (masterLen >= 2 && localizedLen < masterLen) {
        issues.push(`${pathLabel}: expected ${masterLen} items, got ${localizedLen}`);
        return;
      }
      const masterSize = arrayNarrativeSize(masterValue);
      const localizedSize = arrayNarrativeSize(localizedValue);
      if (masterSize >= 450 && localizedSize > 0 && localizedSize < masterSize * minRatio) {
        issues.push(`${pathLabel}: localized narrative is too short compared with master`);
      }
      return;
    }

    if (typeof masterValue === "string") {
      const masterSize = narrativeSizeForParity(masterValue);
      const localizedSize = narrativeSizeForParity(localizedValue);
      if (masterSize >= 450 && localizedSize > 0 && localizedSize < masterSize * minRatio) {
        issues.push(`${pathLabel}: localized narrative is too short compared with master`);
      }
    }
  });

  const masterRisks = master?.deep_assessment?.risk_register;
  const localizedRisks = candidate?.deep_assessment?.risk_register;
  if (Array.isArray(masterRisks) || Array.isArray(localizedRisks)) {
    const masterCount = Array.isArray(masterRisks) ? masterRisks.length : 0;
    const localizedCount = Array.isArray(localizedRisks) ? localizedRisks.length : 0;
    if (masterCount !== localizedCount) {
      issues.push(`deep_assessment.risk_register: expected ${masterCount} items, got ${localizedCount}`);
    } else {
      for (let index = 0; index < masterCount; index += 1) {
        const source = masterRisks[index] || {};
        const translated = localizedRisks[index] || {};
        for (const key of ["impact", "likelihood"]) {
          const sourceValue = Number(source[key]);
          const translatedValue = Number(translated[key]);
          if (!Number.isInteger(translatedValue) || translatedValue < 1 || translatedValue > 5) {
            issues.push(`deep_assessment.risk_register.${index}.${key}: must be an integer from 1 to 5`);
          } else if (Number.isFinite(sourceValue) && translatedValue !== sourceValue) {
            issues.push(`deep_assessment.risk_register.${index}.${key}: differs from canonical master`);
          }
        }
      }
    }
  }

  return issues;
}

function markLocalizationParity(candidate, issues = []) {
  if (!candidate || typeof candidate !== "object") return candidate;
  if (!issues.length) return candidate;
  return {
    ...candidate,
    localization_quality: {
      ...(candidate.localization_quality || {}),
      parity_failed: true,
      parity_issues: issues,
    },
    _telemetry: {
      ...(candidate._telemetry || {}),
      localization_parity_failed: true,
      localization_parity_issues: issues,
    },
  };
}

function getLocalizationStatusMessage(code = "en") {
  const map = {
    da: "Sprogversionen er ikke færdig, er for kort i forhold til den engelske master eller bestod ikke lokaliseringskontrollen. Prøv at vælge sproget igen; hvis fejlen gentager sig, brug den engelske masterrapport.",
    de: "Die Sprachversion ist noch nicht fertig, im Vergleich zum englischen Master zu kurz oder hat die Lokalisierungsprüfung nicht bestanden. Bitte wählen Sie die Sprache erneut; falls der Fehler bleibt, verwenden Sie den englischen Masterbericht.",
    fr: "La version linguistique n’est pas encore prête, est trop courte par rapport au master anglais ou n’a pas passé le contrôle de localisation. Sélectionnez à nouveau la langue; si le problème persiste, utilisez le rapport maître anglais.",
    es: "La versión de idioma no está lista, es demasiado corta frente al master inglés o no superó la comprobación de localización. Vuelva a seleccionar el idioma; si el problema persiste, use el informe maestro en inglés.",
    "zh-Hans": "该语言版本尚未准备好、相对英文主报告过短，或未通过本地化检查。请重新选择该语言；如果问题仍然存在，请使用英文主报告。",
    "pt-BR": "A versão em português do Brasil ainda não está pronta, está curta demais em relação ao master em inglês ou não passou nas verificações de localização. Selecione o idioma novamente; se o problema persistir, use o relatório master em inglês.",
    en: "The selected report language is not ready, is too short compared with the English master, or failed localization checks. Select the language again; if the issue persists, use the English master report.",
  };
  return map[code] || map.en;
}

function getLocalizationPendingCopy(code = "en", languageName = "selected language") {
  const map = {
    da: {
      title: "Den danske rapportversion forberedes",
      pending: "TVAC har gennemført den metodiske vurdering og forbereder nu den valgte danske rapportversion. Rapporten vises først, når sprogversionen er færdig og har bestået lokaliseringskontrollen.",
      failed: "Den danske rapportversion kunne ikke færdiggøres eller bestod ikke lokaliseringskontrollen. Prøv igen, eller brug den engelske masterrapport.",
      retry: "Prøv dansk igen",
      english: "Brug engelsk masterrapport",
    },
    de: {
      title: "Die deutsche Berichtsversion wird vorbereitet",
      pending: "TVAC hat die methodische Bewertung abgeschlossen und bereitet nun die ausgewählte deutsche Berichtsversion vor. Der Bericht wird erst angezeigt, wenn die Sprachversion fertig ist und die Lokalisierungsprüfung bestanden hat.",
      failed: "Die deutsche Berichtsversion konnte nicht fertiggestellt werden oder hat die Lokalisierungsprüfung nicht bestanden. Bitte erneut versuchen oder den englischen Masterbericht verwenden.",
      retry: "Deutsch erneut versuchen",
      english: "Englischen Masterbericht verwenden",
    },
    fr: {
      title: "La version française du rapport est en préparation",
      pending: "TVAC a terminé l’évaluation méthodologique et prépare maintenant la version française du rapport. Le rapport ne s’affiche qu’une fois la version linguistique prête et validée.",
      failed: "La version française n’a pas pu être finalisée ou n’a pas passé le contrôle de localisation. Réessayez ou utilisez le rapport maître anglais.",
      retry: "Réessayer en français",
      english: "Utiliser le rapport maître anglais",
    },
    es: {
      title: "La versión española del informe se está preparando",
      pending: "TVAC ha completado la evaluación metodológica y ahora prepara la versión española del informe. El informe solo se muestra cuando la versión lingüística está lista y ha superado el control de localización.",
      failed: "La versión española no pudo completarse o no superó el control de localización. Inténtelo de nuevo o use el informe maestro en inglés.",
      retry: "Reintentar en español",
      english: "Usar informe maestro en inglés",
    },
    "zh-Hans": {
      title: "正在准备中文报告版本",
      pending: "TVAC 已完成方法评估，现在正在准备所选中文报告版本。只有当语言版本完成并通过本地化检查后，报告才会显示。",
      failed: "中文报告版本未能完成，或未通过本地化检查。请重试，或使用英文主报告。",
      retry: "重新生成中文版本",
      english: "使用英文主报告",
    },
    "pt-BR": {
      title: "Preparando a versão do relatório em português do Brasil",
      pending: "O TVAC concluiu a avaliação metodológica e está preparando a versão selecionada em português do Brasil. O relatório será exibido somente quando a versão estiver pronta e tiver passado nas verificações de localização.",
      failed: "A versão em português do Brasil não pôde ser concluída ou não passou nas verificações de localização. Tente novamente ou use o relatório master em inglês.",
      retry: "Tentar novamente em português do Brasil",
      english: "Usar relatório master em inglês",
    },
    en: {
      title: `Preparing ${languageName} report version`,
      pending: `TVAC has completed the methodological assessment and is preparing the ${languageName} report version. The report will be shown only after the selected language version is ready and has passed localization checks.`,
      failed: `The ${languageName} report version could not be completed or failed localization checks. Retry the selected language or use the English master report.`,
      retry: `Retry ${languageName}`,
      english: "Use English master report",
    },
  };
  return map[code] || map.en;
}


/* ---------- TVAC Scenario Lab (controlled scenario comparison) ---------- */
/*
  Product guardrails:
  - No live-score sliders.
  - No instant score preview.
  - No "optimize my case" language.
  - User-defined scenarios only.
  - Each alternative scenario must be deliberately assessed through the normal TVAC engine.
  - Commercial / upgrade prompts must stay app-only and outside the print-area.
*/
const SCENARIO_LAB_FIELDS = [
  {
    key: "instrumentType",
    label: "Instrument type",
    type: "select",
    options: [
      ["", "Select…"],
      ["wrapper", "Digital wrapper (CSD/TA/registrar)"],
      ["sole-record", "Sole-record / native tokenized security"],
    ],
  },
  {
    key: "custodyModel",
    label: "Custody model",
    type: "select",
    options: [
      ["", "Select…"],
      ["regulated-custodian", "Regulated custodian"],
      ["ta/csd", "Transfer agent / CSD"],
      ["self-custody", "Self-custody (wallets)"],
    ],
  },
  {
    key: "liquidityCadence",
    label: "Secondary-market liquidity",
    type: "select",
    options: [
      ["", "Select…"],
      ["none", "None"],
      ["ad-hoc otc", "Ad-hoc OTC"],
      ["quarterly", "Quarterly windows"],
      ["continuous", "Continuous"],
    ],
  },
  {
    key: "marketVenue",
    label: "Secondary-market venue",
    type: "select",
    options: [
      ["", "Select…"],
      ["ATS/MTF", "ATS / MTF"],
      ["DEX", "DEX (permissioned)"],
      ["OTC", "OTC"],
      ["CSD/TA-registered", "CSD / TA-registered"],
    ],
  },
  {
    key: "settlementModel",
    label: "Settlement model",
    type: "select",
    options: [
      ["", "Select…"],
      ["T+0 atomic", "T+0 atomic"],
      ["T+1/2", "T+1/2"],
      ["off-chain", "Off-chain booking"],
    ],
  },
  { key: "saleJurisdictions", label: "Sale jurisdictions", type: "text" },
  { key: "transferRestrictions", label: "Transfer restrictions", type: "text" },
];

function scenarioLabClone(value) {
  if (value === null || value === undefined) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function scenarioLabStableStringify(value) {
  const normalize = (input) => {
    if (input === null || input === undefined) return null;
    if (Array.isArray(input)) return input.map((item) => normalize(item));
    if (input && typeof input === "object") {
      return Object.keys(input)
        .sort()
        .reduce((acc, key) => {
          const v = input[key];
          if (typeof v === "function" || v === undefined) return acc;
          acc[key] = normalize(v);
          return acc;
        }, {});
    }
    return input;
  };

  try {
    return JSON.stringify(normalize(value));
  } catch {
    return String(value || "");
  }
}

function getScenarioAssessmentSignature(evaluationPayload) {
  const payload = scenarioLabClone(evaluationPayload || {});
  // These fields control request/session handling and should not decide whether
  // the underlying scenario assumptions have changed.
  delete payload.forceFresh;
  delete payload.forceRefresh;
  delete payload.disclaimerAccepted;
  delete payload.email;

  return scenarioLabStableStringify({
    signature_schema: "scenario-lab-assessment-signature-v1",
    report_render_schema: REPORT_RENDER_SCHEMA_VERSION,
    payload,
  });
}

function normalizeScenarioValue(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || "").trim()).filter(Boolean).join(", ");
  if (value && typeof value === "object") {
    if (
      Object.prototype.hasOwnProperty.call(value, "Professional") ||
      Object.prototype.hasOwnProperty.call(value, "Accredited") ||
      Object.prototype.hasOwnProperty.call(value, "Retail")
    ) {
      return [
        value.Professional ? "Professional" : "",
        value.Accredited ? "Accredited" : "",
        value.Retail ? "Retail" : "",
      ].filter(Boolean).join(", ");
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value || "");
    }
  }
  return String(value || "").trim();
}

function getScenarioAddedValue(result) {
  const score = Number(result?.added_value?.score);
  return Number.isFinite(score) ? score : null;
}

function getScenarioPlusMinus(result) {
  const plus = Number(result?.added_value?.plus);
  const minus = Number(result?.added_value?.minus);
  return {
    plus: Number.isFinite(plus) ? plus : null,
    minus: Number.isFinite(minus) ? minus : null,
  };
}

function getScenarioChangedAssumptions(basePayload, candidatePayload) {
  if (!basePayload || !candidatePayload) return [];
  const fields = [
    ...SCENARIO_LAB_FIELDS,
    { key: "investorTypes", label: "Investor types" },
  ];
  return fields
    .map((field) => {
      const before = normalizeScenarioValue(basePayload[field.key]);
      const after = normalizeScenarioValue(candidatePayload[field.key]);
      return before !== after ? { label: field.label, before: before || "—", after: after || "—" } : null;
    })
    .filter(Boolean);
}

function isGenericScenarioName(value) {
  const s = String(value || "").trim().toLowerCase();
  return !s || [
    "alternative",
    "alternative 1",
    "alternative 2",
    "alternative assumption set",
    "scenario alternative",
    "scenario b alternative",
    "scenario c alternative",
  ].includes(s);
}

function getScenarioThemeFromChanges(changes = []) {
  if (!changes.length) return "";
  const labels = changes.map((ch) => String(ch.label || "").toLowerCase());
  const onlyOne = changes.length === 1;

  if (labels.includes("instrument type")) {
    const ch = changes.find((x) => String(x.label || "").toLowerCase() === "instrument type");
    const after = String(ch?.after || "").toLowerCase();
    if (after.includes("sole")) return "Sole-record token test";
    if (after.includes("wrapper")) return "Wrapper model test";
    return "Instrument model test";
  }

  if (labels.includes("sale jurisdictions")) {
    const ch = changes.find((x) => String(x.label || "").toLowerCase() === "sale jurisdictions");
    const beforeCount = String(ch?.before || "").split(",").map((x) => x.trim()).filter(Boolean).length;
    const afterCount = String(ch?.after || "").split(",").map((x) => x.trim()).filter(Boolean).length;
    if (afterCount > beforeCount) return "Expanded jurisdiction test";
    if (afterCount < beforeCount) return "Narrower jurisdiction test";
    return "Jurisdiction scope test";
  }

  if (labels.includes("custody model")) return "Custody model test";
  if (labels.includes("secondary-market liquidity")) return "Liquidity model test";
  if (labels.includes("secondary-market venue")) return "Venue model test";
  if (labels.includes("settlement model")) return "Settlement model test";
  if (labels.includes("transfer restrictions")) return "Transfer-control test";
  if (labels.includes("investor types")) return "Investor-scope test";

  return onlyOne ? `${changes[0].label} test` : "Controlled multi-assumption test";
}

function getScenarioDisplayName(scenario, baselinePayload) {
  if (!scenario) return "—";
  const raw = String(scenario.name || "").trim();
  if (scenario.id === "baseline") return raw || "Baseline";
  if (raw && !isGenericScenarioName(raw)) return raw;
  const changes = getScenarioChangedAssumptions(baselinePayload, scenario.payload);
  return getScenarioThemeFromChanges(changes) || raw || "Alternative scenario";
}

function getScenarioDisplayTitle(scenario, baselinePayload) {
  return `${scenario?.label || "Scenario"} — ${getScenarioDisplayName(scenario, baselinePayload)}`;
}

function getScenarioDriverText(baseResult, candidateResult) {
  if (!baseResult || !candidateResult) return "Assessment completed.";
  const baseFs = baseResult.factor_scores || {};
  const fs = candidateResult.factor_scores || {};
  const candidates = [
    { label: "New Opportunities", delta: Number(fs.new_opportunities || 0) - Number(baseFs.new_opportunities || 0), positiveWhen: "up" },
    { label: "Cost Savings", delta: Number(fs.cost_savings || 0) - Number(baseFs.cost_savings || 0), positiveWhen: "up" },
    { label: "Risk Reduction", delta: Number(fs.risk_reduction || 0) - Number(baseFs.risk_reduction || 0), positiveWhen: "up" },
    { label: "Tokenization Costs", delta: Number(fs.tokenization_costs || 0) - Number(baseFs.tokenization_costs || 0), positiveWhen: "down" },
    { label: "New Risks", delta: Number(fs.new_risks || 0) - Number(baseFs.new_risks || 0), positiveWhen: "down" },
  ].filter((x) => Number.isFinite(x.delta) && x.delta !== 0);

  if (!candidates.length) return "No material factor-score movement versus baseline.";

  const ranked = [...candidates].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const top = ranked[0];
  const direction = top.delta > 0 ? "higher" : "lower";
  const beneficial = top.positiveWhen === "up" ? top.delta > 0 : top.delta < 0;
  return `${top.label} is ${direction} than baseline (${beneficial ? "supportive" : "less supportive"} under the TVAC methodology).`;
}


const SCENARIO_FACTOR_META = [
  { key: "new_opportunities", label: "New Opportunities", short: "NO", goodWhen: "up" },
  { key: "cost_savings", label: "Cost Savings", short: "CS", goodWhen: "up" },
  { key: "risk_reduction", label: "Risk Reduction", short: "RR", goodWhen: "up" },
  { key: "tokenization_costs", label: "Tokenization Costs", short: "TC", goodWhen: "down" },
  { key: "new_risks", label: "New Risks", short: "NR", goodWhen: "down" },
];

function formatScenarioDelta(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "0";
  return n > 0 ? `+${n}` : String(n);
}

function getScenarioFactorMovements(baseResult, candidateResult) {
  const baseFs = baseResult?.factor_scores || {};
  const fs = candidateResult?.factor_scores || {};
  return SCENARIO_FACTOR_META.map((meta) => {
    const before = Number(baseFs[meta.key] || 0);
    const after = Number(fs[meta.key] || 0);
    const delta = after - before;
    const supportive = meta.goodWhen === "up" ? delta > 0 : delta < 0;
    const adverse = meta.goodWhen === "up" ? delta < 0 : delta > 0;
    return { ...meta, before, after, delta, supportive, adverse };
  }).filter((x) => Number.isFinite(x.delta));
}

function getTopScenarioMovements(baseResult, candidateResult, max = 3) {
  return getScenarioFactorMovements(baseResult, candidateResult)
    .filter((x) => x.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, max);
}

function getScenarioFactorExplanation(result, key) {
  const value = result?.factor_explanations?.[key];
  if (Array.isArray(value)) return String(value[0] || "").trim();
  if (typeof value === "string") return value.trim();
  return "";
}

function getScenarioRiskNames(result, limit = 3) {
  const risks = result?.deep_assessment?.risk_register;
  if (!Array.isArray(risks)) return [];
  return risks
    .map((item) => ({
      name: String(item?.risk || "").trim(),
      impact: Number(item?.impact || 0),
      likelihood: Number(item?.likelihood || 0),
    }))
    .filter((x) => x.name)
    .sort((a, b) => (b.impact * b.likelihood) - (a.impact * a.likelihood))
    .slice(0, limit);
}

function getScenarioVerdictText(result) {
  const v = String(result?.verdict || "").toLowerCase().trim();
  if (["go", "viable", "go / viable"].includes(v)) return "Viable";
  if (["no-go", "no go", "non-viable", "non viable", "stop", "non_viable"].includes(v)) return "Non-viable";
  return "Conditional Go";
}

function isWrapperToSoleRecordChange(baseScenario, scenario) {
  const before = normalizeScenarioValue(baseScenario?.payload?.instrumentType).toLowerCase();
  const after = normalizeScenarioValue(scenario?.payload?.instrumentType).toLowerCase();
  return /wrapper/.test(before) && /(sole|native)/.test(after);
}

function isGenericFutureRationale(value) {
  const s = String(value || "").toLowerCase().trim();
  if (!s) return false;
  const futureSignals = ["future", "native", "sole-record", "sole record", "wrapper", "benefit", "modern", "better"];
  const hasFutureSignal = futureSignals.some((x) => s.includes(x));
  const hasEvidenceSignal = /investor demand|legal opinion|custodian|venue|liquidity provider|market maker|distribution|cost model|tax|settlement|enforceability|operational|data|pilot|commitment|term sheet|provider/i.test(s);
  return hasFutureSignal && !hasEvidenceSignal && s.length < 220;
}

function getScenarioThresholdNote(result) {
  const av = getScenarioAddedValue(result);
  const verdict = getScenarioVerdictText(result);
  if (av === null) return "";
  if (verdict === "Non-viable" && av >= -3 && av <= -2) {
    return `This verdict is threshold-sensitive: the scenario falls into the Non-viable band at Added Value ${av}. It should not be read as a catastrophic collapse of the case; it means the scenario no longer preserves enough positive value drivers to remain Conditional Go under the TVAC methodology.`;
  }
  if (verdict === "Conditional Go" && av >= -1 && av <= 1) {
    return `This result is close to the lower Conditional Go boundary. Small changes in evidence, costs, risk controls, or distribution assumptions could move the case into a weaker band, so the factor movements should be read carefully.`;
  }
  if (verdict === "Viable" && av >= 4 && av <= 5) {
    return `This result is close to the Viable threshold. The scenario has crossed into a stronger band, but the conclusion still depends on validating the assumptions that created the uplift.`;
  }
  return "";
}

function getWrapperToSoleRecordInterpretation(baseScenario, scenario) {
  if (!isWrapperToSoleRecordChange(baseScenario, scenario)) return "";
  const rationaleNote = isGenericFutureRationale(scenario?.changeRationale)
    ? " The rationale is directional rather than evidence-based: saying that sole-record/native tokens are the future does not, by itself, explain why this specific real-estate/SPV case gains stronger investor access, liquidity, enforceability, cost efficiency, or operational utility."
    : "";
  return `The wrapper-to-sole-record change is not treated as inherently negative. The issue is case-specific: in the current assumption set, sole-record/native token status does not add a clearly monetisable benefit over the wrapper model, while it increases legal-technical dependence and makes enforceability, registry control, venue acceptance, and professional validation more important.${rationaleNote}`;
}

function getScenarioSpecificInterpretation(baseScenario, scenario) {
  const baseResult = baseScenario?.result;
  const result = scenario?.result;
  if (!baseResult || !result) return "Run the scenario assessment to generate a case-specific comparison.";

  const movements = getScenarioFactorMovements(baseResult, result);
  const avBefore = getScenarioAddedValue(baseResult);
  const avAfter = getScenarioAddedValue(result);
  const avDelta = avBefore == null || avAfter == null ? null : avAfter - avBefore;
  const adverse = movements.filter((m) => m.adverse).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const supportive = movements.filter((m) => m.supportive).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const changed = getScenarioChangedAssumptions(baseScenario?.payload, scenario?.payload);
  const changedLabels = changed.slice(0, 4).map((x) => x.label.toLowerCase()).join(", ");
  const namedChanges = changed.length ? `The tested changes concern ${changedLabels}.` : "No selected assumption change is recorded against the baseline.";
  const soleRecordNote = getWrapperToSoleRecordInterpretation(baseScenario, scenario);
  const thresholdNote = getScenarioThresholdNote(result);
  const suffix = [soleRecordNote, thresholdNote].filter(Boolean).join(" ");

  if (avDelta === 0 || avDelta === null) {
    if (!adverse.length && !supportive.length) {
      return `${namedChanges} Under the current TVAC calibration, these changes do not materially move the factor profile relative to the baseline. That is itself a useful finding: the tested assumptions are not the decisive levers in this case, at least as described.${suffix ? ` ${suffix}` : ""}`;
    }
    return `${namedChanges} The net Added Value is unchanged, but the internal profile shifts: ${supportive[0]?.label || "one factor"} improves while ${adverse[0]?.label || "another factor"} offsets the gain. This suggests a trade-off rather than a clean strengthening of the case.${suffix ? ` ${suffix}` : ""}`;
  }

  if (avDelta > 0) {
    const top = supportive[0];
    const offset = adverse[0];
    return `${namedChanges} This scenario strengthens the TVAC profile by ${formatScenarioDelta(avDelta)} point(s) versus the baseline, mainly because ${top ? `${top.label} moves from ${top.before} to ${top.after}` : "the balance of upside and friction improves"}.${offset ? ` The improvement is not friction-free: ${offset.label} moves from ${offset.before} to ${offset.after}, which should be treated as a remaining validation point.` : " No adverse factor movement is visible in the selected scores."}${suffix ? ` ${suffix}` : ""}`;
  }

  const top = adverse[0];
  const offset = supportive[0];
  return `${namedChanges} This scenario weakens the TVAC profile by ${formatScenarioDelta(avDelta)} point(s) versus the baseline, mainly because ${top ? `${top.label} moves from ${top.before} to ${top.after}` : "the downside side of the model becomes heavier relative to the upside"}.${offset ? ` There is a partial offset from ${offset.label}, which moves from ${offset.before} to ${offset.after}, but not enough to compensate for the deterioration.` : " The tested changes do not create enough compensating upside under the current methodology."}${suffix ? ` ${suffix}` : ""}`;
}


function getScenarioSnapshotItems(payload) {
  const investor = normalizeScenarioValue(payload?.investorTypes) || "—";
  const sale = normalizeScenarioValue(payload?.saleJurisdictions) || normalizeScenarioValue(payload?.jurisdictions) || "—";
  return [
    ["Instrument", normalizeScenarioValue(payload?.instrumentType) || "—"],
    ["Custody", normalizeScenarioValue(payload?.custodyModel) || "—"],
    ["Liquidity", normalizeScenarioValue(payload?.liquidityCadence) || "—"],
    ["Venue", normalizeScenarioValue(payload?.marketVenue || payload?.venueType) || "—"],
    ["Settlement", normalizeScenarioValue(payload?.settlementModel) || "—"],
    ["Sale jurisdictions", sale],
    ["Investor types", investor],
    ["Transfer restrictions", normalizeScenarioValue(payload?.transferRestrictions) || "—"],
  ];
}

function getScenarioComparisonContext(scenarios) {
  const baseline = scenarios.find((s) => s.id === "baseline") || scenarios[0];
  const assessed = scenarios.filter((s) => s.result && s.status === "complete");
  const alternatives = assessed.filter((s) => s.id !== "baseline");

  if (!baseline?.result || assessed.length < 2) {
    return null;
  }

  const ranked = [...assessed].sort(
    (a, b) => (getScenarioAddedValue(b.result) ?? -999) - (getScenarioAddedValue(a.result) ?? -999)
  );
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];
  const baselineAv = getScenarioAddedValue(baseline.result);
  const strongestAv = getScenarioAddedValue(strongest.result);
  const weakestAv = getScenarioAddedValue(weakest.result);
  const spread = strongestAv != null && weakestAv != null ? strongestAv - weakestAv : null;
  const baselineRank = ranked.findIndex((s) => s.id === "baseline") + 1;

  return {
    baseline,
    assessed,
    alternatives,
    ranked,
    strongest,
    weakest,
    baselineAv,
    strongestAv,
    weakestAv,
    spread,
    baselineRank,
  };
}

function getScenarioMaterialityText(spread) {
  if (spread == null) return "The score spread could not be calculated from the available scenario results.";
  if (spread >= 8) {
    return `The ${spread}-point spread between the strongest and weakest assessed scenarios is highly material under the TVAC methodology. It indicates that the tested assumptions do not merely create marginal variation; they materially change the value/friction balance of this case.`;
  }
  if (spread >= 4) {
    return `The ${spread}-point spread between the strongest and weakest assessed scenarios is material. It is large enough to justify reading the scenario reports individually before deciding which assumptions deserve further professional validation.`;
  }
  if (spread > 0) {
    return `The ${spread}-point spread between the strongest and weakest assessed scenarios is relatively narrow. The comparison is still useful, but the value is mainly in understanding trade-offs rather than treating the ranking as decisive.`;
  }
  return "The assessed scenarios produce the same Added Value score. The useful finding is therefore not ranking, but understanding which factor trade-offs remain unchanged across the tested assumptions.";
}

function getScenarioDecisionFocus(ctx) {
  if (!ctx) return "";
  const { strongest, baseline, baselineAv, strongestAv, spread } = ctx;
  const strongestIsBaseline = strongest?.id === "baseline";
  if (strongestIsBaseline) {
    return `The baseline remains the strongest assessed profile. This means the tested alternatives did not improve the value/friction balance under the TVAC methodology. The comparison should therefore be read as a validation of the original baseline assumptions against the specific alternatives tested, not as a recommendation to proceed.`;
  }
  const delta = baselineAv == null || strongestAv == null ? null : strongestAv - baselineAv;
  return `${getScenarioDisplayTitle(strongest, baseline?.payload)} produces the strongest assessed TVAC profile, with an Added Value ${delta == null ? "that is higher than the baseline" : `${formatScenarioDelta(delta)} point(s) versus the baseline`}. This does not mean the scenario should be implemented; it means the selected assumptions deserve closer professional validation because they improve the methodology-based value/friction balance in this comparison.${spread != null && spread >= 4 ? " The spread is large enough to justify detailed review of the full scenario reports before any internal decision is made." : ""}`;
}

function ScenarioComparisonAnalysis({ scenarios }) {
  const ctx = getScenarioComparisonContext(scenarios);
  if (!ctx) return null;

  const {
    baseline,
    alternatives,
    ranked,
    strongest,
    weakest,
    baselineAv,
    strongestAv,
    weakestAv,
    spread,
    baselineRank,
  } = ctx;

  return (
    <div className="card" style={{ marginTop: 12, padding: 16, borderColor: "rgba(0,0,0,0.12)", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div className="muted small" style={{ fontWeight: 800, letterSpacing: 0.2 }}>SCENARIO LAB COMPARISON</div>
          <h4 style={{ margin: "3px 0 8px" }}>Detailed comparative analysis</h4>
        </div>
        <div className="muted small" style={{ maxWidth: 360, lineHeight: 1.45 }}>
          Controlled comparison of user-defined assumptions — not optimization, implementation advice, or a recommendation to choose any scenario.
        </div>
      </div>

      <p className="muted" style={{ margin: 0, lineHeight: 1.55 }}>
        This comparison is based only on the assessed, user-defined scenario assumptions below. It is a TVAC methodology comparison, not a recommendation to implement, reject, structure, finance, issue, market, operate, or technically build any scenario.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 12 }}>
        <div className="card" style={{ padding: 10, background: "#fafafa" }}>
          <div className="muted small">Highest assessed profile</div>
          <div style={{ fontWeight: 800, marginTop: 3 }}>{getScenarioDisplayTitle(strongest, baseline.payload)}</div>
          <div className="small" style={{ marginTop: 4 }}>Added Value: {strongestAv ?? "—"} · Verdict: {getScenarioVerdictText(strongest.result)}</div>
        </div>
        <div className="card" style={{ padding: 10, background: "#fafafa" }}>
          <div className="muted small">Range across assessed scenarios</div>
          <div style={{ fontWeight: 800, marginTop: 3 }}>{spread == null ? "—" : `${spread} point spread`}</div>
          <div className="small" style={{ marginTop: 4 }}>From {getScenarioDisplayTitle(weakest, baseline.payload)} ({weakestAv ?? "—"}) to {getScenarioDisplayTitle(strongest, baseline.payload)} ({strongestAv ?? "—"})</div>
        </div>
        <div className="card" style={{ padding: 10, background: "#fafafa" }}>
          <div className="muted small">Baseline position</div>
          <div style={{ fontWeight: 800, marginTop: 3 }}>Rank {baselineRank} of {ranked.length}</div>
          <div className="small" style={{ marginTop: 4 }}>Baseline Added Value: {baselineAv ?? "—"}</div>
        </div>
      </div>

      <h5 style={{ margin: "16px 0 6px" }}>Executive comparison</h5>
      <p style={{ margin: 0, lineHeight: 1.55 }}>
        {getScenarioDecisionFocus(ctx)} {getScenarioMaterialityText(spread)}
      </p>

      {alternatives.map((scenario) => {
        const av = getScenarioAddedValue(scenario.result);
        const avDelta = baselineAv == null || av == null ? null : av - baselineAv;
        const movements = getTopScenarioMovements(baseline.result, scenario.result, 4);
        const changes = getScenarioChangedAssumptions(baseline.payload, scenario.payload);
        const risks = getScenarioRiskNames(scenario.result, 3);
        return (
          <div key={`analysis-${scenario.id}`} style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #eee" }}>
            <h5 style={{ margin: "0 0 6px" }}>{getScenarioDisplayTitle(scenario, baseline.payload)}</h5>
            <p style={{ margin: "0 0 8px", lineHeight: 1.55 }}>
              {getScenarioSpecificInterpretation(baseline, scenario)} The scenario rationale entered by the user was: <em>{scenario.changeRationale || "No rationale recorded."}</em>
            </p>

            {changes.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div className="small" style={{ fontWeight: 800 }}>Changed assumptions tested</div>
                <ul className="small" style={{ margin: "5px 0 0 18px" }}>
                  {changes.map((ch, idx) => (
                    <li key={idx}><strong>{ch.label}:</strong> {ch.before} → {ch.after}</li>
                  ))}
                </ul>
              </div>
            )}

            {movements.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="small" style={{ fontWeight: 800 }}>Main factor movements versus baseline</div>
                <ul className="small" style={{ margin: "5px 0 0 18px" }}>
                  {movements.map((m) => (
                    <li key={`${scenario.id}-${m.key}`}>
                      <strong>{m.label}:</strong> {m.before} → {m.after} ({formatScenarioDelta(m.delta)}). {m.supportive ? "Supportive movement under the TVAC methodology." : m.adverse ? "Adverse movement under the TVAC methodology." : "Neutral movement."}
                      {getScenarioFactorExplanation(scenario.result, m.key) && (
                        <span> {getScenarioFactorExplanation(scenario.result, m.key)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {risks.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="small" style={{ fontWeight: 800 }}>Risk items to read in the scenario report</div>
                <p className="muted small" style={{ margin: "4px 0 0", lineHeight: 1.45 }}>
                  The scenario report should be reviewed for these case-specific risk items: {risks.map((r) => `${r.name} (impact ${r.impact}, likelihood ${r.likelihood})`).join("; ")}.
                </p>
              </div>
            )}
          </div>
        );
      })}

      <h5 style={{ margin: "16px 0 6px" }}>How to use this comparison</h5>
      <p className="muted" style={{ margin: 0, lineHeight: 1.55 }}>
        Use the comparison to identify which assumptions deserve further professional validation. Do not read the highest TVAC profile as a recommendation to implement that scenario. For each assessed alternative, open the full scenario report below to review the detailed factor explanations, risk register, assumptions, and roadmap before drawing any internal conclusions.
      </p>
    </div>
  );
}

function getScenarioRobustnessAssessment(ctx, scenario) {
  if (!ctx || !scenario?.result) {
    return {
      label: "Not assessed",
      text: "This scenario has not yet been assessed under the TVAC methodology.",
    };
  }

  const baseline = ctx.baseline;
  const baseAv = getScenarioAddedValue(baseline?.result);
  const av = getScenarioAddedValue(scenario.result);
  const movements = scenario.id === "baseline" ? [] : getTopScenarioMovements(baseline?.result, scenario.result, 5);
  const adverse = movements.filter((m) => m.adverse);
  const supportive = movements.filter((m) => m.supportive);
  const verdict = getScenarioVerdictText(scenario.result);
  const delta = baseAv == null || av == null ? null : av - baseAv;
  const thresholdNote = getScenarioThresholdNote(scenario.result);

  if (scenario.id === "baseline") {
    return {
      label: "Reference case",
      text: `Scenario A is the reference point for this comparison. Its robustness should be read through the original factor breakdown, risk register, and stated assumptions rather than as a ranked alternative. ${thresholdNote}`.trim(),
    };
  }

  if (thresholdNote) {
    return {
      label: "Threshold-sensitive",
      text: `${thresholdNote} The comparison is therefore useful mainly for identifying which assumptions need independent validation before the scenario is treated as materially stronger or weaker than the baseline.`,
    };
  }

  if (delta != null && delta > 0 && adverse.length === 0) {
    return {
      label: "More robust than baseline",
      text: `${scenario.label} appears more robust than the baseline under the TVAC methodology because the overall Added Value improves by ${formatScenarioDelta(delta)} point(s) without any adverse factor movement among the assessed score changes. This is still an analytical comparison of assumptions, not an implementation conclusion.`,
    };
  }

  if (delta != null && delta > 0 && adverse.length > 0) {
    return {
      label: "Improved but assumption-dependent",
      text: `${scenario.label} improves the methodology-based profile by ${formatScenarioDelta(delta)} point(s), but the improvement is not clean: ${adverse.map((m) => m.label).join(" and ")} move(s) in an adverse direction. The scenario is therefore stronger only if the assumptions behind the supportive movements can be validated independently.`,
    };
  }

  if (delta != null && delta < 0 && supportive.length > 0) {
    return {
      label: "Weaker but with partial offsets",
      text: `${scenario.label} weakens the overall TVAC profile by ${formatScenarioDelta(delta)} point(s), although ${supportive.map((m) => m.label).join(" and ")} provide(s) partial offsetting support. This indicates a trade-off rather than a simple improvement path.`,
    };
  }

  if (delta != null && delta < 0) {
    return {
      label: "Less robust than baseline",
      text: `${scenario.label} is less robust than the baseline under the TVAC methodology because the Added Value profile deteriorates by ${formatScenarioDelta(delta)} point(s). The main use of the result is to identify which tested assumptions add cost, risk, or complexity without creating compensating value.`,
    };
  }

  if (delta === 0) {
    return {
      label: "Similar robustness to baseline",
      text: `${scenario.label} produces the same Added Value score as the baseline. The comparison is still useful because it shows whether the scenario changes the distribution of opportunity, cost, and risk even when the final score remains unchanged.`,
    };
  }

  return {
    label: verdict === "Viable" ? "Stronger methodology profile" : verdict === "Non-viable" ? "Weak methodology profile" : "Conditional methodology profile",
    text: `${scenario.label} produces a ${verdict} verdict under the TVAC methodology. The result should be read together with the full factor explanations and risk register before any internal conclusion is drawn.`,
  };
}

function getScenarioValidationGates(baseline, scenario) {
  const gates = [];
  const changes = scenario?.id === "baseline" ? [] : getScenarioChangedAssumptions(baseline?.payload, scenario?.payload);
  const movements = scenario?.id === "baseline" ? [] : getTopScenarioMovements(baseline?.result, scenario?.result, 5);
  const risks = getScenarioRiskNames(scenario?.result, 4);

  const changedText = changes.map((ch) => `${ch.label}: ${ch.after}`).join("; ");
  if (changedText) gates.push(`Validate the changed assumption set independently (${changedText}).`);

  if (movements.some((m) => m.key === "new_opportunities" || m.key === "cost_savings" || m.key === "risk_reduction")) {
    gates.push("Validate that the supportive value drivers are evidenced by market demand, operating data, provider capability, or other case-specific support.");
  }

  if (movements.some((m) => m.key === "tokenization_costs")) {
    gates.push("Validate the cost assumptions across legal, compliance, platform, custody, reporting, integration, and operating layers.");
  }

  if (movements.some((m) => m.key === "new_risks")) {
    gates.push("Validate the incremental risk assumptions, including regulatory interpretation, transfer controls, investor eligibility, custody, venue, and operational dependencies.");
  }

  if (risks.length) {
    gates.push(`Review the full scenario report with particular attention to: ${risks.map((r) => r.name).join("; ")}.`);
  }

  if (!gates.length && scenario?.id === "baseline") {
    gates.push("Use the baseline as the reference case and validate the assumptions already highlighted in the original TVAC report.");
  }

  if (!gates.length) {
    gates.push("Validate the scenario's legal, regulatory, technical, commercial, operating-model, and investor-demand assumptions with qualified professionals before treating it as a stronger or weaker case.");
  }

  return Array.from(new Set(gates)).slice(0, 5);
}

function getScenarioImplicationText(ctx, scenario) {
  if (!ctx || !scenario?.result) return "This scenario has not yet been assessed.";
  const baseline = ctx.baseline;
  const av = getScenarioAddedValue(scenario.result);
  const baseAv = getScenarioAddedValue(baseline?.result);
  const delta = scenario.id === "baseline" || baseAv == null || av == null ? null : av - baseAv;
  const verdict = getScenarioVerdictText(scenario.result);
  const driver = scenario.id === "baseline" ? "Baseline reference point." : getScenarioDriverText(baseline?.result, scenario.result);

  if (scenario.id === "baseline") {
    return `Scenario A remains the reference case. Its ${verdict} verdict and Added Value ${av ?? "—"} provide the comparison anchor for the two alternatives.`;
  }

  const direction = delta == null ? "changes the profile" : delta > 0 ? `strengthens the profile by ${formatScenarioDelta(delta)} point(s)` : delta < 0 ? `weakens the profile by ${formatScenarioDelta(delta)} point(s)` : "keeps the same Added Value score as the baseline";
  return `${scenario.label} ${direction} and lands at ${verdict}. ${driver} This is an analytical implication only; it does not select, approve, or recommend the scenario.`;
}

function ScenarioLabComparisonReport({ scenarios }) {
  const ctx = getScenarioComparisonContext(scenarios);
  if (!ctx) return null;

  const {
    baseline,
    assessed,
    alternatives,
    ranked,
    strongest,
    weakest,
    baselineAv,
    strongestAv,
    weakestAv,
    spread,
    baselineRank,
  } = ctx;

  const generated = new Date().toLocaleString();
  const factorRows = SCENARIO_FACTOR_META.map((meta) => ({
    ...meta,
    values: assessed.map((scenario) => Number(scenario?.result?.factor_scores?.[meta.key])),
  }));

  return (
    <div className="scenario-comparison-document">
      <section className="comparison-report-cover">
        <div className="cover-band" aria-hidden="true" />
        <div className="card cover-card" style={{ padding: "22px 22px 18px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
            <div>
              <div className="muted small">Tokenization Value‑Add Calculator</div>
              <h1 style={{ margin: "4px 0 4px", fontSize: 34, lineHeight: 1.08 }}>Scenario Lab Comparison Report</h1>
              <div className="muted">Baseline vs. two controlled alternative assumption sets.</div>
            </div>
            <div className="small muted" style={{ textAlign: "right" }}>
              <div><strong>Generated:</strong> {generated}</div>
              <div><strong>Scenarios assessed:</strong> {assessed.length}</div>
              <div><strong>Baseline:</strong> {getScenarioDisplayName(baseline, baseline.payload)}</div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16, padding: 12, background: "#fafafa" }}>
            <strong>Decision-support boundary</strong>
            <p className="small muted" style={{ margin: "6px 0 0", lineHeight: 1.55 }}>
              This Scenario Lab comparison is provided for informational and methodology-based decision-support purposes only. It does not constitute legal, regulatory, financial, investment, tax, technical, structuring, issuance, marketing, operational, or implementation advice.
            </p>
          </div>

          <h2 style={{ margin: "18px 0 8px" }}>1. Executive comparison</h2>
          <p style={{ lineHeight: 1.55, margin: 0 }}>
            {getScenarioDecisionFocus(ctx)} {getScenarioMaterialityText(spread)}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 16 }}>
            <div className="card" style={{ padding: 10 }}>
              <div className="muted small">Highest assessed profile</div>
              <div style={{ fontWeight: 800 }}>{getScenarioDisplayTitle(strongest, baseline.payload)}</div>
              <div className="small">AV {strongestAv ?? "—"} · {getScenarioVerdictText(strongest.result)}</div>
            </div>
            <div className="card" style={{ padding: 10 }}>
              <div className="muted small">Score range</div>
              <div style={{ fontWeight: 800 }}>{spread == null ? "—" : `${spread} point spread`}</div>
              <div className="small">{getScenarioDisplayTitle(weakest, baseline.payload)} ({weakestAv ?? "—"}) to {getScenarioDisplayTitle(strongest, baseline.payload)} ({strongestAv ?? "—"})</div>
            </div>
            <div className="card" style={{ padding: 10 }}>
              <div className="muted small">Baseline position</div>
              <div style={{ fontWeight: 800 }}>Rank {baselineRank} of {ranked.length}</div>
              <div className="small">Baseline AV {baselineAv ?? "—"}</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>2. Scenario overview</h2>
        <table className="glossary-grid">
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Role</th>
              <th>Short description</th>
              <th>Key changes from baseline</th>
            </tr>
          </thead>
          <tbody>
            {assessed.map((scenario) => {
              const changes = scenario.id === "baseline" ? [] : getScenarioChangedAssumptions(baseline.payload, scenario.payload);
              return (
                <tr key={`overview-${scenario.id}`}>
                  <td><strong>{scenario.label}</strong></td>
                  <td>{scenario.id === "baseline" ? "Baseline" : "Alternative"}</td>
                  <td>{getScenarioDisplayName(scenario, baseline.payload)}</td>
                  <td>{scenario.id === "baseline" ? "Original TVAC assessment." : changes.length ? changes.slice(0, 5).map((ch) => `${ch.label}: ${ch.before} → ${ch.after}`).join("; ") : "No material changed assumptions recorded."}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h2>3. Score and verdict movement</h2>
        <table className="glossary-grid">
          <thead>
            <tr>
              <th>Metric</th>
              {assessed.map((scenario) => <th key={`metric-head-${scenario.id}`}>{scenario.label}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Verdict</strong></td>
              {assessed.map((scenario) => <td key={`verdict-${scenario.id}`}>{getScenarioVerdictText(scenario.result)}</td>)}
            </tr>
            <tr>
              <td><strong>Added Value</strong></td>
              {assessed.map((scenario) => <td key={`av-${scenario.id}`}>{getScenarioAddedValue(scenario.result) ?? "—"}</td>)}
            </tr>
            <tr>
              <td><strong>Plus / Minus</strong></td>
              {assessed.map((scenario) => {
                const pm = getScenarioPlusMinus(scenario.result);
                return <td key={`pm-${scenario.id}`}>{pm.plus ?? "—"} / {pm.minus ?? "—"}</td>;
              })}
            </tr>
            {factorRows.map((row) => (
              <tr key={`factor-row-${row.key}`}>
                <td><strong>{row.short}</strong> — {row.label}</td>
                {assessed.map((scenario) => {
                  const value = Number(scenario?.result?.factor_scores?.[row.key]);
                  return <td key={`${row.key}-${scenario.id}`}>{Number.isFinite(value) ? value : "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small muted" style={{ lineHeight: 1.5 }}>
          Score movement is directional and methodology-based. It should be read together with the driver analysis and the full scenario reports, not as a stand-alone ranking.
        </p>
      </section>

      <section className="scenario-comparison-page-break">
        <h2>4. Case-specific driver analysis</h2>
        {alternatives.map((scenario) => {
          const movements = getTopScenarioMovements(baseline.result, scenario.result, 5);
          const av = getScenarioAddedValue(scenario.result);
          const baseAv = getScenarioAddedValue(baseline.result);
          const avDelta = baseAv == null || av == null ? null : av - baseAv;
          const changes = getScenarioChangedAssumptions(baseline.payload, scenario.payload);

          return (
            <div key={`driver-${scenario.id}`} style={{ marginTop: 16, paddingTop: 10, borderTop: "1px solid #ddd" }}>
              <h3>{getScenarioDisplayTitle(scenario, baseline.payload)}</h3>
              <p style={{ lineHeight: 1.55 }}>
                <strong>Scenario rationale:</strong> <em>{scenario.changeRationale || "No rationale recorded."}</em>
              </p>
              <p style={{ lineHeight: 1.55 }}>
                {getScenarioSpecificInterpretation(baseline, scenario)} Added Value movement versus baseline: {avDelta == null ? "—" : formatScenarioDelta(avDelta)} point(s).
              </p>
              {changes.length > 0 && (
                <p className="small muted" style={{ lineHeight: 1.5 }}>
                  <strong>Changed assumptions tested:</strong> {changes.slice(0, 6).map((ch) => `${ch.label}: ${ch.before} → ${ch.after}`).join("; ")}.
                </p>
              )}
              {movements.length > 0 && (
                <table className="glossary-grid">
                  <thead>
                    <tr>
                      <th>Factor</th>
                      <th>Movement</th>
                      <th>Analytical implication</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m) => (
                      <tr key={`driver-move-${scenario.id}-${m.key}`}>
                        <td>{m.label}</td>
                        <td>{m.before} → {m.after} ({formatScenarioDelta(m.delta)})</td>
                        <td>{m.supportive ? "Supportive movement under the TVAC methodology." : m.adverse ? "Adverse movement under the TVAC methodology." : "Neutral movement."} {getScenarioFactorExplanation(scenario.result, m.key)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </section>

      <section>
        <h2>5. Robustness assessment</h2>
        <table className="glossary-grid">
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Robustness reading</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {assessed.map((scenario) => {
              const robustness = getScenarioRobustnessAssessment(ctx, scenario);
              return (
                <tr key={`robustness-${scenario.id}`}>
                  <td><strong>{scenario.label}</strong><br /><span className="small muted">{getScenarioDisplayName(scenario, baseline.payload)}</span></td>
                  <td>{robustness.label}</td>
                  <td>{robustness.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h2>6. Scenario-by-scenario implications</h2>
        <ul>
          {assessed.map((scenario) => (
            <li key={`implication-${scenario.id}`} style={{ marginBottom: 8 }}>
              <strong>{getScenarioDisplayTitle(scenario, baseline.payload)}:</strong> {getScenarioImplicationText(ctx, scenario)}
            </li>
          ))}
        </ul>
      </section>

      <section className="scenario-comparison-page-break">
        <h2>7. Decision notes and validation gates</h2>
        <p style={{ lineHeight: 1.55 }}>
          These validation gates are analytical review points. They are not implementation instructions and do not replace professional legal, regulatory, tax, financial, technical, or commercial review.
        </p>
        {assessed.map((scenario) => (
          <div key={`gates-${scenario.id}`} style={{ marginTop: 14 }}>
            <h3>{getScenarioDisplayTitle(scenario, baseline.payload)}</h3>
            <ul>
              {getScenarioValidationGates(baseline, scenario).map((gate, idx) => (
                <li key={`gate-${scenario.id}-${idx}`}>{gate}</li>
              ))}
            </ul>
          </div>
        ))}

        <h2>8. Disclaimer</h2>
        <p className="muted" style={{ lineHeight: 1.55 }}>
          This Scenario Lab comparison is provided for informational and methodological decision-support purposes only. It does not constitute legal, financial, investment, tax, regulatory, technical, structuring, issuance, marketing, operational, or implementation advice. The comparison is based on the information and assumptions provided by the user and on the TVAC methodology. All assumptions, legal and regulatory issues, technical feasibility, commercial viability, investor demand, costs, risks, and implementation decisions should be independently validated by qualified professionals.
        </p>
      </section>
    </div>
  );
}



function openScenarioComparisonPrintDocument(sourceNode) {
  if (!sourceNode) {
    window.alert("The Scenario Lab comparison report is not ready for printing yet. Please open the comparison report and try again.");
    return;
  }

  // Use the inner report markup only. The source node itself is intentionally
  // positioned off-screen and transparent in the main app; copying outerHTML
  // would carry those hidden styles into the print window and produce blank pages.
  const html = sourceNode.innerHTML || "";
  const printWindow = window.open("", "tvac_scenario_comparison_report", "width=980,height=900");
  if (!printWindow) {
    window.alert("The print window was blocked by the browser. Please allow pop-ups for this site and try again.");
    return;
  }

  const printCss = `
    :root { --brand:#ff7a00; --text:#222; --muted:#667085; --line:#e5e7eb; }
    * { box-sizing:border-box; }
    html, body { margin:0; padding:0; background:#fff; color:var(--text); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; font-size:13px; line-height:1.42; }
    body { padding: 18mm 15mm; }
    .print-toolbar { max-width:178mm; margin:0 auto 14px; padding:10px 12px; border:1px solid #e5e7eb; border-radius:10px; background:#fff7f0; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .print-toolbar span { color:#667085; font-size:12px; flex:1 1 auto; }
    .print-toolbar button { border:1px solid #ff7a00; background:#ff7a00; color:white; border-radius:9px; padding:7px 10px; font-weight:700; cursor:pointer; }
    .print-toolbar button + button { background:#fff; color:#222; border-color:#e5e7eb; }
    .scenario-comparison-document { max-width: 178mm; margin: 0 auto; }
    .scenario-comparison-document section { margin-bottom: 12px; }
    .card { background:#fff; border:1px solid var(--line); border-radius:10px; padding:12px; box-shadow:none; }
    .cover-band { height:8px; border-radius:999px; background:linear-gradient(90deg, rgba(255,122,0,0.95), rgba(255,122,0,0.20)); margin-bottom:10px; }
    .muted { color: var(--muted); }
    .small { font-size: 0.86em; }
    h1 { font-size: 30px; line-height: 1.08; margin: 4px 0; }
    h2 { font-size: 19px; line-height: 1.2; margin: 16px 0 7px; }
    h3 { font-size: 15px; line-height: 1.25; margin: 14px 0 7px; }
    h4 { font-size: 13px; line-height: 1.25; margin: 12px 0 6px; }
    p { margin: 0 0 8px; line-height: 1.48; }
    ul { margin: 5px 0 8px 20px; padding: 0; }
    li { margin: 0 0 4px; }
    table.glossary-grid { width:100%; border-collapse:collapse; margin: 7px 0 10px; page-break-inside:auto; break-inside:auto; font-size:12px; }
    table.glossary-grid tr { page-break-inside: avoid; break-inside: avoid; }
    .glossary-grid th, .glossary-grid td { border:1px solid #e8e8e8; padding:7px 8px; vertical-align:top; text-align:left; }
    .glossary-grid th { background:#f7f7f7; font-weight:800; }
    .scenario-comparison-page-break { break-before: page; page-break-before: always; }
    @page { size:A4; margin: 14mm 12mm; }
    @media print {
      body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .print-toolbar { display:none !important; }
      .scenario-comparison-document { max-width: none; }
      .card { box-shadow:none !important; }
      h1,h2,h3,h4 { break-after: avoid-page; page-break-after: avoid; }
      p,li { orphans:3; widows:3; }
    }
  `;

  const toolbar = `
    <div class="print-toolbar">
      <strong>TVAC Scenario Lab Comparison Report</strong>
      <span>This window contains only the comparison mini-report.</span>
      <button type="button" onclick="window.print()">Print / save PDF</button>
      <button type="button" onclick="window.close()">Close</button>
    </div>
  `;

  printWindow.document.open();
  printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>TVAC Scenario Lab Comparison Report</title><style>${printCss}</style></head><body>${toolbar}<main>${html}</main><script>window.onload=function(){setTimeout(function(){window.focus();window.print();},220);};<\/script></body></html>`);
  printWindow.document.close();
}


function ScenarioLabField({ field, scenario, baselinePayload, onChange }) {
  const currentValue = scenario?.payload?.[field.key];
  const baselineValue = baselinePayload?.[field.key];
  const changed = normalizeScenarioValue(currentValue) !== normalizeScenarioValue(baselineValue);

  if (field.type === "select") {
    return (
      <div>
        <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>{field.label}</label>
        <select
          value={normalizeScenarioValue(currentValue)}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={scenario.locked || scenario.status === "running"}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 0, background: "#fff" }}
        >
          {field.options.map(([value, label]) => (
            <option key={value || "empty"} value={value}>{label}</option>
          ))}
        </select>
        {changed && <div className="muted small" style={{ marginTop: 4 }}>Changed from baseline: {normalizeScenarioValue(baselineValue) || "—"}</div>}
      </div>
    );
  }

  return (
    <div>
      <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>{field.label}</label>
      <input
        value={normalizeScenarioValue(currentValue)}
        onChange={(e) => onChange(field.key, e.target.value)}
        disabled={scenario.locked || scenario.status === "running"}
        placeholder={field.key === "saleJurisdictions" ? "e.g., Germany, Netherlands" : ""}
        style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 0, background: "#fff" }}
      />
      {changed && <div className="muted small" style={{ marginTop: 4 }}>Changed from baseline: {normalizeScenarioValue(baselineValue) || "—"}</div>}
    </div>
  );
}

function ScenarioInvestorTypesEditor({ scenario, baselinePayload, onChange }) {
  const investorTypes = scenario?.payload?.investorTypes || {};
  const baseline = baselinePayload?.investorTypes || {};
  const changed = normalizeScenarioValue(investorTypes) !== normalizeScenarioValue(baseline);
  const setInvestor = (key, checked) => {
    onChange("investorTypes", {
      ...investorTypes,
      [key]: checked,
    });
  };

  return (
    <div>
      <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Investor types</label>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {["Professional", "Accredited", "Retail"].map((key) => (
          <label key={key} className="small" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={Boolean(investorTypes[key])}
              onChange={(e) => setInvestor(key, e.target.checked)}
              disabled={scenario.locked || scenario.status === "running"}
            />
            {key}
          </label>
        ))}
      </div>
      {changed && <div className="muted small" style={{ marginTop: 4 }}>Changed from baseline: {normalizeScenarioValue(baseline) || "—"}</div>}
    </div>
  );
}

function ScenarioLabPanel({
  scenarios,
  isOpen,
  error,
  runningId,
  activeReportScenarioId,
  onOpen,
  onClose,
  onReset,
  onScenarioMetaChange,
  onScenarioPayloadChange,
  onRunScenario,
  onViewScenarioReport,
  getScenarioAssessmentSignatureForUi,
  showInlineCta = true,
}) {
  const [showComparisonReportPreview, setShowComparisonReportPreview] = useState(false);
  const [replacementOpenById, setReplacementOpenById] = useState({});
  const comparisonReportRef = useRef(null);
  const previousAssessmentSignatureRef = useRef({});

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const previousSignatures = previousAssessmentSignatureRef.current || {};
    const nextSignatures = {};
    let nextReplacementOpenById = null;

    (scenarios || []).forEach((scenario) => {
      const signature = scenario?.lastAssessmentSignature || "";
      nextSignatures[scenario.id] = signature;

      if (
        replacementOpenById?.[scenario.id] &&
        signature &&
        signature !== previousSignatures[scenario.id] &&
        scenario.result &&
        scenario.status === "complete"
      ) {
        nextReplacementOpenById = nextReplacementOpenById || { ...replacementOpenById };
        delete nextReplacementOpenById[scenario.id];
      }
    });

    previousAssessmentSignatureRef.current = nextSignatures;

    if (!scenarios || scenarios.length === 0) {
      if (Object.keys(replacementOpenById || {}).length > 0) {
        setReplacementOpenById({});
      }
      return;
    }

    if (nextReplacementOpenById) {
      setReplacementOpenById(nextReplacementOpenById);
    }
  }, [scenarios, replacementOpenById]);

  if (!scenarios || scenarios.length === 0) {
    if (!showInlineCta) return null;
    return (
      <div className="card no-print scenario-lab-cta-card">
        <div className="scenario-lab-cta-grid">
          <div>
            <div className="scenario-lab-kicker">Optional analytical extension</div>
            <h3 style={{ margin: "2px 0 6px" }}>TVAC Scenario Lab</h3>
            <p className="muted small" style={{ margin: 0, lineHeight: 1.5 }}>
              Open a separate Scenario Lab workspace to compare this completed baseline with two controlled alternative assumption sets. The ordinary report remains clean and shareable.
            </p>
          </div>
          <div className="scenario-lab-cta-metrics" aria-label="Scenario Lab status">
            <div className="scenario-lab-status-pill">Scenario A: current baseline</div>
            <div className="scenario-lab-status-pill">Alternatives: B and C</div>
            <div className="scenario-lab-status-pill">No live optimization</div>
          </div>
          <div className="scenario-lab-cta-actions">
            <button className="btn" type="button" onClick={onOpen}>
              Open Scenario Lab
            </button>
          </div>
        </div>
      </div>
    );
  }

  const baseline = scenarios.find((s) => s.id === "baseline") || scenarios[0];
  const completed = scenarios.filter((s) => s.result && s.status === "complete");
  const hasComparisonReport = baseline?.result && completed.length >= 2;
  const strongest = completed.length
    ? [...completed].sort((a, b) => (getScenarioAddedValue(b.result) ?? -999) - (getScenarioAddedValue(a.result) ?? -999))[0]
    : null;
  const printScenarioComparisonReport = () => {
    if (!hasComparisonReport) return;
    openScenarioComparisonPrintDocument(comparisonReportRef.current);
  };

  return (
    <>
      {showInlineCta && (
      <div className="card no-print scenario-lab-cta-card scenario-lab-cta-card--active">
        <div className="scenario-lab-cta-grid">
          <div>
            <div className="scenario-lab-kicker">Controlled scenario comparison</div>
            <h3 style={{ margin: "2px 0 6px" }}>TVAC Scenario Lab</h3>
            <p className="muted small" style={{ margin: 0, lineHeight: 1.5 }}>
              Continue the Scenario Lab analysis in a dedicated workspace. The baseline report below stays visually separate and remains clean for internal sharing.
            </p>
          </div>
          <div className="scenario-lab-cta-metrics" aria-label="Scenario Lab status">
            <div className="scenario-lab-status-pill">Baseline: {getScenarioDisplayName(baseline, baseline.payload)}</div>
            <div className="scenario-lab-status-pill">Assessed scenarios: {completed.length}/3</div>
            {strongest && (
              <div className="scenario-lab-status-pill">Highest profile: {getScenarioDisplayTitle(strongest, baseline.payload)}</div>
            )}
          </div>
          <div className="scenario-lab-cta-actions">
            <button className="btn" type="button" onClick={onOpen}>
              Open Scenario Lab
            </button>
            <button className="action" type="button" onClick={onReset}>
              Reset Scenario Lab
            </button>
          </div>
        </div>
      </div>
      )}

      {isOpen && (
        <div
          className="no-print scenario-lab-workspace-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="TVAC Scenario Lab workspace"
        >
          <div className="scenario-lab-workspace-shell">
            <div className="scenario-lab-workspace-header">
              <div>
                <div className="scenario-lab-kicker">Controlled scenario comparison</div>
                <h2 style={{ margin: "2px 0 4px", fontSize: 26 }}>TVAC Scenario Lab</h2>
                <p className="muted small" style={{ margin: 0, lineHeight: 1.45 }}>
                  Compare Scenario A with two controlled alternatives under the same TVAC methodology. No live optimization, no implementation advice.
                </p>
              </div>
              <div className="scenario-lab-workspace-header-actions">
                <button className="action" type="button" onClick={onReset}>Reset Scenario Lab</button>
                <button className="btn" type="button" onClick={onClose}>Close workspace</button>
              </div>
            </div>

            <div className="scenario-lab-workspace-body">
              <div className="muted small scenario-lab-boundary-note">
                <strong>Important:</strong> Scenario Lab compares user-defined assumptions under the TVAC methodology. It does not design, recommend, approve, validate, certify, or advise on any legal, regulatory, tax, financial, investment, technical, structuring, or implementation decision. Any decision to proceed must be validated by qualified professional advisers.
              </div>

              <div className="scenario-lab-workspace-summary-row" aria-label="Scenario Lab workspace summary">
                <div className="scenario-lab-summary-tile">
                  <span>Baseline</span>
                  <strong>{getScenarioDisplayName(baseline, baseline.payload)}</strong>
                </div>
                <div className="scenario-lab-summary-tile">
                  <span>Assessment scope</span>
                  <strong>{completed.length}/3 assessed</strong>
                </div>
                <div className="scenario-lab-summary-tile">
                  <span>Current high profile</span>
                  <strong>{strongest ? getScenarioDisplayTitle(strongest, baseline.payload) : "Pending alternatives"}</strong>
                </div>
              </div>

              {error && (
                <div className="card" style={{ marginTop: 12, borderColor: "var(--bad)", color: "var(--bad)", padding: 10 }}>
                  {error}
                </div>
              )}

          <div className="scenario-lab-workspace-grid" style={{ marginTop: 12 }}>
            {scenarios.map((scenario) => {
              const changes = getScenarioChangedAssumptions(baseline.payload, scenario.payload);
              const av = getScenarioAddedValue(scenario.result);
              const pm = getScenarioPlusMinus(scenario.result);
              const isActiveReport = scenario.id === activeReportScenarioId;
              const displayName = getScenarioDisplayName(scenario, baseline.payload);
              const runningScenarioId =
                runningId || scenarios.find((s) => !s.locked && s.status === "running")?.id || "";
              const isThisScenarioRunning = scenario.status === "running" || runningScenarioId === scenario.id;
              const isOtherScenarioRunning = Boolean(runningScenarioId) && runningScenarioId !== scenario.id;
              const scenarioAssessmentSignature = !scenario.locked
                ? getScenarioAssessmentSignatureForUi?.(scenario) || ""
                : "";
              const scenarioAssessmentCurrent = Boolean(
                !scenario.locked &&
                  scenario.result &&
                  scenario.lastAssessmentSignature &&
                  scenario.lastAssessmentSignature === scenarioAssessmentSignature &&
                  scenario.status === "complete"
              );
              const replacementOpen = Boolean(replacementOpenById?.[scenario.id]);
              const scenarioReplacementCount = Number(scenario.replacementCount || 0);
              const scenarioReplacementUsed = scenarioReplacementCount >= 1;
              const scenarioCanReplace = Boolean(
                !scenario.locked &&
                  scenario.result &&
                  scenario.status === "complete" &&
                  !replacementOpen &&
                  !scenarioReplacementUsed
              );
              const scenarioLockedAfterReplacement = Boolean(
                !scenario.locked &&
                  scenarioReplacementUsed &&
                  scenario.result &&
                  scenario.status === "complete"
              );
              const scenarioReplacementInProgress = Boolean(
                !scenario.locked &&
                  scenarioReplacementUsed &&
                  !scenario.result
              );
              const scenarioIsReadOnly = Boolean(
                !scenario.locked &&
                  scenario.result &&
                  scenario.status === "complete" &&
                  !replacementOpen
              );
              return (
                <div key={scenario.id} className={`card scenario-lab-card ${scenario.locked ? "scenario-lab-card--baseline" : "scenario-lab-card--alternative"}`} style={{ padding: 12, borderColor: scenario.locked ? "rgba(0,0,0,0.10)" : "rgba(255,122,0,0.35)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                      <div className="muted small">{scenario.label}</div>
                      <input
                        value={scenario.name || ""}
                        aria-label={`${scenario.label} name: ${displayName}`}
                        placeholder={displayName}
                        onChange={(e) => onScenarioMetaChange(scenario.id, "name", e.target.value)}
                        disabled={scenario.locked || scenario.status === "running" || scenarioIsReadOnly}
                        style={{ width: "100%", marginTop: 4, padding: "7px 8px", border: "1px solid #d1d5db", borderRadius: 8, fontWeight: 700 }}
                      />
                    </div>
                    {scenario.result ? <Badge verdict={scenario.result.verdict} /> : <span className="badge badge-borderline">Draft</span>}
                  </div>

                  {scenario.result ? (
                    <div style={{ marginTop: 10 }}>
                      <div><strong>Added Value:</strong> {av == null ? "—" : av}</div>
                      <div className="muted small" style={{ marginTop: 3 }}>
                        Plus: {pm.plus ?? "—"} · Minus: {pm.minus ?? "—"}
                      </div>
                      <div className="muted small" style={{ marginTop: 6 }}>{getScenarioDriverText(baseline.result, scenario.result)}</div>
                      <div className="scenario-lab-card-actions" style={{ marginTop: 9 }}>
                        <button
                          className="action"
                          type="button"
                          onClick={() => onViewScenarioReport(scenario.id)}
                          disabled={!scenario.result}
                        >
                          {isActiveReport ? "Report shown below" : scenario.id === "baseline" ? "View baseline report" : `View full ${scenario.label} report`}
                        </button>
                        {scenarioCanReplace && (
                          <button
                            className="action scenario-lab-replace-action"
                            type="button"
                            onClick={() =>
                              setReplacementOpenById((prev) => ({
                                ...(prev || {}),
                                [scenario.id]: true,
                              }))
                            }
                          >
                            Edit / replace {scenario.label}
                          </button>
                        )}
                      </div>
                      {scenarioLockedAfterReplacement && (
                        <div className="scenario-lab-locked-note">
                          <strong>Scenario locked.</strong> One controlled replacement has been used. Reset Scenario Lab to define a new alternative.
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="muted small" style={{ marginTop: 10, lineHeight: 1.4 }}>
                      Not assessed yet. Scores appear only after this scenario is deliberately run through the full TVAC assessment engine.
                    </p>
                  )}

                  {!scenario.locked && !scenario.result && (
                    <>
                      {scenarioReplacementInProgress && (
                        <div className="scenario-lab-replacement-limit-note">
                          <strong>Replacement in progress:</strong> this is the final controlled replacement for {scenario.label}. After assessment, the scenario will be locked unless Scenario Lab is reset.
                        </div>
                      )}
                      <div style={{ marginTop: 12 }}>
                        <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Scenario rationale</label>
                        <textarea
                          value={scenario.changeRationale || ""}
                          onChange={(e) => onScenarioMetaChange(scenario.id, "changeRationale", e.target.value)}
                          disabled={scenario.status === "running"}
                          placeholder="Why are these assumptions being tested?"
                          style={{ width: "100%", minHeight: 70, padding: 8, border: "1px solid #d1d5db", borderRadius: 8 }}
                        />
                      </div>

                      <details style={{ marginTop: 10 }} open>
                        <summary style={{ cursor: "pointer", fontWeight: 700 }}>Edit selected assumptions</summary>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 10 }}>
                          {SCENARIO_LAB_FIELDS.map((field) => (
                            <ScenarioLabField
                              key={field.key}
                              field={field}
                              scenario={scenario}
                              baselinePayload={baseline.payload}
                              onChange={(key, value) => onScenarioPayloadChange(scenario.id, key, value)}
                            />
                          ))}
                          <ScenarioInvestorTypesEditor
                            scenario={scenario}
                            baselinePayload={baseline.payload}
                            onChange={(key, value) => onScenarioPayloadChange(scenario.id, key, value)}
                          />
                        </div>
                      </details>
                    </>
                  )}

                  {!scenario.locked && scenario.result && replacementOpen && (
                    <details className="scenario-lab-replace-panel" style={{ marginTop: 10 }} open>
                      <summary style={{ cursor: "pointer", fontWeight: 700 }}>Replace assessed scenario</summary>
                      <div className="scenario-lab-replace-note">
                        <strong>Controlled replacement:</strong> Editing this scenario will replace the current {scenario.label} assessment. Each alternative can be replaced once only. After the replacement assessment is completed, this scenario will be locked unless Scenario Lab is reset.
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Scenario rationale</label>
                        <textarea
                          value={scenario.changeRationale || ""}
                          onChange={(e) => onScenarioMetaChange(scenario.id, "changeRationale", e.target.value)}
                          disabled={scenario.status === "running"}
                          placeholder="Why are these assumptions being tested?"
                          style={{ width: "100%", minHeight: 70, padding: 8, border: "1px solid #d1d5db", borderRadius: 8 }}
                        />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 10 }}>
                        {SCENARIO_LAB_FIELDS.map((field) => (
                          <ScenarioLabField
                            key={field.key}
                            field={field}
                            scenario={scenario}
                            baselinePayload={baseline.payload}
                            onChange={(key, value) => onScenarioPayloadChange(scenario.id, key, value)}
                          />
                        ))}
                        <ScenarioInvestorTypesEditor
                          scenario={scenario}
                          baselinePayload={baseline.payload}
                          onChange={(key, value) => onScenarioPayloadChange(scenario.id, key, value)}
                        />
                      </div>
                      <button
                        className="action"
                        type="button"
                        onClick={() =>
                          setReplacementOpenById((prev) => {
                            const next = { ...(prev || {}) };
                            delete next[scenario.id];
                            return next;
                          })
                        }
                        style={{ marginTop: 10 }}
                      >
                        Cancel replacement
                      </button>
                    </details>
                  )}

                  {changes.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div className="muted small" style={{ fontWeight: 700 }}>Changed assumptions</div>
                      <ul className="small" style={{ margin: "5px 0 0 18px" }}>
                        {changes.slice(0, 6).map((ch, idx) => (
                          <li key={idx}><strong>{ch.label}:</strong> {ch.before} → {ch.after}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!scenario.locked && (
                    <button
                      className={`btn scenario-lab-run-btn ${isThisScenarioRunning ? "scenario-lab-run-btn--running" : ""} ${isOtherScenarioRunning ? "scenario-lab-run-btn--waiting" : ""} ${scenarioAssessmentCurrent ? "scenario-lab-run-btn--current" : ""}`}
                      type="button"
                      onClick={() => onRunScenario(scenario.id)}
                      disabled={isThisScenarioRunning || isOtherScenarioRunning || scenarioAssessmentCurrent}
                      style={{
                        marginTop: 12,
                        ...(scenarioAssessmentCurrent
                          ? {
                              background: "#f3f4f6",
                              borderColor: "#d1d5db",
                              color: "#4b5563",
                              cursor: "default",
                              opacity: 1,
                            }
                          : {}),
                      }}
                      title={scenarioAssessmentCurrent ? "This assessment already reflects the current scenario assumptions." : undefined}
                    >
                      {isThisScenarioRunning
                        ? "Running assessment…"
                        : isOtherScenarioRunning
                        ? "Waiting for other scenario…"
                        : scenarioAssessmentCurrent
                        ? "Assessment current"
                        : scenario.result
                        ? "Re-assess this scenario"
                        : "Assess this scenario"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="muted small" style={{ marginTop: 10 }}>
            This Scenario Lab view is intentionally limited to one baseline and two alternatives to preserve clarity and avoid information overload. Full reports are available on demand for each assessed scenario.
          </div>

          {completed.length > 0 && (
            <div className="card" style={{ marginTop: 12, padding: 12 }}>
              <h4 style={{ margin: "0 0 8px" }}>Scenario comparison</h4>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                <span className="muted small"><strong>View scenario report:</strong></span>
                <span className="muted small scenario-lab-report-switch-note">Opens below and closes this workspace.</span>
                {completed.map((scenario) => (
                  <button
                    key={`selector-${scenario.id}`}
                    className={scenario.id === activeReportScenarioId ? "btn" : "action"}
                    type="button"
                    onClick={() => onViewScenarioReport(scenario.id)}
                  >
                    {scenario.id === "baseline" ? "View baseline report" : `View full ${scenario.label} report`}
                  </button>
                ))}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="glossary-grid" style={{ minWidth: 840 }}>
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th>Verdict</th>
                      <th>Added Value</th>
                      <th>Plus / Minus</th>
                      <th>Main movement</th>
                      <th>Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completed.map((scenario) => {
                      const av = getScenarioAddedValue(scenario.result);
                      const pm = getScenarioPlusMinus(scenario.result);
                      return (
                        <tr key={`row-${scenario.id}`}>
                          <td><strong>{scenario.label}</strong><br /><span className="muted small">{getScenarioDisplayName(scenario, baseline.payload)}</span></td>
                          <td><Badge verdict={scenario.result.verdict} /></td>
                          <td>{av == null ? "—" : av}</td>
                          <td>{pm.plus ?? "—"} / {pm.minus ?? "—"}</td>
                          <td>{getScenarioDriverText(baseline.result, scenario.result)}</td>
                          <td>
                            <button className="action" type="button" onClick={() => onViewScenarioReport(scenario.id)}>
                              {scenario.id === activeReportScenarioId ? "Currently viewing" : "Open report"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {strongest && (
                <p className="muted" style={{ marginTop: 10, lineHeight: 1.45 }}>
                  <strong>Highest TVAC profile among assessed scenarios:</strong> {getScenarioDisplayTitle(strongest, baseline.payload)}. This is a methodology-based comparison of user-defined assumptions, not a recommendation to implement this structure.
                </p>
              )}

              {hasComparisonReport && (
                <div className="card" style={{ marginTop: 12, padding: 12, background: "rgba(255,122,0,0.045)", borderColor: "rgba(255,122,0,0.28)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ maxWidth: 720 }}>
                      <div style={{ fontWeight: 800 }}>Scenario Lab Comparison Report</div>
                      <p className="muted small" style={{ margin: "4px 0 0", lineHeight: 1.45 }}>
                        Preview or print the separate 3–5 page comparison mini-report with executive comparison, score movement, driver analysis, robustness assessment, validation gates, and disclaimer. It is separate from the individual scenario reports and intended for internal decision review.
                      </p>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="action"
                        type="button"
                        onClick={() => setShowComparisonReportPreview(true)}
                      >
                        Preview comparison report
                      </button>
                      <button className="btn" type="button" onClick={printScenarioComparisonReport}>
                        Print / save comparison report
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <details className="scenario-lab-analyst-notes">
                <summary>Analyst notes: detailed on-screen interpretation</summary>
                <p className="muted small" style={{ margin: "8px 0 10px", lineHeight: 1.5 }}>
                  This expandable area is a working interpretation view for on-screen review. The formal output remains the separate Scenario Lab Comparison Report.
                </p>
                <ScenarioComparisonAnalysis scenarios={scenarios} />
              </details>

              {hasComparisonReport && (
                <div
                  ref={comparisonReportRef}
                  className="scenario-comparison-print-source"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-10000px", top: 0, width: 920, pointerEvents: "none", opacity: 0 }}
                >
                  <ScenarioLabComparisonReport scenarios={scenarios} />
                </div>
              )}
            </div>
          )}

            </div>
          </div>
        </div>
      )}

      {showComparisonReportPreview && hasComparisonReport && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Scenario Lab Comparison Report"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            background: "rgba(5, 8, 22, 0.72)",
            padding: "28px 18px",
            overflow: "auto",
          }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto", background: "#fff", borderRadius: 14, boxShadow: "0 24px 70px rgba(0,0,0,0.28)", overflow: "hidden" }}>
            <div style={{ position: "sticky", top: 0, zIndex: 1, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--line)", background: "#fff" }}>
              <div>
                <div style={{ fontWeight: 900 }}>Scenario Lab Comparison Report</div>
                <div className="muted small">Separate decision-support mini-report. It does not replace the baseline or full scenario reports.</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn" type="button" onClick={printScenarioComparisonReport}>Print / save report</button>
                <button className="action" type="button" onClick={() => setShowComparisonReportPreview(false)}>Close</button>
              </div>
            </div>
            <div style={{ padding: "16px 18px 24px" }}>
              <ScenarioLabComparisonReport scenarios={scenarios} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ScenarioLabAddOnCard({ onCheckout, loading = false, error = "", labels, placement = "later" }) {
  const [showAddonDetails, setShowAddonDetails] = useState(false);
  const isNextStep = placement === "next-step";
  const cardClass = isNextStep
    ? "scenario-lab-next-step-card card no-print"
    : "scenario-lab-later-card card no-print";

  return (
    <>
      <div className={cardClass}>
        <div className="scenario-lab-card-copy">
          {isNextStep && <div className="scenario-lab-card-kicker">{labels.nextStepKicker}</div>}
          <h3>{isNextStep ? labels.nextStepTitle : labels.laterTitle}</h3>
          <p className="muted small">
            {isNextStep ? labels.nextStepBody : labels.laterBody}
          </p>
          {isNextStep && <div className="scenario-lab-card-price">{labels.priceDetail}</div>}
        </div>
        <div className="scenario-lab-card-actions">
          <button
            className={isNextStep ? "btn" : "action"}
            type="button"
            onClick={() => setShowAddonDetails(true)}
            disabled={loading}
          >
            {labels.exploreCta}
          </button>
        </div>
      </div>

      {showAddonDetails && (
        <div className="scenario-lab-addon-overlay no-print" role="dialog" aria-modal="true" aria-label={labels.modalTitle}>
          <div className="scenario-lab-addon-modal">
            <div className="scenario-lab-addon-header">
              <div>
                <div className="scenario-lab-addon-kicker">{labels.modalKicker}</div>
                <h3>{labels.modalTitle}</h3>
                <p className="muted small">{labels.modalLead}</p>
              </div>
              <button className="action" type="button" onClick={() => setShowAddonDetails(false)} disabled={loading}>
                {labels.close}
              </button>
            </div>

            <div className="scenario-lab-addon-body">
              <div className="scenario-lab-addon-baseline">
                <span>{labels.baselineLabel}</span>
                <strong>{labels.baselineValue}</strong>
              </div>

              <div className="scenario-lab-addon-included">
                <strong>{labels.included}</strong>
                <ul>
                  <li>{labels.includedAlternativeScenarios}</li>
                  <li>{labels.includedReplacement}</li>
                  <li>{labels.includedComparisonReport}</li>
                </ul>
              </div>

              <div className="scenario-lab-addon-price-row">
                <span>{labels.priceLabel}</span>
                <strong>{labels.priceValue}</strong>
              </div>

              <p className="muted small">{labels.boundary}</p>

              {error && <div className="scenario-lab-addon-error">{error}</div>}
            </div>

            <div className="scenario-lab-addon-actions">
              <button className="action" type="button" onClick={() => setShowAddonDetails(false)} disabled={loading}>
                {labels.notNow}
              </button>
              <button className="btn" type="button" onClick={onCheckout} disabled={loading}>
                {loading ? labels.openingCheckout : labels.checkoutCta}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ScenarioLabAvailableCard({ onOpen, hasScenarios = false, labels, placement = "later" }) {
  const isNextStep = placement === "next-step";
  const cardClass = isNextStep
    ? "scenario-lab-next-step-card card no-print"
    : "scenario-lab-later-card card no-print";

  return (
    <div className={cardClass}>
      <div className="scenario-lab-card-copy">
        {isNextStep && <div className="scenario-lab-card-kicker">{labels.nextStepKicker}</div>}
        <h3>{labels.availableTitle}</h3>
        <p className="muted small">{labels.availableBody}</p>
      </div>
      <div className="scenario-lab-card-actions">
        <button className={isNextStep ? "btn" : "action"} type="button" onClick={onOpen}>
          {hasScenarios ? labels.openWorkspaceCta : labels.openScenarioLabCta}
        </button>
      </div>
    </div>
  );
}

function AnalyzerView({ apiReportRenderToken = "", apiReportRenderMode = false, uiLanguageCode = "en" } = {}) {
  const t = (key, vars = {}) => uiText(uiLanguageCode, key, vars);
  const [inputText, setInputText] = useState("");
  const [showExamples, setShowExamples] = useState(false);
  const [loadedExampleCase, setLoadedExampleCase] = useState(null);
  const [showExampleRunWarning, setShowExampleRunWarning] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showFrontDisclaimer, setShowFrontDisclaimer] = useState(false);
  // Start fail-closed on English. Staff-only language access is verified asynchronously below.
  const [reportLanguageCode, setReportLanguageCode] = useState("en");
  const [internalReportLanguageAccess, setInternalReportLanguageAccess] = useState(false);
  const [reportBranding, setReportBranding] = useState(() => normalizeReportBrandingForDisplay({}));
  const activeReportBranding = reportBranding || normalizeReportBrandingForDisplay({});
  const reportLabels = useMemo(() => polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode), [reportLanguageCode]);
  const scenarioLabLabels = useMemo(() => getScenarioLabUiLabels(reportLanguageCode), [reportLanguageCode]);
  const languageReferenceNote = useMemo(() => getReportLanguageReferenceNote(reportLanguageCode), [reportLanguageCode]);

 // --- Estimated progress for deep assessment (felt %; avoids 0/100 jumps) ---
  const EXPECTED_SECONDS = 200; // adjust if needed
  const rawPct = Math.floor((elapsedSeconds / EXPECTED_SECONDS) * 100);
  const progressPct = loading ? Math.min(Math.max(rawPct, 2), 95) : 0;
  
  const [err, setErr] = useState("");
  const [out, setOut] = useState(null);
  const [masterAssessment, setMasterAssessment] = useState(null);
  const [localizing, setLocalizing] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  useEffect(() => {
    if (apiReportRenderMode) return undefined;

    const email = getStoredTvacAccessEmail();
    if (!email) {
      setInternalReportLanguageAccess(false);
      return undefined;
    }

    let cancelled = false;
    fetch(`${TVAC_API_BASE}/api/access/check`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!cancelled) {
          setInternalReportLanguageAccess(Boolean(response.ok && data?.allowed && data?.source === "staff"));
        }
      })
      .catch(() => {
        if (!cancelled) setInternalReportLanguageAccess(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiReportRenderMode]);

  useEffect(() => {
    if (apiReportRenderMode || out || loading || localizing) return;
    const preferredCode = normalizeReportLanguageCode(uiLanguageCode || "en");
    setReportLanguageCode(
      reportLanguageIsAvailable(preferredCode, internalReportLanguageAccess) ? preferredCode : "en"
    );
  }, [uiLanguageCode, apiReportRenderMode, out, loading, localizing, internalReportLanguageAccess]);

  useEffect(() => {
    const activeLanguageCode = out ? reportLanguageCode : uiLanguageCode;
    const langAttr = activeLanguageCode === "zh-Hans" ? "zh-Hans" : String(activeLanguageCode || "en");
    document.documentElement.setAttribute("lang", langAttr);

    const localizedTitle = out
      ? `${reportLabels.coverTitle || "TVAC Report"} — TVAC`
      : "Tokenization Value-Add Calculator";

    document.title = localizedTitle;

    return () => {
      document.documentElement.setAttribute("lang", "en");
      document.title = "Tokenization Value-Add Calculator";
    };
  }, [out, reportLanguageCode, reportLabels.coverTitle, uiLanguageCode]);

  useEffect(() => {
  const footer = document.getElementById("site-contact-footer");
  if (!footer) return;

  footer.style.display = out ? "none" : "block";

  return () => {
    footer.style.display = "block";
  };
}, [out]);

  // NEW: gem payload brugt til den senest kørte analyse, så stakeholder-afsnit matcher præcist
  const [lastPayload, setLastPayload] = useState(null);
  const hasSettlementCashLegReadiness =
    Array.isArray(out?.deep_assessment?.settlement_cash_leg_readiness) &&
    out.deep_assessment.settlement_cash_leg_readiness.length > 0;

  // Scenario Lab v1: controlled scenario comparison, no live score sliders.
  // Active access must eventually come from backend entitlements. For development/testing,
  // add ?scenarioLab=dev to the URL or set localStorage.tvac_scenario_lab_dev = "true".
  const [scenarioLabOpen, setScenarioLabOpen] = useState(false);
  const [scenarioLabScenarios, setScenarioLabScenarios] = useState([]);
  const [scenarioLabError, setScenarioLabError] = useState("");
  const [scenarioLabRunningId, setScenarioLabRunningId] = useState("");
  const [scenarioLabActiveReportId, setScenarioLabActiveReportId] = useState("");
  const [scenarioLabPaidAccess, setScenarioLabPaidAccess] = useState(false);
  const [scenarioLabAccessScope, setScenarioLabAccessScope] = useState("");
  const [scenarioLabAccessBaselineId, setScenarioLabAccessBaselineId] = useState("");
  const [scenarioLabCheckoutLoading, setScenarioLabCheckoutLoading] = useState(false);
  const [scenarioLabCheckoutError, setScenarioLabCheckoutError] = useState("");

  const timerRef = useRef(null);
  const masterAssessmentRef = useRef(null);
  const baselinePayloadRef = useRef(null);
  const localizationSeqRef = useRef(0);
  const localizedReportCacheRef = useRef(new Map());
  const localizationRepairKeyRef = useRef("");

  const refsMap = {
    caseText: useRef(null),
    issuanceJurisdiction: useRef(null),
    saleJurisdictions: useRef(null),
    investorTypes: useRef(null),
    instrumentType: useRef(null),
    custodyModel: useRef(null),
    liquidityCadence: useRef(null),
    venueType: useRef(null),
    settlementModel: useRef(null),
    transferRestrictions: useRef(null),
    disclosure: useRef(null),
    taxNotes: useRef(null),
  };

  useEffect(() => {
    if (!apiReportRenderMode) return undefined;

    let cancelled = false;
    document.body.classList.add("tvac-api-report-render");
    window.__TVAC_REPORT_READY__ = false;
    window.__TVAC_REPORT_ERROR__ = "";

    const token = String(apiReportRenderToken || "").trim();
    if (!token) {
      const message = "Missing API report render token.";
      setErr(message);
      window.__TVAC_REPORT_ERROR__ = message;
      return () => {
        document.body.classList.remove("tvac-api-report-render");
      };
    }

    async function loadApiReportRenderJob() {
      setLoading(true);
      setErr("");
      try {
        const job = await fetchApiReportRenderJob(token);
        if (cancelled) return;

        const assessment = job.assessment || job.result || job.report || null;
        const payload = job.input || job.payload || {};
        const master = job.masterAssessment || job.master_assessment || assessment;
        const languageCode =
          job.reportLanguageCode ||
          assessment?.reportLanguageCode ||
          payload?.reportLanguageCode ||
          "en";

        if (!assessment || typeof assessment !== "object") {
          throw new Error("API report render job did not include an assessment object.");
        }

        const displayPayload = normalizeApiReportPayloadForDisplay(payload);
        const formPayload = normalizeApiReportPayloadForForm(payload);
        const branding = normalizeReportBrandingForDisplay(job.branding || payload.branding || {});

        setReportLanguageCode(languageCode);
        setReportBranding(branding);
        setInputText(String(displayPayload?.text || displayPayload?.description || ""));
        setForm(formPayload);
        setDisclaimerAccepted(true);
        setOut(assessment);
        setMasterAssessment(master);
        masterAssessmentRef.current = master;
        setLastPayload(displayPayload);
        baselinePayloadRef.current = displayPayload;
        setScenarioLabOpen(false);
        setScenarioLabScenarios([]);
        setScenarioLabError("");
        setScenarioLabRunningId("");
        setScenarioLabActiveReportId("");
      } catch (error) {
        if (cancelled) return;
        const message = error?.message || String(error || "Failed to load API report render job.");
        setErr(message);
        window.__TVAC_REPORT_ERROR__ = message;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadApiReportRenderJob();

    return () => {
      cancelled = true;
      document.body.classList.remove("tvac-api-report-render");
    };
  }, [apiReportRenderMode, apiReportRenderToken]);

  function applyExample(ex) {
  if (!ex || !ex.structured) return;

  // 1) Free-text
  setInputText(ex.text || "");

  // 2) Structured fields (Step 2)
  setForm((prev) => {
    const s = ex.structured || {};
    return {
      ...prev,
      issuanceJurisdiction: s.issuanceJurisdiction || "",
      saleJurisdictions: s.saleJurisdictions || "",
      instrumentType: s.instrumentType || "unknown",
      custodyModel: s.custodyModel || "unknown",
      liquidityCadence: Object.prototype.hasOwnProperty.call(s, "liquidityCadence") ? s.liquidityCadence : "",
      venueType: s.venueType || "unknown",
      settlementModel: s.settlementModel || "not-decided",
      disclosure: Object.prototype.hasOwnProperty.call(s, "disclosure") ? s.disclosure : "",
      transferRestrictions: s.transferRestrictions || "",
      taxNotes: s.taxNotes || "",
      investorTypes: Array.isArray(s.investorTypes) ? s.investorTypes : [],
    };
  });

  // Track that the current case was loaded from an example, so users do not
  // accidentally spend a paid report credit on a demonstration case.
  setLoadedExampleCase({ id: ex.id || "example", title: ex.title || "Example case" });
  setShowExampleRunWarning(false);

  // Close modal
  setShowExamples(false);

  // Optional: scroll to the case field for reassurance
  if (refsMap?.caseText?.current?.scrollIntoView) {
    refsMap.caseText.current.scrollIntoView({ behavior: "smooth", block: "center" });
    if (refsMap.caseText.current.focus) refsMap.caseText.current.focus();
  }
}

  function clearExampleAndStartOwnCase() {
    setShowExampleRunWarning(false);
    setLoadedExampleCase(null);
    setInputText("");
    setForm({});
    setErr("");
    setOut(null);
    setMasterAssessment(null);
    masterAssessmentRef.current = null;
    baselinePayloadRef.current = null;
    setScenarioLabOpen(false);
    setScenarioLabScenarios([]);
    setScenarioLabError("");
    setScenarioLabRunningId("");
    setScenarioLabActiveReportId("");
    setScenarioLabAccessScope("");
    setScenarioLabAccessBaselineId("");

    window.setTimeout(() => {
      if (refsMap?.caseText?.current?.scrollIntoView) {
        refsMap.caseText.current.scrollIntoView({ behavior: "smooth", block: "center" });
        if (refsMap.caseText.current.focus) refsMap.caseText.current.focus();
      }
    }, 0);
  }
   
  useEffect(() => {
    // Cleanup hvis componenten unmountes mens der stadig kører et interval
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  function flashAndFocus(el) {
    if (!el) return;
    el.classList.add("highlight");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el.focus) el.focus();
    setTimeout(() => el.classList.remove("highlight"), 1200);
  }

  function focusFieldFor(text) {
    const t = (text || "").toLowerCase();

    const id =
      t.includes("overall case") ||
      t.includes("business rationale") ||
      t.includes("case description")
        ? "caseText"
        : t.includes("jurisdiction") && !t.includes("sale")
        ? "issuanceJurisdiction"
        : t.includes("sale jurisdiction")
        ? "saleJurisdictions"
        : t.includes("investor")
        ? "investorTypes"
        : t.includes("instrument")
        ? "instrumentType"
        : t.includes("custody")
        ? "custodyModel"
        : t.includes("liquidity")
        ? "liquidityCadence"
        : t.includes("venue") || t.includes("trading")
        ? "venueType"
        : t.includes("settlement")
        ? "settlementModel"
        : t.includes("transfer")
        ? "transferRestrictions"
        : t.includes("disclosure") ||
          t.includes("prospectus") ||
          t.includes("kid")
        ? "disclosure"
        : t.includes("tax")
        ? "taxNotes"
        : null;

    if (id && refsMap[id]?.current) flashAndFocus(refsMap[id].current);
  }

  // Normalize and sanitize any error message into clean EN output
  function cleanErrorMessage(raw) {
    const msg = String(raw || "").trim();
    if (!msg) return "Something went wrong. Please try again.";

    const lowerMsg = msg.toLowerCase();
    const accessVerificationMessage =
      "We could not verify access for this request. Please refresh the page and try again. If the issue continues, contact support@tvacai.com.";
    const temporaryTechnicalMessage =
      "TVAC could not complete the request because of a temporary technical issue. Please wait a moment and try again. If the issue continues, contact support@tvacai.com.";

    const accessErrorSignals = [
      "no_customer_found",
      "customer_not_found",
      "missing_customer",
      "access_denied",
      "unauthorized",
    ];

    const technicalErrorSignals = [
      "failed to fetch",
      "networkerror",
      "network error",
      "timeout",
      "bad_gateway",
      "service_unavailable",
      "gateway_timeout",
      "internal_server_error",
      "missing_api_key",
      "rate_limit",
      "openai",
    ];

    if (
      accessErrorSignals.some((signal) => lowerMsg.includes(signal)) ||
      (lowerMsg.includes("403") && lowerMsg.includes("forbidden"))
    ) {
      return accessVerificationMessage;
    }

    if (
      technicalErrorSignals.some((signal) => lowerMsg.includes(signal)) ||
      /\b50[234]\b/.test(lowerMsg)
    ) {
      return temporaryTechnicalMessage;
    }

    // If we have a mixed-language string (e.g., starts with Danish but contains "Invalid ..."),
    // keep only the English part from the first "Invalid".
    const idxInvalid = msg.indexOf("Invalid ");
    if (idxInvalid > 0) {
      return msg.slice(idxInvalid).trim();
    }

    // If we still have an "API error 400: { ...json... }" style message, try to salvage.
    if (
      msg.toLowerCase().includes("api error") &&
      msg.includes("{") &&
      msg.includes("}")
    ) {
      const first = msg.indexOf("{");
      const last = msg.lastIndexOf("}");
      if (first !== -1 && last !== -1 && last > first) {
        const slice = msg.slice(first, last + 1);
        try {
          const j = JSON.parse(slice);

          // Handle our known backend schema if present
          if (j && typeof j === "object" && typeof j.error === "string") {
            const field = typeof j.field === "string" ? j.field : "";
            if (field === "issuanceJurisdiction") {
              const shown = j.value ? `"${String(j.value)}"` : "";
              const hint =
                typeof j.hint === "string"
                  ? j.hint
                  : 'Use a real country name (e.g., Germany, France, Denmark, United States) or a recognized label (EU/EEA/UK).';
              return `${j.error}${shown ? `: ${shown}` : ""}. ${hint}`;
            }
            if (field === "saleJurisdictions" && Array.isArray(j.invalid)) {
              const bad = j.invalid
                .map((x) => (x && x.value ? `"${String(x.value)}"` : null))
                .filter(Boolean)
                .join(", ");
              const hint =
                typeof j.hint === "string"
                  ? j.hint
                  : 'Provide real country names (e.g., Germany, Spain, Netherlands) or recognized labels (EU/EEA/UK).';
              return `${j.error}: ${bad}. ${hint}`;
            }
            return `${j.error}.`;
          }
        } catch {
          // ignore parsing failures
        }
      }
    }

    // Default: return as-is (assume it's already English from Api.js)
    return msg;
  }

  function cloneReportObject(value) {
    if (!value || typeof value !== "object") return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }


  function stableSignatureNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : "";
  }

  function getCanonicalScoreSignature(report) {
    if (!report || typeof report !== "object") return "no-master";
    const fs = report.factor_scores || {};
    const av = report.added_value || {};
    return [
      report.verdict || "",
      stableSignatureNumber(fs.new_opportunities),
      stableSignatureNumber(fs.cost_savings),
      stableSignatureNumber(fs.risk_reduction),
      stableSignatureNumber(fs.tokenization_costs),
      stableSignatureNumber(fs.new_risks),
      stableSignatureNumber(av.plus),
      stableSignatureNumber(av.minus),
      stableSignatureNumber(av.score),
    ].join("|");
  }

  function recomputeAddedValueFromScores(report) {
    const fs = report?.factor_scores || {};
    const no = Number(fs.new_opportunities || 0);
    const cs = Number(fs.cost_savings || 0);
    const rr = Number(fs.risk_reduction || 0);
    const tc = Number(fs.tokenization_costs || 0);
    const nr = Number(fs.new_risks || 0);
    const plus = no + cs + rr;
    const minus = tc + nr;
    return {
      ...(report?.added_value || {}),
      plus,
      minus,
      score: plus - minus,
      formula:
        report?.added_value?.formula ||
        "Added Value = (New Opportunities + Cost Savings + Risk Reduction) – (Tokenization Costs + New Risks)",
    };
  }

  function enforceCanonicalAssessmentParity(master, candidate, targetLanguageCode = "en") {
    if (!master || !candidate || typeof master !== "object" || typeof candidate !== "object") return candidate;
    const out = cloneReportObject(candidate) || {};
    const masterFs = cloneReportObject(master.factor_scores || {});
    const masterAv = cloneReportObject(master.added_value || recomputeAddedValueFromScores({ factor_scores: masterFs }));
    const masterInputMaturity = cloneReportObject(master.input_maturity || master.inputMaturity || {});
    const masterInputCompleteness = cloneReportObject(master.input_completeness || master.inputCompleteness || {});

    // These values define the canonical TVAC assessment. They must never vary by report language.
    out.verdict = master.verdict;
    out.factor_scores = masterFs;
    out.added_value = masterAv;
    out.input_completeness = masterInputCompleteness;
    out.input_maturity = masterInputMaturity;
    out.maturityLevel = masterInputMaturity?.level;
    out.maturityScore = masterInputMaturity?.score;
    out.maturityNote = masterInputMaturity?.note;
    out.openDesignAreas = masterInputMaturity?.open_design_areas;
    out.maturityToneMode = masterInputMaturity?.tone_mode;
    out.maturityRecommendationMode = masterInputMaturity?.recommendation_mode;
    out.calibration = cloneReportObject(master.calibration || out.calibration);
    out.initiator_viability = cloneReportObject(master.initiator_viability || out.initiator_viability);

    // Keep verdict-critical stop/blocker structure locked to the master shape while preserving localized prose when possible.
    const masterCriteria = master?.deep_assessment?.go_no_go_criteria;
    if (masterCriteria && out.deep_assessment) {
      const localizedCriteria = out.deep_assessment.go_no_go_criteria || {};
      out.deep_assessment.go_no_go_criteria = {
        go_if: Array.isArray(masterCriteria.go_if)
          ? masterCriteria.go_if.map((masterValue, idx) =>
              Array.isArray(localizedCriteria.go_if) && typeof localizedCriteria.go_if[idx] === "string"
                ? localizedCriteria.go_if[idx]
                : masterValue
            )
          : [],
        no_go_if: Array.isArray(masterCriteria.no_go_if)
          ? masterCriteria.no_go_if.map((masterValue, idx) =>
              Array.isArray(localizedCriteria.no_go_if) && typeof localizedCriteria.no_go_if[idx] === "string"
                ? localizedCriteria.no_go_if[idx]
                : masterValue
            )
          : [],
      };
    }

    out.factorScores = {
      newOpportunities: masterFs.new_opportunities,
      costSavings: masterFs.cost_savings,
      riskReduction: masterFs.risk_reduction,
      tokenizationCosts: masterFs.tokenization_costs,
      newRisks: masterFs.new_risks,
      plusTotal: masterAv.plus,
      minusTotal: masterAv.minus,
      addedValue: masterAv.score,
    };

    out.canonical_assessment_id = master.canonical_assessment_id || out.canonical_assessment_id || null;
    out.canonical_master_language = "en";
    out.canonical_reportLanguageCode = "en";
    out.reportLanguageCode = targetLanguageCode;
    out.reportLanguage = getReportLanguageName(targetLanguageCode);
    out.localized_from_canonical = targetLanguageCode !== "en";
    out._telemetry = {
      ...(out._telemetry || {}),
      canonical_score_signature: getCanonicalScoreSignature(master),
      localized_report_language: targetLanguageCode,
      localized_from_canonical: targetLanguageCode !== "en",
      frontend_canonical_parity_enforced: true,
    };

    return out;
  }

  function getCanonicalMasterForLocalization(explicitMaster = null) {
    // Patch 7: always localize from the immutable English master, never from the
    // currently displayed language version. This prevents cross-language contamination
    // when users switch quickly.
    return masterAssessmentRef.current || explicitMaster || masterAssessment || null;
  }

  async function localizeFromMaster(master, targetLanguageCode = "en", options = {}) {
    const targetCode = String(targetLanguageCode || "en").trim() || "en";
    const forceRefresh = Boolean(options.forceRefresh);
    const canonicalMaster = getCanonicalMasterForLocalization(master);
    if (!canonicalMaster) return null;

    const assessmentId =
      canonicalMaster.canonical_assessment_id ||
      canonicalMaster._telemetry?.canonical_assessment_id ||
      "current";
    const cacheKey = `${REPORT_RENDER_SCHEMA_VERSION}:${assessmentId}:${getCanonicalScoreSignature(canonicalMaster)}:${targetCode}`;

    if (!forceRefresh && localizedReportCacheRef.current.has(cacheKey)) {
      const cached = enforceCanonicalAssessmentParity(canonicalMaster, cloneReportObject(localizedReportCacheRef.current.get(cacheKey)), targetCode);
      if (!reportObjectNeedsLocalization(cached, targetCode)) return cached;
      localizedReportCacheRef.current.delete(cacheKey);
    }

    if (targetCode === "en") {
      const english = enforceCanonicalAssessmentParity(canonicalMaster, {
        ...cloneReportObject(canonicalMaster),
        reportLanguageCode: "en",
        reportLanguage: getReportLanguageName("en"),
        localized_from_canonical: false,
      }, "en");
      localizedReportCacheRef.current.set(cacheKey, cloneReportObject(english));
      return english;
    }

    const res = await localizeReport(canonicalMaster, targetCode, {
      forceRefresh,
      reportInput: lastPayload || buildPayload(),
    });
    const rendered = res?.result || res;
    if (rendered) {
      const stampedBase = enforceCanonicalAssessmentParity(canonicalMaster, {
        ...cloneReportObject(rendered),
        reportLanguageCode: targetCode,
        reportLanguage: getReportLanguageName(targetCode),
        localized_from_canonical: targetCode !== "en",
      }, targetCode);
      const parityIssues = getLocalizationParityIssues(canonicalMaster, stampedBase, targetCode);
      const stamped = markLocalizationParity(stampedBase, parityIssues);
      // Do not cache incomplete localized versions. Otherwise a bad Danish/German/etc.
      // result can get stuck in the browser and keep triggering an endless repair loop.
      if (!reportObjectNeedsLocalization(stamped, targetCode)) {
        localizedReportCacheRef.current.set(cacheKey, cloneReportObject(stamped));
      }
      return stamped;
    }
    return rendered;
  }

  async function handleReportLanguageChange(nextCode) {
    const targetCode = String(nextCode || "en").trim() || "en";
    if (!reportLanguageIsAvailable(targetCode, internalReportLanguageAccess)) return;
    const requestId = ++localizationSeqRef.current;

    setReportLanguageCode(targetCode);
    setLastPayload((prev) =>
      prev
        ? {
            ...prev,
            reportLanguageCode: targetCode,
            reportLanguage: getReportLanguageName(targetCode),
          }
        : prev
    );

    // If a report already exists, re-render it from the canonical English master.
    // Never localize from the currently displayed report object.
    if (!getCanonicalMasterForLocalization() || !out || loading) return;

    setErr("");
    setLocalizing(true);
    try {
      const rendered = await localizeFromMaster(getCanonicalMasterForLocalization(), targetCode);
      // Patch 7: ignore stale localization responses. If the user switched language
      // before an earlier localization response returned, that older response must
      // not overwrite the current UI.
      if (rendered && localizationSeqRef.current === requestId) {
        if (!reportObjectNeedsLocalization(rendered, targetCode)) {
          setOut(rendered);
          setErr("");
        } else {
          setErr(getLocalizationStatusMessage(targetCode));
        }
      }
    } catch (e) {
      if (localizationSeqRef.current === requestId) {
        setErr(cleanErrorMessage(e?.message || e));
      }
    } finally {
      if (localizationSeqRef.current === requestId) {
        setLocalizing(false);
      }
    }
  }

  useEffect(() => {
    if (!out || loading || localizing || reportLanguageCode === "en") return;
    if (!reportObjectHasHardLocalizationFailure(out, reportLanguageCode)) return;

    const canonicalMaster = getCanonicalMasterForLocalization();
    if (!canonicalMaster) return;

    const assessmentId =
      canonicalMaster.canonical_assessment_id ||
      canonicalMaster._telemetry?.canonical_assessment_id ||
      "current";
    const repairKey = `${assessmentId}:${getCanonicalScoreSignature(canonicalMaster)}:${reportLanguageCode}`;

    // Avoid an infinite repair loop if the backend returns an incomplete localization.
    if (localizationRepairKeyRef.current === repairKey) return;
    localizationRepairKeyRef.current = repairKey;

    let cancelled = false;
    const requestId = ++localizationSeqRef.current;

    setErr("");
    setLocalizing(true);
    localizeFromMaster(canonicalMaster, reportLanguageCode, { forceRefresh: true })
      .then((rendered) => {
        if (cancelled || localizationSeqRef.current !== requestId) return;
        if (rendered) {
          setOut(rendered);
          if (reportObjectNeedsLocalization(rendered, reportLanguageCode)) {
            setErr(
              "The localized report returned by the backend is incomplete, too short compared with the English master, or still contains untranslated English analysis blocks. Please rerun localization or send the latest files for inspection."
            );
          }
        }
      })
      .catch((e) => {
        if (!cancelled && localizationSeqRef.current === requestId) {
          setErr(cleanErrorMessage(e?.message || e));
        }
      })
      .finally(() => {
        if (!cancelled && localizationSeqRef.current === requestId) {
          setLocalizing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [out, reportLanguageCode, loading, localizing]);

  // Normaliser form → API payload
  function buildPayload() {
    const investorArray = normalizeApiInvestorTypesForForm(form.investorTypes || []);
    const investorSet = new Set(investorArray);
    const investorObj = {
      Professional: investorSet.has("professional"),
      Accredited: investorSet.has("accredited"),
      Retail: investorSet.has("retail"),
    };

    return {
      text: (inputText || "").trim(),
      issuanceJurisdiction: (form.issuanceJurisdiction || "").trim(),
      saleJurisdictions: String(form.saleJurisdictions || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      instrumentType: form.instrumentType || "",
      custodyModel: form.custodyModel || "",
      liquidityCadence: form.liquidityCadence || "",
      marketVenue: form.venueType || "",
      settlementModel: form.settlementModel || "not-decided",
      cashLegModel: form.settlementModel || "not-decided",
      paymentRails: form.settlementModel || "not-decided",
      disclosure: form.disclosure || "",
      transferRestrictions: form.transferRestrictions || "",
      taxNotes: form.taxNotes || "",
      investorTypes: investorObj,
      reportLanguageCode,
      reportLanguage: getReportLanguageName(reportLanguageCode),
      deep: true,
    };
  }

  // Er der reelt brugerinput, selv om completeness evt. siger 0 %?
  const hasUserInputs = Boolean(
    (inputText || "").trim() ||
      Object.values(form || {}).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v && String(v).trim() !== "";
      })
  );

  // Sikrer at Added Value altid er en streng/tal – aldrig et råt objekt
  const addedValueDisplay = useMemo(() => {
    if (!out || out.added_value == null) return "n/a";
    const v = out.added_value;

    if (typeof v === "number") return v.toFixed(2);
    if (typeof v === "string") return v;

    if (typeof v === "object") {
      const plus =
        typeof v.plus === "number" ? v.plus.toFixed(2) : v.plus ?? "";
      const minus =
        typeof v.minus === "number" ? v.minus.toFixed(2) : v.minus ?? "";
      const score =
        typeof v.score === "number" ? v.score.toFixed(2) : v.score ?? "";

      if (score || plus || minus) {
        const plusLabel = reportLabels.plus || "plus";
        const minusLabel = reportLabels.minus || "minus";
        return `${score || "n/a"} (${plusLabel} ${plus || "?"} / ${minusLabel} ${minus || "?"})`;
      }
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }

    return String(v);
  }, [out]);

  function detectOutOfScopeCase(inputText, form = {}) {
  const raw = String(inputText || "").trim();
  const text = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const words = text.split(/\s+/).filter(Boolean);

  const spaceTerms = [
    /\bmoon\b/,
    /\bmanen\b/,
    /\bmars\b/,
    /\bjupiter\b/,
    /\bpluto\b/,
    /\bsaturn\b/,
    /\bvenus\b/,
    /\bmercury\b/,
    /\bneptune\b/,
    /\buranus\b/,
    /\bgalaxy\b/,
    /\bunivers(?:e|et)\b/,
    /\binterplanetary\b/,
    /\bextraterrestrial\b/,
    /\bouter space\b/,
  ];

  const nonHumanInvestorTerms = [
    /\baliens?\b/,
    /\brumvaesener?\b/,
    /\bnon[-\s]?human\b/,
    /\bmartians?\b/,
    /\bjovians?\b/,
  ];

  const fictionTerms = [
    /\bfictional\b/,
    /\bimaginary\b/,
    /\bmade[-\s]?up\b/,
    /\bfantasy\b/,
    /\bdragon(?:s)?\b/,
    /\bwizard(?:s)?\b/,
    /\bghost(?:s)?\b/,
    /\bunicorn(?:s)?\b/,
  ];

  const hasSpace = spaceTerms.some((re) => re.test(text));
  const hasNonHumanInvestors = nonHumanInvestorTerms.some((re) => re.test(text));
  const fictionHits = fictionTerms.filter((re) => re.test(text)).length;

  const impossibleAssetCombo =
    (/\bflowers?\b/.test(text) && hasSpace) ||
    ((/\breal estate\b|\bland\b|\bproperty\b/.test(text)) &&
      (/\bmoon\b|\bmanen\b|\bmars\b|\bjupiter\b|\bpluto\b/.test(text)));

  const impossibleSaleContext =
    hasSpace &&
    (hasNonHumanInvestors ||
      /\bsell\b|\bsale\b|\binvestor(?:s)?\b|\btoken(?:s)?\b|\boffer(?:ing)?\b|\bdistribution\b/.test(text));

  const tooThin = words.length < 8;

  const rejectionMessage =
    "Input rejected: TVAC only assesses real-world tokenization cases with a real asset, a real eligible investor base, and a minimally credible legal/operational setup. This input appears fictional, impossible, or outside TVAC's scope, so no report was generated.";

  if (impossibleAssetCombo) return rejectionMessage;
  if (impossibleSaleContext) return rejectionMessage;
  if (fictionHits >= 2) return rejectionMessage;
  if (tooThin && (hasSpace || hasNonHumanInvestors || fictionHits >= 1)) return rejectionMessage;

  return "";
}
  async function runAssessment() {
    setErr("");
    setOut(null);
    setScenarioLabOpen(false);
    setScenarioLabScenarios([]);
    setScenarioLabError("");
    setScenarioLabRunningId("");
    setScenarioLabActiveReportId("");
    setScenarioLabCheckoutError("");
    setMasterAssessment(null);
    masterAssessmentRef.current = null;
    baselinePayloadRef.current = null;
    localizedReportCacheRef.current.clear();
    localizationSeqRef.current += 1;

    if (!inputText.trim() && Object.keys(form).length === 0) {
      setErr("Please describe a case in free text and fill key details.");
      return;
    }

    const outOfScopeReason = detectOutOfScopeCase(inputText, form);
if (outOfScopeReason) {
  setErr(outOfScopeReason);
  setOut(null);
  return;
}
    
    // Nulstil og start stopur
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedSeconds(0);
    setLoading(true);
    timerRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    try {
      const selectedLanguageCode = reportLanguageCode;
      const payload = buildPayload();
      const payloadForSelectedLanguage = {
        ...payload,
        reportLanguageCode: selectedLanguageCode,
        reportLanguage: getReportLanguageName(selectedLanguageCode),
      };
      const canonicalPayload = {
        ...payload,
        reportLanguageCode: "en",
        reportLanguage: getReportLanguageName("en"),
      };

      // Store the user-facing payload for report rendering, and freeze the canonical
      // baseline payload separately so Scenario Lab cannot later overwrite it.
      setLastPayload(payloadForSelectedLanguage);
      baselinePayloadRef.current = scenarioLabClone(canonicalPayload);

      const res = await evaluate(
        {
          ...canonicalPayload,
          disclaimerAccepted,
        },
        {
          assessmentMode: "single_report",
        }
      );
      const master = res?.result || res;
      setMasterAssessment(master);
      masterAssessmentRef.current = cloneReportObject(master);
      localizedReportCacheRef.current.clear();
      localizedReportCacheRef.current.set(
        `${master?.canonical_assessment_id || "current"}:${getCanonicalScoreSignature(master)}:en`,
        cloneReportObject({
          ...master,
          reportLanguageCode: "en",
          reportLanguage: getReportLanguageName("en"),
          localized_from_canonical: false,
        })
      );

      const englishRender = enforceCanonicalAssessmentParity(master, {
        ...cloneReportObject(master),
        reportLanguageCode: "en",
        reportLanguage: getReportLanguageName("en"),
        localized_from_canonical: false,
      }, "en");

      if (selectedLanguageCode === "en") {
        setOut(englishRender);
      } else {
        // Do not show the English master as if it were the selected language report.
        // Keep the standard assessment loading state active until the selected language
        // version is ready and has passed QA, so the user only sees one report-generation flow.
        setOut(null);
        setLocalizing(true);
        try {
          const rendered = await localizeFromMaster(master, selectedLanguageCode, { forceRefresh: true });
          if (rendered && !reportObjectNeedsLocalization(rendered, selectedLanguageCode)) {
            setOut(rendered);
            setErr("");
          } else {
            setOut(null);
            setErr(getLocalizationStatusMessage(selectedLanguageCode));
          }
        } catch (e) {
          setOut(null);
          setErr(cleanErrorMessage(e?.message || e));
        } finally {
          setLocalizing(false);
        }
      }
    } catch (e) {
      setErr(cleanErrorMessage(e?.message || e));
    } finally {
      setLoading(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }

  async function onAnalyzeClick() {
    if (loadedExampleCase) {
      setShowExampleRunWarning(true);
      return;
    }
    await runAssessment();
  }

  const onPrintClick = async () => {
    if (loading) return;

    if (localizing || reportObjectNeedsLocalization(out, reportLanguageCode)) {
      setErr(getLocalizationStatusMessage(reportLanguageCode));
      const canonicalMaster = getCanonicalMasterForLocalization();
      if (canonicalMaster) {
        try {
          setLocalizing(true);
          const rendered = await localizeFromMaster(canonicalMaster, reportLanguageCode, { forceRefresh: true });
          if (rendered && !reportObjectNeedsLocalization(rendered, reportLanguageCode)) {
            setOut(rendered);
            setErr("");
          } else {
            setErr(getLocalizationStatusMessage(reportLanguageCode));
          }
        } catch (e) {
          setErr(cleanErrorMessage(e?.message || e));
        } finally {
          setLocalizing(false);
        }
      }
      return;
    }

    // Use the server renderer so pagination and Page X / Y are deterministic.
    // Browser print is intentionally not used as a fallback because it cannot
    // guarantee page numbering across Chrome/Edge print settings.
    try {
      const reportElement = document.querySelector(".print-area");
      assertRenderedReportQuality(reportElement?.innerText || "", lastPayload || buildPayload());
      setPdfExporting(true);
      const token = out?.report_export_token;
      const { blob, filename } = await downloadReportPdf(token);
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 1500);
    } catch (e) {
      console.error("[TVAC] Report export blocked by quality gate", {
        code: e?.code || "TVAC_REPORT_EXPORT_BLOCKED",
        details: Array.isArray(e?.details) ? e.details : [],
      });
      setErr("TVAC detected an internal report-quality issue and did not export the report. No action is required from you; please try the assessment again shortly.");
    } finally {
      setPdfExporting(false);
    }
  };

  async function retrySelectedLanguageVersion() {
    if (loading || localizing) return;
    const targetCode = String(reportLanguageCode || "en").trim() || "en";
    const canonicalMaster = getCanonicalMasterForLocalization();
    if (!canonicalMaster) return;

    if (targetCode === "en") {
      showEnglishMasterReport();
      return;
    }

    const requestId = ++localizationSeqRef.current;
    setErr("");
    setLocalizing(true);
    try {
      const rendered = await localizeFromMaster(canonicalMaster, targetCode, { forceRefresh: true });
      if (localizationSeqRef.current !== requestId) return;
      if (rendered && !reportObjectNeedsLocalization(rendered, targetCode)) {
        setOut(rendered);
        setErr("");
      } else {
        setOut(null);
        setErr(getLocalizationStatusMessage(targetCode));
      }
    } catch (e) {
      if (localizationSeqRef.current === requestId) {
        setOut(null);
        setErr(cleanErrorMessage(e?.message || e));
      }
    } finally {
      if (localizationSeqRef.current === requestId) {
        setLocalizing(false);
      }
    }
  }

  function showEnglishMasterReport() {
    const canonicalMaster = getCanonicalMasterForLocalization();
    if (!canonicalMaster) return;
    const englishRender = enforceCanonicalAssessmentParity(canonicalMaster, {
      ...cloneReportObject(canonicalMaster),
      reportLanguageCode: "en",
      reportLanguage: getReportLanguageName("en"),
      localized_from_canonical: false,
    }, "en");
    localizationSeqRef.current += 1;
    setReportLanguageCode("en");
    setLastPayload((prev) =>
      prev
        ? {
            ...prev,
            reportLanguageCode: "en",
            reportLanguage: getReportLanguageName("en"),
          }
        : prev
    );
    setErr("");
    setLocalizing(false);
    setOut(englishRender);
  }

  // Case-specific verdict rationale. Backend-provided rationale is preferred;
  // frontend fallback is only used to avoid generic formula text.
  function getReasonText(result) {
    if (!result) return "";
    const cleanForDisplay = (value) =>
      localizeGeneratedText(scrubScoreLeadInsEverywhere(value), reportLanguageCode);

    const needsInitiatorExplanation = Number(result?.initiator_viability?.asset_owner_net) < 0;
    if (!needsInitiatorExplanation && result.rationale && typeof result.rationale === "string" && !isGenericFormulaReason(result.rationale)) {
      return cleanForDisplay(result.rationale);
    }
    const built = buildClientVerdictRationale(result, reportLabels, reportLanguageCode);
    return cleanForDisplay(built);
  }

  // Scroll-funktion til indholdsfortegnelse
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };


  function scenarioLabDevAccessEnabled() {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search || "");
      return (
        params.get("scenarioLab") === "dev" ||
        window.localStorage?.getItem("tvac_scenario_lab_dev") === "true"
      );
    } catch {
      return false;
    }
  }

  function scenarioLabEntitlementEnabled() {
    if (typeof window === "undefined") return false;
    return Boolean(window.__TVAC_SCENARIO_LAB_ENABLED__ || window.TVAC_SCENARIO_LAB_ENABLED);
  }

  function normalizeScenarioLabAccessId(value) {
    return String(value || "").trim();
  }

  function getScenarioLabBaselineAccessIdFromResult(result) {
    return normalizeScenarioLabAccessId(
      result?.canonical_assessment_id ||
        result?._telemetry?.canonical_assessment_id ||
        result?.assessment_id ||
        result?.id ||
        ""
    );
  }

  function getCurrentScenarioLabBaselineId() {
    return getScenarioLabBaselineAccessIdFromResult(getCurrentScenarioBaselineResult());
  }

  const scenarioLabDevAccess = scenarioLabDevAccessEnabled();
  const currentScenarioLabBaselineId = getCurrentScenarioLabBaselineId();
  const scenarioLabAccessScopeNormalized = String(scenarioLabAccessScope || "").trim().toLowerCase();
  const scenarioLabPaidAccessIsGlobal = ["global", "subscription", "staff"].includes(scenarioLabAccessScopeNormalized);
  const scenarioLabPaidAccessMatchesCurrentReport =
    scenarioLabAccessScopeNormalized === "report" &&
    Boolean(currentScenarioLabBaselineId) &&
    normalizeScenarioLabAccessId(scenarioLabAccessBaselineId) === currentScenarioLabBaselineId;
  const scenarioLabPaidAccessAppliesToCurrentReport =
    scenarioLabPaidAccess && (scenarioLabPaidAccessIsGlobal || scenarioLabPaidAccessMatchesCurrentReport);
  const scenarioLabHasActiveAccess =
    scenarioLabPaidAccessAppliesToCurrentReport || scenarioLabEntitlementEnabled() || scenarioLabDevAccess;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search || "");
    const isScenarioLabSuccess = params.get("checkout") === "scenario_lab_success" || params.get("scenarioLab") === "paid";
    const shouldCheckAccess = isScenarioLabSuccess || Boolean(getStoredTvacAccessEmail());
    if (!shouldCheckAccess) return undefined;

    let cancelled = false;
    const snapshot = isScenarioLabSuccess ? loadScenarioLabPendingBaselineSnapshot() : null;

    const poll = async () => {
      const attempts = isScenarioLabSuccess ? 6 : 1;
      for (let i = 0; i < attempts && !cancelled; i += 1) {
        const status = await refreshScenarioLabAccessStatus("", snapshot?.baselineAssessmentId || "");
        if (cancelled) return;
        if (status?.allowed) {
          setScenarioLabPaidAccess(true);
          if (snapshot) {
            createScenarioLabFromSnapshot(snapshot);
            clearScenarioLabPendingBaselineSnapshot();
          }
          return;
        }
        if (attempts > 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 900));
        }
      }
      if (isScenarioLabSuccess && !cancelled) {
        setScenarioLabCheckoutError("Payment received, but Scenario Lab access is not visible yet. Please refresh in a moment.");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  function getScenarioLabBaselineSnapshot() {
    const baselineResult = getCurrentScenarioBaselineResult();
    const baselinePayload = getCurrentScenarioBaselinePayload();
    if (!baselineResult || !baselinePayload) return null;
    return {
      schema: "tvac-scenario-lab-baseline-v1",
      createdAt: new Date().toISOString(),
      baselineAssessmentId: getScenarioLabBaselineAccessIdFromResult(baselineResult) || null,
      reportLanguageCode: "en",
      result: scenarioLabClone(baselineResult),
      payload: scenarioLabClone(baselinePayload),
    };
  }

  function clearScenarioLabPendingBaselineSnapshot() {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage?.removeItem(SCENARIO_LAB_PENDING_BASELINE_KEY);
    } catch {
      // Best-effort cleanup only.
    }
  }

  function saveScenarioLabPendingBaselineSnapshot(snapshot) {
    if (typeof window === "undefined" || !snapshot) return;
    try {
      // Session storage survives the Stripe redirect in the same browser tab,
      // but is cleared when that tab/session ends. Case content is not written to
      // persistent localStorage.
      window.sessionStorage?.setItem(SCENARIO_LAB_PENDING_BASELINE_KEY, JSON.stringify(snapshot));
    } catch {
      // Best-effort only; checkout can still proceed, but automatic restore may not work.
    }
  }

  function loadScenarioLabPendingBaselineSnapshot() {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage?.getItem(SCENARIO_LAB_PENDING_BASELINE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const createdAtMs = Date.parse(String(parsed?.createdAt || ""));
      const expired = !Number.isFinite(createdAtMs) || Date.now() - createdAtMs > SCENARIO_LAB_PENDING_BASELINE_MAX_AGE_MS;
      if (expired || !parsed?.result || !parsed?.payload) {
        clearScenarioLabPendingBaselineSnapshot();
        return null;
      }
      return parsed;
    } catch {
      clearScenarioLabPendingBaselineSnapshot();
      return null;
    }
  }

  function initializeScenarioLabFromBaseline(baselineResult, baselinePayload) {
    if (!baselineResult || !baselinePayload) {
      setScenarioLabError("Run a TVAC assessment first, then create Scenario Lab from the completed baseline.");
      return;
    }

    const makeAlternative = (id, label, name) => ({
      id,
      label,
      name,
      description: "",
      changeRationale: "",
      payload: scenarioLabClone(baselinePayload),
      result: null,
      status: "draft",
      error: "",
      lastAssessmentSignature: "",
      replacementCount: 0,
      locked: false,
    });

    setScenarioLabScenarios([
      {
        id: "baseline",
        label: "Scenario A",
        name: "Baseline",
        description: "Current case as assessed by TVAC.",
        changeRationale: "Baseline assessment.",
        payload: scenarioLabClone(baselinePayload),
        result: scenarioLabClone(baselineResult),
        status: "complete",
        error: "",
        lastAssessmentSignature: "",
        replacementCount: 0,
        locked: true,
      },
      makeAlternative("scenario-b", "Scenario B", "Alternative 1"),
      makeAlternative("scenario-c", "Scenario C", "Alternative 2"),
    ]);
    setScenarioLabError("");
    setScenarioLabActiveReportId("baseline");
    setScenarioLabOpen(true);
  }

  function getCurrentScenarioBaselineResult() {
    // Scenario Lab must start from the immutable canonical master only.
    // Do not fall back to `out`, because `out` can be a viewed Scenario B/C report.
    return masterAssessmentRef.current || masterAssessment || null;
  }

  function getCurrentScenarioBaselinePayload() {
    const payload = baselinePayloadRef.current || lastPayload || buildPayload();
    return scenarioLabClone({
      ...payload,
      reportLanguageCode: "en",
      reportLanguage: getReportLanguageName("en"),
      deep: true,
    });
  }

  function createScenarioLabFromCurrent() {
    if (!scenarioLabHasActiveAccess) {
      setScenarioLabError("Scenario Lab access is not enabled for this account/session.");
      return;
    }

    initializeScenarioLabFromBaseline(
      getCurrentScenarioBaselineResult(),
      getCurrentScenarioBaselinePayload()
    );
  }

  function createScenarioLabFromSnapshot(snapshot) {
    if (!snapshot?.result || !snapshot?.payload) return;
    setMasterAssessment(snapshot.result);
    masterAssessmentRef.current = scenarioLabClone(snapshot.result);
    baselinePayloadRef.current = scenarioLabClone(snapshot.payload);
    setLastPayload(scenarioLabClone(snapshot.payload));
    setOut(scenarioLabClone(snapshot.result));
    setReportLanguageCode("en");
    initializeScenarioLabFromBaseline(snapshot.result, snapshot.payload);
  }

  function resetScenarioLab() {
    setScenarioLabOpen(false);
    setScenarioLabScenarios([]);
    setScenarioLabError("");
    setScenarioLabRunningId("");
    setScenarioLabActiveReportId("");
  }


  async function refreshScenarioLabAccessStatus(emailOverride = "", baselineAssessmentIdOverride = "") {
    const email = String(emailOverride || getStoredTvacAccessEmail() || "").trim();
    const baselineAssessmentId = normalizeScenarioLabAccessId(
      baselineAssessmentIdOverride || getCurrentScenarioLabBaselineId() || ""
    );

    if (!email) {
      setScenarioLabPaidAccess(false);
      setScenarioLabAccessScope("");
      setScenarioLabAccessBaselineId("");
      return { allowed: false, reason: "missing_email" };
    }

    try {
      const status = await checkScenarioLabAccess(email, baselineAssessmentId);
      const allowed = Boolean(status?.allowed);
      const accessScope = String(status?.accessScope || status?.access_scope || status?.source || "").trim();
      const grantedBaselineId = normalizeScenarioLabAccessId(
        status?.baselineAssessmentId || status?.baseline_assessment_id || status?.scenarioLabBaselineId || ""
      );

      setScenarioLabPaidAccess(allowed);
      setScenarioLabAccessScope(allowed ? accessScope : "");
      setScenarioLabAccessBaselineId(allowed ? grantedBaselineId : "");
      return status || { allowed: false };
    } catch (e) {
      return { allowed: false, reason: cleanErrorMessage(e?.message || e) };
    }
  }

  async function handleScenarioLabAddonCheckout() {
    if (scenarioLabCheckoutLoading) return;
    setScenarioLabCheckoutError("");

    const snapshot = getScenarioLabBaselineSnapshot();
    if (!snapshot) {
      setScenarioLabCheckoutError("Run a TVAC assessment first, then open the Scenario Lab add-on.");
      return;
    }

    const email = String(getStoredTvacAccessEmail() || "").trim();
    if (!email) {
      setScenarioLabCheckoutError("Please sign in or enter your access email before opening checkout.");
      return;
    }

    if (!snapshot.baselineAssessmentId) {
      setScenarioLabCheckoutError("This report is missing a stable baseline ID. Please run the TVAC assessment again before opening Scenario Lab.");
      return;
    }

    setScenarioLabCheckoutLoading(true);
    try {
      const access = await refreshScenarioLabAccessStatus(email, snapshot.baselineAssessmentId);
      if (access?.allowed) {
        setScenarioLabPaidAccess(true);
        createScenarioLabFromSnapshot(snapshot);
        return;
      }

      saveScenarioLabPendingBaselineSnapshot(snapshot);
      const checkout = await createScenarioLabCheckout({
        email,
        baselineAssessmentId: snapshot.baselineAssessmentId,
        reportLanguageCode: snapshot.reportLanguageCode || "en",
      });
      window.location.assign(checkout.url);
    } catch (e) {
      setScenarioLabCheckoutError(cleanErrorMessage(e?.message || e));
    } finally {
      setScenarioLabCheckoutLoading(false);
    }
  }

  function updateScenarioMeta(id, key, value) {
    setScenarioLabScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.id !== id) return scenario;

        const replacementCount = Number(scenario.replacementCount || 0);
        const shouldInvalidateAssessedScenario =
          !scenario.locked && scenario.result && scenario.status === "complete";

        if (shouldInvalidateAssessedScenario && replacementCount >= 1) {
          return scenario;
        }

        const nextReplacementCount = shouldInvalidateAssessedScenario
          ? Math.min(replacementCount + 1, 1)
          : replacementCount;

        if (key === "name") {
          return shouldInvalidateAssessedScenario
            ? { ...scenario, [key]: value, status: "draft", result: null, error: "", lastAssessmentSignature: "", replacementCount: nextReplacementCount }
            : { ...scenario, [key]: value };
        }
        return { ...scenario, [key]: value, status: "draft", result: null, error: "", lastAssessmentSignature: "", replacementCount: nextReplacementCount };
      })
    );
  }

  function updateScenarioPayload(id, key, value) {
    setScenarioLabScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.id !== id || scenario.locked) return scenario;

        const replacementCount = Number(scenario.replacementCount || 0);
        const shouldInvalidateAssessedScenario =
          scenario.result && scenario.status === "complete";

        if (shouldInvalidateAssessedScenario && replacementCount >= 1) {
          return scenario;
        }

        const nextReplacementCount = shouldInvalidateAssessedScenario
          ? Math.min(replacementCount + 1, 1)
          : replacementCount;

        const nextPayload = scenarioLabClone(scenario.payload || {});
        if (key === "saleJurisdictions") {
          nextPayload[key] = String(value || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
        } else {
          nextPayload[key] = value;
        }
        return { ...scenario, payload: nextPayload, result: null, status: "draft", error: "", lastAssessmentSignature: "", replacementCount: nextReplacementCount };
      })
    );
  }

  function buildScenarioEvaluationPayload(scenario) {
    const payload = scenarioLabClone(scenario?.payload || {});
    const baseText = String(payload.text || "").trim();
    const changed = getScenarioChangedAssumptions(getCurrentScenarioBaselinePayload(), payload);
    const changedText = changed.length
      ? changed.map((ch) => `- ${ch.label}: ${ch.before} -> ${ch.after}`).join("\n")
      : "- No changed assumptions recorded.";

    const scenarioNote = [
      "The following Scenario Lab assumption set is user-defined.",
      "TVAC is asked to assess this assumption set under the same methodology; it is not asked to design, recommend, approve, validate, or certify any implementation structure.",
      scenario?.name ? `Scenario name: ${scenario.name}` : "",
      scenario?.description ? `Scenario description: ${scenario.description}` : "",
      scenario?.changeRationale ? `User rationale for testing these assumptions: ${scenario.changeRationale}` : "",
      "Changed assumptions:",
      changedText,
    ].filter(Boolean).join("\n");

    return {
      ...payload,
      text: scenarioNote ? `${baseText}

SCENARIO LAB ASSUMPTION SET
${scenarioNote}`.trim() : baseText,
      reportLanguageCode: "en",
      reportLanguage: getReportLanguageName("en"),
      deep: true,
      // Scenario Lab is only opened from a completed TVAC report, where the
      // user already accepted the TVAC disclaimer before the baseline was run.
      // After Stripe checkout the React state is rehydrated, so the checkbox
      // state may be false even though the baseline flow was valid.
      disclaimerAccepted: true,
      // Request-control metadata. The backend strips these before the evaluator prompt,
      // but uses them for cache namespace, telemetry and baseline integrity.
      assessmentMode: "scenario_lab_variant",
      scenarioId: scenario?.id || "scenario",
      baselineAssessmentId: masterAssessmentRef.current?.canonical_assessment_id || masterAssessment?.canonical_assessment_id || null,
      forceFresh: true,
    };
  }

  function validateScenarioBeforeRun(scenario) {
    if (!scenario || scenario.locked) return "";
    if (!scenarioLabHasActiveAccess) {
      return "Scenario Lab access is not enabled for this account/session.";
    }

    const baseline = scenarioLabScenarios.find((s) => s.id === "baseline");
    const changed = getScenarioChangedAssumptions(baseline?.payload, scenario.payload);
    const rationale = String(scenario.changeRationale || "").trim();
    if (changed.length === 0) {
      return `${scenario.label} has no changed assumptions yet. Change at least one selected assumption before assessing it.`;
    }
    if (rationale.length < 12) {
      return `${scenario.label} needs a short rationale explaining why these assumptions are being tested.`;
    }
    return "";
  }

  async function runScenarioAssessment(id) {
    const scenario = scenarioLabScenarios.find((s) => s.id === id);
    const validationError = validateScenarioBeforeRun(scenario);
    if (validationError) {
      setScenarioLabError(validationError);
      return;
    }

    const evaluationPayload = buildScenarioEvaluationPayload(scenario);
    const assessmentSignature = getScenarioAssessmentSignature(evaluationPayload);

    if (
      scenario?.result &&
      scenario?.lastAssessmentSignature &&
      scenario.lastAssessmentSignature === assessmentSignature &&
      scenario.status === "complete"
    ) {
      // Same baseline + same scenario assumptions + same rationale: keep the
      // existing assessment rather than creating artificial score movement.
      setScenarioLabError("");
      setScenarioLabScenarios((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: "complete", error: "" } : s))
      );
      return;
    }

    setScenarioLabError("");
    setScenarioLabRunningId(id);
    setScenarioLabScenarios((prev) => prev.map((s) => (s.id === id ? { ...s, status: "running", error: "" } : s)));

    try {
      const response = await evaluate(evaluationPayload, {
        assessmentMode: "scenario_lab_variant",
        scenarioId: scenario?.id || "scenario",
        baselineAssessmentId: masterAssessmentRef.current?.canonical_assessment_id || masterAssessment?.canonical_assessment_id || null,
        forceFresh: true,
      });
      const result = response?.result || response;
      setScenarioLabScenarios((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, result, status: "complete", error: "", lastAssessmentSignature: assessmentSignature }
            : s
        )
      );
    } catch (e) {
      const message = cleanErrorMessage(e?.message || e);
      setScenarioLabError(message);
      setScenarioLabScenarios((prev) => prev.map((s) => (s.id === id ? { ...s, status: "error", error: message } : s)));
    } finally {
      setScenarioLabRunningId("");
    }
  }


  function viewScenarioReport(id) {
    const scenario = scenarioLabScenarios.find((s) => s.id === id);
    if (!scenario?.result) {
      setScenarioLabError("This scenario report is not available yet. Run the scenario assessment first.");
      return;
    }

    setScenarioLabError("");
    setScenarioLabActiveReportId(id);
    setReportLanguageCode("en");
    setLastPayload(scenarioLabClone(scenario.payload || lastPayload));
    setOut(scenarioLabClone(scenario.result));

    // Scenario Lab runs in a dedicated overlay. When a user opens a full scenario
    // report, close the workspace and move focus to the selected report below.
    // The return card above the report lets the user re-open Scenario Lab.
    setScenarioLabOpen(false);

    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        const el = document.getElementById("section-cover");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }

const reportVerdictKey = String(
  out?.verdict || out?.overallVerdict || out?.overall_verdict || ""
)
  .toLowerCase()
  .trim();

const reportIsNonViable = new Set([
  "no-go",
  "no go",
  "non-viable",
  "non viable",
  "stop",
  "non_viable",
]).has(reportVerdictKey);

const reportIsConditional = new Set([
  "conditional_go",
  "conditional go",
  "conditional",
]).has(reportVerdictKey);

const reportIsViable = new Set([
  "go",
  "viable",
  "go / viable",
]).has(reportVerdictKey);

const roadmapDisplayLabels = getRoadmapDisplayLabels(
  reportLanguageCode,
  lastPayload || {},
  out || {}
);

const providerDirectoryIncluded = Boolean(out) && !isClosedWorkflowInfrastructureCase(lastPayload || {}, out || {});
const providerDirectoryAppendixLabel = reportLabels.appendixB;
const glossaryAppendixLabel = providerDirectoryIncluded ? reportLabels.appendixC : relabelAppendix(reportLabels.appendixC, "B");
const verbatimAppendixLabel = providerDirectoryIncluded ? reportLabels.appendixD : relabelAppendix(reportLabels.appendixD, "C");
const disclaimerAppendixLabel = providerDirectoryIncluded ? reportLabels.appendixE : relabelAppendix(reportLabels.appendixE, "D");
const verbatimAppendixReference = providerDirectoryIncluded
  ? reportLabels.appendixDReference
  : relabelAppendix(reportLabels.appendixDReference, "C");
const userInputsAppendixLetter = providerDirectoryIncluded ? "D" : "C";
const structuredInputRecord = getStructuredInputRecord(
  lastPayload || {},
  reportLanguageCode,
  reportLabels
);

const providerDirectoryChapterNo = reportIsNonViable ? 10 : 11;
const glossaryChapterNo = reportIsNonViable ? 11 : 12;
const appendixChapterNo = reportIsNonViable ? 12 : 13;
const contactChapterNo = reportIsNonViable ? 13 : 14;
const hasCanonicalMasterForDisplay = Boolean(masterAssessmentRef.current || masterAssessment);
const currentReportNeedsLocalization = Boolean(out && reportObjectNeedsLocalization(out, reportLanguageCode));
const reportIsReadyForDisplay = Boolean(out) && !loading && !localizing && !currentReportNeedsLocalization;
const showLocalizationStatusCard =
  reportLanguageCode !== "en" &&
  !loading &&
  hasCanonicalMasterForDisplay &&
  (localizing || !reportIsReadyForDisplay);
const localizationCopy = getLocalizationPendingCopy(reportLanguageCode, getReportLanguageName(reportLanguageCode));
const currentReportYieldSignal = containsYieldReturnSignal(lastPayload || buildPayload());
const activeScenarioReport = scenarioLabActiveReportId
  ? scenarioLabScenarios.find((s) => s.id === scenarioLabActiveReportId)
  : null;
const activeScenarioReportTitle = activeScenarioReport
  ? getScenarioDisplayTitle(
      activeScenarioReport,
      scenarioLabScenarios.find((s) => s.id === "baseline")?.payload || baselinePayloadRef.current || lastPayload
    )
  : "";

  useEffect(() => {
    if (!apiReportRenderMode) return undefined;
    if (!reportIsReadyForDisplay) return undefined;

    const timer = window.setTimeout(() => {
      try {
        const reportElement = document.querySelector(".print-area");
        assertRenderedReportQuality(reportElement?.innerText || "", lastPayload || {});
        window.__TVAC_REPORT_ERROR__ = "";
        window.__TVAC_REPORT_READY__ = true;
      } catch (e) {
        console.error("[TVAC] Server PDF render blocked by quality gate", {
          code: e?.code || "TVAC_REPORT_RENDER_BLOCKED",
          details: Array.isArray(e?.details) ? e.details : [],
        });
        window.__TVAC_REPORT_READY__ = false;
        window.__TVAC_REPORT_ERROR__ = "TVAC_REPORT_QUALITY_GATE_FAILED";
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [apiReportRenderMode, reportIsReadyForDisplay, lastPayload]);
            
  return (
    <div className="section" style={{ maxWidth: 1024, margin: "0 auto" }}>
<ReportBrandingStyles branding={activeReportBranding} />

<ExampleCasesModal
  open={showExamples}
  onClose={() => setShowExamples(false)}
  onUseExample={applyExample}
  uiLanguageCode={uiLanguageCode}
/>

<ExampleCreditWarningModal
  example={showExampleRunWarning ? loadedExampleCase : null}
  onCancel={() => setShowExampleRunWarning(false)}
  onStartOwnCase={clearExampleAndStartOwnCase}
  onRunAnyway={async () => {
    setShowExampleRunWarning(false);
    await runAssessment();
  }}
  uiLanguageCode={uiLanguageCode}
/>
      
      {/* Intro cards in two-column layout */}
      <div className="grid-2" style={{ marginBottom: 12 }}>
        {/* How to use TVAC */}
        <div className="card card-accent" style={{ marginBottom: 10 }}>
          <h4 style={{ marginTop: 0 }}>{t("intro.howToUse.title")}</h4>
          <ol className="small" style={{ marginTop: 6 }}>
            <li>
              <strong>{t("intro.howToUse.1")}</strong> – {t("intro.howToUse.1body")}
            </li>
            <li>
              <strong>{t("intro.howToUse.2")}</strong> – {t("intro.howToUse.2body")}
            </li>
            <li>
              <strong>{t("intro.howToUse.3")}</strong> – {t("intro.howToUse.3body")}
            </li>
          </ol>
          <p className="muted small" style={{ marginTop: 6 }}>
            {t("intro.howToUse.disclaimer")}
          </p>
        </div>

        {/* Guidance card: how to describe the case */}
        <div className="card card-accent" style={{ marginBottom: 10 }}>
          <h4 style={{ marginTop: 0 }}>{t("intro.describe.title")}</h4>
          <p className="muted small" style={{ marginTop: 4 }}>
            {t("intro.describe.intro")}
          </p>
          <ul className="small toc" style={{ marginTop: 6 }}>
            <li>
              <strong>{t("intro.describe.asset")}</strong> – {t("intro.describe.assetBody")}
            </li>
            <li>
              <strong>{t("intro.describe.jurisdictions")}</strong> – {t("intro.describe.jurisdictionsBody")}
            </li>
            <li>
              <strong>{t("intro.describe.investors")}</strong> – {t("intro.describe.investorsBody")}
            </li>
            <li>
              <strong>{t("intro.describe.liquidity")}</strong> – {t("intro.describe.liquidityBody")}
            </li>
            <li>
              <strong>{t("intro.describe.technology")}</strong> – {t("intro.describe.technologyBody")}
            </li>
            <li>
              <strong>{t("intro.describe.objectives")}</strong> – {t("intro.describe.objectivesBody")}
            </li>
            <li>
              <strong>{t("intro.describe.constraints")}</strong> – {t("intro.describe.constraintsBody")}
            </li>
            <li>
              <strong>{t("intro.describe.evidence")}</strong> – {t("intro.describe.evidenceBody")}
            </li>
          </ul>
        </div>
      </div>

      {/* Step 1 + Step 2 combined card */}
      <div
        className="card card-accent"
        style={{ marginTop: 8, marginBottom: 12 }}
      >
        {/* Step 1 */}
        <h3
          className="section-title"
          style={{ margin: "0 0 4px", fontSize: "1.1rem" }}
        >
          {t("step1.title")}
        </h3>
       <p className="muted small" style={{ margin: "0 0 8px" }}>
  {t("step1.description.before")}{" "}
  <strong>{t("step1.description.bold")}</strong> {t("step1.description.after")}
</p>

        <div
          className="small"
          style={{
            margin: "0 0 10px",
            padding: "9px 11px",
            border: "1px solid rgba(255,122,0,0.22)",
            background: "rgba(255,122,0,0.05)",
            lineHeight: 1.45,
          }}
        >
          <strong>{t("step1.scopeTitle")}:</strong> {t("step1.scopeBody")}
          <div style={{ marginTop: 6 }}>
            <strong>{t("step1.optionalTitle")}:</strong> {t("step1.optionalBody")}
          </div>
        </div>

        <textarea
          ref={refsMap.caseText}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={t("step1.placeholder")}
          style={{
            width: "100%",
            minHeight: 110,
            padding: 12,
            borderRadius: 0,
            border: "1px solid var(--brand)",
            marginBottom: 10,
            background: "#ffffff",
          }}
        />

<div className="tvac-case-actions">
  <button className="action" type="button" onClick={() => setShowExamples(true)}>
    {t("step1.examples", { count: EXAMPLE_CASES.length })}
  </button>
</div>

{loadedExampleCase && (
  <div className="tvac-example-loaded-note small" role="status">
    <strong>{t("step1.exampleLoaded")}:</strong> {loadedExampleCase.title}. {t("step1.exampleCreditNote")}
    <button className="tvac-link-button" type="button" onClick={clearExampleAndStartOwnCase}>
      {t("step1.clearExample")}
    </button>
  </div>
)}

        {/* Step 2 */}
        <h3
          className="section-title"
          style={{ margin: "10px 0 4px", fontSize: "1.1rem" }}
        >
          {t("step2.title")}
        </h3>
        <p className="muted small" style={{ margin: "0 0 8px" }}>
          {t("step2.description")}
        </p>

        <StructuredInputs state={form} setState={setForm} refsMap={refsMap} uiLanguageCode={uiLanguageCode} />

        {/* Længde & data/privacy note, nu logisk placeret efter Step 1/2 */}
        <p className="muted small" style={{ margin: "8px 0 0" }}>
          {t("step2.privacy.before")}{" "}
          <strong>{t("step2.privacy.bold")}</strong>. {t("step2.privacy.after")}
        </p>
      </div>

      {/* Step 3 */}
      <div style={{ marginTop: 20 }}>
        <h3
          className="section-title"
          style={{ margin: "0 0 4px", fontSize: "1.1rem" }}
        >
          {t("step3.title")}
        </h3>
        <p className="muted small" style={{ margin: 0 }}>
          {t("step3.description")}
        </p>
      </div>


<div className="card" style={{ marginTop: 12, marginBottom: 10, padding: 12 }}>
  <label
    htmlFor="tvac-report-language"
    style={{ display: "block", fontWeight: 600, marginBottom: 4 }}
  >
    {t("reportLanguage.label")}
  </label>
  <select
    id="tvac-report-language"
    value={reportLanguageCode}
    onChange={(e) => handleReportLanguageChange(e.target.value)}
    style={{
      width: "100%",
      maxWidth: 360,
      padding: "8px 10px",
      border: "1px solid #d1d5db",
      borderRadius: 0,
      background: "#ffffff",
    }}
  >
    {REPORT_LANGUAGES.map((lang) => {
      const publiclyAvailable = PUBLIC_REPORT_LANGUAGE_CODES.has(lang.code);
      const available = reportLanguageIsAvailable(lang.code, internalReportLanguageAccess);
      const statusLabel = publiclyAvailable
        ? ""
        : internalReportLanguageAccess && available
          ? " — Internal testing"
          : " — Coming soon";

      return (
        <option key={lang.code} value={lang.code} disabled={!available}>
          {lang.label}{statusLabel}
        </option>
      );
    })}
  </select>
  <p className="muted small" style={{ margin: "6px 0 0" }}>
    {t("reportLanguage.helper")}
  </p>
  {reportLanguageCode !== "en" && (
    <p className="muted small" style={{ margin: "8px 0 0", maxWidth: 760, lineHeight: 1.45 }}>
      <strong>{languageReferenceNote.title}:</strong> {languageReferenceNote.body}
    </p>
  )}
</div>
<div
  style={{
    marginTop: 10,
    marginBottom: 6,
    fontSize: 14,
    color: "#4a5568",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 10,
    maxWidth: 920,
  }}
>
  <input
    id="tvac-disclaimer"
    type="checkbox"
    checked={disclaimerAccepted}
    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
    style={{
      width: 16,
      height: 16,
      marginTop: 3,
      marginLeft: 0,
      marginRight: 0,
      flex: "0 0 auto",
    }}
  />
 <label
  htmlFor="tvac-disclaimer"
  style={{
    cursor: "pointer",
    margin: 0,
    lineHeight: 1.5,
  }}
>
  {t("disclaimer.accept")}
</label>{" "}
<button
  type="button"
  onClick={() => setShowFrontDisclaimer((v) => !v)}
  style={{
    color: "#1a73e8",
    textDecoration: "underline",
    fontSize: "inherit",
    marginLeft: 4,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
  }}
  aria-expanded={showFrontDisclaimer}
  aria-controls="pre-run-full-disclaimer"
>
  {t("disclaimer.full")}
</button>
</div>

{showFrontDisclaimer && (
  <div
    id="pre-run-full-disclaimer"
    className="card"
    style={{
      marginTop: 10,
      marginBottom: 10,
      maxWidth: 920,
      borderColor: "#f4b183",
      background: "#fffdf8",
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>{reportLabels.disclaimer || "Disclaimer"}</h3>
      <button
        type="button"
        onClick={() => setShowFrontDisclaimer(false)}
        className="action"
        style={{ flex: "0 0 auto" }}
      >
        {t("disclaimer.close")}
      </button>
    </div>

    {reportLanguageCode !== "en" && (
      <>
        <p className="muted small" style={{ marginTop: 0, lineHeight: 1.45 }}>
          <strong>{languageReferenceNote.title}:</strong> {languageReferenceNote.body}
        </p>
        <h4 style={{ marginTop: 14 }}>{reportLabels.translatedDisclaimerHeading}</h4>
        <div className="disclaimer small" style={{ lineHeight: 1.5 }}>
          {getReportTranslatedDisclaimerParagraphs(reportLanguageCode).map((para, i) => (
            <p key={`pre-run-translated-disclaimer-${i}`}>{para}</p>
          ))}
        </div>
        <h4 style={{ marginTop: 18 }}>{reportLabels.englishDisclaimerHeading}</h4>
      </>
    )}

    <div className="disclaimer small" style={{ lineHeight: 1.5 }}>
      {ENGLISH_FULL_DISCLAIMER.map((para, i) => (
        <p key={`pre-run-english-disclaimer-${i}`}>{para}</p>
      ))}
    </div>
  </div>
)}
            
<div className="cta-row" style={{ marginTop: 8 }}>
  <button
    onClick={onAnalyzeClick}
    disabled={loading || localizing || !disclaimerAccepted}
    className="btn"
  >
    {loading && <span className="spinner" aria-hidden="true" />}
    {loading ? t("run.analyzing") : t("run.button")}
  </button>
</div>

    {loading ? (
  <>
    <p className="muted small" style={{ marginTop: 6 }}>
      {t("run.progress.before")}{" "}
      <strong>{formatElapsed(elapsedSeconds)}</strong> {t("run.progress.after")}
    </p>

    <div
  className="no-print progress-wrap"
  role="progressbar"
  aria-label={t("run.progressAria")}
  aria-valuemin={0}
  aria-valuemax={100}
  aria-valuenow={progressPct}
>
  <div className="progress-track">
    <div className="progress-fill" style={{ width: `${progressPct}%` }} />

  </div>
</div>

  </>
) : (
  <p className="muted small" style={{ marginTop: 6 }}>
    {t("run.idle")}
  </p>
)}
    
      {/* Verdict guide */}
      <div className="card card-accent" style={{ marginBottom: 12 }}>
        <strong>{t("verdict.title")}</strong>
        <p className="muted small" style={{ marginTop: 6 }}>
          {t("verdict.body")}
        </p>
        <ul style={{ marginTop: 8 }}>
        <li>
            <strong>Viable</strong> - {t("verdict.viable")}
          </li>
          <li>
            <strong>Conditional Go</strong> - {t("verdict.conditional")}
          </li>
          <li>
            <strong>Non-viable</strong> - {t("verdict.nonviable")}
          </li>
        </ul>
      </div>

      {err && (
        <div
          className="card"
          style={{
            borderColor: "var(--bad)",
            color: "var(--bad)",
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      )}

      {showLocalizationStatusCard && (
        <div className="card card-accent" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ maxWidth: 720 }}>
              <strong>{localizationCopy.title}</strong>
              <p className="muted small" style={{ marginTop: 6, lineHeight: 1.45 }}>
                {localizing ? localizationCopy.pending : localizationCopy.failed}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {localizing && <span className="spinner" aria-hidden="true" />}
              {!localizing && (
                <>
                  <button className="action" type="button" onClick={retrySelectedLanguageVersion}>
                    {localizationCopy.retry}
                  </button>
                  <button className="action" type="button" onClick={showEnglishMasterReport}>
                    {localizationCopy.english}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {reportIsReadyForDisplay && (
        <>
          {scenarioLabHasActiveAccess && (
            <ScenarioLabPanel
              scenarios={scenarioLabScenarios}
              isOpen={scenarioLabOpen}
              error={scenarioLabError}
              runningId={scenarioLabRunningId}
              devAccess={scenarioLabDevAccess}
              activeReportScenarioId={scenarioLabActiveReportId}
              onOpen={scenarioLabScenarios.length ? () => setScenarioLabOpen(true) : createScenarioLabFromCurrent}
              onClose={() => setScenarioLabOpen(false)}
              onReset={resetScenarioLab}
              onScenarioMetaChange={updateScenarioMeta}
              onScenarioPayloadChange={updateScenarioPayload}
              onRunScenario={runScenarioAssessment}
              onViewScenarioReport={viewScenarioReport}
              getScenarioAssessmentSignatureForUi={(scenario) => getScenarioAssessmentSignature(buildScenarioEvaluationPayload(scenario))}
              showInlineCta={false}
            />
          )}

          {scenarioLabHasActiveAccess && activeScenarioReport && (
            <div className="card no-print scenario-lab-report-context-card">
              <div>
                <div className="scenario-lab-kicker">Scenario Lab report view</div>
                <h3 style={{ margin: "2px 0 4px" }}>
                  Viewing full report: {activeScenarioReportTitle}
                </h3>
                <p className="muted small" style={{ margin: 0, lineHeight: 1.45 }}>
                  This is the selected full scenario report. Return to the Scenario Lab workspace to switch reports, compare scenarios, or open the comparison mini-report.
                </p>
              </div>
              <div className="scenario-lab-report-context-actions">
                <button className="btn" type="button" onClick={() => setScenarioLabOpen(true)}>
                  Back to Scenario Lab
                </button>
              </div>
            </div>
          )}

        <div
          className={activeReportBranding.enabled ? "print-area tvac-branded-report" : "print-area"}
          style={getReportBrandingStyleVars(activeReportBranding)}
        >
          {/* Rapport-header – stor, sort overskrift uden card */}
          <section id="section-cover" className="chapter cover">
            <div className="cover-band" aria-hidden="true" />
            <div className="card cover-card" style={{ marginTop: 24, marginBottom: 16, padding: "22px 22px 18px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div className="cover-left-row" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <ReportCoverBrand branding={activeReportBranding} />

                  <div className="cover-title-block">
                    <div className="muted small" style={{ marginBottom: 2 }}>
                      {getReportCoverKicker(activeReportBranding)}
                    </div>
                    <h1 style={{ margin: 0, fontSize: 44, lineHeight: 1.05 }}>{reportLabels.coverTitle}</h1>
                    <div className="muted" style={{ marginTop: 6 }}>
                      {reportLabels.coverSubtitle}
                    </div>
                  </div>
                </div>

                <div className="small muted" style={{ textAlign: "right" }}>
                  <div><strong>{reportLabels.generated}:</strong> {new Date().toLocaleString()}</div>
                  {out?.input_completeness?.score != null && (
                    <div><strong>{reportLabels.userInputCoverage}:</strong> {getAssessmentInputStatus(out.input_completeness, reportLabels)}</div>
                  )}
                  {(out?.evidence_readiness || out?.evidenceReadiness) && (
                    <div>
                      <strong>{reportLabels.evidenceReadiness}:</strong>{" "}
                      {getSupportingEvidenceLevel(out.evidence_readiness || out.evidenceReadiness, reportLabels)}
                    </div>
                  )}
                  {(out?.regulatory_integrity || out?.regulatoryIntegrity)?.issuance_profile && (
                    <div>
                      <strong>{reportLabels.regulatoryCoverage}:</strong>{" "}
                      {(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.name || (out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.resolved_id}
                      {(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.verification_status
                        ? ` · ${(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.verification_status}`
                        : ""}
                    </div>
                  )}
                    {out?.maturityLevel && (
                    <div>
                      <strong>{reportLabels.caseDesignMaturity}:</strong> {localizeMaturityLevel(out.maturityLevel, reportLanguageCode)}
                    </div>
                  )}
                </div>
              </div>
              <div className="cover-formula-block small">
                {reportLabels.reportFormulaIntro}
                <div className="formula-brand-box cover-formula-line">
                  {reportLabels.formulaText}
                </div>
                {reportLabels.languageCode !== "en" && (
                  <div className="muted small" style={{ marginTop: 12, maxWidth: 820, lineHeight: 1.5 }}>
                    <strong>{languageReferenceNote.title}:</strong> {languageReferenceNote.body}
                  </div>
                )}
              </div>

              <div className="cover-gray-blocks">
                {out?.maturityNote && (
                  <div className="cover-design-box small">
                    <strong>{reportLabels.caseDesignInterpretation || "Design-stage interpretation"}:</strong>
                    <div style={{ marginTop: 4 }}>{localizeMaturityNoteText(out.maturityNote, reportLanguageCode)}</div>

                    {Array.isArray(out?.openDesignAreas) && out.openDesignAreas.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <span className="muted"><strong>{reportLabels.openDesignAreas}:</strong> {out.openDesignAreas
                          .map((item) => localizeGeneratedText(String(item || "").trim().replace(/[.,;:]+$/, ""), reportLanguageCode))
                          .filter(Boolean)
                          .join("; ")}</span>
                      </div>
                    )}
                  </div>
                )}

              {lastPayload?.text && (
                <div className="cover-case-block">
                  <div className="small"><strong>{reportLabels.reportAnalyzesCase}</strong></div>
                  <div className="case-excerpt">
                    {coverCaseExcerpt(lastPayload.text, 560)}
                  </div>
                  <div className="muted small" style={{ marginTop: 6, marginBottom: 14 }}>
                    {verbatimAppendixReference}
                  </div>
                  <div className="cover-disclaimer" style={{ marginTop: 14, maxWidth: 760 }}>
                    <p style={{ margin: 0 }}>
                      {reportLabels.coverDisclaimer1}
                    </p>
                    <p style={{ margin: "6px 0 0 0" }}>
                      {reportLabels.coverDisclaimer2}
                    </p>

                  </div>
                </div>
              )}
              </div>
            </div>
          </section>

          {/* Asset class & market context */}
          {/* Lille indholdsfortegnelse for selve analysen */}
          <div className="card toc-card" style={{ marginBottom: 12 }}>
            <strong>{reportLabels.tableOfContents}</strong>

 <ul className="toc">
    <li>
    <a
      href="#exec-summary"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("exec-summary");
      }}
    >
      1. {reportLabels.executiveSummary}
    </a>

    <ul className="toc-sub">
      <li className="toc-level-2">
        <a
          href="#section-what-is-different"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-what-is-different");
          }}
        >
          1.1 {reportLabels.whatDifferent}
        </a>
      </li>
    </ul>
  </li>
  <li>
    <a
      href="#section-asset-class"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-asset-class");
      }}
    >
      2. {reportLabels.assetClassContext}
    </a>
  </li>

  <li>
    <a
      href="#section-analysis"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-analysis");
      }}
    >
      3. {reportLabels.analysisResult}
    </a>

    <ul className="toc-sub">
      <li className="toc-level-2">
        <a
          href="#section-analysis"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-analysis");
          }}
        >
          3.1 {reportLabels.keyAssumptions}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-value-pathways"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-value-pathways");
          }}
        >
          3.2 {reportLabels.valuePathways}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-analysis"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-analysis");
          }}
        >
          3.3 {reportLabels.investorSegmentation}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-analysis"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-analysis");
          }}
        >
          3.4 {reportLabels.operationalCosts}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-regulatory-fit"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-regulatory-fit");
          }}
        >
          3.5 {reportLabels.regulatoryPath}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-regulatory-fit"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-regulatory-fit");
          }}
        >
          3.6 {reportLabels.custodyVenueFit}
        </a>
      </li>

      {hasSettlementCashLegReadiness && (
        <li className="toc-level-2">
          <a
            href="#section-regulatory-fit"
            className="toc-link"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("section-regulatory-fit");
            }}
          >
            3.7 {getSettlementCashLegHeading(reportLanguageCode)}
          </a>
        </li>
      )}

      <li className="toc-level-2">
        <a
          href="#section-analysis"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-analysis");
          }}
        >
          3.{hasSettlementCashLegReadiness ? "8" : "7"} {reportLabels.riskRegister}
        </a>
      </li>
    </ul>
  </li>

  <li>
    <a
      href="#section-stakeholders"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-stakeholders");
      }}
    >
      4. {reportLabels.stakeholderAddedValue}
    </a>

    <ul className="toc-sub">
      <li className="toc-level-2">
        <a
          href="#section-stakeholders"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-stakeholders");
          }}
        >
          4.1 {reportLabels.stakeholderScores}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-stakeholders"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-stakeholders");
          }}
        >
          4.2 {reportLabels.assetOwnerIssuer}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-stakeholders"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-stakeholders");
          }}
        >
          4.3 {reportLabels.investor}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-stakeholders"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-stakeholders");
          }}
        >
          4.4 {reportLabels.infrastructureProviders}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-stakeholders"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-stakeholders");
          }}
        >
          4.5 {reportLabels.whoBearsCostsRisks}
        </a>
      </li>
    </ul>
  </li>

  <li>
    <a
      href="#section-factor-breakdown"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-factor-breakdown");
      }}
    >
      5. {reportLabels.factorBreakdown}
    </a>
  </li>

  <li>
    <a
      href="#section-risk-heatmap"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-risk-heatmap");
      }}
    >
      6. {reportLabels.riskHeatmap}
    </a>
  </li>

  <li>
    <a
      href="#section-recommendations"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-recommendations");
      }}
    >
      7. {reportLabels.recommendationsNextSteps}
    </a>

    <ul className="toc-sub">
      <li className="toc-level-2">
        <a
          href="#rec-improve"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("rec-improve");
          }}
        >
          7.1 {reportLabels.improveScore}
        </a>

               <ul className="toc-sub">
          {getRecImproveTocItems(out, buildPayload()).map((title, idx) => (
            <li key={`rec-improve-${idx}`} className="toc-level-3">
              <a
                href="#rec-improve"
                className="toc-link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("rec-improve");
                }}
              >
                {`7.1.${idx + 1} ${localizeStatic(title, reportLanguageCode)}`}
              </a>
            </li>
          ))}
        </ul>
      </li>

     {!reportIsNonViable && (
  <>
    <li className="toc-level-2">
      <a
        href="#rec-whatif"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("rec-whatif");
        }}
      >
        7.2 {reportLabels.designWhatIf}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#rec-future"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("rec-future");
        }}
      >
        7.3 {reportLabels.futureScenarios}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#rec-next-steps"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("rec-next-steps");
        }}
      >
        7.4 {reportLabels.suggestedNextSteps}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#rec-risk-actions"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("rec-risk-actions");
        }}
      >
        7.5 {reportLabels.riskMitigationActions}
      </a>
    </li>
  </>
)}
 </ul>
</li>

  {!reportIsNonViable && (
  <li>
    <a
      href="#section-roadmap"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("section-roadmap");
      }}
    >
      8. {reportLabels.roadmap}
    </a>

    <ul className="toc-sub">
      <li className="toc-level-2">
        <a
          href="#section-roadmap-operational"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-operational");
          }}
        >
          8.1 {roadmapDisplayLabels.operationalRoadmap}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-roadmap-phase-1"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-phase-1");
          }}
        >
          8.2 {roadmapDisplayLabels.phase1}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-roadmap-phase-2"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-phase-2");
          }}
        >
          8.3 {roadmapDisplayLabels.phase2}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-roadmap-phase-3"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-phase-3");
          }}
        >
          8.4 {roadmapDisplayLabels.phase3}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-roadmap-phase-4"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-phase-4");
          }}
        >
          8.5 {roadmapDisplayLabels.phase4}
        </a>
      </li>

      <li className="toc-level-2">
        <a
          href="#section-roadmap-phase-5"
          className="toc-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("section-roadmap-phase-5");
          }}
        >
          8.6 {roadmapDisplayLabels.phase5}
        </a>
      </li>
    </ul>
  </li>
)}          


<li>
  <a
    href="#section-methodology"
    className="toc-link"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection("section-methodology");
    }}
  >
    {reportLabels.appendixA}
  </a>

  <ul className="toc-sub">
    <li className="toc-level-2">
      <a
        href="#section-how-to-read"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-how-to-read");
        }}
      >
        A.1 {reportLabels.howToRead}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#section-decision-integrity"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-decision-integrity");
        }}
      >
        A.2 {reportLabels.decisionIntegrity}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#section-minimum-evidence"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-minimum-evidence");
        }}
      >
        A.3 {reportLabels.minimumEvidence}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#section-sensitivity-levers"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-sensitivity-levers");
        }}
      >
        A.4 {reportLabels.sensitivityLevers}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#section-how-we-compute"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-how-we-compute");
        }}
      >
        A.5 {reportLabels.computeAddedValue}
      </a>
    </li>

    <li className="toc-level-2">
      <a
        href="#section-provenance-register"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-provenance-register");
        }}
      >
        A.6 {reportLabels.provenanceRegister || "Evidence, provenance & regulatory source register"}
      </a>
    </li>
  </ul>
</li>
  {providerDirectoryIncluded && (
  <li>
    <a
      href="#provider-directory"
      className="toc-link"
      onClick={(e) => {
        e.preventDefault();
        scrollToSection("provider-directory");
      }}
    >
      {providerDirectoryAppendixLabel}
    </a>
  </li>
  )}

<li>
  <a
    href="#section-glossary"
    className="toc-link"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection("section-glossary");
    }}
  >
    {glossaryAppendixLabel}
  </a>
</li>

<li>
  <a
    href="#section-appendix"
    className="toc-link"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection("section-appendix");
    }}
  >
    {verbatimAppendixLabel}
  </a>
  <ul className="toc-sub">
    <li className="toc-level-2">
      <a
        href="#section-appendix-free-text"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-appendix-free-text");
        }}
      >
        {userInputsAppendixLetter}.1 {reportLabels.freeTextCaseDescription}
      </a>
    </li>
    <li className="toc-level-2">
      <a
        href="#section-appendix-structured-inputs"
        className="toc-link"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("section-appendix-structured-inputs");
        }}
      >
        {userInputsAppendixLetter}.2 {reportLabels.structuredInputRecord}
      </a>
    </li>
  </ul>
</li>

<li>
  <a
    href="#section-disclaimer"
    className="toc-link"
    onClick={(e) => {
      e.preventDefault();
      scrollToSection("section-disclaimer");
    }}
  >
    {disclaimerAppendixLabel}
  </a>
</li>
</ul>  
 
          </div>

                  {/* Case summary */}
              <section id="section-case-summary" className="chapter">
              <CaseSummary result={out} payload={lastPayload || buildPayload()} reportLanguageCode={reportLanguageCode} />
          </section>

          <section id="section-asset-class" className="chapter">
            <AssetClassContext
              reportLanguageCode={reportLanguageCode}
              assetClassContext={
                out.asset_class_context ||
                out.assetClassContext ||
                out.assetClass
              }
            />
          </section>

          {/* Print / PDF-knap */}
          <div
            className="card no-print"
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div>
              <strong>{reportLabels.exportReport || "Export report"}</strong>
              <p className="muted small" style={{ marginTop: 4 }}>
                {reportLabels.exportReportHelp ||
                  "Download the finished paginated PDF with fixed Page X / Y numbering."}
              </p>
              {(localizing || reportObjectNeedsLocalization(out, reportLanguageCode)) && (
                <p className="muted small" style={{ marginTop: 6, color: "#9a3412", maxWidth: 620 }}>
                  {getLocalizationStatusMessage(reportLanguageCode)}
                </p>
              )}
            </div>
            <button
              className="btn no-print"
              onClick={onPrintClick}
              disabled={loading || localizing || pdfExporting || reportObjectNeedsLocalization(out, reportLanguageCode)}
              title={localizing || reportObjectNeedsLocalization(out, reportLanguageCode) ? getLocalizationStatusMessage(reportLanguageCode) : (reportLabels.printSavePdf || "Print / Save as PDF")}
            >
              {pdfExporting
                ? "Preparing paginated PDF…"
                : localizing || reportObjectNeedsLocalization(out, reportLanguageCode)
                ? (reportLabels.preparingLanguageVersion || "Preparing language version…")
                : (reportLabels.printSavePdf || "Print / Save as PDF")}
            </button>
          </div>


          {/* Analysis result card */}
          <div className="card analysis-result-card" id="section-analysis">
            <div className="analysis-result-top">
              <h3 className="analysis-result-title" style={{ margin: 0 }}>3. {reportLabels.analysisResult}</h3>
              <div
                className={`verdict-panel ${getVerdictPanelTone(out.verdict)}`}
                aria-label={reportLabels.tokenizationValueVerdict || reportLabels.overallVerdict}
              >
                <div className="verdict-panel-row">
                  <div className="verdict-panel-label">
                    {reportLabels.tokenizationValueVerdict || reportLabels.overallVerdict}
                  </div>
                  <div className="verdict-panel-badge">
                    <Badge verdict={out.verdict} reportLanguageCode={reportLanguageCode} />
                  </div>
                </div>
                <div className="verdict-panel-metrics">
                  <span><strong>{reportLabels.addedValue || "Added Value"}:</strong> {formatReportNumber(out?.added_value?.score, { signed: true })}</span>
                  <span className="verdict-panel-separator" aria-hidden="true">|</span>
                  <span><strong>{reportLabels.plus ? reportLabels.plus.charAt(0).toUpperCase() + reportLabels.plus.slice(1) : "Plus"}:</strong> {formatReportNumber(out?.added_value?.plus)}</span>
                  <span className="verdict-panel-separator" aria-hidden="true">|</span>
                  <span><strong>{reportLabels.minus ? reportLabels.minus.charAt(0).toUpperCase() + reportLabels.minus.slice(1) : "Minus"}:</strong> {formatReportNumber(out?.added_value?.minus)}</span>
                </div>
              </div>
            </div>

            {/* Case-specific verdict rationale */}
            <div className="verdict-rationale-box" style={{ marginTop: 10 }}>
              <strong>{reportLabels.reason}:</strong>
              <p className="muted" style={{ marginTop: 4 }}>
                {getReasonText(out)}
              </p>
            </div>

            <ScopeNoteBox labels={reportLabels} showYieldNote={currentReportYieldSignal} compact />

            {scenarioLabHasActiveAccess ? (
              <ScenarioLabAvailableCard
                placement="next-step"
                labels={scenarioLabLabels}
                hasScenarios={scenarioLabScenarios.length > 0}
                onOpen={scenarioLabScenarios.length ? () => setScenarioLabOpen(true) : createScenarioLabFromCurrent}
              />
            ) : (
              <ScenarioLabAddOnCard
                placement="next-step"
                labels={scenarioLabLabels}
                onCheckout={handleScenarioLabAddonCheckout}
                loading={scenarioLabCheckoutLoading}
                error={scenarioLabCheckoutError}
              />
            )}

            {/* Factor score profile as customer-facing analysis table */}
            {out.factor_scores && (
              <div style={{ marginTop: 10 }}>
                <div className="muted small" style={{ marginBottom: 6 }}>
                  {reportLabels.technicalFactorScores}
                </div>
                <FactorScoreTable result={out} reportLanguageCode={reportLanguageCode} />
              </div>
            )}

            {Array.isArray(out.deal_breakers) &&
              out.deal_breakers.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Deal-breakers:</strong>
                  <ul>
                    {out.deal_breakers.map((d, i) => (
                      <li key={i} style={{ color: "var(--bad)" }}>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {Array.isArray(out.compliance_flags) &&
              out.compliance_flags.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Compliance flags:</strong>
                  <ul>
                    {out.compliance_flags.map((f, i) => (
                      <li key={i}>
                        {f.flag} — <em>{f.severity}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            <div className="result-grid" style={{ marginTop: 12 }}>
              <div>
                <div style={{ marginBottom: 6 }}>
                  <strong>{reportLabels.addedValue}: </strong>
                  <span>{addedValueDisplay}</span>
                </div>

                

<div
  className="muted"
  style={{
    padding: "8px 10px",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 10,
    background: "rgba(0,0,0,0.02)",
    lineHeight: 1.35,
    maxWidth: 520,
    marginTop: 6,
  }}
>
  <b>{reportLabels.interpretationNote}:</b> {reportLabels.interpretationNoteText}
</div>
{(() => {
  const noGo = (out?.deep_assessment?.go_no_go_criteria?.no_go_if || [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  const stopItems = noGo
    .filter((x) => getCriterionKind(x) === "STOP")
    .map((x) => (x === 'STOP: If no credible liquidity pathway (venue + participants / MM) is committed, stop or revert to a conventional structure.' ? 'STOP: If the project depends on near-term transferability or secondary liquidity and no legally valid, operationally defined transfer process with committed participants exists, stop or revert to a conventional structure.' : x))
    .slice(0, 6);

  const blockerItems = noGo
    .filter((x) => getCriterionKind(x) === "BLOCKER")
    .slice(0, 6);

  const verdictKey = String(
  out?.verdict || out?.overallVerdict || out?.overall_verdict || ""
)
  .toLowerCase()
  .trim();

const showStandardStopCriteria =
  [
    "conditional_go",
    "conditional go",
    "conditional",
    "no-go",
    "no go",
    "non-viable",
    "non viable",
    "stop",
    "non_viable",
  ].includes(verdictKey) || blockerItems.length > 0;
  
  const boxStyle = {
    padding: "8px 10px",
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 10,
    background: "rgba(0,0,0,0.02)",
    lineHeight: 1.35,
    maxWidth: 720,
    marginTop: 10,
  };

  return (
    <>
      {showStandardStopCriteria && (
  <div className="muted" style={boxStyle}>
    <b>{reportLabels.standardStopCriteria}:</b>
    {stopItems.length === 0 ? (
      <div className="small" style={{ marginTop: 6 }}>
        {reportLabels.noneListed}
      </div>
    ) : (
      <>
        <div className="small" style={{ marginTop: 6 }}>
          {reportLabels.proceedOnlyIfStop}
        </div>
        <ul style={{ margin: "6px 0 0 18px" }}>
          {stopItems.map((t, i) => (
            <li key={i}>{localizeStopCriterionText(t, reportLanguageCode)}</li>
          ))}
        </ul>
      </>
    )}
  </div>
)}

      <div className="muted" style={boxStyle}>
        <b>{reportLabels.caseSpecificBlockersThisCase}:</b>
        {blockerItems.length === 0 ? (
          <div className="small" style={{ marginTop: 6 }}>
            {getNoHardBlockerNarrative(out?.verdict, reportLanguageCode)}
          </div>
        ) : (
          <>
            <div className="small" style={{ marginTop: 6 }}>
              {reportLabels.blockersExplanation}
            </div>
            <ul style={{ margin: "6px 0 0 18px" }}>
              {blockerItems.map((t, i) => (
                <li key={i}>{localizeBlockerCriterionText(t, reportLanguageCode)}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
})()}
{out.input_completeness && (
                  <div style={{ marginTop: 10, maxWidth: 760 }}>
                    <strong>{reportLabels.userInputCoverage}:</strong>{" "}
                    {getAssessmentInputStatus(out.input_completeness, reportLabels)}
                    <div className="muted small" style={{ marginTop: 4, lineHeight: 1.45 }}>
                      {reportLabels.assessmentInputExplanation || "All required TVAC input sections were completed sufficiently for this assessment. This indicates form completion only; it does not mean that the case is fully specified, independently validated or supported by evidence."}
                    </div>
                  </div>
                )}

                {(out?.evidence_readiness || out?.evidenceReadiness) && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: "rgba(0,0,0,0.03)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      maxWidth: 760,
                    }}
                  >
                    <strong>{reportLabels.evidenceReadiness}:</strong>{" "}
                    {getSupportingEvidenceLevel(out.evidence_readiness || out.evidenceReadiness, reportLabels)}
                    <div className="muted small" style={{ marginTop: 6, lineHeight: 1.45 }}>
                      {reportLabels.supportingEvidenceExplanation || "This status indicates how far the central legal, commercial, provider and operational assumptions are supported by evidence described by the user. Supporting evidence is not required for an initial TVAC assessment."}
                    </div>
                    <div className="muted small" style={{ marginTop: 4, lineHeight: 1.45 }}>
                      {reportLabels.supportingEvidenceUserReportedNote || "Any supporting evidence mentioned in the case description is treated as user-reported and has not been independently reviewed or verified by TVAC."}
                    </div>
                    {Array.isArray((out.evidence_readiness || out.evidenceReadiness).gaps) && (out.evidence_readiness || out.evidenceReadiness).gaps.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div className="muted small"><strong>{reportLabels.highestPriorityEvidenceGaps || "Highest-priority evidence gaps"}:</strong></div>
                        <ul style={{ marginTop: 4 }}>
                          {(out.evidence_readiness || out.evidenceReadiness).gaps.slice(0, 3).map((gap, index) => <li key={index}>{localizeGeneratedText(gap, reportLanguageCode)}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {(out?.regulatory_integrity || out?.regulatoryIntegrity)?.issuance_profile && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: "rgba(0,0,0,0.03)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      maxWidth: 760,
                    }}
                  >
                    <strong>{reportLabels.regulatoryCoverage}:</strong>{" "}
                    {(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.name || (out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.resolved_id}
                    {(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.verification_status
                      ? ` · ${(out.regulatory_integrity || out.regulatoryIntegrity).issuance_profile.verification_status}`
                      : ""}
                    <div className="muted small" style={{ marginTop: 6, lineHeight: 1.45 }}>
                      {reportLanguageCode === "pt-BR"
                        ? "Este relatório utiliza o contexto de referência aprovado e quaisquer claims versionados ativados. Nenhum claim verificado e específico da jurisdição é ativado, salvo quando expressamente apresentado abaixo; a aplicação local final permanece sem solução e exige verificação local qualificada."
                        : "This report uses approved baseline context and any activated versioned claims. No verified case-specific jurisdictional claim is activated unless explicitly shown below; final local application remains unresolved and requires qualified local verification."}
                    </div>
                    <div className="muted small" style={{ marginTop: 6 }}>
                      {reportLanguageCode === "pt-BR"
                        ? "Os IDs dos claims, as versões das fontes e as questões de validação estão documentados no Apêndice A.6."
                        : "Claim IDs, source versions and validation questions are documented in Appendix A.6."}
                    </div>
                  </div>
                )}

                {out.input_completeness &&
                  Array.isArray(out.input_completeness.missing_fields) &&
                  out.input_completeness.missing_fields.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div className="muted">
                        {reportLanguageCode === "pt-BR" ? "Questões em aberto / informações ausentes:" : "Open questions / missing details:"}
                      </div>
                      <ul>
                        {out.input_completeness.missing_fields.map((m, i) => (
                          <li key={i}>
                            {localizeGeneratedText(m, reportLanguageCode)}{" "}
                            <button
                              className="action"
                              onClick={() => focusFieldFor(m)}
                            >
                              Fill now
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>

            {Array.isArray(out.assumptions) && out.assumptions.length > 0 && (
  <div style={{ marginTop: 10 }}>
    <h4 style={{ margin: "0 0 6px 0" }}>3.1 {reportLabels.keyAssumptions}</h4>
    <ul className="small" style={{ marginTop: 4 }}>
      {out.assumptions.map((a, i) => {
        const str = String(a || "");
        const looksLikeTable =
          str.includes("|") && str.includes("\n") && (str.includes("---") || str.includes(":-"));
        if (looksLikeTable) {
          return (
            <li key={i} style={{ listStyle: "none", marginLeft: -18 }}>
              <pre className="case-verbatim" style={{ marginTop: 8 }}>{str}</pre>
            </li>
          );
        }
        return <li key={i}>{localizeGeneratedText(a, reportLanguageCode)}</li>;
      })}
    </ul>
  </div>
)}

{/* Deep assessment sections (if present) */}
{out.deep_assessment && (
  <section id="section-deep-assessment" style={{ marginTop: 8 }}>
    <div className="grid-2">
      <div className="card" id="section-value-pathways">
        <h4 style={{ marginTop: 0, marginBottom: 6 }}>3.2 {reportLabels.valuePathways}</h4>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
          {out.deep_assessment.value_pathways?.map((x, i) => (
            <li key={i}>{localizeGeneratedText(x, reportLanguageCode)}</li>
          ))}
        </ul>

        <h4 style={{ marginTop: 10, marginBottom: 6 }}>3.3 {reportLabels.investorSegmentation}</h4>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
          {out.deep_assessment.investor_segmentation?.map((x, i) => (
            <li key={i}>{localizeGeneratedText(x, reportLanguageCode)}</li>
          ))}
        </ul>

        <h4 style={{ marginTop: 10, marginBottom: 6 }}>3.4 {reportLabels.operationalCosts}</h4>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
          {out.deep_assessment.operational_costs?.map((x, i) => (
            <li key={i}>{localizeGeneratedText(x, reportLanguageCode)}</li>
          ))}
        </ul>
      </div>

      <div className="card" id="section-regulatory-fit">
        <h4 style={{ marginTop: 0, marginBottom: 6 }}>3.5 {reportLabels.regulatoryPath}</h4>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
          {out.deep_assessment.regulatory_path?.map((x, i) => (
            <li key={i}>{localizeGeneratedText(x, reportLanguageCode)}</li>
          ))}
        </ul>

        <h4 style={{ marginTop: 10, marginBottom: 6 }}>3.6 {reportLabels.custodyVenueFit}</h4>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
          {out.deep_assessment.custody_venue_fit?.map((x, i) => (
            <li key={i}>{localizeGeneratedText(x, reportLanguageCode)}</li>
          ))}
        </ul>

        {Array.isArray(out.deep_assessment?.settlement_cash_leg_readiness) &&
          out.deep_assessment.settlement_cash_leg_readiness.length > 0 && (
            <>
              <h4 style={{ marginTop: 10, marginBottom: 6 }}>3.7 {getSettlementCashLegHeading(reportLanguageCode)}</h4>
              <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
                {out.deep_assessment.settlement_cash_leg_readiness.map((x, i) => (
                  <li key={i}>{formatSettlementCashLegReadinessText(x, reportLanguageCode)}</li>
                ))}
              </ul>
            </>
          )}
      </div>
    </div>

    {Array.isArray(out.deep_assessment?.risk_register) &&
      out.deep_assessment.risk_register.length > 0 && (
        <div className="card" style={{ marginTop: 6 }}>
          <h4 style={{ marginTop: 0, marginBottom: 6 }}>3.{hasSettlementCashLegReadiness ? "8" : "7"} {reportLabels.riskRegister}</h4>
          <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
            {out.deep_assessment.risk_register.map((raw, i) => {
              const r = raw || {};
              const mitigationLabel = reportLanguageCode === "da" ? "afbødning" : reportLanguageCode === "es" ? "mitigación" : reportLanguageCode === "de" ? "Minderung" : reportLanguageCode === "fr" ? "atténuation" : reportLanguageCode === "zh-Hans" ? "缓释措施" : reportLanguageCode === "pt-BR" ? "mitigação" : "mitigation";
              const impactLabel = reportLanguageCode === "da" ? "konsekvens" : reportLanguageCode === "es" ? "impacto" : reportLanguageCode === "de" ? "Auswirkung" : reportLanguageCode === "fr" ? "impact" : reportLanguageCode === "zh-Hans" ? "影响" : reportLanguageCode === "pt-BR" ? "impacto" : "impact";
              const likelihoodLabel = reportLanguageCode === "da" ? "sandsynlighed" : reportLanguageCode === "es" ? "probabilidad" : reportLanguageCode === "de" ? "Wahrscheinlichkeit" : reportLanguageCode === "fr" ? "probabilité" : reportLanguageCode === "zh-Hans" ? "可能性" : reportLanguageCode === "pt-BR" ? "probabilidade" : "likelihood";
              return (
                <li key={i}>
                  <strong>{localizeGeneratedText(r.risk, reportLanguageCode)}</strong> — {mitigationLabel}: {" "}
                  {localizeGeneratedText(r.mitigation, reportLanguageCode)} (<em>{impactLabel}:</em> {r.impact},{" "}
                  <em>{likelihoodLabel}:</em> {r.likelihood})
                </li>
              );
            })}
          </ul>
        </div>
      )}

       {Array.isArray(out.deep_assessment?.milestones) &&
      out.deep_assessment.milestones.length > 0 && (
        <div
          className="card"
          id="section-milestones"
          style={{ marginTop: 12 }}
        >
          <strong>Milestones</strong>
          <ol>
            {out.deep_assessment.milestones.map((m, i) => (
              <li key={i}>
                {m.step} — <span className="muted">{m.owner}</span>{" "}
                (ETA: {m.eta_weeks}w)
              </li>
            ))}
          </ol>
        </div>
      )}

{false && Array.isArray(out.deep_assessment?.go_no_go_criteria?.no_go_if) &&
      out.deep_assessment.go_no_go_criteria.no_go_if.length > 0 && null}
  </section>
)}
          </div>

          {/* Confidence card just under the main result */}
          <ConfidenceCard confidence={out.confidence} />

            {/* NEW: Stakeholder Level 2 (frontend-only, no backend changes) */}
            <section id="section-stakeholders" style={{ marginTop: 14 }}>
              <StakeholderLayerV2
                result={out}
                payload={lastPayload || buildPayload()}
                reportLanguageCode={reportLanguageCode}
              />
            </section>

          <section id="section-factor-breakdown" className="chapter" style={{ paddingTop: 28 }}>
            <h3 style={{ marginTop: 0 }}>5. {reportLabels.factorBreakdown}</h3>
            <p className="muted small" style={{ marginTop: 0, marginBottom: 8 }}>
              {getLocalizedFactorReadAcrossText(reportLanguageCode)}
            </p>
            <div className="factor-breakdown-body">
              <ValueBreakdown result={out} context={buildPayload()} reportLanguageCode={reportLanguageCode} />
            </div>
          </section>

          <section id="section-risk-heatmap" className="chapter">
            <RiskHeatmap result={out} reportLanguageCode={reportLanguageCode} />
          </section>

          <section id="section-recommendations" className="chapter">
            <Recommendations result={out} payload={buildPayload()} roadmapExternal={true} reportLanguageCode={reportLanguageCode} />
          </section>

          {(() => {
  const roadmapPayload = lastPayload || buildPayload();
  const closedWorkflowInfrastructure = isClosedWorkflowInfrastructureCase(roadmapPayload, out);
  const permissionedDefiLending = isPermissionedDefiLendingCase(roadmapPayload, out);
  const structuredRoadmapEnabled = hasStructuredRoadmap(out);
  if (!structuredRoadmapEnabled || reportIsNonViable) return null;

    const reportMaturityLevel =
    out?.maturityLevel ||
    out?.input_maturity?.level ||
    "";

  const roadmapLang = reportLabels.languageCode || reportLanguageCode || "en";

  const roadmapMaturityNotes = {
    en: {
      "Early Exploration": " Because the case is still at an early exploration stage, this roadmap should be read primarily as clarification and design-definition sequencing rather than as a build-ready implementation plan.",
      "Structured Concept": " Because the case is best understood as a structured concept, this roadmap should be read as gated sequencing that still depends on finalizing the remaining open structural choices.",
      "Designed Structure": " Because the case appears substantially designed, this roadmap can be read more directly as an execution-oriented sequence for the structure as described.",
    },
    da: {
      "Early Exploration": " Da casen stadig befinder sig på et tidligt udforskningsstadie, skal roadmapet primært læses som en rækkefølge for afklaring og design — ikke som en implementeringsplan, der er klar til opbygning.",
      "Structured Concept": " Da casen bedst forstås som et struktureret koncept, skal roadmapet læses som en faseopdelt rækkefølge med beslutningsporte, der fortsat afhænger af afklaring af de resterende åbne strukturvalg.",
      "Designed Structure": " Da casen fremstår væsentligt designet, kan roadmapet læses mere direkte som en eksekveringsorienteret rækkefølge for den beskrevne struktur.",
    },
    de: {
      "Early Exploration": " Da sich der Fall noch in einer frühen Explorationsphase befindet, sollte dieser Fahrplan vor allem als Klärungs- und Designsequenzierung gelesen werden, nicht als umsetzungsfertiger Projektplan.",
      "Structured Concept": " Da der Fall am besten als strukturiertes Konzept zu verstehen ist, sollte dieser Fahrplan als Sequenz mit Gates gelesen werden, die weiterhin von der Finalisierung offener Strukturentscheidungen abhängt.",
      "Designed Structure": " Da der Fall bereits weitgehend ausgestaltet erscheint, kann dieser Fahrplan direkter als umsetzungsorientierte Sequenz für die beschriebene Struktur gelesen werden.",
    },
    fr: {
      "Early Exploration": " Comme le cas reste à un stade d’exploration initiale, cette feuille de route doit surtout être lue comme une séquence de clarification et de définition du design, et non comme un plan de mise en œuvre prêt à construire.",
      "Structured Concept": " Comme le cas correspond plutôt à un concept structuré, cette feuille de route doit être lue comme une séquence avec gates qui dépend encore de la finalisation des choix structurels ouverts.",
      "Designed Structure": " Comme le cas paraît déjà substantiellement conçu, cette feuille de route peut être lue plus directement comme une séquence orientée exécution pour la structure décrite.",
    },
    es: {
      "Early Exploration": " Como el caso aún está en una fase temprana de exploración, esta hoja de ruta debe leerse principalmente como secuenciación de aclaración y definición de diseño, no como un plan listo para construir.",
      "Structured Concept": " Como el caso se entiende mejor como un concepto estructurado, esta hoja de ruta debe leerse como una secuencia con gates que aún depende de cerrar decisiones estructurales abiertas.",
      "Designed Structure": " Como el caso parece sustancialmente diseñado, esta hoja de ruta puede leerse de forma más directa como una secuencia orientada a ejecución para la estructura descrita.",
    },
    "zh-Hans": {
      "Early Exploration": " 鉴于该案例仍处于早期探索阶段，本路线图应主要被理解为澄清和设计定义的阶段安排，而不是可直接建设的实施计划。",
      "Structured Concept": " 鉴于该案例更适合作为结构化概念来理解，本路线图应被视为带有决策门槛的阶段安排，并仍取决于剩余开放结构选择的最终确定。",
      "Designed Structure": " 鉴于该案例看起来已完成较充分设计，本路线图可以更直接地作为针对所述结构的执行导向阶段安排。",
    },
    "pt-BR": {
      "Early Exploration": " Como o caso ainda está em estágio inicial de exploração, este roadmap deve ser lido principalmente como uma sequência de esclarecimento e definição de desenho, e não como um plano de implementação pronto para execução.",
      "Structured Concept": " Como o caso é melhor entendido como um conceito estruturado, este roadmap deve ser lido como uma sequência com gates que ainda depende do fechamento das escolhas estruturais em aberto.",
      "Designed Structure": " Como o caso parece substancialmente desenhado, este roadmap pode ser lido mais diretamente como uma sequência orientada à execução da estrutura descrita.",
    },
  };

  const roadmapIntroByLang = {
    en: {
      conditional: "This roadmap is illustrative only. For a Conditional Go case, it should be read as gated sequencing rather than execution momentum. Move forward only if the core structuring, compliance, custody/settlement, and delivery conditions are locked in first. No schedule or fixed duration is assumed; timing and costs must be defined from the final scope and independently validated.",
      viable: "This roadmap is illustrative only. It sketches how a typical tokenization project might be phased for a case that already has a viable Added Value profile. No schedule or fixed duration is assumed; timing and costs must be defined from the final scope and independently validated. An executive roadmap is followed by an operational checklist-style roadmap for planning purposes.",
    },
    da: {
      conditional: "Denne implementeringsplan er alene indikativ. For en Conditional Go-case skal den læses som en faseopdelt rækkefølge med beslutningsporte — ikke som et signal om, at eksekvering bør sættes i gang. Fortsæt kun, hvis de centrale forhold om strukturering, compliance, forvaring, afvikling og levering først er afklaret. Der antages ingen fast tidsplan eller varighed; timing og omkostninger skal defineres ud fra det endelige scope og valideres uafhængigt.",
      viable: "Denne implementeringsplan er alene indikativ. Den skitserer, hvordan et typisk tokeniseringsprojekt kan faseopdeles for en case, der allerede har en levedygtig merværdiprofil. Der antages ingen fast tidsplan eller varighed; timing og omkostninger skal defineres ud fra det endelige scope og valideres uafhængigt. En overordnet implementeringsplan efterfølges af en operationel plan i tjeklisteform.",
    },
    de: {
      conditional: "Dieser Fahrplan ist nur indikativ. Bei einem Conditional-Go-Fall ist er als Sequenz mit Gates zu lesen, nicht als Ausführungsimpuls. Fahren Sie nur fort, wenn Strukturierung, Compliance, Custody/Settlement und Lieferbedingungen zuerst abgesichert sind. Es wird kein fester Zeitplan und keine feste Dauer angenommen; Zeit und Kosten müssen aus dem finalen Umfang abgeleitet und unabhängig validiert werden.",
      viable: "Dieser Fahrplan ist nur indikativ. Er skizziert, wie ein typisches Tokenisierungsprojekt für einen Fall mit bereits tragfähigem Mehrwertprofil phasenweise strukturiert werden könnte. Es wird kein fester Zeitplan und keine feste Dauer angenommen; Zeit und Kosten müssen aus dem finalen Umfang abgeleitet und unabhängig validiert werden. Auf einen Executive-Fahrplan folgt ein operativer checklistenartiger Fahrplan.",
    },
    fr: {
      conditional: "Cette feuille de route est uniquement indicative. Pour un cas Conditional Go, elle doit être lue comme une séquence avec gates, et non comme un signal d’exécution. N’avancez que si les conditions clés de structuration, compliance, custody/settlement et livraison sont d’abord verrouillées. Aucun calendrier ni durée fixe n’est supposé ; le calendrier et les coûts doivent être définis à partir du périmètre final et validés indépendamment.",
      viable: "Cette feuille de route est uniquement indicative. Elle esquisse comment un projet de tokenisation typique peut être phasé pour un cas qui présente déjà un profil de valeur ajoutée viable. Aucun calendrier ni durée fixe n’est supposé ; le calendrier et les coûts doivent être définis à partir du périmètre final et validés indépendamment. Une feuille de route exécutive est suivie d’une feuille de route opérationnelle sous forme de checklist.",
    },
    es: {
      conditional: "Esta hoja de ruta es únicamente indicativa. Para un caso Conditional Go, debe leerse como una secuencia con gates, no como impulso de ejecución. Avance solo si primero quedan cerradas las condiciones centrales de estructuración, compliance, custodia/settlement y entrega. No se supone un calendario ni una duración fija; el calendario y los costes deben definirse a partir del alcance final y validarse de forma independiente.",
      viable: "Esta hoja de ruta es únicamente indicativa. Esboza cómo podría fasearse un proyecto típico de tokenización para un caso que ya tiene un perfil viable de valor añadido. No se supone un calendario ni una duración fija; el calendario y los costes deben definirse a partir del alcance final y validarse de forma independiente. Una hoja de ruta ejecutiva va seguida de una hoja de ruta operativa tipo checklist.",
    },
    "zh-Hans": {
      conditional: "本路线图仅为指示性内容。对于 Conditional Go 案例，应将其理解为带有决策门槛的阶段安排，而不是直接执行信号。只有在结构设计、合规、托管/结算和交付条件首先得到落实时，才应继续推进。本报告不假设固定日程或工期；时间安排和成本必须依据最终范围确定并经过独立验证。",
      viable: "本路线图仅为指示性内容。它概述了对于已经具备可行增值结构的案例，典型代币化项目可能如何分阶段推进。本报告不假设固定日程或工期；时间安排和成本必须依据最终范围确定并经过独立验证。执行层路线图之后会跟随一个面向规划用途的运营清单式路线图。",
    },
    "pt-BR": {
      conditional: "Este roadmap é apenas indicativo. Para um caso Conditional Go, deve ser lido como uma sequência com gates, e não como impulso para execução. Avance somente se as condições centrais de estruturação, compliance, custódia/liquidação e execução forem primeiro definidas. Não se presume cronograma nem duração fixa; prazo e custos devem ser definidos a partir do escopo final e validados de forma independente.",
      viable: "Este roadmap é apenas indicativo. Ele ilustra como um projeto típico de tokenização pode ser estruturado em fases para um caso que já apresenta um perfil Viable de Valor Agregado. Não se presume cronograma nem duração fixa; prazo e custos devem ser definidos a partir do escopo final e validados de forma independente. Um roadmap executivo é seguido por um roadmap operacional em formato de checklist para fins de planejamento.",
    },
  };

  const roadmapIntroSet = roadmapIntroByLang[roadmapLang] || roadmapIntroByLang.en;
  const permissionedDefiRoadmapCopy = permissionedDefiLending
    ? getPermissionedDefiRoadmapOperationalCopy(roadmapLang)
    : null;
  const workflowRoadmapIntroByLang = {
    en: "This roadmap is illustrative only. It sets out one gated operational sequence for the closed institutional workflow described. No schedule or fixed duration is assumed; timing and costs must be defined from the final scope and independently validated.",
    da: "Dette roadmap er alene indikativt. Det beskriver en faseopdelt operationel rækkefølge med beslutningsporte for den lukkede institutionelle arbejdsgang, der er beskrevet. Der antages ingen fast tidsplan eller varighed; timing og omkostninger skal defineres ud fra det endelige scope og valideres uafhængigt.",
    de: "Dieser Fahrplan ist nur indikativ. Er beschreibt eine einzige gesteuerte operative Sequenz für den dargestellten geschlossenen institutionellen Workflow. Es wird kein fester Zeitplan und keine feste Dauer angenommen; Zeit und Kosten müssen aus dem finalen Umfang abgeleitet und unabhängig validiert werden.",
    fr: "Cette feuille de route est uniquement indicative. Elle décrit une seule séquence opérationnelle avec gates pour le workflow institutionnel fermé présenté. Aucun calendrier ni durée fixe n’est supposé ; le calendrier et les coûts doivent être définis à partir du périmètre final et validés indépendamment.",
    es: "Esta hoja de ruta es únicamente indicativa. Describe una única secuencia operativa con gates para el flujo institucional cerrado descrito. No se supone un calendario ni una duración fija; el calendario y los costes deben definirse a partir del alcance final y validarse de forma independiente.",
    "zh-Hans": "本路线图仅为指示性内容。它为所述封闭式机构工作流提供一条带有决策门槛的运营阶段安排。本报告不假设固定日程或工期；时间安排和成本必须依据最终范围确定并经过独立验证。",
    "pt-BR": "Este roadmap é apenas indicativo. Ele apresenta uma sequência operacional com gates para o workflow institucional fechado descrito. Não se presume cronograma nem duração fixa; prazo e custos devem ser definidos a partir do escopo final e validados de forma independente.",
  };
  const roadmapIntroBase = closedWorkflowInfrastructure
    ? (workflowRoadmapIntroByLang[roadmapLang] || workflowRoadmapIntroByLang.en)
    : permissionedDefiLending
    ? (permissionedDefiRoadmapCopy?.fallbackIntro || permissionedDefiRoadmapCopy?.operationalIntro)
    : (reportIsConditional ? roadmapIntroSet.conditional : roadmapIntroSet.viable);
  const roadmapMaturityNote = (roadmapMaturityNotes[roadmapLang] || roadmapMaturityNotes.en)?.[reportMaturityLevel] || "";
  const roadmapIntro = `${roadmapIntroBase}${roadmapMaturityNote}`;

  return (
    <section id="section-roadmap" className="chapter">
      <div className="card">
        <h3 style={{ marginTop: 0 }}>8. {reportLabels.roadmap}</h3>
        <p className="muted small" style={{ marginTop: 4 }}>
          {roadmapIntro}
        </p>
         <RoadmapChapter suppressIntro={true} payload={roadmapPayload} result={out} reportLanguageCode={reportLanguageCode} />
      </div>
    </section>
  );
})()}  

          {/* Appendix and closing sections */}

                  <section className="chapter page-break" aria-label="Appendices separator">
            <div
              style={{
                minHeight: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                paddingTop: "18vh",
                textAlign: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: 30, fontWeight: 600, letterSpacing: "0.01em" }}>
                {reportLabels.appendices}
              </h2>
            </div>
          </section>

<section className="chapter page-break">
  <MethodologyCard modules={out.report_modules || out.reportModules} result={out} reportLanguageCode={reportLanguageCode} payload={lastPayload || buildPayload()} />
</section>
  
            {providerDirectoryIncluded && (
              <ProviderDirectorySection chapterLabel={providerDirectoryAppendixLabel} reportLanguageCode={reportLanguageCode} />
            )}

                   <section id="section-glossary" className="chapter">
            <div className="card" style={{ marginBottom: 12 }}>
              <h3 style={{ marginTop: 0 }}>{glossaryAppendixLabel}</h3>
              <div className="muted small">
                {reportLabels.appendixCIntro || "A practical reference for the most common terms and abbreviations used in this report."}
              </div>

              {(() => {
                const entries = [
                  { term: "Accredited investor", def: "An investor category used primarily in the United States for persons or entities meeting specific income, net worth, or institutional eligibility thresholds for participation in certain exempt offerings." },
                  { term: "Added Value (AV)", def: "TVAC’s net assessment of whether tokenization appears to improve a case overall once both upside and friction are weighed together. Formula: AV = (NO + CS + RR) – (TC + NR)." },
                  { term: "AFM", def: "Authority for the Financial Markets in the Netherlands." },
                  { term: "AIF / AIFM", def: "Alternative Investment Fund / Alternative Investment Fund Manager under EU regulatory frameworks." },
                  { term: "AML", def: "Anti-Money Laundering controls intended to detect, prevent, and report suspicious financial activity." },
                  { term: "Asset owner / Issuer", def: "The originating party, sponsor, manager, or issuing structure responsible for launching or sponsoring the tokenized case being assessed." },
                  { term: "ATS", def: "Alternative Trading System. A regulated trading venue category used in some jurisdictions, particularly the United States." },
                  { term: "AVM", def: "Automated Valuation Model." },
                  { term: "BaFin", def: "Federal Financial Supervisory Authority in Germany." },
                  { term: "Bankruptcy-remote SPV", def: "A special-purpose vehicle structured to reduce the risk that the insolvency of a sponsor or related party directly affects the ring-fenced assets or obligations of the issuance vehicle." },
                  { term: "Cap table", def: "Capitalization table. A record of ownership interests, investor positions, or security holders in a structure or company." },
                  { term: "Cash leg", def: "The cash side of a transaction or settlement flow, as distinct from the asset or token side." },
                  { term: "CFT / CFTC", def: "Commodity Futures Trading Commission in the United States." },
                  { term: "Conditional Go", def: "A Tokenization Value Verdict indicating that tokenization may add net value, but remains materially dependent on design discipline, implementation choices, and resolution of key gating conditions." },
                  { term: "Counterparty risk", def: "The risk that one party to a transaction fails to perform its contractual obligations." },
                  { term: "CSD", def: "Central Securities Depository." },
                  { term: "Custody", def: "The safeguarding, control, and recordkeeping of assets or positions on behalf of investors or market participants." },
                  { term: "DeFi", def: "Decentralized Finance. A blockchain-based ecosystem of protocols and applications designed to enable financial activity without relying entirely on traditional centralized intermediaries." },
                  { term: "DEX", def: "Decentralized Exchange." },
                  { term: "Distribution scope", def: "The intended investor reach of an offering or tokenized structure, including geography, investor type, and access conditions." },
                  { term: "DvP", def: "Delivery versus Payment. A settlement model in which transfer of the asset and transfer of payment are linked so that one does not occur without the other." },
                  { term: "Economic rights", def: "The financial entitlements attached to a token or instrument, such as income participation, coupon, principal repayment, or profit-sharing." },
                  { term: "Eligibility controls", def: "Rules and mechanisms that determine who may acquire, hold, or transfer a token or instrument." },
                  { term: "ESG", def: "Environmental, Social, and Governance factors." },
                  { term: "Execution risk", def: "The risk that a concept that appears viable in theory fails in practice because implementation, coordination, economics, or governance proves weaker than assumed." },
                  { term: "Factor breakdown", def: "The report section explaining how each of TVAC’s five factors contributes to the overall assessment." },
                  { term: "FCA", def: "Financial Conduct Authority in the United Kingdom." },
                  { term: "FINRA", def: "Financial Industry Regulatory Authority in the United States." },
                  { term: "Friction factors", def: "In TVAC, the two downside factors: Tokenization Costs (TC) and New Risks (NR)." },
                  { term: "Governance", def: "The decision-making, control, accountability, and escalation framework surrounding a structure, platform, issuer, or operating model." },
                  { term: "Assessment input", def: "TVAC’s qualitative indication of whether the required input sections were completed sufficiently for the assessment to run. It measures input-form completion only and does not imply factual certainty, independent validation or supporting evidence." },
                  { term: "Institutional investor", def: "A professional market participant such as a fund, bank, insurer, treasury function, pension investor, or other organization investing at scale." },
                  { term: "Investor segmentation", def: "The way the target investor base is defined and limited by investor type, sophistication, jurisdiction, or eligibility criteria." },
                  { term: "KYC", def: "Know Your Customer procedures used to verify customer identity and onboarding eligibility." },
                  { term: "Legal wrapper", def: "The legal structure through which tokenized rights or exposures are issued, held, or governed." },
                  { term: "Liquidity window", def: "A predefined period or mechanism allowing limited secondary trading, transfer, redemption, or exit opportunities." },
                  { term: "MiCA", def: "Markets in Crypto-Assets Regulation in the European Union." },
                  { term: "MiFID II", def: "Markets in Financial Instruments Directive II in the European Union." },
                  { term: "MMF", def: "Money Market Fund." },
                  { term: "MTF", def: "Multilateral Trading Facility under EU regulatory frameworks." },
                  { term: "Net Added Value", def: "See Added Value (AV)." },
                  { term: "New Opportunities (NO)", def: "Case-specific upside that tokenization creates or materially strengthens, such as new investor reach, new operational utility, collateral use, programmability, or improved strategic flexibility." },
                  { term: "New Risks (NR)", def: "Additional risks introduced by tokenization, including legal, regulatory, operational, technological, governance, distribution, or market-structure risks." },
                  { term: "Non-viable", def: "A Tokenization Value Verdict indicating that the current structure does not support a positive tokenization value case under the TVAC methodology and should not proceed without substantial redesign." },
                  { term: "NO / CS / RR / TC / NR", def: "Shorthand for TVAC’s five factors: New Opportunities, Cost Savings, Risk Reduction, Tokenization Costs, and New Risks." },
                  { term: "Off-chain settlement", def: "A settlement process that occurs through conventional systems, institutions, or cash rails rather than being fully completed on a blockchain." },
                  { term: "On-chain settlement", def: "A settlement process in which the relevant transfer logic is executed on a blockchain or distributed ledger environment." },
                  { term: "Operational costs", def: "The ongoing cost burden of running a structure, including servicing, reporting, compliance, governance, custody, registry, and technology overhead." },
                  { term: "Operational utility", def: "Practical workflow benefit created by tokenization, such as faster settlement, better collateral mobility, reduced reconciliation, or more efficient transfer control." },
                  { term: "Parallel processes", def: "A situation in which tokenized and legacy workflows both remain active, limiting the real efficiency gains from tokenization." },
                  { term: "Permissioned", def: "A system design in which access, participation, or transfers are restricted to approved participants under defined rules." },
                  { term: "Professional investor", def: "An investor category used in many jurisdictions for parties assumed to have greater sophistication, capacity, or experience than retail investors." },
                  { term: "Prospectus", def: "A formal disclosure document that may be required for certain public offerings or investor solicitations under applicable law." },
                  { term: "Provider Directory", def: "A reference section listing indicative categories of service providers relevant to tokenization implementation. It is illustrative only and not a recommendation list." },
                  { term: "Reg D / Reg S", def: "Common shorthand for U.S. securities offering exemptions under Regulation D and Regulation S." },
                  { term: "Registry", def: "The official or operational record of holdings, ownership, or investor positions." },
                  { term: "Regulatory perimeter", def: "The boundary of laws, rules, approvals, and supervisory expectations that may apply to a structure or activity." },
                  { term: "Repo", def: "Repurchase agreement. A financing transaction in which securities are sold and later repurchased, typically functioning as a collateralized short-term funding arrangement." },
                  { term: "Retail investor", def: "A non-professional investor who generally receives higher levels of regulatory protection than professional or institutional investors." },
                  { term: "Rights mapping", def: "The process of making clear what legal, economic, governance, and transfer rights a token actually represents and how those rights are enforceable." },
                  { term: "Risk heatmap", def: "A report visualization that summarizes the relative severity and concentration of key risk areas identified in the case." },
                  { term: "Risk Reduction (RR)", def: "The extent to which tokenization reduces existing risk in the conventional setup, for example through better controls, improved transparency, clearer auditability, or lower reconciliation burden." },
                  { term: "SEC", def: "Securities and Exchange Commission in the United States." },
                  { term: "Self-custody", def: "A model in which the end holder directly controls the relevant wallet, keys, or token position without relying on a traditional third-party custodian." },
                  { term: "Settlement model", def: "The way asset transfer, cash transfer, record updates, and finality are operationally achieved." },
                  { term: "SPV", def: "Special Purpose Vehicle. A ring-fenced legal entity often used for issuance, structuring, asset isolation, or liability segregation." },
                  { term: "Stakeholder added value", def: "A TVAC perspective layer showing how value and friction may be distributed differently across key participants such as the issuer, investors, and infrastructure providers." },
                  { term: "Stop criteria", def: "Standard decision-integrity triggers indicating that a case should stop, re-scope, or be materially redesigned unless the issue is resolved." },
                  { term: "Structured product", def: "A financial instrument combining defined economic exposure with a formal legal wrapper and specified payout logic." },
                  { term: "TA", def: "Transfer Agent." },
                  { term: "Tokenization Costs (TC)", def: "The additional costs introduced by tokenization, including structuring, legal work, compliance, integration, platform costs, governance overhead, servicing changes, and operating complexity." },
                  { term: "Transfer controls", def: "Rules, permissions, and technical or legal mechanisms governing who may transfer, receive, or hold the token." },
                  { term: "Transfer restrictions", def: "Specific limitations placed on secondary transfers, often for regulatory, contractual, or investor-eligibility reasons." },
                  { term: "TVAC", def: "Tokenization Value-Add Calculator. A structured decision-support methodology for assessing whether tokenization appears to create net added value in a specific case." },
                  { term: "Value pathways", def: "The concrete ways in which tokenization might create value in the case, such as improved distribution, lower friction, stronger control, faster settlement, or better collateral use." },
                  { term: "Vendor-capture risk", def: "The risk that a structure becomes economically or operationally overly dependent on one platform, provider, or implementation path in a way that shifts value away from the initiating party." },
                  { term: "Venue fit", def: "The degree to which the chosen issuance, transfer, trading, or liquidity venue matches the asset, investor scope, regulatory posture, and operating model of the case." },
                  { term: "Viable", def: "A Tokenization Value Verdict indicating that the TVAC methodology identifies a positive tokenization value case under the described structure, subject to validation and disciplined execution. It is not an investment, credit, yield, valuation, legal, tax, or suitability assessment." },
                  { term: "Whitelist / Whitelisting", def: "A mechanism for restricting participation or transferability to pre-approved investors, wallets, counterparties, or participants." },
                ].sort((a, b) =>
                  String(a.term).localeCompare(String(b.term), undefined, { sensitivity: "base" })
                );

                // Patch 7: preserve full glossary content in every language version.
                // Do not substitute the shortened localized glossary, because that makes
                // non-English reports look like reduced/light versions of the English master.
                const caseRelevantEntries = (() => {
                  if (normalizeReportLanguageCode(reportLanguageCode) !== "en") return entries;

                  const context = JSON.stringify({
                    payload: lastPayload || buildPayload(),
                    regulatoryPath: out?.deep_assessment?.regulatory_path,
                    legalRegulatory: out?.deep_assessment?.legal_regulatory,
                    issuanceProfile: (out?.regulatory_integrity || out?.regulatoryIntegrity)?.issuance_profile,
                  });
                  const contextTermPatterns = {
                    "AFM": /\bAFM\b/i,
                    "AIF / AIFM": /\bAIFM?\b/i,
                    "AVM": /\bAVM\b/i,
                    "BaFin": /\bBaFin\b/i,
                    "CFT / CFTC": /\bCFTC?\b/i,
                    "FCA": /\bFCA\b/i,
                    "FINRA": /\bFINRA\b/i,
                    "MiCA": /\bMiCA\b/i,
                    "MiFID II": /\bMiFID\s*II\b/i,
                    "MMF": /\bMMF\b/i,
                    "Reg D / Reg S": /\bReg(?:ulation)?\s+[DS]\b/i,
                    "Repo": /\brepos?\b/i,
                    "SEC": /\bSEC\b/i,
                  };
                  const retained = entries.filter((entry) => {
                    const pattern = contextTermPatterns[entry.term];
                    return !pattern || pattern.test(context);
                  });

                  const issuanceProfile = (out?.regulatory_integrity || out?.regulatoryIntegrity)?.issuance_profile || {};
                  const issuanceKey = String(issuanceProfile.resolved_id || issuanceProfile.name || (lastPayload || buildPayload())?.issuanceJurisdiction || "").toLowerCase();
                  const additions = [
                    { term: "Supporting evidence", def: "TVAC’s qualitative indicator of how far critical legal, commercial, provider and operational assumptions are supported by evidence described by the user. User-reported evidence is not independently verified by TVAC, and supporting evidence is not required for an initial assessment." },
                    { term: "Regulatory coverage", def: "The jurisdiction-specific scope supported by activated, versioned regulatory claims in the TVAC profile. It does not determine how the law applies to the final facts." },
                    { term: "Verified regulatory claim", def: "A versioned statement derived from an identified source and approved for conditional regulatory scoping. It is not a legal opinion or case-specific compliance conclusion." },
                  ];
                  if (issuanceKey === "br" || issuanceKey.includes("brazil")) {
                    additions.push(
                      { term: "BCB", def: "Banco Central do Brasil, Brazil’s central bank and a relevant authority for banking, payment, foreign-exchange and specified virtual-asset activities." },
                      { term: "Book-entry registration", def: "A regulated recordkeeping model in which securities positions and transfers are maintained through an authorized book-entry system rather than relying only on a token record." },
                      { term: "CDI", def: "Brazilian interbank deposit rate commonly used as a benchmark for floating-rate financial instruments. TVAC does not assess whether a stated CDI-linked return is realistic or suitable." },
                      { term: "CVM", def: "Comissão de Valores Mobiliários, Brazil’s securities regulator." },
                      { term: "CVM Resolution 88", def: "Brazilian CVM rules governing specified public securities offerings conducted with registration exemption through registered electronic participatory-investment platforms, subject to the rule’s conditions." },
                      { term: "Duplicate assignment", def: "The risk that the same receivable or economic claim is transferred, pledged or represented more than once, creating conflicting claims or control failures." },
                      { term: "Electronic participatory-investment platform", def: "A platform category addressed by CVM Resolution 88 for qualifying offerings. Whether a particular platform and transaction meet the rule must be independently confirmed." },
                      { term: "Pix", def: "Brazil’s instant-payment system operated under the oversight of Banco Central do Brasil." },
                      { term: "Receivables assignment", def: "The legal transfer of rights to receive payment under one or more receivables. Validity, perfection, priority, servicing and enforcement depend on the applicable structure and law." },
                      { term: "Revolving receivables pool", def: "A financing pool in which maturing or collected receivables may be replaced with newly eligible receivables under defined eligibility, concentration, control and reporting rules." }
                    );
                  }
                  const byTerm = new Map(retained.map((entry) => [entry.term, entry]));
                  additions.forEach((entry) => byTerm.set(entry.term, entry));
                  return Array.from(byTerm.values()).sort((a, b) => String(a.term).localeCompare(String(b.term), undefined, { sensitivity: "base" }));
                })();
                const displayEntries = getReportGlossaryEntries(caseRelevantEntries, reportLanguageCode);

                return (
                  <table className="glossary-grid">
                    <thead>
                      <tr>
                        <th style={{ width: "28%" }}>{reportLabels.term}</th>
                        <th>{reportLabels.meaning}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEntries.map((e) => (
                        <tr key={e.term}>
                          <td><strong>{e.term}</strong></td>
                          <td>{e.def}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </section>

              {/* ---------- Appendix: user-supplied case inputs ---------- */}
              <section id="section-appendix" className="chapter page-break">
                <div className="card" style={{ marginBottom: 12 }}>
                  <h3 style={{ marginTop: 0 }}>{verbatimAppendixLabel}</h3>
                  <h4 id="section-appendix-free-text" style={{ marginTop: 16 }}>
                    {userInputsAppendixLetter}.1 {reportLabels.freeTextCaseDescription}
                  </h4>
                  <div className="muted" style={{ marginTop: -6, marginBottom: 10 }}>
                    {reportLabels.fullCaseInput}
                  </div>
                    <div
                    className="appendix-verbatim"
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 12,
                      padding: 14,
                      background: "#fbfbfc",
                      lineHeight: 1.45,
                    }}
                  >
                    {String(lastPayload?.text || "").trim()
                      ? String(lastPayload?.text || "")
                          .trim()
                          .split(/\n\s*\n/)
                          .filter((p) => p.trim())
                          .map((para, i) => (
                            <p
                              key={i}
                              style={{ margin: "0 0 14px 0", whiteSpace: "pre-wrap" }}
                            >
                              {para.trim()}
                            </p>
                          ))
                      : "—"}
                  </div>

                  <h4 id="section-appendix-structured-inputs" style={{ marginTop: 22 }}>
                    {userInputsAppendixLetter}.2 {reportLabels.structuredInputRecord}
                  </h4>
                  <p className="muted small" style={{ marginBottom: 10 }}>
                    {reportLabels.structuredInputIntro}
                  </p>
                  <table className="structured-input-record">
                    <thead>
                      <tr>
                        <th style={{ width: "24%" }}>{reportLabels.field}</th>
                        <th style={{ width: "30%" }}>{reportLabels.userResponse}</th>
                        <th>{reportLabels.otherAvailableOptionsNotSelected}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {structuredInputRecord.map((row) => (
                        <tr key={row.field}>
                          <td><strong>{row.field}</strong></td>
                          <td>{row.response}</td>
                          <td>{row.alternatives}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="muted small" style={{ marginTop: 10 }}>
                    {reportLabels.unselectedOptionsNote}
                  </p>
                </div>
              </section>

<section id="section-disclaimer" className="chapter page-break">
  <div className="card" style={{ marginBottom: 12 }}>
    <h3 style={{ marginTop: 0 }}>{disclaimerAppendixLabel}</h3>

    {reportLabels.languageCode !== "en" && (
      <>
        <p className="muted small" style={{ marginTop: 6, lineHeight: 1.5 }}>
          <strong>{languageReferenceNote.title}:</strong> {languageReferenceNote.body}
        </p>
        <p className="muted small" style={{ marginTop: 8 }}>
          {reportLabels.translatedDisclaimerNotice}
        </p>
        <h4 style={{ marginTop: 18 }}>{reportLabels.translatedDisclaimerHeading}</h4>
        <div className="disclaimer small" style={{ marginTop: 8, maxWidth: 860, marginLeft: "auto", marginRight: "auto", textAlign: "left" }}>
          {getReportTranslatedDisclaimerParagraphs(reportLanguageCode).map((para, i) => (
            <p key={`translated-disclaimer-${i}`}>{para}</p>
          ))}
        </div>
        <h4 style={{ marginTop: 22 }}>{reportLabels.englishDisclaimerHeading}</h4>
      </>
    )}

    {reportLabels.languageCode === "en" && (
      <h4 style={{ marginTop: 18 }}>{reportLabels.disclaimer}</h4>
    )}

    <div
      id="front-page-disclaimer"
      className="disclaimer small"
      style={{ marginTop: 8, maxWidth: 860, marginLeft: "auto", marginRight: "auto", textAlign: "left" }}
    >
      {ENGLISH_FULL_DISCLAIMER.map((para, i) => (
        <p key={`english-disclaimer-${i}`}>{para}</p>
      ))}
    </div>

    <ReportContactBlock branding={activeReportBranding} />
  </div>
</section>

                    {/* --- PRINT-ONLY: Contact & disclaimer as final report section --- */}
          <span className="print-page-number" aria-hidden="true" />
        </div>

          {scenarioLabHasActiveAccess ? (
            <ScenarioLabAvailableCard
              labels={scenarioLabLabels}
              hasScenarios={scenarioLabScenarios.length > 0}
              onOpen={scenarioLabScenarios.length ? () => setScenarioLabOpen(true) : createScenarioLabFromCurrent}
            />
          ) : (
            <ScenarioLabAddOnCard
              labels={scenarioLabLabels}
              onCheckout={handleScenarioLabAddonCheckout}
              loading={scenarioLabCheckoutLoading}
              error={scenarioLabCheckoutError}
            />
          )}
        </>
      )}

    </div>
  );
}

/* ---------- About view (subpage) ---------- */

function AboutView({ onBack, uiLanguageCode = "en" }) {
  const t = (key) => uiText(uiLanguageCode, key);
  return (
    <div className="section" style={{ maxWidth: 980, margin: "0 auto" }}>
      {/* Back-knap */}
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="topbar-link" onClick={onBack}>
          {t("about.back")}
        </button>
      </div>

      <div className="card card-accent">
        <h3 style={{ marginTop: 0 }}>
          About the Tokenization Value-Add Calculator
        </h3>
        <p className="muted">
          The Tokenization Value-Add Calculator is an evaluation engine
          for real-world asset tokenization cases. It is designed to answer a
          single question:{" "}
          <strong>
            does this specific tokenization design create net value compared to
            conventional structures?
          </strong>
        </p>
        <p className="muted small">
          TVAC does not replace detailed legal, tax or technical due diligence.
          Instead, it provides a structured, repeatable way to stress-test your
          concept before you commit serious time, money or reputation.
        </p>
      </div>

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <h4>What TVAC looks at</h4>
          <ul className="small">
            <li>
              Asset type and legal wrapper (SPV, fund, notes, equity, etc.).
            </li>
            <li>Issuance and sale jurisdictions and basic regulatory path.</li>
            <li>Investor segments, minimum tickets and distribution model.</li>
            <li>Secondary-market design, liquidity cadence and venue type.</li>
            <li>Custody, settlement model and transfer restrictions.</li>
            <li>
              Your stated objectives: access, cost, compliance, transparency,
              etc.
            </li>
          </ul>
        </div>
        <div className="card">
          <h4>How TVAC scores a case</h4>
          <p className="small">
            Every case is evaluated across five core factors:
          </p>
          <ul className="small">
            <li>New Opportunities</li>
            <li>Cost Savings</li>
            <li>Risk Reduction</li>
            <li>Tokenization Costs</li>
            <li>New Risks</li>
          </ul>
          <p className="small">
            Each factor is scored 0–10 against an internal rubric. The three
            upside factors are added together, the two downside factors are
            subtracted, and the resulting <strong>Added Value</strong> number is
            mapped to a verdict: Viable, Conditional Go or Non-viable.
          </p>
        </div>
      </div>

      {/* NEW: Applicability & limitations (static vs dynamic) */}
      <div className="card" style={{ marginTop: 14 }}>
        <h4>Applicability &amp; limitations (TVAC v1)</h4>
        <p className="muted small" style={{ marginTop: 6 }}>
          TVAC v1 is strongest for tokenization cases where the structure can be
          evaluated as a relatively stable setup (“static” cases): clear legal
          wrapper, clear investor scope, and an explicit custody, venue,
          settlement and disclosure path.
        </p>
        <p className="muted small" style={{ marginTop: 6 }}>
          In more “dynamic” DeFi-native designs, value creation can be driven by
          adoption, integrations, liquidity network effects and strategy layers.
          TVAC can still analyse such cases, but it deliberately scores the{" "}
          <strong>baseline</strong> more conservatively because both{" "}
          <strong>New Opportunities</strong> and <strong>New Risks</strong> are
          often conditional and system-dependent.
        </p>

        <div className="grid-2" style={{ marginTop: 12 }}>
          <div>
            <strong>Best suited for (typical examples)</strong>
            <ul className="small toc" style={{ marginTop: 6 }}>
              <li>
                Tokenized funds and fund shares (including feeder/umbrella
                structures).
              </li>
              <li>
                Private credit and loan portfolios (incl. securitized /
                structured notes).
              </li>
              <li>
                Receivables, trade finance obligations and invoice financing
                structures.
              </li>
              <li>
                Private equity / unlisted shares (cap-table and transfer-restricted
                structures).
              </li>
              <li>
                Real estate via SPVs or funds (where liquidity promises are
                realistic).
              </li>
              <li>Infrastructure / project finance cash-flow structures.</li>
              <li>Permissioned distributions to professional / accredited investors.</li>
              <li>
                Design comparisons: custody/venue/settlement/disclosure variants
                of the same core idea.
              </li>
            </ul>
          </div>

          <div>
            <strong>
              Less suited for in v1 (or requires careful interpretation)
            </strong>
            <ul className="small toc" style={{ marginTop: 6 }}>
              <li>
                DeFi-native “flywheel” cases where value depends on fast
                adoption and composability.
              </li>
              <li>
                Tokenized equities used as collateral with strategy/yield layers
                as the main value driver.
              </li>
              <li>Synthetics-first or derivatives-first designs (IOU/swap/CFD-like exposure).</li>
              <li>
                Cases where upside is primarily a function of liquidity mining,
                incentives or integrations.
              </li>
              <li>
                Precise ROI modelling in currency terms (TVAC is not a financial
                forecasting tool).
              </li>
            </ul>
          </div>
        </div>

        <p className="muted small" style={{ marginTop: 10 }}>
          If your case relies heavily on integrations or liquidity network
          effects, interpret the upside as <strong>conditional upside</strong>{" "}
          that may materialise if adoption succeeds, rather than guaranteed
          baseline value.
        </p>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h4>Methodology &amp; calibration (in brief)</h4>
        <ul className="small">
          <li>
            TVAC uses a fixed scoring framework with five factors and a
            transparent Added Value formula:
            <br />
            <span className="mono">
              Added Value = (New Opportunities + Cost Savings + Risk Reduction) – (Tokenization Costs + New Risks)
            </span>
          </li>
          <li>
            Factor scores and verdicts are calibrated on a small internal set of
            representative tokenization cases across different asset types and
            structures.
          </li>
          <li>
            The evaluator is intentionally cautious: in marginal or finely balanced situations,
            the more conservative interpretation is usually chosen rather than
            the most optimistic one.
          </li>
          <li>
            When the underlying prompts or model versions are updated, a set of
            reference cases is re-run internally to check that profiles remain
            broadly consistent over time.
          </li>
        </ul>
        <p className="muted small" style={{ marginTop: 6 }}>
          For users who want a deeper technical description – including factor
          definitions, the scoring workflow, and an overview of the internal
          calibration approach – you can download the full methodology note:
        </p>
        <p className="small" style={{ marginTop: 4 }}>
          <a href="/tvac-methodology-v1.pdf" target="_blank" rel="noreferrer">
            TVAC Methodology &amp; Calibration v1 (PDF)
          </a>
        </p>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h4>Data usage &amp; privacy</h4>
        <ul className="small">
          <li>
            For standard self-service reports, case descriptions and structured
            inputs are processed transiently and are not intentionally written
            to a persistent customer-case database. Reviewed TVAC logging paths
            do not intentionally log the full case description or structured answers.
          </li>
          <li>
            Analyses are generated via the OpenAI API. Astoval has disabled
            voluntary data sharing and ordinary API-call logging, and TVAC sends
            requests with optional application-state storage disabled. OpenAI
            states that API data is not used for model training unless the API
            customer opts in, but separate internal abuse-monitoring retention
            may still apply for up to 30 days.
          </li>
          <li>
            The report remains in the active browser page while it is open. A
            generated assessment may be held in TVAC server memory for no more
            than one hour and is then deleted automatically; no permanent
            server-side PDF copy is kept in the standard self-service flow.
          </li>
          <li>
            You should avoid entering unnecessary personal data or highly
            sensitive confidential information.
          </li>
        </ul>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h4>FAQ (short version)</h4>
        <ul className="small">
          <li>
            <strong>Is TVAC legal or investment advice?</strong> – No. It is an
            informational analysis tool that must be complemented with
            jurisdiction-specific professional advice.
          </li>
          <li>
            <strong>Can I copy or export the full report?</strong> – Yes. Use
            the <em>Print / Save as PDF</em> button on the main page to export a
            structured report for internal use.
          </li>
          <li>
            <strong>Can the rubrics be customised?</strong> – In principle yes,
            but the current public version uses a single calibrated rubric to
            keep results comparable across users and cases.
          </li>
        </ul>
      </div>
    </div>
  );
}

/* ---------- Stakeholder Layer V2 (inline, frontend-only) ---------- */

function StakeholderLayerV2({ result, payload, reportLanguageCode = "en" }) {
  const labels = polishReportLabels(getReportLabels(reportLanguageCode), reportLanguageCode);
  const genericStakeholders = genericStakeholderCopy(reportLanguageCode);
  const workflowStakeholders = workflowStakeholderCopy(reportLanguageCode);
  const warehouseReceiptStakeholders = getWarehouseReceiptStakeholderCopy(reportLanguageCode);
  const workflowTimeHorizonCopy = workflowStakeholderTimeHorizonCopy(reportLanguageCode);
  const capitalFormationCopy = capitalFormationStakeholderCopy(reportLanguageCode);
  const issuanceLifecycleStakeholders = issuanceLifecycleStakeholderCopy(reportLanguageCode);
  // Purpose: translate the deep assessment into a stakeholder view (who wins, who pays, and what must be agreed).
  if (!result) return null;

  const p = payload || {};
  const fundLifecycleScope = deriveFundLifecycleScope(p);
  const fs = result.factor_scores || {};
  const missing = Array.isArray(result?.input_completeness?.missing)
    ? result.input_completeness.missing
    : [];

  function safeStr(v) {
    if (v === null || v === undefined) return "";
    if (Array.isArray(v)) return v.filter(Boolean).join(", ");
    return String(v);
  }

  function normalizeValueKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ");
  }

  function localizedInputValue(value, kind = "generic") {
    const raw = safeStr(value).trim();
    if (!raw) return raw;
    if (labels.languageCode === "en") {
      if (["custody", "venue", "settlement", "liquidity", "disclosure"].includes(kind)) {
        return humanizeReportInputLabel(raw, kind);
      }
      return raw;
    }
    const key = normalizeValueKey(raw);
    const maps = {
      da: {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "digital juridisk wrapper", "sole-record": "primær juridisk registrering", "digital wrapper": "digital juridisk wrapper" },
        custody: { "regulated-custodian": "reguleret depotforvaring", "regulated custodian": "reguleret depotforvaring", "ta/csd": "transfer agent / CSD", "self-custody": "selvforvaring" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "ingen" },
        liquidity: { quarterly: "kvartalsvis", monthly: "månedlig", continuous: "kontinuerlig", none: "ingen", "ad-hoc": "ad hoc", "ad-hoc otc": "ad hoc OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 atomar afvikling", "off-chain": "afvikling uden for blockchainen" },
        disclosure: { exempt: "undtagelsesbaseret", prospectus: "prospekt", "private placement": "privat placering" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "Hvidlistning, EU-adgangskontrol og 12 måneders bindingsperiode",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "Hvidlistning, kun professionelle modparter og kontrolleret DvP-afvikling",
          whitelist: "Hvidlistning",
        },
        investor: { professional: "Professionelle investorer", accredited: "Akkrediterede/kvalificerede investorer", retail: "Detailinvestorer" },
      },
      de: {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "rechtliche Struktur", "sole-record": "primärer Rechtsnachweis", "digital wrapper": "digitale rechtliche Struktur" },
        custody: { "regulated-custodian": "regulierter Verwahrer", "regulated custodian": "regulierter Verwahrer", "ta/csd": "Transfer Agent / CSD", "self-custody": "Selbstverwahrung" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "keine" },
        liquidity: { quarterly: "vierteljährlich", monthly: "monatlich", continuous: "kontinuierlich", none: "keine", "ad-hoc": "ad hoc", "ad-hoc otc": "ad hoc OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 atomar", "off-chain": "off-chain" },
        disclosure: { exempt: "ausnahmebasiert", prospectus: "Prospekt", "private placement": "Private Placement" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "Whitelist, EU-Zugangskontrolle und zwölfmonatige Sperrfrist",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "Whitelist, ausschließlich professionelle Gegenparteien und kontrollierte DvP-Abwicklung",
          whitelist: "Whitelist",
        },
        investor: { professional: "Professionelle", accredited: "Akkreditierte/qualifizierte", retail: "Privatanleger" },
      },
      fr: {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "structure juridique", "sole-record": "registre juridique principal", "digital wrapper": "structure juridique numérique" },
        custody: { "regulated-custodian": "conservateur réglementé", "regulated custodian": "conservateur réglementé", "ta/csd": "agent de transfert / CSD", "self-custody": "auto-conservation" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "aucune" },
        liquidity: { quarterly: "trimestrielle", monthly: "mensuelle", continuous: "continue", none: "aucune", "ad-hoc": "ad hoc", "ad-hoc otc": "ad hoc OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 atomique", "off-chain": "off-chain" },
        disclosure: { exempt: "exemption", prospectus: "prospectus", "private placement": "placement privé" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "Liste blanche, contrôle d’accès UE et blocage de douze mois",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "Liste blanche, contreparties exclusivement professionnelles et règlement DvP contrôlé",
          whitelist: "Liste blanche",
        },
        investor: { professional: "Professionnels", accredited: "Accrédités/qualifiés", retail: "Particuliers" },
      },
      es: {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "estructura jurídica", "sole-record": "registro jurídico principal", "digital wrapper": "estructura jurídica digital" },
        custody: { "regulated-custodian": "custodio regulado", "regulated custodian": "custodio regulado", "ta/csd": "agente de transferencia / CSD", "self-custody": "autocustodia" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "ninguna" },
        liquidity: { quarterly: "trimestral", monthly: "mensual", continuous: "continua", none: "ninguna", "ad-hoc": "ad hoc", "ad-hoc otc": "ad hoc OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 atómico", "off-chain": "off-chain" },
        disclosure: { exempt: "exento", prospectus: "folleto", "private placement": "colocación privada" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "Lista blanca, control de acceso de la UE y bloqueo de doce meses",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "Lista blanca, contrapartes exclusivamente profesionales y liquidación DvP controlada",
          whitelist: "Lista blanca",
        },
        investor: { professional: "Profesionales", accredited: "Acreditados/cualificados", retail: "Minoristas" },
      },
      "zh-Hans": {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "包装结构", "sole-record": "主要法律登记", "digital wrapper": "数字包装结构" },
        custody: { "regulated-custodian": "受监管托管方", "regulated custodian": "受监管托管方", "ta/csd": "转让代理 / CSD", "self-custody": "自托管" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "无" },
        liquidity: { quarterly: "季度", monthly: "每月", continuous: "连续", none: "无", "ad-hoc": "临时", "ad-hoc otc": "临时 OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 原子结算", "off-chain": "链下" },
        disclosure: { exempt: "豁免", prospectus: "招股说明书", "private placement": "私募" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "白名单、欧盟准入控制及十二个月锁定期",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "白名单、仅限专业交易对手以及受控 DvP 结算",
          whitelist: "白名单",
        },
        investor: { professional: "专业投资者", accredited: "合格/认证投资者", retail: "零售投资者" },
      },
      "pt-BR": {
        generic: { "not specified": labels.notSpecified },
        instrument: { wrapper: "estrutura de wrapper", "sole-record": "registro jurídico principal", "digital wrapper": "wrapper digital" },
        custody: { "regulated-custodian": "custodiante regulado", "regulated custodian": "custodiante regulado", "ta/csd": "agente de transferência / CSD", "self-custody": "self-custody" },
        venue: { "ats/mtf": "ATS/MTF", otc: "OTC", none: "nenhum" },
        liquidity: { quarterly: "trimestral", monthly: "mensal", continuous: "contínua", none: "nenhuma", "ad-hoc": "ad hoc", "ad-hoc otc": "ad hoc OTC" },
        settlement: { "t+1/2": "T+1/2", "t+0 atomic": "T+0 atômico", "off-chain": "off-chain" },
        disclosure: { exempt: "isenta", prospectus: "prospecto", "private placement": "colocação privada" },
        transfers: {
          "whitelist + eu gating + 12-month lock-up": "Whitelist, controle de acesso da UE e lock-up de doze meses",
          "whitelist + professional-only + permissioned counterparties + controlled dvp settlement": "Whitelist, contrapartes exclusivamente profissionais e liquidação DvP controlada",
          whitelist: "Whitelist",
        },
        investor: { professional: "Profissionais", accredited: "Qualificados/acreditados", retail: "Varejo" },
      },
    };
    return maps[labels.languageCode]?.[kind]?.[key] || maps[labels.languageCode]?.generic?.[key] || raw;
  }

  function localizedCountryName(value) {
    const raw = safeStr(value).trim();
    if (!raw || labels.languageCode === "en") return raw;
    const countryMaps = {
      da: { Germany: "Tyskland", Netherlands: "Nederlandene", Luxembourg: "Luxembourg", France: "Frankrig", Spain: "Spanien", Denmark: "Danmark", "United States": "USA", "United Kingdom": "Storbritannien" },
      de: { Germany: "Deutschland", Netherlands: "Niederlande", Luxembourg: "Luxemburg", France: "Frankreich", Spain: "Spanien", Denmark: "Dänemark", "United States": "USA", "United Kingdom": "Vereinigtes Königreich" },
      fr: { Germany: "Allemagne", Netherlands: "Pays-Bas", Luxembourg: "Luxembourg", France: "France", Spain: "Espagne", Denmark: "Danemark", "United States": "États-Unis", "United Kingdom": "Royaume-Uni" },
      es: { Germany: "Alemania", Netherlands: "Países Bajos", Luxembourg: "Luxemburgo", France: "Francia", Spain: "España", Denmark: "Dinamarca", "United States": "Estados Unidos", "United Kingdom": "Reino Unido" },
      "zh-Hans": { Germany: "德国", Netherlands: "荷兰", Luxembourg: "卢森堡", France: "法国", Spain: "西班牙", Denmark: "丹麦", "United States": "美国", "United Kingdom": "英国" },
      "pt-BR": { Germany: "Alemanha", Netherlands: "Países Baixos", Luxembourg: "Luxemburgo", France: "França", Spain: "Espanha", Denmark: "Dinamarca", "United States": "Estados Unidos", "United Kingdom": "Reino Unido" },
    };
    return countryMaps[labels.languageCode]?.[raw] || raw;
  }

  function localizedCountryList(values) {
    return (Array.isArray(values) ? values : []).map(localizedCountryName).join(", ");
  }

  function localizedStakeholderIntro() {
    const map = {
      en: "",
      da: "Dette kapitel oversætter den dybe vurdering til et interessentperspektiv: hvem får del i værdien, hvem bærer omkostninger og risici, og hvad der skal aftales, for at casen kan fungere. Interessentscorerne bruger samme femfaktor-model som den samlede projektscore.",
      de: "Dieses Kapitel überträgt die Tiefenbewertung in eine Perspektive der Beteiligten: Wer erfasst den Nutzen, wer trägt Kosten und Risiken, und was muss vereinbart werden, damit der Tokenisierungsfall funktioniert. Die Beteiligten-Scores verwenden dasselbe Fünf-Faktoren-Modell wie der Gesamtscore des Projekts.",
      fr: "Ce chapitre traduit l’évaluation approfondie en perspective des parties prenantes : qui capte le potentiel positif, qui supporte les coûts et les risques, et ce qui doit être convenu pour que ce cas de tokenisation fonctionne. Les scores par partie prenante utilisent le même modèle à cinq facteurs que le score global du projet.",
      es: "Este capítulo traduce la evaluación profunda a una visión por partes interesadas: quién captura el potencial positivo, quién asume los costes y riesgos, y qué debe acordarse para que este caso de tokenización funcione. Las puntuaciones por parte interesada utilizan el mismo modelo de cinco factores que la puntuación global del proyecto.",
      "zh-Hans": "本章将深度评估转换为利益相关方视角：谁获得上行价值、谁承担成本和风险，以及各方必须达成哪些约定才能使本代币化案例成立。利益相关方评分使用与项目总体评分相同的五因素模型。",
      "pt-BR": "Este capítulo traduz a avaliação aprofundada para uma perspectiva de stakeholders: quem captura os benefícios, quem assume custos e riscos e o que precisa ser acordado para que o caso de tokenização funcione. As pontuações por stakeholder usam o mesmo modelo de cinco fatores da pontuação geral do projeto.",
    };
    return map[labels.languageCode] || map.en;
  }

  function localizedStakeholderGapNote() {
    if (!explicitStakeholderGapNote) return "";
    const map = {
      da: "Her kan den samlede TC for projektet forblive moderat, mens TC for aktivejeren er væsentligt højere, fordi setup, integration, juridisk/compliance-arbejde og leverancebyrde ligger koncentreret hos den initierende part. Tilsvarende kan interessent-NR overstige projektets samlede NR, hvis en bestemt part bærer mere usikkerhed om forvaring, overdragelse, oplysningsregime eller exit-vej end strukturen som helhed.",
      de: "Hier kann die gesamte TC des Projekts moderat bleiben, während die TC des Asset Owners deutlich höher ist, weil Setup, Integration, Rechts-/Compliance-Arbeit und Lieferlast bei der initiierenden Partei konzentriert sind. Ebenso kann das NR eines bestimmten Beteiligten über dem gesamten Projekt-NR liegen, wenn eine bestimmte Partei mehr Unsicherheit bei Verwahrung, Übertragung, Offenlegung oder Exit-Pfad trägt als die Struktur insgesamt.",
      fr: "Ici, le TC global du projet peut rester modéré alors que le TC du propriétaire de l’actif est nettement plus élevé, car la mise en place, l’intégration, le travail juridique/conformité et la charge d’exécution sont concentrés chez la partie initiatrice. De même, le NR d’une partie prenante peut dépasser le NR global du projet lorsqu’une partie supporte davantage d’incertitude sur la conservation, les transferts, l’information ou la voie de sortie que la structure dans son ensemble.",
      es: "Aquí, el TC global del proyecto puede seguir siendo moderado mientras que el TC del propietario del activo es materialmente más alto, porque la configuración, integración, trabajo legal/compliance y carga de ejecución se concentran en la parte iniciadora. Del mismo modo, el NR de una parte interesada puede superar el NR global del proyecto cuando una parte soporta más incertidumbre de custodia, transferencia, información o salida que la estructura en su conjunto.",
      "zh-Hans": "在这里，项目整体TC可能仍属中等，但资产所有人的TC会明显更高，因为设置、集成、法律/合规工作和交付负担集中在发起方。同样，如果某一方承担的托管、转让、披露或退出路径不确定性高于整体结构，其利益相关方NR可能高于项目总体NR。",
      "pt-BR": "Aqui, o TC geral do projeto pode permanecer moderado enquanto o TC do proprietário do ativo é materialmente maior, porque setup, integração, trabalho jurídico/compliance e carga de execução se concentram na parte que iniciou o projeto. Da mesma forma, o NR de um stakeholder pode exceder o NR geral do projeto quando essa parte assume mais incerteza de custódia, transferência, disclosure ou rota de saída do que a estrutura como um todo.",
    };
    return labels.languageCode === "en" ? explicitStakeholderGapNote : map[labels.languageCode] || "";
  }

  function localizedInvestorScope() {
    const euJurisdictions = new Set([
      "austria", "belgium", "bulgaria", "croatia", "cyprus", "czech republic", "czechia",
      "denmark", "estonia", "finland", "france", "germany", "greece", "hungary", "ireland",
      "italy", "latvia", "lithuania", "luxembourg", "malta", "netherlands", "poland",
      "portugal", "romania", "slovakia", "slovenia", "spain", "sweden", "eu", "european union"
    ]);
    const touchesEu = [issuance, ...sales]
      .map((value) => String(value || "").trim().toLowerCase())
      .some((value) => euJurisdictions.has(value));

    const euEligibleMap = {
      en: "other eligible non-retail investors (local classification unconfirmed)",
      da: "andre berettigede ikke-detailinvestorer (lokal klassifikation ikke bekræftet)",
      de: "andere zulässige Nicht-Retail-Anleger (lokale Einordnung unbestätigt)",
      fr: "autres investisseurs non-retail éligibles (qualification locale non confirmée)",
      es: "otros inversores no minoristas elegibles (clasificación local no confirmada)",
      "zh-Hans": "其他符合条件的非零售投资者（当地分类尚未确认）",
      "pt-BR": "outros investidores não varejo elegíveis (classificação local não confirmada)",
    };

    const explicit = [
      investorsProfessional ? localizedInputValue("professional", "investor") : null,
      investorsAccredited
        ? (touchesEu ? (euEligibleMap[labels.languageCode] || euEligibleMap.en) : localizedInputValue("accredited", "investor"))
        : null,
      investorsRetail ? localizedInputValue("retail", "investor") : null,
    ].filter(Boolean);
    return explicit.length ? explicit.join(" • ") : labels.notSpecified;
  }

  function pickVal(obj, keys) {
    if (!obj) return "";
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
        const s = safeStr(obj[k]).trim();
        if (s) return s;
      }
    }
    return "";
  }

  function pickArr(obj, keys) {
    for (const k of keys) {
      const v = obj?.[k];
      if (Array.isArray(v)) return v.filter(Boolean).map((x) => safeStr(x).trim()).filter(Boolean);
      const s = safeStr(v).trim();
      if (s) {
        // split common delimiters if user pasted a list
        const parts = s.split(/[,;]\s*/).map((x) => x.trim()).filter(Boolean);
        if (parts.length) return parts;
      }
    }
    return [];
  }

  function toNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function clamp10(n) {
    const x = Math.round(toNum(n, 0));
    return Math.max(0, Math.min(10, x));
  }

  function scorePack(scores) {
    const no = clamp10(scores.new_opportunities);
    const cs = clamp10(scores.cost_savings);
    const rr = clamp10(scores.risk_reduction);
    const tc = clamp10(scores.tokenization_costs);
    const nr = clamp10(scores.new_risks);
    const plus = no + cs + rr;
    const minus = tc + nr;
    const net = plus - minus;
    return { new_opportunities: no, cost_savings: cs, risk_reduction: rr, tokenization_costs: tc, new_risks: nr, plus, minus, net };
  }

  // ---------- Case snapshot ----------
  const issuance = pickVal(p, ["issuanceJurisdiction", "issuance", "issuance_country", "issuance_jurisdiction", "issuanceCountry"]);
  const sales = pickArr(p, ["saleJurisdictions", "salesJurisdictions", "sale_jurisdictions", "saleCountries", "sales"]);
  const instrumentType = pickVal(p, ["instrumentType", "instrument", "instrument_type"]);
  const custodyModel = pickVal(p, ["custodyModel", "custody", "custody_model"]);
  const venue = pickVal(p, ["marketVenue", "venue", "market_venue"]);
  const liquidity = pickVal(p, ["liquidityCadence", "liquidity", "liquidity_cadence"]);
  const settlement = pickVal(p, ["settlementModel", "settlement", "settlement_model"]);
  const disclosure = pickVal(p, ["disclosure", "disclosureRegime", "disclosure_regime"]);
  const transfers = pickVal(p, ["transferRestrictions", "transfers", "transfer_restrictions"]);

  const toYes = (v) => {
    if (v === true) return "yes";
    if (v === false || v == null) return "";
    const s = String(v).toLowerCase().trim();
    if (s === "true" || s === "1") return "yes";
    return s;
  };

const investorsProfessional =
  !!(
    p?.investorTypes?.Professional ||
    p?.investorTypes?.professional ||
    toYes(pickVal(p, ["investorsProfessional", "professionalInvestors", "investorProfessional", "professional"])) === "yes"
  );

const investorsAccredited =
  !!(
    p?.investorTypes?.Accredited ||
    p?.investorTypes?.accredited ||
    p?.investorTypes?.qualified_investors ||
    toYes(pickVal(p, ["investorsAccredited", "accreditedInvestors", "investorAccredited", "accredited"])) === "yes"
  );

const investorsRetail =
  !!(
    p?.investorTypes?.Retail ||
    p?.investorTypes?.retail ||
    p?.investorTypes?.retail_investors ||
    toYes(pickVal(p, ["investorsRetail", "retailInvestors", "investorRetail", "retail"])) === "yes"
  );

  function jurisdictionComparisonKey(value) {
    const normalized = normalizeValueKey(value).replace(/[^a-z0-9]+/g, "");
    const aliases = {
      unitedstates: "us",
      unitedstatesofamerica: "us",
      usa: "us",
    };
    return aliases[normalized] || normalized;
  }

  const issuanceJurisdictionKey = jurisdictionComparisonKey(issuance);
  const crossBorder =
    issuance && sales.length > 0
      ? sales.some((j) => jurisdictionComparisonKey(j) !== issuanceJurisdictionKey)
      : sales.length > 1;
  const hasWhitelist = /white/i.test(transfers);
  const hasRegCustody = /regulated/i.test(custodyModel);
  const hasMtfAts = /(mtf|ats)/i.test(venue);
  const hasT0Atomic = /(t\+0|t0|atomic)/i.test(settlement);

  // Asset-class hints (lightweight inference from summary text)
  const summary = safeStr(result?.narrative_summary || result?.rationale || "").toLowerCase();
  const hints = [];
  if (/(real estate|property|residential|commercial)/i.test(summary)) hints.push("Real estate / property-linked");
  if (/(fund|nav|redemption|units)/i.test(summary)) hints.push("Fund-like structure");
  if (/(debt|note|bond|private credit|loan|receivable)/i.test(summary)) hints.push("Debt / private credit / note-like");
  const hintText = hints.length ? hints.join("; ") : "";

  // ---------- Overall project score (as baseline) ----------
  const overall = scorePack({
    new_opportunities: toNum(fs.new_opportunities, 0),
    cost_savings: toNum(fs.cost_savings, 0),
    risk_reduction: toNum(fs.risk_reduction, 0),
    tokenization_costs: toNum(fs.tokenization_costs, 0),
    new_risks: toNum(fs.new_risks, 0),
  });

  // Only qualify the time-horizon explanation where the submitted case itself
  // supports it: controlled institutional workflow, high recurring volume,
  // explicit initiating-party benefit, and actual retirement of legacy steps.
  const stakeholderWorkflowNarrative = [
    p?.text,
    p?.description,
    p?.case_description,
    p?.narrative,
    p?.caseText,
    p?.caseDescription,
  ].map(safeStr).join(" ").toLowerCase();
  const userStatesQuarterlyTransfers = /\bquarterly\b/i.test(
    `${safeStr(liquidity)} ${safeStr(transfers)} ${stakeholderWorkflowNarrative}`
  );
  const userStatesContinuousTransfers = /\bcontinuous(?:ly)?\b|24\/7/i.test(
    `${safeStr(liquidity)} ${safeStr(transfers)} ${stakeholderWorkflowNarrative}`
  );

  const explicitFundCollateralUtility = /\b(?:use|used|using|usable|usability|utility|mobili[sz]e|mobilisation|mobilization|pledge|pledged|pledging)\b[^.!?\n]{0,100}\b(?:fund )?(?:units?|shares?|positions?)?\b[^.!?\n]{0,60}\bcollateral\b|\bcollateral\b[^.!?\n]{0,100}\b(?:use|usability|utility|mobili[sz]ation|pledge|treasury)\b/i.test(
    stakeholderWorkflowNarrative
  );

  // The backend applies the same narrow evidence test before setting this flag.
  // Use that canonical result so the verdict rationale and stakeholder chapter
  // cannot disagree about whether long-horizon treatment applies.
  const qualifiedWorkflowTimeHorizon =
    result?.initiator_viability?.long_horizon_signals === true;

  // ---------- Stakeholder scoring heuristic ----------
  // We start from the overall factor-scores and re-allocate them across stakeholders depending on who typically captures benefits
  // vs who typically carries costs/risks, then nudge using this case's inputs.
  function stakeholderScores(kind) {
    if (kind === "asset_owner" && result?.initiator_viability?.asset_owner_scores) {
      return scorePack(result.initiator_viability.asset_owner_scores);
    }

    const workflowNarrative = [
      p?.text,
      p?.description,
      p?.case_description,
      p?.narrative,
      p?.caseText,
      p?.caseDescription,
    ].map(safeStr).join(" ").toLowerCase();

    // A controlled post-trade workflow must not inherit the generic issuer/
    // capital-formation allocation when the input explicitly states that the
    // initiating institution captures the operational savings and risk reduction.
    const directWorkflowSponsorBenefit =
      kind === "asset_owner" &&
      isClosedWorkflowInfrastructureCase(p, result) &&
      /(?:asset owner|initiating (?:participant|institution|party))[^.]{0,180}primary (?:beneficiary|value recipient)/i.test(
        workflowNarrative
      );

    // multipliers
    const mult = directWorkflowSponsorBenefit
      ? { no: 1.0, cs: 1.0, rr: 1.0, tc: 1.25, nr: 1.25 }
      : {
          asset_owner: { no: 0.95, cs: 0.95, rr: 0.85, tc: 1.25, nr: 1.25 },
          investors: { no: 1.10, cs: 0.55, rr: 0.95, tc: 0.75, nr: 1.05 },
          infrastructure: { no: 0.95, cs: 0.35, rr: 0.35, tc: 0.55, nr: 0.55 },
        }[kind];

    const base = {
      new_opportunities: overall.new_opportunities * mult.no,
      cost_savings: overall.cost_savings * mult.cs,
      risk_reduction: overall.risk_reduction * mult.rr,
      tokenization_costs: overall.tokenization_costs * mult.tc,
      new_risks: overall.new_risks * mult.nr,
    };

    // case-specific nudges (small integers, then clamp)
    const bump = { no: 0, cs: 0, rr: 0, tc: 0, nr: 0 };

    if (crossBorder) {
      if (kind === "asset_owner") { bump.tc += 1; bump.nr += 1; }
      if (kind === "investors") { bump.nr += 1; }
      if (kind === "infrastructure") { bump.no += 1; bump.tc += 1; }
    }

    if (investorsRetail) {
      if (kind === "asset_owner") bump.nr += 1;
      if (kind === "investors") { bump.nr += 1; bump.tc += 1; }
    }

    if (hasRegCustody) {
      if (kind === "investors") { bump.rr += 1; bump.nr -= 1; }
      if (kind === "asset_owner" && !directWorkflowSponsorBenefit) bump.tc += 1;
      if (kind === "infrastructure") { bump.no += 1; bump.tc += 1; }
    }

    if (hasMtfAts) {
      if (kind === "investors") bump.no += 1;
      if (kind === "asset_owner") { bump.tc += 1; bump.nr += 1; }
      if (kind === "infrastructure") bump.no += 1;
    }

    if (hasWhitelist) {
      if (kind === "investors") { bump.no -= 1; bump.tc += 1; bump.nr += 1; }
      if (kind === "asset_owner" && !directWorkflowSponsorBenefit) bump.tc += 1;
    }

    if (hasT0Atomic) {
      if (kind === "investors") bump.rr += 1;
      if (kind === "asset_owner") bump.tc += 1;
      if (kind === "infrastructure") { bump.tc += 1; bump.nr += 1; }
    }

    if (/wrapper/i.test(instrumentType)) {
      if (kind === "asset_owner" && !directWorkflowSponsorBenefit) bump.tc += 1;
      if (kind === "investors") bump.nr += 1;
    }

    if (/exempt/i.test(disclosure)) {
      if (kind === "investors") bump.nr += 1;
      if (kind === "asset_owner" && !directWorkflowSponsorBenefit) bump.nr += 1;
    }

    // tax missing is a broad risk amplifier
    const taxMissing = missing.some((x) => /tax/i.test(String(x)));
    if (taxMissing || /tax/i.test(safeStr(p.taxNotes || ""))) {
      if (kind === "asset_owner") bump.nr += 1;
      if (kind === "investors") bump.nr += 1;
    }

    return scorePack({
      new_opportunities: base.new_opportunities + bump.no,
      cost_savings: base.cost_savings + bump.cs,
      risk_reduction: base.risk_reduction + bump.rr,
      tokenization_costs: base.tokenization_costs + bump.tc,
      new_risks: base.new_risks + bump.nr,
    });
  }

  // ---------- Narrative builders (case-specific but not "internal notes") ----------
  function mkJur() {
    if (!issuance && sales.length === 0) return "your chosen jurisdictions";
    if (issuance && sales.length === 0) return issuance;
    if (issuance && sales.length > 0) return `${issuance} (issuance) + ${sales.join(", ")} (sales)`;
    return sales.join(", ");
  }

const distributionCase =
  investorsRetail ||
  hasMtfAts ||
  /^(continuous|quarterly)$/i.test(String(liquidity || "").trim());

const closedWorkflowCase =
  !distributionCase &&
  !investorsRetail &&
  /^(none|ad-hoc otc)$/i.test(String(liquidity || "").trim());

const summaryText = [
  p?.text,
  p?.description,
  p?.caseDescription,
  p?.case_description,
  p?.narrative,
  p?.caseSummary,
  p?.case_summary,
]
  .map((value) => String(value || "").trim())
  .filter(Boolean)
  .join("\n")
  .toLowerCase();
const issuanceLc = String(issuance || "").toLowerCase();
const salesLc = sales.map((s) => String(s || "").toLowerCase()).join(" | ");
const transfersLc = String(transfers || "").toLowerCase();
const disclosureLc = String(disclosure || "").toLowerCase();
const instrumentLc = String(instrumentType || "").toLowerCase();
const custodyLc = String(custodyModel || "").toLowerCase();
const venueLc = String(venue || "").toLowerCase();
const settlementLc = String(settlement || "").toLowerCase();

const modifiers = {
  professional_only: !investorsRetail,
  retail_scope: investorsRetail,
  cross_border: crossBorder,
  permissioned: hasWhitelist || /permissioned|closed user group|administrator-approved/i.test(transfersLc),
  regulated_custody: hasRegCustody,
  self_custody: /self-custody/i.test(custodyLc),
  periodic_liquidity: /quarterly/i.test(String(liquidity || "").toLowerCase()),
  ambiguous_rights: /ambiguous|informal profit-sharing|membership rights|not fully defined/i.test(summaryText),
};

const commercialPaperContext = /\bcommercial paper\b|\bcp program(?:me)?\b|\bcp notes?\b/.test(summaryText);
const realEstateContext = /\breal estate\b|\bresidential\b|\brental\b|\bproperties?\b|\btenant[- ]area\b|\brenovation\b|\bfaçade\b|\bfacade\b|\bnet rental cash flow\b/.test(summaryText);
const privateCreditContext = !commercialPaperContext && /\bprivate credit\b|\breceivables?\b|\btrade credit\b|\binvoices?\b|\boriginators?\b|\bcollections?\b|\btrue sale\b|\bbankruptcy[- ]remote\b|\bthird[- ]party servicing\b|\breceivables? assignment\b/.test(summaryText);
const mixedRealEstateCreditContext = realEstateContext && privateCreditContext;

function deriveArchetype() {
  const score = {
    fi_treasury_fund: 0,
    real_estate_income_wrapper: 0,
    private_credit_notes: 0,
    closed_workflow_infrastructure: 0,
    st_equity_or_capital_raise: 0,
    retail_collectible_nft: 0,
  };

 // 1) FI treasury / MMF
if (/money market fund|mmf|treasury|cash managers|liquidity investors/.test(summaryText)) score.fi_treasury_fund += 4;
if (/collateral usability|collateral utility|treasury flexibility|subscription\/redemption|fund units/.test(summaryText)) score.fi_treasury_fund += 2;
if (modifiers.professional_only) score.fi_treasury_fund += 1;
if (modifiers.permissioned) score.fi_treasury_fund += 1;
if (/off-chain/.test(settlementLc)) score.fi_treasury_fund += 1;
if (/ta\/csd/.test(custodyLc)) score.fi_treasury_fund += 1;
if (/csd\/ta-registered/.test(venueLc)) score.fi_treasury_fund += 1;
if (/wrapper/.test(instrumentLc)) score.fi_treasury_fund += 1;
if (modifiers.periodic_liquidity) score.fi_treasury_fund += 1;

  // 2) Real estate income wrapper
  if (realEstateContext || /\bprofit-participating notes\b|\bproperty spv\b/.test(summaryText)) score.real_estate_income_wrapper += 3;
  if (/wrapper/.test(instrumentLc)) score.real_estate_income_wrapper += 1;
  if (modifiers.cross_border) score.real_estate_income_wrapper += 1;
  if (modifiers.periodic_liquidity) score.real_estate_income_wrapper += 1;

  // 3) Private credit notes
  if (privateCreditContext) score.private_credit_notes += 3;
  if (/wrapper/.test(instrumentLc)) score.private_credit_notes += 1;
  if (modifiers.professional_only) score.private_credit_notes += 1;

  // 4) Closed workflow infrastructure
  if (/collateral rail|repo|post-trade|reconciliation|workflow|settlement rail|dvp/.test(summaryText)) score.closed_workflow_infrastructure += 3;
  if (closedWorkflowCase) score.closed_workflow_infrastructure += 2;
  if (modifiers.permissioned) score.closed_workflow_infrastructure += 1;
  if (/t\+0 atomic/.test(settlementLc)) score.closed_workflow_infrastructure += 1;

  // 5) STO / capital raise
  if (/\bcapital raise\b|\bequity\b|\braise capital\b|\bfundraising\b|\bsecurity token offering\b|\bsto\b|\bcommercial paper\b|\bcp program(?:me)?\b|\bdebt programme?\b|\bbond issuance\b/.test(summaryText)) score.st_equity_or_capital_raise += 3;
  if (distributionCase) score.st_equity_or_capital_raise += 1;
  if (/wrapper|sole-record/.test(instrumentLc)) score.st_equity_or_capital_raise += 1;

  // 6) Retail collectible NFT
  if (/fine art|artwork|collectible|nft/.test(summaryText)) score.retail_collectible_nft += 3;
  if (modifiers.retail_scope) score.retail_collectible_nft += 2;
  if (modifiers.self_custody) score.retail_collectible_nft += 1;
  if (/dex/.test(venueLc)) score.retail_collectible_nft += 1;
  if (modifiers.ambiguous_rights) score.retail_collectible_nft += 2;

  // Closed workflow copy is activated only by backend-declared workflow metadata.
  // The local asset-context heuristic must never create that top-level route.
  const ranked = Object.entries(score)
    .filter(([key]) => key !== "closed_workflow_infrastructure")
    .sort((a, b) => b[1] - a[1]);
  const top = ranked[0];
  const second = ranked[1];

const hardFiTreasuryMatch =
  /money market fund|mmf/.test(summaryText) &&
  /off-chain/.test(settlementLc) &&
  /ta\/csd/.test(custodyLc) &&
  modifiers.permissioned &&
  modifiers.professional_only;

const hardPrivateCreditMatch =
  privateCreditContext &&
  !mixedRealEstateCreditContext &&
  /wrapper/.test(instrumentLc) &&
  modifiers.professional_only &&
  modifiers.permissioned;

const hardRealEstateMatch =
  realEstateContext &&
  !mixedRealEstateCreditContext &&
  /wrapper/.test(instrumentLc) &&
  modifiers.cross_border &&
  modifiers.periodic_liquidity &&
  modifiers.professional_only &&
  !investorsRetail;

const primaryArchetype =
  hardFiTreasuryMatch
    ? "fi_treasury_fund"
    : hardRealEstateMatch
    ? "real_estate_income_wrapper"
    : hardPrivateCreditMatch
    ? "private_credit_notes"
    : top && top[1] >= 3 && (!second || top[1] - second[1] >= 2)
    ? top[0]
    : "generic_neutral";

  return {
    primaryArchetype,
    modifiers,
    archetypeScore: score,
  };
}

const archetypeDebug = deriveArchetype();
const declaredReportArchetype = String(
  result?.report_case_archetype || result?.reportCaseArchetype || ""
).trim().toLowerCase();
const declaredClosedWorkflow = isClosedWorkflowInfrastructureCase(p, result);
const declaredWarehouseReceipt = isWarehouseReceiptWorkflowCase(p, result);
const declaredIssuanceLifecycle = isIssuanceLifecycleOptimizationCase(p, result);
const declaredFundAdministration = isFundAdministrationOptimizationCase(p, result);
const declaredPermissionedDefi = isPermissionedDefiLendingCase(p, result);
const declaredCapitalFormation = declaredReportArchetype === "capital_formation";
const declaredHybrid = declaredReportArchetype === "hybrid";
const primaryArchetype = declaredClosedWorkflow
  ? "closed_workflow_infrastructure"
  : declaredIssuanceLifecycle
  ? "issuance_lifecycle_optimization"
  : declaredFundAdministration
  ? "fi_treasury_fund"
  : declaredPermissionedDefi
  ? "permissioned_defi_lending"
  : archetypeDebug.primaryArchetype === "generic_neutral" && (declaredCapitalFormation || declaredHybrid || commercialPaperContext)
  ? "st_equity_or_capital_raise"
  : archetypeDebug.primaryArchetype;

// Capital-formation interpretation is intentionally separate from the workflow
// time-horizon logic. It is editorial only: no scores, factor weights or
// verdicts are changed. The condition requires an explicit financing/raise
// signal in the customer's own case text and excludes closed workflow cases.
const qualifiedCapitalFormationInterpretation =
  !declaredClosedWorkflow &&
  !declaredIssuanceLifecycle &&
  !declaredFundAdministration &&
  !declaredPermissionedDefi &&
  (declaredCapitalFormation || declaredHybrid ||
    /\b(?:capital[\s-]?(?:raise|raising|formation)|fundrais(?:e|ing)|raise\s+capital|raising\s+(?:around|approximately|up\s+to|€|eur|\$|usd|\d)|seeking\s+(?:to\s+)?raise|new\s+financing|financing\s+round|issuance\s+proceeds|offer(?:ing)?\s+(?:to|for)\s+investors?|commercial\s+paper|bond\s+issuance)\b/i.test(
      stakeholderWorkflowNarrative
    ));

  function bulletsAssetOwner() {
    const plus = [];
    const minus = [];
    const next = [];
    
 if (primaryArchetype === "fi_treasury_fund") {
  plus.push(
    explicitFundCollateralUtility
      ? "Operational efficiency, investor-lifecycle administration, reporting/control improvements, reliable cash-leg workflows, and the explicitly stated collateral utility are the main sources of value if the operating model is adopted at scale."
      : "Operational efficiency, investor-lifecycle administration, clearer position records, reporting/control improvements, and reliable cash-leg workflows are the main sources of value if the operating model is adopted at scale."
  );
} else if (primaryArchetype === "permissioned_defi_lending") {
  plus.push("The main initiating-party upside comes from programmable collateral controls, standardized borrower and lender workflows, and controlled digital-cash settlement if the legal and operational model is proven under stress.");
} else if (primaryArchetype === "real_estate_income_wrapper") {
  plus.push("The main upside comes from alternative capital access, a clearer income-linked investor proposition and more disciplined investor administration than a realistic conventional non-tokenized alternative.");
} else if (primaryArchetype === "private_credit_notes") {
  plus.push("The intended upside is scalable investor onboarding, cleaner cash-flow servicing, and more efficient reporting/distribution mechanics; these benefits remain to be demonstrated against the conventional alternative.");
} else if (primaryArchetype === "closed_workflow_infrastructure") {
  plus.push("Operational efficiency, control, reconciliation benefits, and post-trade resilience are more relevant than broader distribution in this setup.");
} else if (primaryArchetype === "st_equity_or_capital_raise") {
  plus.push("The main upside depends on whether tokenization materially improves capital access, investor reach, and fundraising efficiency versus a conventional structure.");
} else if (primaryArchetype === "retail_collectible_nft") {
  plus.push("Any theoretical upside depends on enforceable rights, compliant distribution, and a credible investor-protection model; without those, token-native reach alone is not meaningful value creation.");
} else if (distributionCase) {
  plus.push("Broader capital access and potentially smaller ticket sizes through token-native distribution (if the product/venue supports it).");
} else if (closedWorkflowCase) {
  plus.push("Operational efficiency, control, and post-trade resilience are more relevant than broader distribution in this setup.");
} else {
  plus.push("The main potential upside depends on a credible operating model for the defined eligible holder base, rather than on broad distribution expansion.");
}
    
    if (hasMtfAts) plus.push("The user-selected transfer environment may support a more structured transfer process, but no legal characterization, market depth or liquidity outcome is assumed.");
    if (hasRegCustody) plus.push("The selected custody model may support investor confidence and onboarding discipline if its legal and operational role is validated.");
    if (hasT0Atomic && primaryArchetype !== "retail_collectible_nft") {
      plus.push("Cleaner post-trade operations (fewer settlement fails) if the cash leg and controls are properly designed.");
    }

    if (/wrapper/i.test(instrumentType)) {
  minus.push(
    primaryArchetype === "fi_treasury_fund"
      ? explicitFundCollateralUtility
        ? `Higher upfront legal, fund-structuring, and operational-design work is needed to ensure that tokenized fund-unit rights, transfer controls, ${fundLifecycleScope.phrase}, and the explicitly stated collateral use are enforceable and operationally coherent through the wrapper.`
        : `Higher upfront legal, fund-structuring, and operational-design work is needed to ensure that tokenized fund-unit rights, authoritative records, ${fundLifecycleScope.phrase} and transfer approvals are enforceable and operationally coherent through the wrapper.`
      : "Higher upfront legal/structuring work to ensure enforceability of token-holder rights through the wrapper."
  );
}
    if (crossBorder) minus.push(`Cross-border compliance complexity across ${mkJur()} (marketing rules, investor eligibility, reporting).`);
    if (investorsRetail) minus.push("Additional investor-protection obligations and operational burden (suitability, disclosures, complaints handling).");
    if (hasMtfAts) minus.push("Listing/admission, ongoing disclosure expectations, and vendor/venue dependency can increase OPEX.");
    if (hasWhitelist) minus.push("Whitelist controls can constrain liquidity and require continuous compliance operations.");

    next.push("Lock the operating model: rights (term sheet), custody setup, venue path, cash-flow/distribution mechanics, and transfer rules.");
next.push("Resolve the highest-priority evidence gaps identified in the report before spending further on build or integration.");

if (primaryArchetype === "private_credit_notes") {
  next.push(
    userStatesQuarterlyTransfers
      ? "Stress-test the explicitly selected quarterly transfer process: define approvals, eligible counterparties, settlement steps and what happens if matching demand is thin. Do not infer an OTC-market status."
      : "Define the transfer process without assuming a cadence, market status, matching mechanism or liquidity support that the user has not stated."
  );
} else if (primaryArchetype === "real_estate_income_wrapper") {
  next.push(
    userStatesQuarterlyTransfers
      ? "Stress-test the explicitly selected quarterly transfer process: define the operator role, approvals, settlement steps and what happens if matching demand is shallow. No liquidity support is assumed."
      : "Define the transfer process without assuming a cadence, market status, matching mechanism or liquidity support that the user has not stated."
  );
} else if (primaryArchetype === "fi_treasury_fund") {
  next.push(
    explicitFundCollateralUtility
      ? "Be explicit about operational reality: who can subscribe, redeem, transfer, pledge or move units, under which approvals, and with what evidence for the explicitly stated treasury or collateral utility."
      : `Be explicit about investor-lifecycle reality: define how ${fundLifecycleScope.phrase} and unit transfers are processed, which record is authoritative, and which existing administration steps are actually removed.`
  );
} else if (primaryArchetype === "permissioned_defi_lending") {
  next.push("Define enforceable pool and collateral rights, oracle and valuation controls, margin and liquidation rules, cash-leg finality, emergency powers, recovery and formal reassessment triggers for protocol or governance changes.");
} else if (primaryArchetype === "closed_workflow_infrastructure") {
  next.push("Be explicit about transfer reality: who can move positions, under which workflow, with what settlement controls, and which duplicated legacy steps are actually removed.");
} else if (primaryArchetype === "retail_collectible_nft") {
  next.push("Do not assume token-native liquidity solves the case: define whether any compliant exit path exists at all, and stop if rights, custody, and investor-protection architecture remain weak.");
} else if (distributionCase) {
  next.push(
    userStatesContinuousTransfers
      ? "Stress-test the user-stated continuous-transfer objective: define what it means operationally, which legally validated process supports it and what realistic limitations apply. No market-making arrangement is assumed."
      : "Define the transfer process without assuming continuous trading, a cadence, a matching mechanism or liquidity support that the user has not stated."
  );
} else {
  next.push("Be explicit about transfer reality: who can transfer, how often, through which workflow, and with what evidence of executable demand or committed counterparties.");
}

    return { plus, minus, next };
  }

  function bulletsInvestors() {
    const plus = [];
    const minus = [];
    const next = [];

   if (primaryArchetype === "fi_treasury_fund") {
  plus.push(
    explicitFundCollateralUtility
      ? "The main investor-side value lies in reliable fund-unit records, operational utility, lifecycle control, reporting clarity and the explicitly stated collateral utility for professional or institutional users."
      : `The main investor-side value lies in reliable fund-unit records, operational utility, lifecycle control, reporting clarity and dependable workflows for ${fundLifecycleScope.phrase} for professional or institutional users.`
  );
} else if (primaryArchetype === "permissioned_defi_lending") {
  plus.push("Lender-side value depends on enforceable pool rights, transparent collateral eligibility and valuation, reliable digital-cash settlement, disciplined liquidation and controlled governance rather than on assumed token liquidity.");
} else if (primaryArchetype === "real_estate_income_wrapper") {
  plus.push("The intended investor-side value is access to an income-linked real-asset exposure with potential improvements in reporting and transfer discipline; any advantage over a conventional non-tokenized alternative remains to be demonstrated.");
} else if (primaryArchetype === "private_credit_notes") {
  plus.push("The intended investor-side value is cleaner access to cash-flow-linked credit exposure, with potential improvements in reporting, servicing, and transfer discipline; superiority to conventional private-credit formats has not been demonstrated.");
} else if (primaryArchetype === "closed_workflow_infrastructure") {
  plus.push("Value for eligible participants depends more on enforceable rights, transfer controls, custody, and operational reliability than on broader access expansion.");
} else if (primaryArchetype === "st_equity_or_capital_raise") {
  plus.push("The investor case depends mainly on whether tokenization creates meaningful access, cleaner ownership mechanics, and an operationally supportable route to future transfers versus a conventional offering.");
} else if (primaryArchetype === "retail_collectible_nft") {
  plus.push("Any investor-side value depends first on legally validated rights, compliant distribution, and effective investor protections; without those, retail access and token-native transferability are not meaningful benefits.");
} else if (distributionCase) {
  plus.push("Potential access to exposures that are hard to reach in conventional formats (fractional participation, smaller minimums, broader distribution).");
} else if (closedWorkflowCase) {
  plus.push("Value for eligible participants depends more on enforceable rights, transfer controls, custody, and operational reliability than on broader access expansion.");
} else {
  plus.push("The main investor-side value depends on clearer rights, servicing, and transfer mechanics for the defined eligible holder base.");
}
    
    if (hasRegCustody) plus.push("The selected regulated-custodian design may support safeguarding and auditability if account, ownership and control arrangements are validated.");
    if (hasMtfAts) plus.push("A clearer secondary-trading route than typical private market holdings (but liquidity depth is not guaranteed).");
    if (hasT0Atomic && primaryArchetype !== "retail_collectible_nft") {
      plus.push("Reduced settlement risk if atomic/T+0 is implemented with a legally and operationally validated cash leg and proper controls.");
    }

    if (/wrapper/i.test(instrumentType)) {
  minus.push(
    primaryArchetype === "fi_treasury_fund"
      ? explicitFundCollateralUtility
        ? `Need extra clarity on legal and operational enforceability: what exactly the tokenized fund unit represents, how claims are exercised, and how ${fundLifecycleScope.phrase}, transfer approval, and the explicitly stated collateral use work in practice.`
        : `Need extra clarity on legal and operational enforceability: what exactly the tokenized fund unit represents, how claims are exercised, and how ${fundLifecycleScope.phrase} and transfer approvals work in practice.`
      : "Need extra clarity on legal enforceability: what exactly the token represents and how claims are exercised."
  );
}
    if (hasWhitelist) minus.push("Transfer restrictions (whitelists) can limit who you can sell to, and when.");
    if (investorsRetail) minus.push("Higher friction: eligibility checks, disclosures (KID/Prospectus or exemptions), and suitability constraints.");
    if (crossBorder) minus.push("Cross-border compliance can reduce distribution breadth and may introduce withholding/reporting complexity.");
    if (/exempt/i.test(disclosure)) minus.push("Information asymmetry risk if disclosure is light—investors will demand compensating safeguards (audits, reporting, governance).");

    if (primaryArchetype === "private_credit_notes") {
  next.push(
    userStatesQuarterlyTransfers
      ? "Demand a clean transfer note for the explicitly selected quarterly process: eligible counterparties, approval steps, settlement mechanics and what happens if there is no matching demand."
      : "Demand a clean transfer note covering eligible counterparties, approval steps, settlement mechanics and unresolved cadence; no matching demand or exit route is assumed."
  );
} else if (primaryArchetype === "real_estate_income_wrapper") {
  next.push(
    userStatesQuarterlyTransfers
      ? "Demand a clean transfer-process note covering the operator role, the explicitly selected quarterly cadence, transfer restrictions, settlement mechanics and what happens if secondary demand is shallow."
      : "Demand a clean transfer-process note covering the operator role, transfer restrictions, settlement mechanics and unresolved cadence; no secondary demand is assumed."
  );
} else if (primaryArchetype === "fi_treasury_fund") {
  next.push(
    explicitFundCollateralUtility
      ? "Demand a clean rights-and-workflow note: who can subscribe, redeem, transfer, pledge or mobilize tokenized fund units, under what approvals and control points, how beneficial ownership and claims are evidenced, and what fallback applies if the stated collateral utility is narrower than expected."
      : `Demand a clean rights-and-workflow note covering ${fundLifecycleScope.phrase} and unit transfers: define which record is authoritative, how beneficial ownership and claims are evidenced, and what fallback applies when a provider or workflow fails.`
  );
} else if (primaryArchetype === "closed_workflow_infrastructure") {
  next.push("Demand a clean operational-control note: who can move positions, under which settlement workflow, with what finality, and what fallback applies if the tokenized lane is unavailable.");
} else if (primaryArchetype === "retail_collectible_nft") {
  next.push("Do not rely on a generic exit story: demand a legally defensible account of rights, venue, transfer restrictions, and whether any compliant liquidity path exists at all.");
} else {
  next.push("Demand a clean 'how to exit' note: venue, cadence, transfer rules, and what happens if liquidity is thin.");
}
    if (primaryArchetype === "retail_collectible_nft") {
      next.push("Verify the custody and control model in operational detail: wallet and key-recovery risks, authority over transfers, evidence of beneficial ownership, incident handling, and the legal relationship between token records and the underlying artwork.");
      next.push("Clarify the full economics: artwork acquisition, storage, insurance, appraisal and provenance work, platform and venue fees, legal and disclosure costs, distributions, resale proceeds and dispute handling.");
    } else if (primaryArchetype === "permissioned_defi_lending") {
      next.push("Verify pool, custody and collateral controls in operational detail: lender and borrower position records, collateral eligibility and valuation, oracle responsibility, margin and liquidation workflows, incident handling, auditability, emergency powers and recovery.");
      next.push("Clarify economics: pool and provider fees, oracle and audit costs, expected reporting, cash-leg dependencies and how lender cash flows are distributed.");
    } else {
      next.push("Verify custody and operational controls in operational detail: account model, beneficial ownership evidence, transfer-approval workflow, incident handling, auditability, and how investor rights are evidenced across the custody, registry and operating stack.");
      next.push("Clarify economics: fee stack (custody/venue/platform), expected reporting cadence, and how cash flows are distributed.");
    }

    return { plus, minus, next };
  }

  function bulletsInfrastructure() {
    const plus = [];
    const minus = [];
    const next = [];

    if (primaryArchetype === "fi_treasury_fund") {
  plus.push(
    explicitFundCollateralUtility
      ? "Infrastructure value depends on supplying institution-grade fund administration, authoritative-record integration, custody/control, reporting, cash-leg coordination, and the explicitly stated collateral-enablement capability."
      : `Infrastructure value depends on supplying institution-grade fund administration, authoritative-record integration, custody/control, reporting, and reliable workflows for ${fundLifecycleScope.phrase} and the cash leg.`
  );
} else if (primaryArchetype === "permissioned_defi_lending") {
  plus.push("Infrastructure value depends on reliable smart-contract, oracle, collateral-monitoring, liquidation, custody, digital-cash, governance and incident-recovery services within a permissioned model.");
} else if (primaryArchetype === "real_estate_income_wrapper") {
  plus.push("Infrastructure providers may benefit if they can support a legally and operationally validated wrapper, reporting, custody, and user-defined transfer stack for cross-border income-linked instruments.");
} else if (primaryArchetype === "private_credit_notes") {
  plus.push("Infrastructure value depends on servicing discipline, cash-flow reporting, custody/control, and compliant transfer workflows for a controlled credit product.");
} else if (primaryArchetype === "closed_workflow_infrastructure") {
  plus.push("Infrastructure value comes mainly from post-trade efficiency, reconciliation reduction, controlled settlement flows, and robust operational governance rather than from broad distribution.");
} else if (primaryArchetype === "st_equity_or_capital_raise") {
  plus.push("Infrastructure providers benefit if they can support issuance, onboarding, cap-table/ownership logic, and a credible route to compliant post-issuance transfers.");
} else if (primaryArchetype === "retail_collectible_nft") {
  plus.push("Any infrastructure value depends first on compliance, rights clarity, and controlled investor protection; token-native trading rails alone do not create durable value in this setup.");
} else {
  plus.push("Commercial upside from recurring service fees (platform/custody/venue/transfer controls), especially in multi-jurisdiction projects.");
}

if (hasRegCustody) plus.push("The selected custody model may be relevant to the stated investor scope, but demand and legal necessity are not assumed.");
if (hasMtfAts) plus.push("Venue participation can expand revenue sources (listing, surveillance, member onboarding, reporting).");
if (hasT0Atomic) plus.push("Settlement innovation can be a differentiator if the cash leg is compliant and operationally robust.");

    
    minus.push(
      primaryArchetype === "permissioned_defi_lending"
        ? "Integration and operational complexity across oracle feeds, collateral monitoring, margining, liquidation, cash-leg coordination, governance changes and incident response can scale faster than expected."
        : primaryArchetype === "fi_treasury_fund"
        ? `Integration and operational complexity across reconciliations, ${fundLifecycleScope.phrase}, transfer approvals, reporting and incident response can scale faster than expected.`
        : "Integration and operational complexity (reconciliations, reporting, corporate actions, incident response) often scales faster than expected."
    );
    if (crossBorder) minus.push("Regulatory perimeter and liability exposure can increase with cross-border sales, investor-scope expansion, and more complex distribution structures.");
    if (hasT0Atomic) minus.push("T+0/atomic settlement increases error-cost: reversals are harder, controls must be tighter.");
    if (hasWhitelist) minus.push("Whitelist logic can interfere with matching/settlement and requires coordination across venue + custodian + issuer.");

    next.push(
  primaryArchetype === "fi_treasury_fund"
    ? explicitFundCollateralUtility
      ? `Define interfaces and responsibilities early (RACI): who owns KYC/AML, whitelist administration, ${fundLifecycleScope.phrase}, transfer approvals, reconciliations, reporting, cash-leg handling, and the explicitly stated collateral-mobilisation workflow across the fund-administration and custody stack.`
      : `Define interfaces and responsibilities early (RACI): who owns KYC/AML, whitelist administration, ${fundLifecycleScope.phrase}, transfer approvals, reconciliations, reporting and cash-leg handling across the fund-administration and custody stack.`
    : primaryArchetype === "permissioned_defi_lending"
    ? "Define interfaces and responsibilities early (RACI): who owns KYC/AML, pool and collateral records, oracle data, valuation, margining, liquidation, cash-leg operations, governance changes, incident response and recovery."
    : "Define interfaces and responsibilities early (RACI): who owns KYC/AML, whitelists, transfers, reconciliations, and reporting."
);
    next.push("Agree evidence expectations: SOC reports, audit trails, code audits, incident runbooks, and SLA/exit clauses.");
    next.push("Align commercial terms with actual delivery responsibilities and evidenced outcomes; avoid adding a token layer without measurable operational or commercial value.");

    return { plus, minus, next };
  }

  const stakeholders = [
    {
      key: "asset_owner",
      title: "Asset owner / Issuer (sponsor, manager)",
      subtitle:
  primaryArchetype === "fi_treasury_fund"
    ? explicitFundCollateralUtility
      ? "Expected value depends mainly on investor-lifecycle efficiency, authoritative position records, reporting/control improvements, reliable cash-leg workflows and the explicitly stated collateral utility, while most setup and delivery burden still sits with the initiating Asset Owner."
      : "Expected value depends mainly on investor-lifecycle efficiency, authoritative position records, reporting/control improvements and reliable cash-leg workflows, while most setup and delivery burden still sits with the initiating Asset Owner."
    : primaryArchetype === "permissioned_defi_lending"
    ? "Expected value depends on programmable collateral control and controlled lending operations, while the initiating party bears substantial legal, oracle, liquidation, smart-contract, cash-leg and governance design burden."
    : primaryArchetype === "closed_workflow_infrastructure"
    ? "Expected value depends mainly on operational efficiency, control, reconciliation reduction, and post-trade resilience, while integration and execution burden still sits primarily with the initiating participant."
    : primaryArchetype === "private_credit_notes"
    ? "Expected value depends mainly on scalable investor onboarding, cleaner servicing/reporting flows, and disciplined transfer mechanics, while most structuring, compliance, and delivery burden still sits with the initiating Asset Owner."
    : primaryArchetype === "real_estate_income_wrapper"
    ? `Expected value depends mainly on whether a ${issuance || "single-jurisdiction"}-anchored income-linked structure${sales.length ? ` marketed into ${sales.join(", ")}` : ""} can improve funding flexibility and investor proposition without cross-border compliance, periodic-liquidity design, and venue/custody costs overwhelming Asset Owner economics.`
    : "Typically captures strategic upside, but often carries most upfront cost and execution risk.",
      scores: stakeholderScores("asset_owner"),
      bullets: bulletsAssetOwner(),
    },
    {
      key: "investors",
      title: "Investors",
      subtitle:
  primaryArchetype === "fi_treasury_fund"
    ? explicitFundCollateralUtility
      ? "Expected value depends mainly on reliable fund-unit records, operational utility, reporting clarity, lifecycle control and the explicitly stated collateral utility for professional or institutional users."
      : `Expected value depends mainly on reliable fund-unit records, operational utility, reporting clarity, ${fundLifecycleScope.phrase}, and controlled transfers for professional or institutional users.`
    : primaryArchetype === "permissioned_defi_lending"
    ? "Expected value depends on enforceable pool rights, collateral and oracle transparency, reliable liquidation and digital-cash settlement, controlled governance and robust incident recovery."
    : primaryArchetype === "closed_workflow_infrastructure"
    ? "Expected value depends mainly on enforceable rights, transfer controls, custody reliability, and operational resilience rather than on broader access or speculative liquidity."
    : primaryArchetype === "private_credit_notes"
    ? "Expected value depends mainly on enforceable cash-flow rights, disciplined transfer mechanics, reporting quality and a workable process for the user-selected professional and qualified/accredited labels; local legal mapping remains unconfirmed."
    : primaryArchetype === "real_estate_income_wrapper"
    ? `Expected value depends mainly on whether investors can gain a clearer income-linked exposure through a ${issuance || "structured"} note setup${sales.length ? ` marketed across ${sales.join(", ")}` : ""}, with better reporting and a user-stated transfer process that is independently validated rather than assumed to provide broad market liquidity.`
    : "Capture access/liquidity value if rights are enforceable and the operating model is credible.",
      scores: stakeholderScores("investors"),
      bullets: bulletsInvestors(),
    },
    {
      key: "infrastructure",
      title: "Infrastructure providers",
      subtitle:
  primaryArchetype === "fi_treasury_fund"
    ? explicitFundCollateralUtility
      ? "Expected value depends on supplying institution-grade fund administration, authoritative-record integration, custody/control, reporting, cash-leg coordination and the explicitly stated collateral-enablement capability."
      : "Expected value depends on supplying institution-grade fund administration, authoritative-record integration, custody/control, reporting and reliable investor-lifecycle and cash-leg workflows."
    : primaryArchetype === "permissioned_defi_lending"
    ? "Expected value depends on supplying reliable smart-contract, oracle, collateral, liquidation, custody, cash-leg, governance and recovery capabilities for a tightly permissioned lending model."
    : primaryArchetype === "closed_workflow_infrastructure"
    ? "Expected value depends on supplying controlled settlement, custody, reconciliation, and operational-governance capabilities that address the stated post-trade friction rather than merely adding a token layer."
    : primaryArchetype === "private_credit_notes"
    ? "Expected value depends on servicing discipline, cash-flow reporting, custody/control, and compliant transfer workflows for a tightly scoped private-credit product."
    : primaryArchetype === "real_estate_income_wrapper"
    ? `Expected value depends on whether providers can support a ${issuance || "structured"}-anchored wrapper${sales.length ? ` distributed across ${sales.join(", ")}` : ""}, with reporting, custody and any user-stated transfer mechanics that are operationally credible without assuming secondary-market depth.`
    : "Platforms, custodians, transfer agents, venues, and settlement/payment partners.",
      scores: stakeholderScores("infrastructure"),
      bullets: bulletsInfrastructure(),
    },
  ];

    const stakeholderHeadingNumber = {
    asset_owner: "4.2",
    investors: "4.3",
    infrastructure: "4.4",
  };

  const stakeholderHeading = (s) => {
    const localizedTitle = primaryArchetype === "closed_workflow_infrastructure" || primaryArchetype === "issuance_lifecycle_optimization"
      ? s.title
      : labels.languageCode === "en"
      ? s.title
      : (s.key === "asset_owner" ? labels.assetOwnerIssuer : s.key === "investors" ? labels.investors : labels.infrastructureProviders);
    return stakeholderHeadingNumber[s.key]
      ? `${stakeholderHeadingNumber[s.key]} ${localizedTitle}`
      : localizedTitle;
  };

  const displayStakeholder = (s) => {
    if (primaryArchetype === "closed_workflow_infrastructure") {
      const workflow = declaredWarehouseReceipt
        ? warehouseReceiptStakeholders?.[s.key]
        : workflowStakeholders?.[s.key];
      if (workflow) {
        return {
          ...s,
          title: workflow.title,
          subtitle: workflow.subtitle,
          bullets: {
            plus: [
              ...(workflow.plus || []),
              ...(qualifiedWorkflowTimeHorizon && s.key === "asset_owner" && workflowTimeHorizonCopy?.asset_owner_plus
                ? [workflowTimeHorizonCopy.asset_owner_plus]
                : []),
            ],
            minus: [
              ...(workflow.minus || []),
              ...(qualifiedWorkflowTimeHorizon && s.key === "asset_owner" && workflowTimeHorizonCopy?.asset_owner_minus
                ? [workflowTimeHorizonCopy.asset_owner_minus]
                : []),
            ],
            next: [
              ...(workflow.next || []),
              ...(qualifiedWorkflowTimeHorizon && s.key === "asset_owner" && workflowTimeHorizonCopy?.asset_owner_next
                ? [workflowTimeHorizonCopy.asset_owner_next]
                : []),
            ],
          },
        };
      }
    }

    if (primaryArchetype === "issuance_lifecycle_optimization") {
      const issuanceLifecycle = issuanceLifecycleStakeholders?.[s.key];
      if (issuanceLifecycle) {
        return {
          ...s,
          title: issuanceLifecycle.title,
          subtitle: issuanceLifecycle.subtitle,
          bullets: {
            plus: issuanceLifecycle.plus || [],
            minus: issuanceLifecycle.minus || [],
            next: issuanceLifecycle.next || [],
          },
        };
      }
    }

    const capitalFormationAssetOwner =
      qualifiedCapitalFormationInterpretation &&
      s.key === "asset_owner" &&
      capitalFormationCopy;

    const g = genericStakeholders?.[s.key];
    const localizedBase = g && labels.languageCode !== "en"
      ? { ...s, title: stakeholderHeading(s).replace(/^4\.\d\s+/, ""), subtitle: g.subtitle, bullets: { plus: g.plus, minus: g.minus, next: g.next } }
      : s;

    if (!capitalFormationAssetOwner) return localizedBase;

    return {
      ...localizedBase,
      bullets: {
        ...localizedBase.bullets,
        plus: [
          ...(localizedBase.bullets?.plus || []),
          ...(capitalFormationCopy?.asset_owner_plus ? [capitalFormationCopy.asset_owner_plus] : []),
        ],
        next: [
          ...(localizedBase.bullets?.next || []),
          ...(capitalFormationCopy?.asset_owner_next ? [capitalFormationCopy.asset_owner_next] : []),
        ],
      },
    };
  };
  
  const assetOwnerStake = stakeholders.find((s) => s.key === "asset_owner")?.scores;
const investorStake = stakeholders.find((s) => s.key === "investors")?.scores;
const infrastructureStake = stakeholders.find((s) => s.key === "infrastructure")?.scores;
const allStakeholdersPositive = [assetOwnerStake, investorStake, infrastructureStake]
  .every((score) => score && Number(score.net) > 0);

const explicitVendorCaptureConclusion =
  !qualifiedWorkflowTimeHorizon &&
  assetOwnerStake &&
  investorStake &&
  infrastructureStake &&
  assetOwnerStake.net <= -4 &&
  infrastructureStake.net >= 1 &&
  investorStake.net <= 0
    ? primaryArchetype === "fi_treasury_fund"
      ? "Under the current initial configuration, and before scale, reuse, or collateral-utility benefits are proven, this tokenization appears commercially more attractive to service providers than to the bank itself."
      : primaryArchetype === "closed_workflow_infrastructure"
      ? "Under the current initial configuration, this workflow appears commercially more attractive to service providers than to the initiating institution. Rebalance delivery burden, fees and measurable operational commitments before scale-up."
      : qualifiedCapitalFormationInterpretation && capitalFormationCopy?.vendor_capture_conclusion
      ? capitalFormationCopy.vendor_capture_conclusion
      : "Under the current initial configuration, this tokenization appears commercially more attractive to service providers than to the initiating Asset Owner."
    : "";

  const explicitStakeholderGapNote =
  assetOwnerStake &&
  investorStake &&
  (
    (assetOwnerStake.tokenization_costs - overall.tokenization_costs >= 3) ||
    (investorStake.new_risks - overall.new_risks >= 3)
  )
    ? [
        assetOwnerStake.tokenization_costs - overall.tokenization_costs >= 3
          ? "Here, overall project TC can remain moderate while Asset Owner TC is materially higher, because setup, integration, legal/compliance work, and delivery burden are concentrated on the initiating party rather than spread evenly across the structure."
          : null,
        investorStake.new_risks - overall.new_risks >= 3
          ? "Likewise, stakeholder NR can exceed overall project NR where a specific party bears more custody, transfer, disclosure, or exit-path uncertainty than the structure as a whole."
          : null,
      ].filter(Boolean).join(" ")
    : "";
  
  function scoreBadge(net) {
    if (net >= 4) return "strong positive";
    if (net >= 1) return "positive";
    if (net === 0) return "neutral";
    if (net <= -4) return "strong negative";
    return "negative";
  }

  const whoBearsCopy =
    qualifiedWorkflowTimeHorizon && workflowTimeHorizonCopy?.who_bears
      ? workflowTimeHorizonCopy.who_bears
      : qualifiedCapitalFormationInterpretation && capitalFormationCopy?.who_bears
      ? capitalFormationCopy.who_bears
      : getLocalizedWhoBearsCopy(labels.languageCode);

  // ---------- Shared constraints (practical, case-tied) ----------
  const constraints = [];
  if (missing.length) {
    // Keep it short and readable
    const top = missing.slice(0, 4).map((x) => safeStr(x));
    for (const item of top) constraints.push(item);
  } else {
    // fallback based on flags
    if (!safeStr(p.taxNotes).trim()) constraints.push("Tax considerations (not specified)");
    if (/wrapper/i.test(instrumentType)) constraints.push("Legal enforceability of wrapper/token-holder rights");
    if (hasMtfAts) constraints.push("Liquidity realism (avoid over-promising continuous liquidity)");
  }

  // ---------- Render ----------
  return (
    <div className="sectionCard">
      <h3>4. {labels.stakeholderAddedValue}</h3>

      <p className="muted">
        {labels.languageCode === "en" ? (<>This chapter translates the deep assessment into a stakeholder view: <b>who captures the upside</b>, <b>who carries the costs/risks</b>, and <b>what must be agreed</b> for this tokenization case to work. Stakeholder scores use the <span className="mono">same 5-factor Added Value framework</span> as the overall project score.</>) : localizedStakeholderIntro()}
      </p>

        <div className="card">
        <h4 style={{ marginTop: 0, marginBottom: 6 }}>4.1 {labels.stakeholderScores}</h4>
        <ul style={{ marginTop: 2, marginBottom: 6, paddingLeft: 18 }}>
          {issuance && <li><b>{primaryArchetype === "closed_workflow_infrastructure" ? labels.workflowOperatingJurisdiction : labels.issuance}:</b> {localizedCountryName(issuance)}</li>}
          {sales.length > 0 && <li><b>{primaryArchetype === "closed_workflow_infrastructure" ? labels.workflowParticipantJurisdiction : labels.sales}:</b> {localizedCountryList(sales)}</li>}
          {instrumentType && <li><b>{labels.instrumentType}:</b> {localizedInputValue(instrumentType, "instrument")}</li>}
          {custodyModel && <li><b>{labels.custody}:</b> {localizedInputValue(custodyModel, "custody")}</li>}
          {venue && <li><b>{labels.venue}:</b> {localizedInputValue(venue, "venue")}</li>}
          {liquidity && <li><b>{labels.liquidity}:</b> {localizedInputValue(liquidity, "liquidity")}</li>}
          {settlement && <li><b>{labels.settlement}:</b> {localizedInputValue(settlement, "settlement")}</li>}
          {primaryArchetype === "closed_workflow_infrastructure" ? (
            <li>
              <b>{labels.workflowOfferingScope || "Public offering / investor distribution"}:</b>{" "}
              {labels.workflowOfferingNotInScope || "Not in scope for this closed institutional workflow"}
            </li>
          ) : (
            disclosure && <li><b>{labels.disclosure}:</b> {localizedInputValue(disclosure, "disclosure")}</li>
          )}
          {transfers && <li><b>{labels.transfers}:</b> {localizedInputValue(transfers, "transfers")}</li>}
          <li>
            <b>{primaryArchetype === "closed_workflow_infrastructure" ? labels.workflowParticipantScope : labels.investorScope}:</b> {localizedInvestorScope()}
          </li>
          {hintText && <li><b>{labels.assetStructureHints}:</b> {hintText}</li>}
          <li><b>{labels.overallVerdict}:</b> {localizeVerdictLabel(result?.verdict, labels.languageCode)}</li>
          <li>
            <b>{labels.overallAddedValue}:</b> {fmt2(overall.net)}{" "}
            <span className="muted">
              ({labels.plus} {fmt2(overall.plus)} − {labels.minus} {fmt2(overall.minus)})
            </span>
          </li>
        </ul>
        {primaryArchetype !== "closed_workflow_infrastructure" && (
          <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>
            {getInterfaceLabelBoundaryNote(labels.languageCode)}
          </p>
        )}
      </div>

      <div className="card">
        <h4 style={{ marginTop: 0, marginBottom: 6 }}>{labels.caseSnapshot}</h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 6px" }}>{labels.stakeholder}</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>NO</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>CS</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>RR</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>TC</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>NR</th>
                <th style={{ textAlign: "center", padding: "8px 6px" }}>{labels.net}</th>
              </tr>
            </thead>
            <tbody>
              {stakeholders.map((raw) => {
                const s = displayStakeholder(raw);
                return (
                  <tr key={s.key} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "10px 6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <b>{s.title}</b>
                      </div>
                      <div className="muted small">{s.subtitle}</div>
                    </td>
                
<td style={{ textAlign: "center" }}>{fmt1(s.scores.new_opportunities)}</td>
<td style={{ textAlign: "center" }}>{fmt1(s.scores.cost_savings)}</td>
<td style={{ textAlign: "center" }}>{fmt1(s.scores.risk_reduction)}</td>
<td style={{ textAlign: "center" }}>{fmt1(s.scores.tokenization_costs)}</td>
<td style={{ textAlign: "center" }}>{fmt1(s.scores.new_risks)}</td>
<td style={{ textAlign: "center" }}><b>{fmt2(s.scores.net)}</b></td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

       <p className="muted small" style={{ marginTop: 10 }}>
  {getLocalizedStakeholderScoreNote(labels.languageCode)}
</p>

{localizedStakeholderGapNote() && (
  <p className="muted small" style={{ marginTop: 8 }}>
    {localizedStakeholderGapNote()}
  </p>
)}

{qualifiedWorkflowTimeHorizon && workflowTimeHorizonCopy?.table_note && (
  <div
    className="small stakeholder-interpretation-note"
    style={{
      marginTop: 8,
      padding: "8px 10px",
      border: "1px solid rgba(0,0,0,0.10)",
      borderRadius: 8,
      background: "rgba(255,122,0,0.05)",
      lineHeight: 1.4,
    }}
  >
    <strong>{workflowTimeHorizonCopy.heading}:</strong>{" "}
    {workflowTimeHorizonCopy.table_note}
  </div>
)}

{qualifiedCapitalFormationInterpretation && capitalFormationCopy?.table_note && (
  <div
    className="small stakeholder-interpretation-note"
    style={{
      marginTop: 8,
      padding: "8px 10px",
      border: "1px solid rgba(0,0,0,0.10)",
      borderRadius: 8,
      background: "rgba(255,122,0,0.05)",
      lineHeight: 1.4,
    }}
  >
    <strong>{capitalFormationCopy.heading}:</strong>{" "}
    {capitalFormationCopy.table_note}
  </div>
)}

        {result?.stakeholder_methodology_note && labels.languageCode === "en" && (
          <p className="muted small" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
            {result.stakeholder_methodology_note}
          </p>
        )}
      </div>

      {stakeholders.map((raw) => {
        const s = displayStakeholder(raw);
        return (
        <div className="card stakeholder-detail-card" key={s.key}>
          <h3 className="stakeholder-detail-title">{stakeholderHeading(s)}</h3>

          <div className="grid-2">
            <div>
              <h4 className="stakeholder-detail-subtitle">{labels.expectedValuePlus}</h4>
              <ul>
                {s.bullets.plus.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>

            <div>
              <h4 className="stakeholder-detail-subtitle">{labels.costsRisksMinus}</h4>
              <ul>
                {s.bullets.minus.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          </div>

          <h4 className="stakeholder-detail-subtitle">{labels.agreeNext}</h4>
          <ul>
            {s.bullets.next.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
        );
      })}

      <div className="card">
        <h3>4.5 {labels.whoBearsCostsRisks}</h3>
       <p className="muted">
      {whoBearsCopy.intro}
    </p>
        {explicitVendorCaptureConclusion && (
  <p className="muted" style={{ marginTop: 8 }}>
    <b>{labels.caseSpecificConclusion}:</b> {labels.languageCode === "en" ? explicitVendorCaptureConclusion : whoBearsCopy.vendorCaptureConclusion}
  </p>
)}
        <ul>
          <li>{whoBearsCopy.assetOwnerNegative}</li>
          <li>{whoBearsCopy.investorNegative}</li>
          {allStakeholdersPositive && <li>{whoBearsCopy.everyonePositive}</li>}
        </ul>
      </div>
    </div>
  );
}

function LegalInfoModal({ type, onClose }) {
  if (!type) return null;

  const isTerms = type === "terms";
  const isPrivacy = type === "privacy";
  const isDisclaimer = type === "disclaimer";

  const title = isTerms
    ? "Terms of Service"
    : isPrivacy
    ? "Privacy Policy"
    : "Disclaimer";

  const updated = "Last updated: 3 August 2026";

  const termsSections = [
    {
      heading: "1. Scope of the service",
      paragraphs: [
        "TVAC is an informational decision-support tool designed to help users assess whether a proposed tokenization case appears likely to create added value compared with conventional alternatives.",
        "TVAC does not provide legal, regulatory, financial, tax, accounting, investment, credit, yield, valuation, technical structuring, suitability, or underwriting advice. Outputs must be independently reviewed by qualified professionals before any decision is made."
      ],
    },
    {
      heading: "2. User responsibility",
      paragraphs: [
        "You are responsible for the quality, accuracy, completeness, and lawfulness of the information you submit. TVAC outputs depend on the inputs provided and on assumptions that may be incomplete, uncertain, or later become outdated.",
        "You must not use TVAC outputs as investment approval, legal opinion, regulatory approval, due diligence report, investor recommendation, credit assessment, valuation opinion, or confirmation of commercial feasibility."
      ],
    },
    {
      heading: "3. Access, payment, and report credits",
      paragraphs: [
        "Access may be provided through single-report purchases, subscriptions, internal/staff access, or other access models made available by TVAC. Payment processing may be handled by Stripe or another payment provider.",
        "Unless otherwise stated, a report credit is used when a full assessment is generated. Once a report has been generated, refunds are generally not available except where required by applicable law or where TVAC has made a clear billing or access error."
      ],
    },
    {
      heading: "4. Availability and support",
      paragraphs: [
        "TVAC is provided on a best-efforts basis. The service may be unavailable, delayed, interrupted, or changed due to maintenance, hosting issues, third-party service outages, software updates, API limitations, or other technical factors.",
        "Support is provided by email at support@tvacai.com. Response times may vary. No guaranteed response time, service level, uptime, or dedicated support team is promised unless separately agreed in writing."
      ],
    },
    {
      heading: "5. Third-party services",
      paragraphs: [
        "TVAC may rely on third-party infrastructure and service providers, including hosting, database, payment, email, analytics, and AI model providers. These providers may process data only as necessary to operate, secure, support, and maintain the service or comply with applicable obligations.",
        "TVAC is not responsible for third-party outages, delays, security events, or changes outside TVAC's reasonable control."
      ],
    },
    {
      heading: "6. Intellectual property and permitted use",
      paragraphs: [
        "TVAC, its methodology, software, scoring logic, user interface, report structure, text, branding, and related materials are protected by intellectual property rights. You may use generated reports for your own internal analysis and professional discussion, subject to these terms.",
        "You may not copy, reverse engineer, resell, scrape, reproduce, publish, or commercially exploit TVAC, its methodology, or its outputs as a competing service without prior written permission."
      ],
    },
    {
      heading: "7. Limitation of liability",
      paragraphs: [
        "To the maximum extent permitted by law, TVAC and Astoval are not liable for losses arising from reliance on TVAC outputs, including commercial losses, investment losses, regulatory outcomes, implementation choices, missed opportunities, data errors, service interruptions, or third-party provider issues.",
        "Users remain solely responsible for all commercial, legal, regulatory, operational, investment, strategic, and implementation decisions related to any tokenization initiative."
      ],
    },
    {
      heading: "8. Contact",
      paragraphs: [
        "For access, payment, or technical issues, contact support@tvacai.com."
      ],
    },
  ];

  const privacySections = [
    {
      heading: "1. Who operates TVAC",
      paragraphs: [
        "For the current pre-launch/service phase, TVAC is operated by Michael Juul Rugaard / Astoval. This may later be updated to Astoval ApS or another legal entity if the service is formally transferred.",
        "Contact: support@tvacai.com."
      ],
    },
    {
      heading: "2. What data TVAC may process",
      paragraphs: [
        "TVAC may process information needed to provide the service, including email address, access or payment status, free-text case descriptions, structured assessment inputs, report language, assessment outputs, support requests, and limited technical or security logs.",
        "Do not submit passwords, private keys, wallet seed phrases, identification documents, special-category personal data, regulated client records, or information you are not authorised to share. Use anonymised names, ranges, and placeholders where practical."
      ],
    },
    {
      heading: "3. How case content is used",
      paragraphs: [
        "For standard self-service reports, the free-text case description and structured answers are used to generate and deliver the assessment requested by the user and for strictly necessary operation, security, and troubleshooting.",
        "TVAC does not create or maintain a customer-derived training or fine-tuning dataset, use case content to benchmark other customers, build unrelated products, sell case data, or disclose it to partners, investors, or other customers."
      ],
    },
    {
      heading: "4. Legal basis",
      paragraphs: [
        "Depending on the situation, processing may be based on performance of a contract or pre-contractual steps, legitimate interests in operating and securing the service, compliance with legal obligations, or consent where consent is specifically requested."
      ],
    },
    {
      heading: "5. Service providers and AI processing",
      paragraphs: [
        "TVAC uses technical service providers where needed for hosting, access management, database functions, payment processing, AI processing, email delivery, monitoring, and support. Providers may include Render, Supabase, OpenAI, Stripe, Resend, Google Workspace, and related infrastructure providers.",
        "Astoval has disabled voluntary sharing of API inputs and outputs with OpenAI and has disabled ordinary API-call logging on the OpenAI Platform. OpenAI states that API data is not used to train or improve its models unless the API customer explicitly opts in. TVAC also sends production assessment requests with optional application-state storage disabled. Under OpenAI's standard data controls, API content and related metadata may nevertheless be retained in separate internal abuse-monitoring logs for up to 30 days.",
        "Payment card details are handled by payment providers such as Stripe and are not intended to be stored directly by TVAC."
      ],
    },
    {
      heading: "6. International processing",
      paragraphs: [
        "Because TVAC uses cloud, payment, email, and AI infrastructure providers, data may be processed in countries outside your own country or outside the European Economic Area. Where required, appropriate contractual or organisational safeguards should be used."
      ],
    },
    {
      heading: "7. Retention of case content",
      paragraphs: [
        "For standard self-service reports, the submitted case input is processed transiently during the request and is not intentionally written to a persistent customer-case database or reusable account history.",
        "The input and generated report remain in the active browser page's working memory while the page is open, so the user can review, localise, print, or save the report. TVAC does not write the standard case or report to browser local storage. They are normally cleared from the browser when the page is reloaded or the tab is closed. Any PDF saved by the user remains on the user's own device.",
        "The generated assessment may remain in short-lived TVAC server-memory caches for no more than one hour to support report delivery, language preparation, and technical consistency. It is then deleted automatically, and a server restart clears it sooner. The current standard self-service flow does not retain a permanent server-side PDF copy.",
        "OpenAI's separate internal abuse-monitoring logs may retain API content and related metadata for up to 30 days under its standard data controls, even though voluntary data sharing, ordinary API-call logging, and optional application-state storage are disabled.",
        "If Scenario Lab is purchased from a completed report, a temporary baseline snapshot may be kept in that browser tab's session storage for up to two hours to restore the report after checkout. It is removed after successful restoration, expiry, or the end of the browser session.",
        "Partner, API, white-label, or institutional workspaces may intentionally retain submitted inputs, assessment results, and report files to provide private report history, status tracking, and downloads. Those arrangements are separately governed and should not be treated as standard self-service use.",
        "Account, payment, entitlement, support, security, and basic technical records may be retained for as long as reasonably necessary for the relevant purpose, legal obligations, dispute handling, or accounting."
      ],
    },
    {
      heading: "8. Your rights",
      paragraphs: [
        "Depending on applicable law, you may have rights to request access, correction, deletion, restriction, portability, or objection to certain processing of your personal data, and you may have the right to lodge a complaint with a supervisory authority.",
        "To make a privacy-related request, contact support@tvacai.com."
      ],
    },
    {
      heading: "9. Security",
      paragraphs: [
        "TVAC uses reasonable technical and organisational measures to protect the service and the data processed through it. However, no online service can guarantee absolute security, and TVAC should not be used as a secure document repository."
      ],
    },
  ];

  const sections = isTerms ? termsSections : privacySections;

  return (
    <div className="tvac-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="legal-info-modal-title">
      <div className="tvac-modal card" style={{ maxWidth: 860 }}>
        <div className="tvac-modal-header">
          <div>
            <h3 id="legal-info-modal-title" style={{ margin: 0 }}>{title}</h3>
            <div className="muted small" style={{ marginTop: 4 }}>
              {updated}
            </div>
          </div>
          <button className="tvac-modal-close" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="disclaimer small" style={{ lineHeight: 1.55, marginTop: 10 }}>
          {isDisclaimer ? (
            ENGLISH_FULL_DISCLAIMER.map((para, i) => (
              <p key={`footer-disclaimer-${i}`}>{para}</p>
            ))
          ) : (
            <>
              {sections.map((section) => (
                <div key={section.heading} style={{ marginTop: 14 }}>
                  <h4 style={{ margin: "0 0 6px 0" }}>{section.heading}</h4>
                  {section.paragraphs.map((para, i) => (
                    <p key={`${section.heading}-${i}`} style={{ marginTop: 0 }}>{para}</p>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="action" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- App ---------- */

export default function App({ apiReportRenderToken = "", apiReportRenderMode = false } = {}) {
  const [view, setView] = useState("tool"); // "tool" or "about"
  const [legalModal, setLegalModal] = useState(null); // "terms", "privacy", or "disclaimer"
  const [uiLanguageCode, setUiLanguageCode] = useState(() => {
    if (typeof window === "undefined") return "en";
    try {
      const queryLanguage = new URLSearchParams(window.location.search).get("lang") || "";
      const storedLanguage = window.localStorage?.getItem("tvac_ui_language") || "";
      return normalizeUiLanguageCode(queryLanguage || storedLanguage || "en");
    } catch {
      return "en";
    }
  });
  const t = (key, vars = {}) => uiText(uiLanguageCode, key, vars);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage?.setItem("tvac_ui_language", uiLanguageCode);
    } catch {}
  }, [uiLanguageCode]);

  const handleContactClick = () => {
  setView("tool");
  setTimeout(() => {
    const reportEl = document.getElementById("section-contact");
    const siteEl = document.getElementById("site-contact-footer");
    const el = reportEl || siteEl;
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, 0);
};
 
  return (
    <AccessGate>
      <div className={apiReportRenderMode ? "container tvac-api-render-container" : "container"}>
        <PrintStyles />
        {!apiReportRenderMode && <LegalInfoModal type={legalModal} onClose={() => setLegalModal(null)} />}
        {/* Top navigation bar */}
        {!apiReportRenderMode && <div className="topbar">
          <div className="topbar-left">
            <div className="logo-pill">TVAC</div>
            <div className="topbar-title">Tokenization Value-Add Calculator</div>
          </div>
          <div className="topbar-right">
            <label className="small muted" htmlFor="tvac-interface-language" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span>{t("language.label")}</span>
              <select
                id="tvac-interface-language"
                value={uiLanguageCode}
                onChange={(e) => setUiLanguageCode(normalizeUiLanguageCode(e.target.value))}
                aria-label={t("language.label")}
                style={{ minWidth: 150 }}
              >
                {UI_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={`topbar-link ${
                view === "about" ? "topbar-link-active" : ""
              }`}
              onClick={() => setView("about")}
            >
              {t("nav.about")}
            </button>
            <button
              type="button"
              className="topbar-link"
              onClick={handleContactClick}
            >
              {t("nav.contact")}
            </button>
          </div>
        </div>}

        {/* Hero / intro only on main tool view */}
        {!apiReportRenderMode && view === "tool" && (
          <header className="hero">
            <h1 className="hero-title">{t("hero.title")}</h1>
            <p className="hero-tagline">
              {t("hero.tagline")}
            </p>
            <p className="hero-intro muted">
              {t("hero.intro")}
            </p>
            <FormulaBox uiLanguageCode={uiLanguageCode} />
          </header>
        )}

        {/* Main content */}
        {view === "tool" || apiReportRenderMode ? (
          <AnalyzerView apiReportRenderToken={apiReportRenderToken} apiReportRenderMode={apiReportRenderMode} uiLanguageCode={uiLanguageCode} />
        ) : (
          <AboutView onBack={() => setView("tool")} uiLanguageCode={uiLanguageCode} />
        )}

        {/* Footer (fælles) */}
{!apiReportRenderMode && <footer id="site-contact-footer">
         
          <div className="contact-block">
            <div>
              <strong>{t("footer.contactTitle")}</strong>
            </div>
            <div>{t("footer.contactIntro")}</div>
            <div>
              <a href="mailto:support@tvacai.com">support@tvacai.com</a>
            </div>
            <div className="muted small">
              {t("footer.supportNote")}
            </div>
          </div>

          <p className="disclaimer small" style={{ marginTop: 10 }}>
            <strong>{t("footer.disclaimerTitle")}.</strong> {t("footer.disclaimer")}
          </p>

          <div className="small" style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setLegalModal("terms")}
              style={{
                color: "#1a73e8",
                textDecoration: "underline",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              {t("footer.terms")}
            </button>
            <span className="muted"> · </span>
            <button
              type="button"
              onClick={() => setLegalModal("privacy")}
              style={{
                color: "#1a73e8",
                textDecoration: "underline",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              {t("footer.privacy")}
            </button>
            <span className="muted"> · </span>
            <button
              type="button"
              onClick={() => setLegalModal("disclaimer")}
              style={{
                color: "#1a73e8",
                textDecoration: "underline",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              {t("footer.disclaimerLink")}
            </button>
          </div>

          <div className="copy">
            <span className="print-page-number" aria-hidden="true" />
            © 2026 Astoval · All rights reserved.
          </div>
        </footer>}
      </div>
    </AccessGate>
  );
}
